/**
 * Dicionários + `genUiT`/`resolveGenUiLocale` — o catálogo de copy da UI
 * generativa. Mora no contrato porque builders traduzem com ele
 * (`hotel_room_identity_label.ts`, via `hotel_rooms_builder.ts`). O hook
 * `useGenUiI18n`, que PRECISA de `useGenUiConfig`, fica no kit
 * (`shared/ui/i18n.ts`, que reexporta daqui).
 */

export type GenUiLocale = 'pt-BR' | 'en' | 'es';

type Values = Record<string, string | number | null | undefined>;

const ptBR = {
  'common.close': 'Fechar',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.copyFailed': 'Não foi possível copiar',
  'common.total': 'Total',
  'common.perPerson': 'Por pessoa',
  'common.direct': 'Direto',
  'common.notInformed': 'Não informado',
  'common.toConfirm': 'A confirmar',
  'common.toDefine': 'A definir',
  'common.loadingMore': 'Carregando mais…',
  'common.tryAgain': 'Tentar de novo',
  // O pedido OFFLINE à equipe VOLL no pé das páginas determinísticas de resultado (hotel e voo) — o concierge do legado. A copy por domínio mora em `hotel.results.offline.*`/`flightExplorer.offline.*`; o checkout do pedido é `conciergeCheckout.*`.
  'resultsOffline.offerAction': 'Pedir à equipe VOLL',
  'consultPlaces.aria': 'Lugares recomendados na consulta',
  'consultPlaces.title': 'Lugares para conhecer',
  'consultPlaces.place': 'Lugar',
  'consultPlaces.rating': 'Nota {{rating}} de 5',
  'consultPlaces.open': 'Aberto agora',
  'consultPlaces.closed': 'Fechado agora',
  'consultPlaces.details': 'Ver detalhes — {{place}}',
  'consultPlaces.loadingPlace': 'Carregando lugar…',
  'consultPlaces.placeErrorAt': 'Não foi possível carregar o lugar {{position}} da lista.',
  'consultPlaces.loadingMap': 'Carregando mapa…',
  'consultPlaces.mapAria': 'Mapa dos lugares recomendados',
  'consultPlaces.mapUnavailable': 'Mapa indisponível nesta sessão',
  'consultPlaces.mapError':
    'Não foi possível carregar o mapa. Os lugares continuam disponíveis abaixo.',
  'consultPlaces.retryMap': 'Tentar de novo',
  'consultPlaces.list': 'Lista de lugares',
  'consultPlaces.detailsShort': 'Ver detalhes',
  'consultPlaces.loadingDetails': 'Carregando detalhes…',
  'consultPlaces.detailsError': 'Não foi possível carregar os detalhes deste lugar.',
  'consultPlaces.photos': 'Fotos do lugar',
  'consultPlaces.address': 'Endereço',
  'consultPlaces.phone': 'Telefone',
  'consultPlaces.website': 'Site',
  'consultPlaces.openWebsite': 'Abrir site',
  'consultPlaces.hours': 'Horários',
  'consultPlaces.reviews': 'Avaliações',
  'consultPlaces.sourceGoogle': 'Ver avaliação no Google Maps',
  'consultPlaces.sourcePlaceGoogle': 'Informações do Google Maps',
  'consultPlaces.expandedMap': 'Mapa ampliado',
  'consultPlaces.referencePlace': 'Lugar de referência',
  'consultPlaces.expandMap': 'Ampliar mapa',
  'consultPlaces.openGoogleMaps': 'Abrir no Google Maps',
  'nextServices.hotel': 'Reservar hotel',
  'nextServices.flight': 'Buscar voos',
  'nextServices.seat': 'Reservar assento',
  'nextServices.car': 'Alugar um carro',
  'nextServices.train': 'Buscar trens',
  'nextServices.bus': 'Buscar ônibus',
  'nextServices.insurance': 'Adicionar seguro viagem',
  'nextServices.transferToAirport': 'Carro até o aeroporto',
  'nextServices.transferAirportHotel': 'Carro do aeroporto ao hotel',
  'nextServices.transferToHotel': 'Carro até o hotel',
  'nextServices.recommended': 'Recomendado',
  'nextServices.note': 'Você pode adicionar o resto depois, diretamente na página da sua viagem.',
  'flight.nextServices.ask.hotelRange': 'Quero reservar um hotel em {{city}} de {{from}} a {{to}}.',
  'flight.nextServices.ask.hotelOpen': 'Quero reservar um hotel em {{city}} a partir de {{from}}.',
  'flight.nextServices.ask.hotelBare': 'Quero reservar um hotel em {{city}}.',
  'flight.nextServices.ask.seat': 'Quero escolher meus assentos do voo que acabei de reservar.',
  'flight.nextServices.ask.flight': 'Quero buscar outro voo.',
  'flight.nextServices.ask.carRange': 'Quero alugar um carro em {{city}} de {{from}} a {{to}}.',
  'flight.nextServices.ask.carOpen': 'Quero alugar um carro em {{city}} a partir de {{from}}.',
  'flight.nextServices.ask.carBare': 'Quero alugar um carro em {{city}}.',
  'flight.nextServices.ask.train': 'Quero buscar passagens de trem para {{city}}.',
  'flight.nextServices.ask.bus': 'Quero buscar passagens de ônibus para {{city}}.',
  'flight.nextServices.ask.insurance': 'Quero adicionar seguro viagem à minha viagem.',
  'flight.nextServices.ask.transferToAirport': 'Quero agendar um carro até o aeroporto.',
  'flight.nextServices.ask.transferAirportHotel':
    'Quero agendar um carro do aeroporto até o hotel.',
  'flight.nextServices.ask.transferToHotel': 'Quero agendar um carro até o hotel.',
  'nextServices.ask.hotelRange': 'Quero reservar um hotel perto de {{place}} de {{from}} a {{to}}.',
  'nextServices.ask.hotelOpen': 'Quero reservar um hotel perto de {{place}} a partir de {{from}}.',
  'nextServices.ask.hotelBare': 'Quero reservar um hotel perto de {{place}}.',
  'nextServices.ask.hotelTrip': 'Quero reservar um hotel para essa viagem.',
  'nextServices.ask.carRange': 'Quero alugar um carro perto de {{place}} de {{from}} a {{to}}.',
  'nextServices.ask.carOpen': 'Quero alugar um carro perto de {{place}} a partir de {{from}}.',
  'nextServices.ask.carBare': 'Quero alugar um carro perto de {{place}}.',
  'nextServices.ask.carTrip': 'Quero alugar um carro para essa viagem.',
  'nextServices.ask.insurance': 'Quero adicionar seguro viagem à minha viagem.',
  'nextServices.ask.flight': 'Quero buscar um voo para esta viagem.',
  'nextServices.ask.seat': 'Quero escolher os assentos do meu voo.',
  'nextServices.ask.train': 'Quero buscar passagens de trem para essa viagem.',
  'nextServices.ask.bus': 'Quero buscar passagens de ônibus para essa viagem.',
  'nextServices.ask.transferToAirport': 'Quero agendar um carro até o aeroporto.',
  'nextServices.ask.transferAirportHotel': 'Quero agendar um carro do aeroporto até o hotel.',
  'nextServices.ask.transferToHotel': 'Quero agendar um carro até o hotel.',
  'hotel.nextServices.ask.carRange':
    'Quero alugar um carro perto do hotel {{hotel}}, de {{from}} a {{to}}.',
  'hotel.nextServices.ask.carOpen':
    'Quero alugar um carro perto do hotel {{hotel}} a partir de {{from}}.',
  'hotel.nextServices.ask.carBare': 'Quero alugar um carro perto do hotel {{hotel}}.',
  'hotel.nextServices.ask.carTrip': 'Quero alugar um carro para essa viagem.',
  'hotel.nextServices.ask.train': 'Quero buscar passagens de trem para chegar ao hotel {{hotel}}.',
  'hotel.nextServices.ask.trainTrip': 'Quero buscar passagens de trem para essa viagem.',
  'hotel.nextServices.ask.bus': 'Quero buscar passagens de ônibus para chegar ao hotel {{hotel}}.',
  'hotel.nextServices.ask.busTrip': 'Quero buscar passagens de ônibus para essa viagem.',
  'hotel.nextServices.ask.insurance': 'Quero adicionar seguro viagem à minha viagem.',
  'hotel.nextServices.ask.seat': 'Quero escolher os assentos do meu voo.',
  'hotel.nextServices.ask.flight': 'Quero buscar um voo para esta viagem.',
  'hotel.nextServices.ask.hotel': 'Quero reservar mais um hotel nessa viagem.',
  'hotel.nextServices.ask.transferToAirport': 'Quero agendar um carro até o aeroporto.',
  'hotel.nextServices.ask.transferAirportHotel': 'Quero agendar um carro do aeroporto até o hotel.',
  'hotel.nextServices.ask.transferToHotel': 'Quero agendar um carro até o hotel.',
  /* O convite acima dos atalhos. Duas variantes porque a conversa tem dois
   * donos possíveis (#2952): a viagem do próprio cliente e o pedido para OUTRA
   * pessoa — "reservar você mesmo" lido logo depois de "quero pedir uma viagem
   * para Northon" sugeria que a viagem tinha virado a dele. Os dois falam de
   * ESCOLHER O SERVIÇO, então o que muda entre eles é só para quem. */
  'guidedFlows.label': 'Ou prefere escolher o serviço você mesmo?',
  'guidedFlows.label.onBehalf': 'Ou prefere escolher o serviço para {{traveler}}?',
  'guidedFlows.flow.flight': 'Voos',
  'guidedFlows.flow.hotel': 'Hotéis',
  'guidedFlows.flow.rental': 'Carros',
  'guidedFlows.flow.bus': 'Ônibus',
  'guidedFlows.reply.flight': 'Quero reservar um voo.',
  'guidedFlows.reply.hotel': 'Quero reservar um hotel.',
  'guidedFlows.reply.rental': 'Quero alugar um carro.',
  'guidedFlows.reply.bus': 'Quero uma passagem de ônibus.',
  /* O texto que o toque manda quando o pedido é para outra pessoa: sem o
   * viajante, o balão em 1ª pessoa desfazia o contexto de terceiro que o turno
   * anterior tinha acabado de estabelecer (#2952). */
  'guidedFlows.reply.flight.onBehalf': 'Quero reservar um voo para {{traveler}}.',
  'guidedFlows.reply.hotel.onBehalf': 'Quero reservar um hotel para {{traveler}}.',
  'guidedFlows.reply.rental.onBehalf': 'Quero alugar um carro para {{traveler}}.',
  'guidedFlows.reply.bus.onBehalf': 'Quero uma passagem de ônibus para {{traveler}}.',
  'flightOffers.title': 'Melhores voos',
  'flightOffers.outbound': 'Ida',
  'flightOffers.return': 'Volta',
  'flightOffers.itinerary_one': '{{count}} itinerário',
  'flightOffers.itinerary_other': '{{count}} itinerários',
  'flightOffers.seeAll': 'Ver todos os voos',
  'flightOffers.quoteSpecific': 'Quero cotar o voo {{carrier}} {{flightNumber}}{{where}}.',
  'flightOffers.quoteGeneric': 'Quero cotar essa opção.',
  'flightOffers.viewFares': 'Ver tarifas',
  'flightOffers.closeFares': 'Fechar tarifas',
  'flightOffers.faresBundled': 'Tarifas · ida + volta',
  'flightOffers.bothLegs': 'Ida e volta',
  'flightOffers.asymmetricBaggage':
    'Você despacha em um trecho só — {{article}} {{leg}} está sem despacho.',
  /** A variante BRANDA da assimetria: a outra perna despacha, mas cobrando.
   *  Taxa não é ausência — quem lê precisa saber que a mala CABE, só custa. */
  'flightOffers.asymmetricBaggageFee':
    'Você despacha de graça em um trecho só — {{article}} {{leg}} despacha mediante taxa.',
  'flightOffers.legArticleF': 'a',
  /* Rótulo do selo do eixo. Vem do CARD, não do wire: o `kind` é que é o dado
   * (`best|cheap|fast`), e o texto é decisão de UI — assim ele fala o idioma de
   * quem LÊ, não o do processo que produziu o card. "Recomendado" sem "VOLL"
   * porque a marca já está no glifo do selo. */
  'flight.badge.best': 'Recomendado',
  'flight.badge.cheap': 'Menor preço',
  'flight.badge.fast': 'Menos tempo',
  /* Legenda sob o preço: o que o número cobre. Uma frase fechada por tipo de
   * viagem, na ordem do desenho. */
  'flightOffers.baseOneWay': 'Só ida por',
  'flightQuote.oneWay': 'só ida',
  'flightQuote.roundTrip': 'ida e volta',
  'flightOffers.baseRoundTrip': 'Ida e volta por',
  'flightOffers.recommended': 'Recomendada',
  'flightOffers.loadingFares': 'Carregando tarifas…',
  'flightOffers.faresError': 'Não foi possível carregar as tarifas.',
  'flightOffers.faresStale': 'Esta opção expirou. Faça a busca de novo.',
  /** A companhia tarifou só a OUTRA perna do par (#6000): refazer a busca devolve o
   *  mesmo voo sem tarifa, então o caminho é escolher outro voo desta perna. */
  'flightOffers.faresLegUnavailableOutbound':
    'O voo de ida escolhido está sem tarifas disponíveis. Escolha outro voo.',
  'flightOffers.faresLegUnavailableReturn':
    'O voo de volta escolhido está sem tarifas disponíveis. Escolha outro voo.',
  'flightOffers.searchAgain': 'Buscar de novo',
  'flightOffers.searchingAgain': 'Buscando de novo…',
  /** A fala do viajante no turno do "Buscar de novo" (#5277): o agente lê o gesto
   *  estruturado, o texto é só a mensagem visível. */
  'flightOffers.searchAgainReply': 'Pode buscar de novo? Essas opções expiraram.',
  'flightOffers.seats': 'Assentos',
  'flightOffers.legOutbound': 'Ida',
  'flightOffers.legReturn': 'Volta',
  'flightOffers.legSingle': 'Voo',
  'flightOffers.fromPrice': 'A partir de',
  'flightOffers.slideAria': 'Oferta {{position}} de {{total}}',
  'flightOffers.pickedFares':
    'Selecionei a opção {{position}} ({{carrier}}) com a tarifa {{fares}}.',
  'flightOffers.chosen': 'Escolhido',
  'flightMultiOffers.title': 'Escolha o voo de cada trecho',
  'flightMultiOffers.leg': 'Trecho {{n}}',
  'flightMultiOffers.bestOptions': '{{count}} melhores opções',
  'flightMultiOffers.chosenCount': '{{chosen}} de {{total}} escolhidos',
  'flightMultiOffers.partialTotal': 'Total parcial',
  'flightMultiOffers.choosingNow': 'Escolhendo agora…',
  'flightMultiOffers.waiting': 'Aguardando…',
  'flightMultiOffers.waitingOutbound': 'Aguardando sua ida…',
  /* Rótulo da BOLHA do passo concluído. O tique substitui o número, e o glifo é
   * `aria-hidden` — sem isto o passo pronto chegava ao leitor de tela sem ordem
   * nenhuma e sem dizer que acabou (o "concluído" era só a cor verde). */
  'flightMultiOffers.stepDone': 'Trecho {{n}} concluído',
  'flightMultiOffers.acceptCheapestFares_one': 'Aceitar a tarifa mais barata (1)',
  'flightMultiOffers.acceptCheapestFares_other': 'Aceitar as tarifas mais baratas ({{count}})',
  'flightMultiOffers.acceptCheapestFaresShort_one': 'Tarifa mais barata (1)',
  'flightMultiOffers.acceptCheapestFaresShort_other': 'Tarifas mais baratas ({{count}})',
  'flightMultiOffers.undo': 'Desfazer',
  'flightMultiOffers.confirmPick': 'Confirmar',
  'flightMultiOffers.pickedAll':
    'Escolhi os voos dos {{count}} trechos do roteiro. Total {{total}}. Quero seguir para o checkout.',
  'busOffers.outbound': 'Ida',
  'busOffers.return': 'Volta',
  'busOffers.connection_one': '{{count}} conexão',
  'busOffers.connection_other': '{{count}} conexões',
  /**
   * O eco do quick-reply de escolha (#2327). Frase FECHADA e não montada em
   * runtime: o texto é o que a PESSOA aparenta ter dito no chat, e uma sessão em
   * espanhol recebendo português quebra a experiência inteira — foi assim que o
   * defeito chegou (relato de produção, ago/26).
   */
  'busOffers.pickText': 'Quero a passagem da opção {{position}} ({{carrier}} {{from}}→{{to}}).',
  'busResults.title': '{{leg}} — {{place}} · {{date}}',
  'busResults.legOutbound': 'Ida',
  'busResults.legReturn': 'Volta',
  'busResults.headline': '{{leg}} · {{date}}',
  'busResults.share': 'Compartilhar a busca',
  'busResults.chosen_one': '{{chosen}} de {{count}} escolhido',
  'busResults.chosen_other': '{{chosen}} de {{count}} escolhidos',
  'busResults.total_one': '{{count}} ônibus',
  'busResults.total_other': '{{count}} ônibus',
  'busResults.visible': '{{visible}} de {{total}} ônibus',
  'busResults.resultsLabel': 'Ônibus encontrados',
  'busResults.applyFilters': 'Aplicar filtros',
  'busResults.loadMore': 'Ver mais ônibus',
  'busResults.dates': 'Trocar a data',
  'busResults.filteredEmpty': 'Nenhum ônibus com esses filtros.',
  'busResults.clearFilters': 'Limpar filtros',
  'busResults.sort': 'Ordenar',
  'busResults.chips.toolbar': 'Filtros e ordenação',
  'busResults.chips.back': 'Voltar',
  'busResults.chips.policy': 'Dentro da política',
  'busResults.order.departure': 'Horário de partida',
  'busResults.order.arrival': 'Horário de chegada',
  'busResults.order.price': 'Menor preço',
  'busResults.order.duration': 'Menor duração',
  'busResults.card.preferredCarrier': 'Sua viação',
  'busResults.card.preferredSeatClass': 'Sua classe',
  'busResults.card.preferredDeparture': 'Seu horário',
  'busResults.card.select': 'Selecionar',
  'busResults.card.perPerson': '/pessoa',
  'busResults.card.details': 'Detalhes',
  'busResults.card.direct': 'Direto',
  'busResults.card.withConnection': 'Com conexão',
  'busResults.card.withConnectionAt': 'Com conexão em {{stop}}',
  'busResults.card.connectionStop': 'Conexão em {{stop}}',
  'busResults.card.connectionTimes': 'Chega às {{arrival}} e sai às {{departure}}',
  'busResults.card.connectionWait': 'Espera de {{duration}} na conexão',
  'busResults.card.seatsLeft_one': '{{seatClass}} · {{count}} poltrona livre',
  'busResults.card.seatsLeft_other': '{{seatClass}} · {{count}} poltronas livres',
  'busResults.outOfPolicy': 'Fora da política',
  'busResults.legs.label': 'Pernas da viagem',
  'busResults.legs.outboundDone': 'Ida escolhida',
  'busResults.legs.returnDone': 'Volta escolhida',
  'busResults.legs.choosing': 'Escolhendo agora…',
  'busResults.legs.pending': 'Aguardando sua ida…',
  'busResults.dayStrip.label': 'Buscar em outro dia',
  'busOffers.pickDayText': 'Quero ver as partidas de {{day}}',
  'busResults.legs.seat_one': 'poltrona {{seats}}',
  'busResults.legs.seat_other': 'poltronas {{seats}}',
  'busResults.filters.filters': 'Filtros',
  'busResults.filters.collapse': 'Recolher os filtros',
  'busResults.filters.close': 'Fechar os filtros',
  'busResults.filters.reset': 'Redefinir',
  'busResults.filters.policy': 'Política',
  'busResults.filters.withinPolicy': 'Apenas dentro da política',
  'busResults.filters.seatClass': 'Classe de assento',
  'busResults.filters.time': 'Horário',
  'busResults.filters.departure': 'Saída',
  'busResults.filters.arrival': 'Chegada',
  'busResults.filters.price': 'Preço até',
  'busResults.filters.minimumPrice': 'Preço mínimo',
  'busResults.filters.maximumPrice': 'Preço máximo',
  'busResults.filters.company': 'Companhia',
  'busResults.shortlist': 'Partidas separadas para você',
  'busResults.seeAll': 'Ver as {{count}} partidas',
  'busResults.close': 'Fechar a lista de ônibus',
  'busResults.seatsTitle': 'Escolha sua poltrona',
  'busResults.seatsClose': 'Fechar escolha de poltrona',
  'bus.nextServices.note': 'Você pode adicionar o resto depois, direto na página da sua viagem.',
  'bus.nextServices.ask.hotelRange': 'Quero reservar hotel em {{city}} de {{from}} a {{to}}.',
  'bus.nextServices.ask.hotelOpen': 'Quero reservar hotel em {{city}} a partir de {{from}}.',
  'bus.nextServices.ask.hotelTrip': 'Quero reservar hotel para essa viagem.',
  'bus.nextServices.ask.carRange': 'Quero alugar um carro em {{city}} de {{from}} a {{to}}.',
  'bus.nextServices.ask.carOpen': 'Quero alugar um carro em {{city}} a partir de {{from}}.',
  'bus.nextServices.ask.carTrip': 'Quero alugar um carro para essa viagem.',
  'bus.nextServices.ask.insurance': 'Quero adicionar seguro viagem à minha viagem.',
  'bus.nextServices.ask.seat': 'Quero escolher outra poltrona.',
  'bus.nextServices.ask.flight': 'Quero buscar um voo para esta viagem.',
  'bus.nextServices.ask.train': 'Quero buscar passagens de trem para essa viagem.',
  'bus.nextServices.ask.bus': 'Quero buscar mais uma passagem de ônibus.',
  'bus.nextServices.ask.transferToAirport': 'Quero agendar um carro até o aeroporto.',
  'bus.nextServices.ask.transferAirportHotel': 'Quero agendar um carro do aeroporto até o hotel.',
  'bus.nextServices.ask.transferToHotel': 'Quero agendar um carro até o hotel.',
  'busSearch.who': 'Quem',
  'busSearch.origin': 'Origem',
  'busSearch.destination': 'Destino',
  'busSearch.originPlaceholder': 'Cidade de origem',
  'busSearch.destinationPlaceholder': 'Cidade de destino',
  'busSearch.departingFrom': 'Saindo de {{origin}}',
  'busSearch.swap': 'Inverter origem e destino',
  'busSearch.tripType.roundTrip': 'Ida e volta',
  'busSearch.tripType.oneWay': 'Somente ida',
  'busSearch.recentGroup': 'Recentes',
  'busSearch.noStops': 'Nenhuma rodoviária encontrada',
  'busSearch.stopError': 'Não foi possível carregar as rodoviárias',
  'busSearch.stopPrompt': 'Digite ao menos 2 letras',
  'busSearch.when': 'Quando',
  'busSearch.toBeDefined': 'A definir',
  'busSearch.chooseRoundTrip': 'Escolha a ida e a volta',
  'busSearch.returnPending': '{{date}} → escolha a volta',
  'busSearch.confirmDates': 'Confirmar {{range}}',
  'busSearch.confirmDatesSheet': 'Confirmar datas',
  'busSearch.confirmAndSearch': 'Confirmar e buscar',
  'busSearch.previousMonth': 'Mês anterior',
  'busSearch.nextMonth': 'Próximo mês',
  'busSearch.submit': 'Buscar',
  'busSearch.back': 'Voltar',
  'busSearch.searchText': 'Ônibus de {{from}} para {{to}}, {{when}}.',
  'rentalSearch.driver': 'Quem',
  'rentalSearch.swap': 'Inverter retirada e devolução',
  'rentalSearch.sameDropoff': 'Devolução no mesmo local',
  'rentalSearch.location': 'Local de retirada e devolução',
  'rentalSearch.pickupLocation': 'Local de retirada',
  'rentalSearch.dropoffLocation': 'Local de devolução',
  'rentalSearch.locationPlaceholder': 'Local ou aeroporto',
  'rentalSearch.noPlaces': 'Nenhum lugar encontrado',
  'rentalSearch.placeError': 'Não foi possível carregar os lugares',
  'rentalSearch.myLocation': 'Minha localização',
  'rentalSearch.myLocationError': 'Não foi possível usar sua localização',
  'rentalSearch.when': 'Quando',
  'rentalSearch.dates': 'Retirada e devolução',
  'rentalSearch.chooseDates': 'Escolha a retirada e a devolução',
  'rentalSearch.chooseDropoff': '{{date}} · escolha a devolução',
  'rentalSearch.days_one': '{{count}} dia',
  'rentalSearch.days_other': '{{count}} dias',
  'rentalSearch.confirmDates': 'Confirmar {{range}}',
  'rentalSearch.confirmDatesSheet': 'Confirmar datas',
  'rentalSearch.confirmAndSearch': 'Confirmar e buscar',
  'rentalSearch.pickupTime': 'Hora de retirada',
  'rentalSearch.dropoffTime': 'Hora de devolução',
  'rentalSearch.previousMonth': 'Mês anterior',
  'rentalSearch.nextMonth': 'Próximo mês',
  'rentalSearch.submit': 'Buscar',
  'rentalSearch.back': 'Voltar',
  'rentalSearch.retry': 'Tentar novamente',
  'rentalSearch.change': 'Mudar datas ou horários',
  'rentalSearch.searchTextSame': 'Buscar carros com retirada e devolução em {{place}}, {{window}}.',
  'rentalSearch.searchTextDifferent':
    'Buscar carros com retirada em {{pickup}}, {{pickupAt}} e devolução em {{dropoff}}, {{dropoffAt}}.',
  'mobilityPlanner.origin': 'De onde',
  'mobilityPlanner.destination': 'Para onde',
  'mobilityPlanner.placeholder': 'Endereço ou lugar',
  'mobilityPlanner.suggestions': 'Sugestões',
  'mobilityPlanner.noPlaces': 'Nenhum lugar encontrado',
  'mobilityPlanner.placesError': 'Não foi possível carregar os lugares',
  'mobilityPlanner.retry': 'Tentar de novo',
  'mobilityPlanner.prompt': 'Digite ao menos 2 letras',
  'mobilityPlanner.submit': 'Buscar carros',
  'mobilityPlanner.when': 'Para quando',
  'mobilityPlanner.whenPending': 'A definir',
  'mobilityPlanner.whenMissing': 'Informe data e horário',
  'mobilityPlanner.whenMissingDate': 'Informe a data',
  'mobilityPlanner.whenMissingTime': 'Informe o horário',
  'mobilityPlanner.whenConfirm': 'Confirmar',
  'mobilityPlanner.whenBack': 'Voltar',
  'mobilityPlanner.previousMonth': 'Mês anterior',
  'mobilityPlanner.nextMonth': 'Próximo mês',
  'mobilityPlanner.hour': 'Hora',
  'mobilityPlanner.minute': 'Minuto',
  'mobilityPlanner.who': 'Quem',
  'mobilityPlanner.tripType': 'Sentido',
  'mobilityPlanner.tripType.oneWay': 'Só ida',
  'mobilityPlanner.tripType.roundTrip': 'Ida e volta',
  'mobilityPlanner.passengers': 'Pessoas',
  'mobilityPlanner.passengersCount_one': '{{count}} pessoa',
  'mobilityPlanner.passengersCount_other': '{{count}} pessoas',
  'mobilityPlanner.passengersLess': 'Menos uma pessoa',
  'mobilityPlanner.passengersMore': 'Mais uma pessoa',
  'mobilityPlanner.outbound': 'Ida',
  'mobilityPlanner.return': 'Volta',
  'mobilityPlanner.returnRoute': 'Volta · {{from}} → {{to}}',
  'mobilityPlanner.searchText': 'Corrida de {{from}} para {{to}}.',
  'mobilityPlanner.searchTextRoundTrip': 'Corrida de {{from}} para {{to}}, ida e volta.',
  'mobilityPlanner.searchTextNoDestination': 'Carro saindo de {{from}}.',
  'busResults.badge.recommended': 'Recomendado',
  'busResults.badge.cheapest': 'Menor preço',
  'busResults.badge.fastest': 'Menos tempo',
  'busResults.merit.recommended': 'O melhor equilíbrio entre preço e tempo, em {{seatClass}}.',
  'busResults.merit.cheapest': '{{amount}} a menos que a recomendada.',
  'busResults.merit.cheapestAt': '{{amount}} a menos — com troca de ônibus em {{stop}}.',
  'busResults.merit.fastest': '{{duration}} mais rápido que a recomendada.',
  'busResults.merit.fastestDirect': '{{duration}} mais rápido e sem paradas.',
  'busSeats.segments': 'Trechos da viagem',
  'busSeats.segment': 'Trecho {{segment}}',
  'busSeats.segmentRoute': 'Trecho {{segment}} · {{from}} → {{to}}',
  'busSeats.entrance': 'Entrada',
  'busSeats.driver': 'Motorista',
  'busSeats.floor': 'Piso {{floor}}',
  'busSeats.seat': 'Poltrona {{code}}',
  'busSeats.seatOccupied': 'Poltrona {{code}}, ocupada',
  'busSeats.loading': 'Carregando as poltronas…',
  'busSeats.error':
    'Não conseguimos carregar as poltronas deste trecho agora. Tente de novo em instantes.',
  'busSeats.offline':
    'Não conseguimos carregar as poltronas. Verifique sua conexão e tente novamente.',
  'busSeats.notOffered': 'A empresa não permite escolher assento neste trecho.',
  'busSeats.retry': 'Tentar novamente',
  'busSeats.full': 'Não há poltronas livres para todos neste trecho.',
  'busSeats.noSeat': 'Seguir sem assento',
  'busSeats.noSeatSummary': 'sem assento',
  'busSeats.pending_one': 'Escolha mais {{count}} poltrona',
  'busSeats.pending_other': 'Escolha mais {{count}} poltronas',
  'busSeats.confirm': 'Selecionar',
  'busSeats.suggested': 'Assento recomendado com base nas suas preferências',
  'busSeats.legend.free': 'Livre',
  'busSeats.legend.occupied': 'Ocupada',
  'busSeats.legend.selected': 'Sua',
  'busSeats.legend.recommended': 'Recomendada',
  'busSeats.legend.window': 'Janela',
  'busSeats.detail.window': 'Janela',
  'busSeats.detail.aisle': 'Corredor',
  'busSeats.detail.windowAndAisle': 'Janela e corredor',
  'busSeats.detail.individual': 'Individual',
  'busSeats.detail.area.front': 'Frente',
  'busSeats.detail.area.middle': 'Meio',
  'busSeats.detail.area.back': 'Fundo',
  'busSeats.detail.neighborFree': 'Vizinho livre',
  'busSeats.detail.neighborOccupied': 'Vizinho ocupado',
  'busSeats.detail.recommended': 'Recomendada pelas suas preferências',
  'busSeats.detail.hint': 'Toque numa poltrona para ver os detalhes.',
  'busSeats.collapse': 'Fechar escolha de poltrona',
  'busSeats.price.oneWay': 'só ida, por pessoa',
  'busSeats.price.outbound': 'ida, por pessoa',
  'busSeats.price.return': 'volta, por pessoa',
  'busOffers.pickSeatsText': 'Quero a viagem das {{time}} ({{carrier}}), {{seats}}.',
  'busOffers.pickSeat': 'poltrona {{code}}',
  'busOffers.pickNoSeat': 'sem assento',
  'busStops.aria': 'Escolha de rodoviária',
  'busStops.titleOrigin': 'De qual rodoviária você quer sair?',
  'busStops.titleDestination': 'Para qual rodoviária você vai?',
  'bus.inPolicy': 'Dentro da política',
  'busSeatMap.aria': 'Mapa de poltronas',
  'busSeatMap.title': 'Escolha sua poltrona',
  'busSeatMap.picked': 'Quero a poltrona {{code}}.',
  'busSeatMap.free': 'Poltrona {{code}} (livre)',
  'busSeatMap.occupied': 'Poltrona {{code}} (ocupada)',
  'busSeatMap.legendFree': 'Livre',
  'busSeatMap.legendOccupied': 'Ocupada',
  'busSeatMap.legendSelected': 'Selecionada',
  'busSeatMap.noSeat': 'Seguir sem assento',
  'busSeatMap.noSeatHint': 'A equipe VOLL escolhe a poltrona pra você.',
  'busSeatMap.noSeatPicked': 'Pode seguir sem assento — a equipe VOLL escolhe.',
  'busSeatMap.pickedSeat': 'poltrona {{code}}',
  'busSeatMap.pickedNoSeat': 'sem assento',
  'busSeatMap.pickedRound': 'Ida: {{outbound}} · Volta: {{return}}.',
  'busSeatMap.errorTitle': 'Não consegui carregar os assentos',
  'busSeatMap.otherTrip': 'Escolher outra viagem',
  'busSeatMap.otherTripText': 'Quero escolher outra viagem.',
  'busSeatMap.continue': 'Continuar',
  'busSeatMap.legOutbound': 'Ida',
  'busSeatMap.legReturn': 'Volta',
  'busSeatMap.noSeatSelected': 'Sem assento (a equipe VOLL escolhe)',
  'busSeatMap.front': 'Frente',
  'busSeatMap.floor': '{{n}}º andar',
  'flightFilter.aria': 'Filtrar voos',
  'flightFilter.title': 'Voos',
  'flightFilter.option_one': '{{count}} opção',
  'flightFilter.option_other': '{{count}} opções',
  'flightFilter.empty.title': 'Nenhum voo com esses filtros.',
  'flightFilter.empty.hint': 'Tente afrouxar paradas, horário ou preço.',
  // `axes` já vem em minúsculas e unido pelo `Intl.ListFormat` do locale
  // ("paradas, política ou número do voo") — ver `useFilterEmptyHint`.
  'flightFilter.empty.hintAxes': 'Tente afrouxar {{axes}}.',
  'flightFilter.filteredAria': 'Voos filtrados',
  'flightFilter.selectOffer': 'Selecionar',
  'flightFilter.selectOfferAria': 'Selecionar oferta — opção {{position}}',
  'flightFilter.found_one': '{{count}} voo encontrado',
  'flightFilter.found_other': '{{count}} voos encontrados',
  'flightFilter.foundOfTotal': '{{count}} de {{total}} voos',
  'flightFilter.sort.label': 'Ordenar',
  'flightFilter.sort.price': 'Mais barato',
  'flightFilter.sort.dep': 'Mais cedo',
  'flightFilter.sort.dur': 'Mais rápido',
  // O ordenador da bancada fala por SUBSTANTIVO ("Preço"), não pelo adjetivo do
  // drawer do card ("Mais barato"): no gatilho compacto o rótulo aparece sozinho,
  // depois do ícone, e o adjetivo lia como uma frase cortada.
  'flightFilter.sortMenu.rec': 'Recomendado',
  'flightFilter.sortMenu.price': 'Preço',
  'flightFilter.sortMenu.dur': 'Duração',
  'flightFilter.sortMenu.dep': 'Partida',
  'flightFilter.sortMenu.arr': 'Chegada',
  'flightFilter.stops.label': 'Paradas',
  'flightFilter.stops.direct': 'Voo direto',
  'flightFilter.stops.one': 'Até 1 parada',
  'flightFilter.stops.two': 'Até 2 paradas',
  'flightFilter.stops.above': 'Acima de 2 paradas',
  'flightFilter.airline': 'Companhias aéreas',
  // Eixo de horário: `time` é o rótulo do bloco; `departs`/`arrives` são as duas
  // metades visíveis, e `departureTime`/`arrivalTime` seguem como nome a11y das
  // grades ("Sai" sozinho não diz de que eixo é a metade).
  'flightFilter.time': 'Horários',
  'flightFilter.departs': 'Saída',
  'flightFilter.arrives': 'Chegada',
  'flightFilter.departureTime': 'Horário de partida',
  'flightFilter.price': 'Preço',
  'flightFilter.minPrice': 'Preço mínimo',
  'flightFilter.maxPrice': 'Preço máximo',
  'flightFilter.flightNumber': 'Número do voo',
  'flightFilter.flightNumberPlaceholder': 'Ex.: VL 0000',
  'flightFilter.filters': 'Filtros',
  'flightFilter.apply': 'Aplicar filtros',
  'flightFilter.reset': 'Redefinir todos',
  'flightFilter.close': 'Fechar os filtros',
  'flightFilter.policy': 'Política',
  'flightFilter.withinPolicy': 'Apenas dentro da política',
  'flightExplorer.chips.toolbar': 'Filtros e ordenação',
  'flightExplorer.chips.back': 'Voltar',
  'flightExplorer.chips.policy': 'Dentro da política',
  'flightFilter.fromPrice': 'a partir de {{price}}',
  'flightFilter.arrivalTime': 'Horário de chegada',
  'flightFilter.airports': 'Aeroportos',
  'flightFilter.otherCities': 'Outros',
  // Filtro por texto — chips (spec flight-text-filter-chips)
  'flightChips.placeholder': 'Filtre do seu jeito',
  'flightChips.send': 'Aplicar filtro por texto',
  'flightChips.dictate': 'Ditar filtro',
  'flightChips.dictationCancel': 'Cancelar ditado',
  'flightChips.dictationStop': 'Parar e revisar',
  'flightChips.dictationSend': 'Parar e aplicar',
  'flightChips.transcribing': 'Transcrevendo…',
  'flightChips.dictationFailed': 'Não deu para transcrever. Tente de novo.',
  'flightChips.applying': 'Aplicando seu filtro…',
  'flightChips.remove': 'Remover filtro: {{label}}',
  'flightChips.leg.outbound': 'Ida',
  'flightChips.leg.inbound': 'Volta',
  'flightChips.leg.n': 'Trecho {{n}}',
  'flightChips.leg.prefixed': '{{leg}} · {{label}}',
  'flightChips.airline.only': 'Só {{name}}',
  'flightChips.airline.exclude': 'Sem {{name}}',
  'flightChips.stops.nonstop': 'Voo direto',
  'flightChips.stops.up_to_1': 'Até 1 parada',
  'flightChips.stops.up_to_2': 'Até 2 paradas',
  'flightChips.stops.any': 'Com ou sem conexão',
  'flightChips.time.arriveBefore': 'Chegada até {{time}}',
  'flightChips.time.arriveAfter': 'Chegada a partir de {{time}}',
  'flightChips.time.arriveBetween': 'Chegada entre {{start}} e {{end}}',
  'flightChips.time.departBefore': 'Saída até {{time}}',
  'flightChips.time.departAfter': 'Saída a partir de {{time}}',
  'flightChips.time.departBetween': 'Saída entre {{start}} e {{end}}',
  'flightChips.price.max': 'Até {{price}}',
  'flightChips.price.min': 'A partir de {{price}}',
  'flightChips.price.between': 'Entre {{min}} e {{max}}',
  'flightChips.policy.only': 'Só dentro da política',
  'flightChips.policy.release': 'Com opções fora da política',
  'flightChips.via.avoid': 'Sem conexão em {{place}}',
  'flightChips.via.through': 'Com conexão em {{place}}',
  'flightChips.airport.from': 'Saindo de {{place}}',
  'flightChips.airport.to': 'Chegando em {{place}}',
  'flightChips.airport.only': 'Só {{place}}',
  'flightChips.airport.exclude': 'Sem {{place}}',
  'flightChips.sort.rec': 'Recomendados primeiro',
  'flightChips.sort.price': 'Mais baratos primeiro',
  'flightChips.sort.dur': 'Mais rápidos primeiro',
  'flightChips.sort.dep': 'Saída mais cedo primeiro',
  'flightChips.sort.arr': 'Chegada mais cedo primeiro',
  'flightChips.axis.punctuality': 'Pontualidade',
  'flightChips.axis.baggage': 'Bagagem',
  'flightChips.axis.seat': 'Assento',
  'flightChips.axis.aircraft': 'Aeronave',
  'flightChips.axis.layover': 'Tempo de conexão',
  'flightChips.axis.duration': 'Duração total',
  'flightChips.axis.cabin': 'Cabine',
  'flightChips.axis.fare': 'Tarifa',
  'flightChips.axis.airline': 'Companhia',
  'flightChips.axis.via': 'Conexão',
  'flightChips.axis.airport': 'Aeroporto',
  'flightChips.axis.time': 'Horário',
  'flightChips.axis.stops': 'Paradas',
  'flightChips.axis.period': 'Período do dia',
  'flightChips.axis.price': 'Preço',
  'flightChips.axis.policy': 'Política',
  'flightChips.axis.sort': 'Ordenação',
  'flightChips.axis.other': 'Filtro',
  'flightChips.reason.airport_not_in_search': 'nenhuma opção desta busca passa por aí',
  'flightChips.reason.airline_not_in_search': 'essa companhia não está nesta busca',
  'flightChips.reason.no_punctuality_data': 'sem dado de pontualidade nesta lista',
  'flightChips.reason.axis_not_available': 'esse critério não está disponível nesta lista',
  'flightChips.reason.axis_not_in_panel': 'tempo de conexão e duração ainda não filtram por aqui',
  'flightChips.reason.window_not_contiguous':
    'não dá pra excluir um período do meio do dia por aqui — ajuste a régua',
  'flightChips.reason.low_confidence': 'não entendi com segurança — tente reescrever',
  'flightChips.reason.other': 'não deu pra aplicar',
  'flightChips.note.question': 'Isso é uma pergunta — faça no chat.',
  'flightChips.note.new_search': 'Parece uma busca nova — use a busca no topo.',
  'flightChips.note.noise': 'Não entendi como filtro.',
  'flightChips.note.failed': 'Não deu pra interpretar agora. Tente de novo.',
  'flightChips.note.cabin': 'Refiz a busca em {{cabin}}',
  'flightChips.note.cabinSame': 'A busca já está em {{cabin}}',
  'flightChips.note.cabinUnavailable': 'Cabine não muda por aqui nesta tela',
  'flightChips.note.reset': 'Filtros limpos',
  'flightExplorer.cabin': 'Cabine',
  'flightExplorer.cabin.economy': 'Econômica',
  'flightExplorer.cabin.premium': 'Premium',
  'flightExplorer.cabin.business': 'Executiva',
  'flightExplorer.cabin.first': 'Primeira',
  'flightExplorer.cabinRefetch': 'Trocar a cabine refaz a busca',
  'flightExplorer.cabinOutOfPolicy': 'fora da política da empresa',
  'flightExplorer.cabinAllowedFromHours': 'permitida neste trecho (voos a partir de {{hours}}h)',
  'flightExplorer.cabinOutBelowHours':
    'fora da política neste trecho (só em voos a partir de {{hours}}h)',
  'flightExplorer.searchCta': 'Buscar',
  'flightExplorer.searching': 'Buscando voos…',
  'flightExplorer.searchingLong':
    'A busca continua em andamento. Algumas rotas levam mais de um minuto.',
  'flightExplorer.refreshing': 'atualizando a lista…',
  // Justificativa determinística do card Recomendado (#2108) — os sufixos só
  // entram quando a oferta os sustenta (direto / dentro da política).
  'flightExplorer.whyRecommended':
    'Melhor equilíbrio entre preço, tempo de voo e escalas desta lista.',
  'flightExplorer.whyRecommendedDirect':
    'Melhor equilíbrio entre preço, tempo de voo e escalas desta lista — e é direto.',
  'flightExplorer.whyRecommendedInPolicy':
    'Melhor equilíbrio entre preço, tempo de voo e escalas desta lista — e está dentro da política.',
  'flightExplorer.whyRecommendedDirectInPolicy':
    'Melhor equilíbrio entre preço, tempo de voo e escalas desta lista — e é direto, dentro da política.',
  'flightExplorer.loadMore': 'Ver mais voos',
  'flightExplorer.offline.offerTitle': 'Não encontrou o voo que precisa?',
  'flightExplorer.offline.offerBody':
    'A equipe VOLL busca voos fora do catálogo online — companhias, tarifas e horários — que batam com o seu perfil e devolve a cotação na conversa da viagem.',
  'flightExplorer.offline.returnOn': 'volta {{date}}',
  // #2294 — chip que filtra a lista para só os voos fixados (`count` = fixados
  // vivos na lista corrente).
  'flightExplorer.pinnedFilter': 'Fixados ({{count}})',
  'flightExplorer.error': 'Não foi possível buscar os voos',
  // O prazo estourado (#4465): desfecho próprio, e na cidade inteira ("todos os
  // aeroportos") a saída é buscar um aeroporto dela, não repetir a mesma busca.
  'flightExplorer.timeout': 'A busca demorou demais',
  'flightExplorer.timeoutHint': 'O fornecedor não respondeu a tempo. Tente de novo em instantes.',
  'flightExplorer.timeoutCity': 'A busca em todos os aeroportos de {{city}} demorou demais',
  'flightExplorer.timeoutCityHint': 'Escolha um aeroporto para buscar mais rápido.',
  'flightExplorer.timeoutAirports': 'Buscar um aeroporto',
  'flightExplorer.policyEvaluation': 'Não foi possível avaliar a política do viajante',
  'flightExplorer.policyEvaluationHint': 'Fale com o administrador da empresa.',
  'flightExplorer.searchExpired': 'Esta busca expirou',
  'flightExplorer.searchExpiredHint':
    'O fornecedor não guarda a busca por muito tempo. Busque de novo: mantemos a sua ida, se ela ainda estiver disponível.',
  'flightExplorer.searchExpiredAction': 'Buscar de novo',
  'flightExplorer.outboundGone':
    'O voo de ida que você escolheu não está mais disponível. Escolha outra ida.',
  'flightExplorer.chooseOutbound': 'Escolha o voo de ida',
  'flightExplorer.chooseReturn': 'Escolha o voo de volta',
  'flightExplorer.removePick': 'Remover a escolha do trecho {{n}}',
  // A linha do protótipo é composta de DUAS metades: o preço e o trecho
  // enfatizado (sublinhado) — por isso duas chaves, não uma frase só.
  'flightExplorer.policyCeilingAbove': 'Acima de {{price}}',
  'flightExplorer.policyCeilingOut': 'está fora da política',
  'flightFares.policyReason': 'Fora da política: {{reasons}}',
  'flightExplorer.listTitle': 'Voos para {{city}} · {{date}}',
  'flightExplorer.share': 'Compartilhar a busca',
  'flightExplorer.listTitleOutbound': 'Escolha a ida — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.listTitleReturn': 'Escolha a volta — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.confirm': 'Confirmar',
  // #3213 — a re-busca de UMA perna (a cabine da coluna) falhou e a perna voltou
  // ao quadro anterior: lista e escolha do trecho ficam, o aviso oferece re-tentar.
  'flightExplorer.researchKeptCabin':
    'Não foi possível buscar em {{cabin}}. Mantivemos a lista anterior.',
  'flightExplorer.researchEmptyCabin':
    'Não há voos em {{cabin}} neste trecho. Mantivemos a lista anterior.',
  'flightExplorer.researchKept': 'Não foi possível refazer a busca. Mantivemos a lista anterior.',
  'flightExplorer.researchKeptPolicy':
    'Não foi possível avaliar a política do viajante. Mantivemos a lista anterior; fale com o administrador da empresa.',
  'flightExplorer.draftPending': 'Busque antes de confirmar — a faixa tem alterações não buscadas.',
  'flightExplorer.confirmUnavailable':
    'Não foi possível fechar a seleção: os trechos vieram em moedas diferentes. Troque a tarifa ou refaça a busca.',
  'flightExplorer.replacesTrip': 'Confirmar substitui a viagem do card.',
  'flightExplorer.replacesTripDiscards':
    'Confirmar substitui a viagem e descarta as escolhas dos outros trechos.',
  'flightExplorer.aria': 'Explorar voos',
  'flightExplorer.pillAria': 'Contexto da busca',
  'flightExplorer.pill.legs_one': '{{count}} trecho',
  'flightExplorer.pill.legs_other': '{{count}} trechos',
  'flightExplorer.pillEdit': 'Editar busca',
  'flightExplorer.searchAgain': 'Buscar novamente',
  'flightExplorer.legCta': 'Ver todos os voos',
  'flightExplorer.legCtaShort': 'Todos os voos',
  'flightExplorer.frozenEmpty': 'As ofertas desta busca não estão mais disponíveis.',
  // #2339 — empty page do ZERO REAL do fornecedor (não o vazio-por-filtro): o
  // destino vem da busca corrente; as datas seguem na pill do topo ("nessas datas").
  'flightExplorer.emptyResults': 'Não encontramos voos para {{destination}} nessas datas',
  // Ações da empty page (voo E hotel) — o componente único é o dono desta copy.
  'searchEmpty.newSearch': 'Nova busca',
  'searchEmpty.retry': 'Tentar novamente',
  'flightSeatLink.cta': 'Escolher assento',
  'ride99Link.title': 'Corrida com a 99',
  'ride99Link.cta': 'Pedir corrida',
  'walletLink.title.balance': 'Pedido de saldo',
  'walletLink.title.home': 'Carteira',
  'walletLink.cta': 'Abrir Carteira',
  'eventPremises.missing_one': 'Falta {{count}} item na ficha',
  'eventPremises.missing_other': 'Faltam {{count}} itens na ficha',
  'eventPremises.ready': 'Ficha pronta para confirmar',
  'eventPremises.draft': 'Rascunho · {{done}} de {{total}}',
  'eventPremises.readyToPublish': 'Pronto para publicar',
  'eventPremises.published': 'Publicadas',
  'trips.unnamed': 'Viagem sem nome',
  'trips.routeTbd': 'Rota a definir',
  'trips.totalTbd': 'A confirmar',
  'trips.collapse': 'Recolher',
  'trips.seeAll': 'Ver todas as {{count}} viagens',
  'trips.drawer.ariaFallback': 'Detalhe da viagem',
  'trips.drawer.summary': 'Resumo',
  'trips.drawer.period': 'Período',
  'trips.drawer.path': 'Trajeto',
  'trips.drawer.modals': 'Modais',
  'trips.drawer.requester': 'Solicitante',
  'trips.drawer.company': 'Empresa',
  'trips.drawer.vouchers': 'Vouchers',
  'trips.drawer.processingTitle': 'Vouchers em processamento',
  'trips.drawer.processingBody':
    'Os vouchers aparecem aqui assim que a reserva for confirmada pelos fornecedores.',
  'trip.modal.FLIGHT': 'Voo',
  'trip.modal.HOTEL': 'Hotel',
  'trip.modal.BUS': 'Ônibus',
  'trip.modal.RENTAL': 'Locação',
  'trip.modal.MOBILITY': 'Mobilidade',
  'trip.status.UPCOMING': 'Próxima',
  'trip.status.ONGOING': 'Em viagem',
  'trip.status.COMPLETED': 'Concluída',
  'trip.status.CANCELLED': 'Cancelada',
  'voucher.status.IN_PROGRESS': 'Em andamento',
  'voucher.status.AWAITING_APPROVAL': 'Em aprovação',
  'voucher.status.CONFIRMED': 'Confirmado',
  'voucher.status.CANCELLED': 'Cancelado',
  'voucher.status.EXPIRED': 'Expirado',
  'voucher.status.DECLINED': 'Recusado',
  'voucher.meta.supplier': 'Fornecedor',
  'voucher.meta.status': 'Status',
  'voucher.meta.reservation': 'Reserva',
  'voucher.meta.issuedAt': 'Emitido em',
  'voucher.meta.notIssued': 'Não emitida',
  'voucher.segment.outbound': 'Ida',
  'voucher.segment.return': 'Volta',
  'voucher.segment.generic': 'Trecho',
  'voucher.segment.count': '{{dir}} · trecho {{index}} de {{total}}',
  'voucher.detail.airline': 'Companhia',
  'voucher.detail.locator': 'Localizador',
  'voucher.detail.departure': 'Partida',
  'voucher.detail.arrival': 'Chegada',
  'voucher.detail.duration': 'Duração',
  'voucher.detail.connections': 'Conexões',
  'voucher.detail.stop_one': '{{count}} parada',
  'voucher.detail.stop_other': '{{count}} paradas',
  'voucher.detail.cabin': 'Cabine',
  'voucher.detail.seat': 'Assento',
  'voucher.detail.baggage': 'Bagagem',
  'voucher.detail.operator': 'Operadora',
  'voucher.detail.boarding': 'Embarque',
  'voucher.detail.disembark': 'Desembarque',
  'voucher.detail.class': 'Classe',
  'voucher.detail.hotel': 'Hotel',
  'voucher.detail.address': 'Endereço',
  'voucher.detail.city': 'Cidade',
  'voucher.detail.checkIn': 'Check-in',
  'voucher.detail.checkOut': 'Check-out',
  'voucher.detail.nights': 'Noites',
  'voucher.detail.rooms': 'Quartos',
  'voucher.detail.guests': 'Hóspedes',
  'voucher.detail.cancellationPolicy': 'Política de cancelamento',
  'voucher.detail.fine': 'Multa {{value}}',
  'voucher.detail.available': 'Disponível',
  'voucher.detail.rentalCompany': 'Locadora',
  'voucher.detail.pickup': 'Retirada',
  'voucher.detail.dropoff': 'Devolução',
  'voucher.detail.model': 'Modelo',
  'voucher.detail.group': 'Grupo',
  'voucher.detail.category': 'Categoria',
  'voucher.detail.distance': 'Distância',
  'voucher.block.lodging': 'Hospedagem',
  'voucher.block.rental': 'Locação',
  'voucher.block.mobility': 'Mobilidade',
  'flight.policyDetails.title': 'Detalhes da Política',
  'flight.policyDetails.inPolicy': 'Dentro da política',
  'flight.policyDetails.outPolicy': 'Fora da política',
  'flight.policyRule.advance': 'Antecedência mínima',
  'flight.policyRule.allowedClass': 'Classe permitida',
  'flight.policyRule.baggage': 'Bagagem',
  'flight.policyRule.baggageOnlyAboveTripLength': 'Bagagem por duração da viagem',
  'flight.policyRule.baggageOnlyOnMultipleSegments': 'Bagagem em múltiplos trechos',
  'flight.policyRule.bookingWindow': 'Antecedência mínima',
  'flight.policyRule.bookingWindowInternational': 'Antecedência mínima internacional',
  'flight.policyRule.cheapestPrice': 'Menor preço',
  'flight.policyRule.cheapestPriceFastest': 'Menor preço entre os voos mais rápidos',
  'flight.policyRule.cheapestPriceNonStop': 'Menor preço sem escala',
  'flight.policyRule.cheapestPricePeriod': 'Menor preço no período',
  'flight.policyRule.class': 'Classe permitida',
  'flight.policyRule.classLimits': 'Preço máximo por classe',
  'flight.policyRule.price': 'Preço máximo',
  'flight.policyRule.withinPolicy': 'Política da viagem',
  'flight.policyRule.withinPolitics': 'Política da viagem',
  'flight.policyRule.policyProfile': 'Política aplicada',
  'flight.policyRule.policyProfileUnresolved': 'Política do viajante não identificada',
  'flight.policyRule.companyGuidance': 'Orientações da empresa',
  'flight.policyRule.bookingWindowText_one':
    'Antecedência mínima de {{count}} dia para viagens {{scope}}.',
  'flight.policyRule.bookingWindowText_other':
    'Antecedência mínima de {{count}} dias para viagens {{scope}}.',
  'flight.policyRule.scope.domestic': 'nacionais',
  'flight.policyRule.scope.international': 'internacionais',
  'flight.policyRule.classLimitsText':
    'O preço desta tarifa está acima do limite definido pela política para esta classe.',
  'flight.policyRule.classLimitsTextWithLimit':
    'O preço desta tarifa está acima do teto de {{value}} definido pela política para esta classe.',
  'flight.policyRule.withinPolicyText':
    'Esta tarifa está fora da política da viagem, mas o fornecedor não informou qual regra foi descumprida.',
  'flight.row.outPolicyAria': 'Voo fora da política',
  'flight.segment.viewDetail': 'Detalhes',
  // #2294 — toggle "Fixar" da linha da bancada (texto visível = nome acessível;
  // `aria-pressed` carrega o estado).
  'flight.pin.off': 'Fixar',
  'flight.pin.on': 'Fixado',
  'flight.segment.detailsTitle': 'Detalhe do voo',
  'flight.segment.detailBack': 'Voltar',
  'flight.segment.arrivesNextDay_one': 'Chega no dia seguinte',
  'flight.segment.arrivesNextDay_other': 'Chega {{count}} dias depois',
  'flight.fareBreakdown.fare': 'Tarifa',
  'flight.fareBreakdown.taxes': 'Taxas e encargos',
  'flight.fareBreakdown.totalPerPerson': 'Total por pessoa',
  'flight.quote.outbound': 'Ida',
  'flight.quote.return': 'Volta',
  'flight.reservation.aria': 'Bilhete da reserva',
  'flight.reservation.leg': 'Trecho {{n}}',
  'flight.reservation.locator': 'Localizador',
  'flight.reservation.locatorPending': 'será enviado em instantes',
  // Frase de RODAPÉ do estado `pending` (o PNR ainda não saiu), não subtítulo de
  // header: o card confirmado não escreve status nenhum.
  'flight.reservation.emailSent': 'Reserva em emissão · enviamos os detalhes para o seu e-mail',
  'flight.reservation.cpf': 'CPF {{value}}',
  // Rótulos de recibo COMPARTILHADOS entre os cards de reserva (voo e hotel) —
  // fora do namespace `flight.*` porque um card de hotel pedindo `flight.…` é
  // mentira de contrato, e duplicar a string nos dois namespaces é pior.
  'traveler.policyHolder': 'Titular da política',
  'traveler.costCenter': 'Centro de custo',
  'traveler.motive': 'Motivo da viagem',
  'flight.reservation.flight': 'Voo',
  'flight.reservation.date': 'Data',
  'flight.reservation.outboundDate': 'Data ida',
  'flight.reservation.returnDate': 'Data volta',
  'flight.reservation.totalPaid': 'Total pago',
  'flight.search.aria': 'Confirmação da busca',
  'flight.search.cabin.economy': 'Econômica',
  'flight.search.cabin.premium': 'Premium',
  'flight.search.cabin.business': 'Executiva',
  'flight.search.cabin.first': 'Primeira',
  'flight.search.passenger_one': '{{count}} passageiro',
  'flight.search.passenger_other': '{{count}} passageiros',
  'flight.search.adult_one': '{{count}} adulto',
  'flight.search.adult_other': '{{count}} adultos',
  'flight.search.child_one': '{{count}} criança',
  'flight.search.child_other': '{{count}} crianças',
  'flight.search.infant_one': '{{count}} bebê',
  'flight.search.infant_other': '{{count}} bebês',
  'flight.search.adult': 'Adulto',
  'flight.search.child': 'Criança',
  'flight.search.infant': 'Bebê',
  'flight.search.roundTrip': 'Ida e volta',
  'flight.search.oneWay': 'Somente ida',
  'flight.search.tripType': 'Tipo de viagem',
  'flight.search.day_one': '{{count}} dia',
  'flight.search.day_other': '{{count}} dias',
  'flight.search.class': 'Classe',
  'flight.search.searchFlights': 'Buscar voos',
  /** O eco do quick-reply "Buscar voos" (#2327/D5) — o card
   *  `flight_search_confirmation` some depois do toque, então a frase é o que a
   *  PESSOA aparenta ter dito, e precisa sair no idioma da sessão. */
  'flightSearchConfirmation.replySearch': 'Buscar voos.',
  'flightCheaperDates.roundTrip': 'Ida e Volta',
  'flightCheaperDates.oneWay': 'Somente Ida',
  'flightCheaperDates.dates': 'Datas',
  'flightCheaperDates.cheapest': 'mais barato',
  'flightCheaperDates.mostExpensive': 'mais caro',
  'flightCheaperDates.search': 'Buscar voos nessas datas',
  'flightCheaperDates.keep': 'Manter {{date}}',
  /** Variantes FECHADAS por forma de viagem (#2327): concatenar "e volta X" a uma
   *  frase traduzida não sobrevive ao primeiro idioma que muda a ordem ou a
   *  pontuação — e o eco é o texto que a pessoa aparenta ter dito. */
  'flightCheaperDates.searchTextOneWay':
    'Buscar voos de {{origin}} para {{destination}} ({{originCode}}→{{destinationCode}}), ida {{outbound}}.',
  'flightCheaperDates.searchTextRoundTrip':
    'Buscar voos de {{origin}} para {{destination}} ({{originCode}}→{{destinationCode}}), ida {{outbound}} e volta {{return}}.',
  'flightPlanner.calDisabledHint': 'Escolha origem e destino para ver os preços',
  'flightPlanner.allAirports': 'todos os aeroportos',
  // A saia: à esquerda o estado da escolha, à direita o que o agente afirma. Ela
  // substituiu o título/subtítulo do card — a rota já está nos campos DE/PARA.
  'flightPlanner.stateNeedsOut': 'Escolha a data de ida',
  'flightPlanner.stateNeedsRet': 'Ida {{date}} · escolha a volta',
  'flightPlanner.stateOneWay': 'Ida {{date}}',
  // Contado: o dicionário usa `_one`/`_other` e quem escolhe o sufixo é o chamador
  // (`genUiT` não faz plural). Sem isso, uma viagem de uma noite dizia "1 noites".
  'flightPlanner.stateRange_one': '{{from}} - {{to}} • {{nights}} noite',
  'flightPlanner.stateRange_other': '{{from}} - {{to}} • {{nights}} noites',
  'flightPlanner.bestDayOut': '{{date}} é a ida mais econômica',
  // Multi-trecho: as frases nomeiam a PERNA — no multi não há ida nem volta, há o
  // trecho que está sendo escolhido.
  'flightPlanner.stateLeg': 'Trecho {{leg}} · {{date}}',
  'flightPlanner.stateNeedsLegDate': 'Escolha a data do trecho {{leg}}',
  'flightPlanner.bestDayLeg': '{{date}} é a data mais econômica do trecho {{leg}}',
  'flightPlanner.bestDayRet': '{{date}} é a volta mais econômica',
  // A 2ª linha da saia (#5246): a economia de trocar a data escolhida pelo dia
  // mais barato (total do grupo), ou a espera enquanto a seleção não fecha.
  'flightPlanner.savesLine': 'Voe neste dia e economize {{amount}}',
  'flightPlanner.savesLineInline': 'voe neste dia e economize {{amount}}',
  'flightPlanner.chosenCheapest': 'Você escolheu a data mais econômica',
  'flightPlanner.awaitingDate': 'Aguardando a data...',
  'flightPlanner.who': 'Quem',
  /** Rótulo do MESMO campo na faixa de busca do explorer, onde ele só move o
   *  sujeito da política. */
  'flightPlanner.whoPolicy': 'Política de',
  'flightPlanner.from': 'Origem',
  'flightPlanner.to': 'Destino',
  'flightPlanner.when': 'Quando',
  'flightPlanner.whenEmpty': 'A definir',
  'flightPlanner.whenReturnPending': '– Volta',
  /* Modo colapsado (bancada): o CTA que fecha a sessão de escolha das datas no
   * popover, e o par transacional da lista de trechos. "Confirmar datas" e não
   * "Buscar": a busca é da chrome hospedeira — aqui a pessoa fecha a escolha. */
  'flightPlanner.confirmDates': 'Confirmar datas',
  'flightPlanner.confirmAndSearch': 'Confirmar e buscar',
  /** O par FECHADO no rótulo do confirmar ("Confirmar 15–22 set", #2106). */
  'flightPlanner.confirmRange': 'Confirmar {{range}}',
  /** O título da FOLHA de datas do celular por modo (#3446): "Ida e volta" é a
   *  própria aba; somente ida nomeia o destino, ou fica só "Ida" sem ele. */
  'flightPlanner.sheetOneWay': 'Ida',
  'flightPlanner.sheetOneWayTo': 'Ida para {{city}}',
  /** A linha sob o título na folha de um trecho do multi: posição e origem em
   *  IATA (a cidade pediria artigo — "do Rio", "de São Paulo"). */
  'flightPlanner.sheetLeg': '{{n}} de {{total}}',
  'flightPlanner.sheetLegFrom': '{{n}} de {{total}} · saindo de {{code}}',
  'flightPlanner.cancelEdit': 'Cancelar',
  'flightPlanner.back': 'Voltar',
  'flightPlanner.applyEdit': 'Atualizar',
  'flightPlanner.loadingPrices': 'Buscando preços…',
  'flightPlanner.noPriceTrend': 'Sem tendência de preço para o período',
  'flightPlanner.swap': 'Inverter origem e destino',
  'flightPlanner.analyzed': '{{count}} datas analisadas',
  'flightPlanner.reset': 'Redefinir',
  'flightPlanner.outboundTag': 'IDA',
  'flightPlanner.returnTag': 'VOLTA',
  'flightPlanner.prevMonth': 'Mês anterior',
  'flightPlanner.nextMonth': 'Próximo mês',
  'flightPlanner.search': 'Buscar voos nessas datas',
  /* O CTA curto do NAKED (#2167): pílula inline na fileira — o rótulo longo não
     cabe nela. O mesmo "Buscar" do `BotaoDeBusca` do protótipo. */
  'flightPlanner.searchShort': 'Buscar',
  /**
   * **O campo VAZIO pergunta; ele não descreve o que aceita.** É a régua do desenho
   * (`flight_planner_v2/campos_da_busca.tsx`, prop `vazio`): o pedido de cada ponta é
   * uma pergunta, e ela é o único texto do formulário que diz o que falta ali.
   *
   * Duas chaves e não uma: "De onde parte?" e "Para onde?" não são a mesma frase com
   * a ponta trocada, e o par assimétrico é o que faz cada campo se explicar sozinho
   * quando o rótulo sai do enquadramento (largura de bolso).
   *
   * No `PlaceField` a busca é NO CAMPO (decisão do designer, 2026-08-19): a
   * pergunta é o placeholder do campo fechado E do input aberto — um slot só.
   */
  'flightPlanner.fromEmpty': 'De onde parte?',
  'flightPlanner.toEmpty': 'Para onde?',
  // O convite abaixo de `minChars`, no corpo do painel (`promptLabel`).
  'flightPlanner.airportSearchPrompt': 'Digite para buscar',
  'flightPlanner.noAirports': 'Nenhum aeroporto',
  /** A BUSCA falhando (fornecedor de praças fora, #2566) — nunca a frase do
   *  vazio: "Nenhum aeroporto" numa indisponibilidade lê como "esse aeroporto
   *  não existe" e faz o viajante desistir do termo certo. */
  'flightPlanner.airportsSearchFailed': 'Não conseguimos buscar aeroportos agora. Tente de novo.',
  'flightPlanner.airportsSearchRetry': 'Tentar de novo',
  'flightPlanner.sameAirports': 'Origem e destino precisam ser diferentes.',
  /** Título da seção pré-digitação: o histórico do viajante nesta ponta da rota. */
  'flightPlanner.airportRecent': 'Recentes',
  'travelers.you': 'Você',
  'travelers.searchPlaceholder': 'Buscar pessoa',
  'travelers.searchError': 'Não foi possível buscar pessoas. Tente novamente.',
  'travelers.loadMore': 'Carregar mais',
  'travelers.policyLabel': 'Aplicar política de',
  'travelers.done': 'Pronto',
  'travelers.travelerRole': 'viajante',
  'travelers.selectAria': 'Selecionar viajantes',
  'travelers.count_one': '{{count}} viajante',
  'travelers.count_other': '{{count}} viajantes',
  'travelers.back': 'Voltar',
  'travelers.empty': 'Nenhuma pessoa encontrada',
  /** Grupo dos já escolhidos no topo da lista, sem busca digitada. */
  'travelers.selectedGroup': 'Na viagem',
  /** Legendas das linhas travadas pelo teto (1..9 do schema) e pelo piso. */
  'travelers.maxReached': 'Máximo de {{max}} viajantes',
  'travelers.minReached': 'Ao menos 1 viajante',
  /** O rodapé do popover: quantos vão na busca, ao lado do Pronto. */
  'travelers.inSearch_one': '{{count}} viajante na busca',
  'travelers.inSearch_other': '{{count}} viajantes na busca',
  'flightPlanner.tripType.oneWay': 'Somente ida',
  'flightPlanner.tripType.roundTrip': 'Ida e volta',
  'flightPlanner.tripType.multiLeg': 'Várias cidades',
  /** O eco da busca (#2327) — uma variante FECHADA por forma de viagem. O trecho
   *  do roteiro tem chave PRÓPRIA: a seta e os parênteses são pontuação, e
   *  pontuação é tradução. */
  'flightPlanner.searchTextOneWay':
    'Buscar voos de {{origin}} para {{destination}}, ida {{outbound}}.',
  'flightPlanner.searchTextRoundTrip':
    'Buscar voos de {{origin}} para {{destination}}, ida {{outbound}} e volta {{return}}.',
  'flightPlanner.searchTextMulti': 'Buscar voos: {{legs}}.',
  'flightPlanner.searchTextMultiLeg': '{{origin}} → {{destination}} ({{date}})',
  /* Modo colapsado: o gatilho da linha-resumo da viagem multi. */
  'flightPlanner.multiLeg.editLegs': 'Editar trechos',
  'flightPlanner.multiLeg.addLeg': 'Adicionar trecho',
  'flightPlanner.multiLeg.removeLeg': 'Remover trecho',
  /* O Redefinir da SAIA do multi (#2362): limpa todos os campos e devolve o
     construtor a 2 trechos. Chave própria, separada do `flightPlanner.reset`
     (a saia do popover do intervalo) e do `flightFilter.reset` "Redefinir todos". */
  'flightPlanner.multiLeg.reset': 'Redefinir',
  // "traslado": a origem divergiu de onde o trecho anterior chegou. Selo neutro, não
  // aviso — quem monta LIS → POA depois de pousar em JFK sabe o que está fazendo.
  'flightPlanner.multiLeg.transfer': 'traslado',
  'flightPlanner.multiLeg.summaryUndefined': 'A definir',
  // Primeira pessoa: quem fala é quem vai cotar. Nada de "envie no composer" —
  // `composer` é palavra de dentro do código.
  'flightPlanner.multiLeg.missing.origin': 'Falta a origem do trecho {{n}}.',
  'flightPlanner.multiLeg.missing.destination': 'Falta o destino do trecho {{n}}.',
  'flightPlanner.multiLeg.missing.date': 'Falta a data do trecho {{n}}.',
  'flightPlanner.multiLeg.ready': 'Seus {{count}} trechos estão prontos.',
  /** O motivo do selo "Recomendada" — a régua é determinística (a mais barata
   *  COM despacho declarado), e quem lê tem direito de saber qual é. */
  'fareSelector.recommendedWhy.checked': 'Mais barata com despacho incluído',
  'fareSelector.recommendedWhy.cheapest': 'Mais barata',
  /**
   * As cinco dimensões na forma de LINHA — o card de família (`FareFamilyCard`)
   * empilha uma frase por dimensão e lê de cima a baixo. Afirmativo e negativo
   * são chaves irmãs, não derivadas: o dia em que um idioma abrir a frase por
   * outra palavra, a divergência é de tradução, não de código.
   */
  'fareCard.carryYes': 'Bagagem de mão',
  'fareCard.carryNo': 'Sem bagagem de mão',
  'fareCard.checkedYes': 'Com despacho',
  'fareCard.checkedNo': 'Sem despacho',
  'fareCard.seatYes': 'Assento',
  'fareCard.seatNo': 'Sem escolha de assento',
  'fareCard.changeYes': 'Remarcação',
  'fareCard.changeNo': 'Sem remarcação',
  'fareCard.refundYes': 'Reembolso',
  'fareCard.refundNo': 'Sem reembolso',
  /** "Tem, mas cobra" na gramática de linha — frase FECHADA e não sufixo: em
   *  espanhol e inglês o qualificador não pende do mesmo lado da palavra. */
  'fareCard.fee': '{{benefit}} com taxa',
  /** "O fornecedor não disse" na gramática de linha. A omissão é uma LINHA, e
   *  nunca um "sem": a comparação horizontal da página depende de todas as
   *  colunas terem as mesmas cinco linhas. */
  'fareCard.unknown': '{{benefit}} · não informado',
  /** A linha incluída com a franquia que a tarifa declara (#6542): só o que ela diz —
   *  peças sem peso, peso sem peças. */
  'fareCard.allowancePiecesKg_one': '{{benefit}} · {{count}} peça de {{kg}} kg',
  'fareCard.allowancePiecesKg_other': '{{benefit}} · {{count}} peças de {{kg}} kg',
  'fareCard.allowancePieces_one': '{{benefit}} · {{count}} peça',
  'fareCard.allowancePieces_other': '{{benefit}} · {{count}} peças',
  'fareCard.allowanceKg': '{{benefit}} · até {{kg}} kg',
  /** DUAS vozes usam esta MESMA chave. (1) O `<summary>` do `<details>` que
   *  devolve as frases CRUAS do fornecedor — a camada de bullets que o legado
   *  mostrava (print 1) e que o intelligence descartava em silêncio para o
   *  que não cabe nas cinco dimensões (eCredit, milhas, cabine). Fechado por
   *  padrão: é fonte, não resumo. (2) A legenda `.ffs-source` do card
   *  (`fare_family_card.tsx`) quando `attributes.source === 'wooba'`: o
   *  fornecedor tipou a família (`details[]` do tariff) e o texto cru some da
   *  tela — a mesma frase muda de elemento, nunca as duas ao mesmo tempo. */
  'fareCard.supplierNotes': 'Regras informadas pelo fornecedor',
  /** Segunda voz: o mapa curado da VOLL por companhia casou a família e
   *  completou assento, troca e reembolso onde o fornecedor calou
   *  (`attributes.source === 'voll'`). Diz de quem é a voz, como a primeira. */
  'fareCard.curatedRules': 'Regras do fornecedor, completadas pela VOLL',
  /* A tela "Escolha as tarifas de cada voo" (#2262) — a fase 3 do ida e volta
     determinístico, onde a escolha de tarifa deixou a lista e virou página. */
  'flightFares.title': 'Escolha as tarifas de cada voo',
  /** Sem contagem de viajantes: a BUSCA de voo não tem eixo de passageiros (o
   *  corpo do `POST /flight/search` não tem `travelers`), então quem sabe quantas
   *  pessoas viajam é o host do checkout — e a casca do chat não sabe. */
  'flightFares.subtitle': '{{route}} · {{trip}}',
  'flightFares.subtitleWithTravelers': '{{route}} · {{trip}} · {{travelers}}',
  'flightFares.selectedFlights': 'Voos selecionados',
  'flightFares.airportSwitch.title': 'Atenção: aeroportos diferentes',
  'flightFares.airportSwitch.body':
    'A ida chega em {{arrival}}, mas a volta parte de {{departure}}.',
  'flightFares.airportSwitch.ack': 'Estou ciente de que a volta parte de outro aeroporto',
  'flightOffers.airportSwitch.row':
    'Parte de {{departure}} — aeroporto diferente da chegada da ida ({{arrival}})',
  'flightOffers.airportSwitch.rowExpected': 'Parte de {{departure}}',
  'flightExplorer.airportSwitchNotice':
    'A ida chega em {{arrival}}. Há voltas nesta lista partindo de outro aeroporto — confira antes de escolher.',
  'flightFares.changeFlight': 'Alterar voo',
  'flightFares.changeFlightAria': 'Alterar o voo de {{leg}}',
  'flightFares.faresOf': 'Tarifas · {{leg}}',
  'flightFares.perTraveler': '/viajante',
  'flightFares.railPrevious': 'Voltar em {{group}}',
  'flightFares.railNext': 'Avançar em {{group}}',
  'flightFares.checkout': 'Ir para checkout',
  'flightFares.selected': 'Selecionado',
  'flightFares.back': 'Voltar',
  'flightFares.titleShort': 'Escolha as tarifas',
  'flightFares.slideAria': 'Tarifa {{position}} de {{total}}',
  'flightFares.next': 'Próximo',
  'flightFares.summaryAria': 'Tarifas escolhidas',
  'rental.viewPolicies': 'Ver políticas',
  // A página de resultados do takeover "Ver mais opções" (a MESMA
  // `RentalResultsPage` da bancada determinística) — o que o card já não
  // tinha palavra para: contagens, ordenador, cortes e a pill de contexto.
  'rental.results.title': 'Carros em {{place}}',
  'rental.results.total_one': 'Encontramos {{count}} carro',
  'rental.results.total_other': 'Encontramos {{count}} carros',
  'rental.results.visible': '{{visible}} de {{total}} carros',
  'rental.results.resultsLabel': 'Carros encontrados',
  'rental.results.filteredEmpty': 'Nenhum carro com esses filtros.',
  'rental.results.clearFilters': 'Limpar filtros',
  'rental.results.applyFilters': 'Aplicar filtros',
  'rental.results.showMap': 'Mapa',
  'rental.results.loadMore': 'Ver mais carros',
  'rental.results.sort': 'Ordenar',
  'rental.results.chips.toolbar': 'Filtros e ordenação',
  'rental.results.chips.back': 'Voltar',
  'rental.results.chips.policy': 'Dentro da política',
  'rental.results.chips.dailyPrice': 'Diária',
  'rental.results.chips.priceUpTo': 'Até {{price}}',
  'rental.results.chips.priceFrom': 'A partir de {{price}}',
  'rental.results.chips.priceBetween': '{{min}} – {{max}}',
  'rental.results.order.recommended': 'Recomendado',
  'rental.results.order.price': 'Preço',
  'rental.results.order.distance': 'Distância',
  'rental.results.order.category': 'Categoria',
  'rental.results.order.company': 'Agência',
  'rental.results.filters.collapse': 'Recolher os filtros',
  'rental.results.filters.close': 'Fechar os filtros',
  'rental.results.filters.accessories': 'Acessórios',
  'rental.results.filters.automatic': 'Automático',
  'rental.results.filters.airConditioning': 'Ar condicionado',
  'rental.results.filters.dailyPrice': 'Diária até',
  'rental.results.filters.minimumPrice': 'Diária mínima',
  'rental.results.filters.maximumPrice': 'Diária máxima',
  'rental.results.filters.company': 'Agência',
  'rental.results.priceDetail_one': '{{count}} diária',
  'rental.results.priceDetail_other': '{{count}} diárias · {{daily}}/diária',
  'rental.results.dailyPrice': '{{daily}}/diária',
  'rental.results.cheapest': 'Menor preço',
  'rental.results.agencyFallback': 'Agência {{company}}',
  'rental.results.pillAria': 'Contexto da busca de carros',
  'rental.results.close': 'Fechar carros',
  'rental.vehicle.orSimilar': 'ou similar',
  'rental.drawer.title': 'Todos os veículos',
  'rental.filters': 'Filtros',
  'rental.reset': 'Redefinir',
  'rental.category': 'Categoria',
  'rental.company': 'Locadora',
  'rental.policy': 'Política',
  'rental.onlyInPolicy': 'Apenas dentro da política',
  'rental.loadingVehicles': 'Carregando veículos…',
  'rental.loadError': 'Não foi possível carregar. Tente de novo.',
  'rental.automatic': 'Automático',
  'rental.automaticShort': 'Autom.',
  'rental.manual': 'Manual',
  'rental.airConditioning': 'Ar cond.',
  'rental.noAirConditioning': 'Sem ar',
  'rental.inPolicy': 'Na política',
  'rental.outPolicy': 'Fora da política',
  'rental.rent': 'Alugar',
  /** O eco do pick "Alugar" (#2327/D5) — mesma régua de `busOffers.pickText`:
   *  frase FECHADA, montada com `{{label}}` já pontuado (espaço + parênteses,
   *  ou vazio quando o veículo não tem nome). */
  'rentalOffers.pickText': 'Quero o {{title}} na {{company}}, {{total}}.',
  'rentalOffers.title': 'Melhores carros',
  'rentalOffers.total_one': '{{count}} carro',
  'rentalOffers.total_other': '{{count}} carros',
  'rentalOffers.viewPolicies': 'Ver suas políticas',
  'rentalOffers.seeAll': 'Ver todos os carros',
  'rentalOffers.slideAria': 'Carro {{position}} de {{total}}',
  'rentalOffers.badge.recommended': 'Recomendado',
  'rentalOffers.badge.cheapest': 'Menor preço',
  'rentalOffers.badge.closest': 'Mais perto',
  'rentalOffers.merit.recommended': 'Menor total entre os carros dentro da sua política.',
  'rentalOffers.merit.recommendedOutside': 'Menor total da busca.',
  'rentalOffers.merit.cheapest': '{{amount}} a menos que o recomendado.',
  'rentalOffers.merit.closest': 'Retirada a {{distance}} do endereço buscado.',
  'rental.policyDrawer.title': 'Políticas de locação',
  'rental.deckToggleAria': 'Expandir ou recolher lista de veículos',
  'rental.selection.continue': 'Continuar',
  'rental.selection.chooseAnother': 'Escolher outro veículo',
  // Dica do campo CPF EDITÁVEL (sem CPF em ficha, #5809): o CPF digitado vai na
  // reserva e preenche o perfil do próprio viajante. O par é escolhido pelo
  // `cpfNotOnFileHint` do document_field: a variante com passaporte/DNI só
  // aparece quando o manifesto oferece a alternância.
  'productCheckout.hint.cpfNotOnFile':
    'CPF não cadastrado. Informe o CPF aqui ou use passaporte/documento nacional.',
  'productCheckout.hint.cpfNotOnFileNoAlternative': 'CPF não cadastrado. Informe o CPF aqui.',
  'productCheckout.hint.cpfInvalid': 'CPF inválido. Confira os números.',
  'productCheckout.chooseAnotherReply': 'Quero ver as outras opções.',
  'mobility.sectionRide': 'Corrida',
  'mobility.sectionCarDriver': 'Carro com motorista',
  'mobility.inPolicy': 'Na política',
  'mobility.outPolicy': 'Fora da política',
  'mobility.choose': 'Escolher',
  'mobility.chosen': 'Escolhida',
  'mobility.chooseAria': 'Escolher {{title}}, {{price}}, {{label}}',
  'mobility.estimate': 'valor previsto',
  'mobility.roundTripEstimate': 'ida e volta · valor previsto',
  'mobility.estimateNote': 'O valor final depende do trajeto e do trânsito.',
  'mobility.distanceKmPerLeg': '{{km}} km por trecho',
  'mobility.overLimit': 'Fora da política · {{km}} km, o limite da sua empresa é {{limit}} km',
  'mobility.overLimitNoCap': 'Fora da política · {{km}} km, acima do limite da sua empresa',
  'mobility.priceOnRequest': 'Sob consulta',
  'mobility.distanceKm': '{{km}} km',
  'mobility.hoursPerDay': '{{hours}}h/dia',
  'mobility.day_one': '{{count}} diária',
  'mobility.day_other': '{{count}} diárias',
  'mobility.bilingual': 'Motorista bilíngue',
  'mobility.overnight_one': '{{count}} pernoite',
  'mobility.overnight_other': '{{count}} pernoites',
  'mobility.stops_one': 'Parada: {{stops}}',
  'mobility.stops_other': 'Paradas: {{stops}}',
  'mobility.freeKm': 'km livre',
  'mobility.excludesStops': 'Valor previsto sem as paradas.',
  'mobility.franchise': 'Diária {{hours}}h com {{km}} km',
  'mobility.franchiseFreeKm': 'Diária {{hours}}h com km livre',
  'mobility.perDailySuffix': 'por diária',
  'mobility.daysOfDaily_one': '{{count}} diária de {{daily}}',
  'mobility.daysOfDaily_other': '{{count}} diárias de {{daily}}',
  'productCheckout.otherOptions': 'Ver outras opções',
  'productCheckout.frozenNotice':
    'Esta cotação não está mais ativa. Peça ao agente para cotar de novo.',
  'productCheckout.mobility.cooperative': 'Táxi de cooperativa',
  'productCheckout.mobility.outward': 'Ida',
  'productCheckout.mobility.return': 'Volta',
  'productCheckout.mobility.start': 'Início',
  'productCheckout.mobility.pickup': 'saída',
  'productCheckout.mobility.package': 'Pacote',
  'productCheckout.mobility.companions_one': '{{count}} acompanhante',
  'productCheckout.mobility.companions_other': '{{count}} acompanhantes',
  'productCheckout.mobility.companionsLabel': 'Acompanhantes',
  'productCheckout.mobility.extras': 'Extras',
  'productCheckout.mobility.details': 'Detalhes',
  'productCheckout.mobility.detailsTitle': 'Detalhes da corrida',
  'productCheckout.mobility.pickupAt': 'Saída',
  'productCheckout.mobility.from': 'Origem',
  'productCheckout.mobility.to': 'Destino',
  'productCheckout.mobility.ride': 'Corrida',
  'productCheckout.mobility.policy': 'Política',
  'productCheckout.mobility.stops': 'Paradas',
  'productCheckout.mobility.stop': 'Parada',
  'productCheckout.mobility.flight': 'Voo',
  'productCheckout.mobility.totalEstimated': 'Total previsto',
  'productCheckout.mobility.totalEstimatedRoundTrip': 'Total previsto · ida e volta',
  'productCheckout.mobility.estimateNote': 'O valor final depende do trajeto e do trânsito.',
  'productCheckout.mobility.notes': 'Observações',
  'productCheckout.mobility.contact': 'Contato',
  'productCheckout.mobility.done': 'Corrida agendada',
  'productCheckout.mobility.pendingApproval':
    'Enviada para aprovação. A corrida será agendada quando o gestor aprovar.',
  'productCheckout.mobility.pendingApprovalShort': 'Enviada para aprovação',
  'productCheckout.mobility.outOfPolicyDistance':
    'Fora da política: {{km}} km, limite {{limit}} km',
  'productCheckout.mobility.outOfPolicyKm': 'Fora da política: {{km}} km',
  'productCheckout.oneClick.suggested': 'com sugestão da IA',
  'mobilityRideDetails.title': 'Detalhes da corrida',
  'mobilityRideDetails.phoneSelf': 'Seu telefone',
  'mobilityRideDetails.phoneOf': 'Telefone de {{name}}',
  'mobilityRideDetails.phoneHelpSelf': 'O motorista usa este número só nesta corrida.',
  'mobilityRideDetails.phoneHelpOf':
    'O motorista usa este número só nesta corrida. Ele não fica salvo no perfil de {{name}}.',
  'mobilityRideDetails.phoneError': 'Confira o número com DDD',
  'mobilityRideDetails.phonePlaceholder': '+55 (11) 9 0000-0000',
  'mobilityRideDetails.notes': 'Observações para o motorista (opcional)',
  'mobilityRideDetails.notesHelp':
    'Ex.: número do voo, motorista de preferência, veículo acessível.',
  'mobilityRideDetails.continue': 'Continuar',
  'mobilityRideDetails.echoSelf': 'Meu telefone: {{phone}}.',
  'mobilityRideDetails.echoOf': 'Telefone de {{name}}: {{phone}}.',
  'mobilityRideDetails.echoNotes': 'Recado: {{notes}}',
  // O card 1click (#5279): o próprio card confere e solicita; a tela cheia
  // fica atrás de «Revisar dados».
  'productCheckout.loyalty.applied': 'Programa de milhas aplicado',
  'productCheckout.loyalty.appliedProgram': 'Programa de fidelidade aplicado',
  'productCheckout.loyalty.add': 'Adicionar programa de fidelidade',
  'productCheckout.loyalty.addAria': 'Adicionar {{program}} ao perfil',
  'productCheckout.loyalty.aria': 'Programas de fidelidade',
  'productCheckout.oneClick.reviewTitle': 'Revise os dados abaixo',
  'productCheckout.oneClick.prefilledHint': 'Com base no seu perfil. Confira se está certo.',
  'productCheckout.oneClick.completeTitle': 'Complete seus dados',
  'productCheckout.oneClick.completeHint':
    'Faltam no seu perfil. O que você preencher aqui fica salvo nele.',
  'productCheckout.oneClick.completeTitleGuest': 'Complete os dados de {{name}}',
  'productCheckout.oneClick.completeHintGuest':
    'Faltam no perfil de {{name}}. Valem só para esta reserva.',
  'productCheckout.oneClick.prefilledHintMulti':
    'Com base no perfil de cada viajante. Confira se está certo.',
  'productCheckout.oneClick.bookingTitle': 'Do pedido (vale para todos)',
  'productCheckout.oneClick.required': 'Obrigatório',
  'productCheckout.oneClick.review': 'Revisar dados',
  'productCheckout.oneClick.submit': 'Pedir com 1-Click',
  'productCheckout.oneClick.budgetInsufficient': 'Saldo insuficiente para esta solicitação.',
  'productCheckout.oneClick.submitting': 'Solicitando',
  'productCheckout.oneClick.done': 'Solicitado com sucesso!',
  'productCheckout.oneClick.indeterminate': 'Solicitação em análise. Avisaremos quando confirmar.',
  'productCheckout.oneClick.failed': 'Não deu para solicitar. Veja o motivo na resposta abaixo.',
  'productCheckout.oneClick.failedNewCheckout':
    'Não deu para solicitar. Há um novo pedido logo abaixo.',
  'productCheckout.oneClick.unsent': 'Não conseguimos enviar. Tente de novo.',
  'productCheckout.oneClick.loadError':
    'Não deu para carregar seus dados. Revise e solicite pela tela completa.',
  'productCheckout.oneClick.loading': 'Carregando seus dados',
  'productCheckout.outOfPolicy': 'Fora da política',
  'productCheckout.reserveRental': 'Solicitar carro',
  'productCheckout.hotel.outOfPolicy': 'Acima do teto da política',
  'productCheckout.reserveFlight': 'Solicitar aéreo',
  'checkoutTravelDocs.title': 'Documentos da viagem',
  'checkoutTravelDocs.source': 'Pela base IATA Timatic',
  'checkoutTravelDocs.fallback': 'Não conseguimos consultar a base agora',
  'checkoutTravelDocs.entry': 'Entrada · {{country}}',
  'checkoutTravelDocs.transit': 'Conexão · {{country}}',
  'checkoutTravelDocs.have': 'Tenho',
  'checkoutTravelDocs.status.required': 'Exigido',
  'checkoutTravelDocs.status.attention': 'Atenção',
  'checkoutTravelDocs.status.ok': 'OK',
  'checkoutTravelDocs.detail': 'Ver detalhe',
  'checkoutTravelDocs.footer':
    'Quem valida é a companhia aérea e a imigração; confirme com o consulado.',
  'productCheckout.flight.outOfPolicyAria': 'Voo fora da política',
  'productCheckout.reserveHotel': 'Solicitar hotel',
  'productCheckout.reserveBus': 'Solicitar ônibus',
  'productCheckout.reserveMobility': 'Solicitar corrida',
  'productCheckout.rentalDate': 'Data locação',
  'productCheckout.rentalDays': 'Número diárias',
  'productCheckout.pickupLocation': 'Local retirada',
  // O bloco «Contrato» do carro no card 1click (#5282).
  'productCheckout.rental.orSimilar': '{{model}} ou similar',
  'productCheckout.rental.daysNote_one': 'pela {{count}} diária',
  'productCheckout.rental.daysNote_other': 'pelas {{count}} diárias',
  'productCheckout.hotelStay': 'Período da estadia',
  // O bloco «Hospedagem» do hotel no card 1click (#5281).
  'productCheckout.hotel.head': 'Hospedagem',
  'productCheckout.hotel.distanceFrom': '{{km}} km de {{place}}',
  'productCheckout.hotel.details': 'Detalhes',
  'productCheckout.hotel.nightsNote_one': 'pela {{count}} noite',
  'productCheckout.hotel.nightsNote_other': 'pelas {{count}} noites',
  'productCheckout.genderMale': 'Masculino',
  'productCheckout.genderFemale': 'Feminino',
  'productCheckout.documentMatchWarning': 'Os dados precisam ser idênticos ao documento de viagem.',
  'productCheckout.motiveTitle': 'Qual o motivo da viagem?',
  'productCheckout.mobility.motiveTitle': 'Qual o motivo da corrida?',
  'productCheckout.mobility.blockerMotive': 'motivo da corrida',
  'productCheckout.fieldJustificationOptional': 'Justificativa (opcional)',
  'productCheckout.fieldProjectOptional': 'Projeto (opcional)',
  'productCheckout.budget.sufficientIn':
    'Limite suficiente em {{costCenter}} para esta solicitação.',
  'productCheckout.budget.warning': 'Atenção! O limite está chegando ao fim.',
  'productCheckout.budget.sufficient': 'Limite suficiente para a solicitação.',
  'rental.priceChange.continue': 'Continuar mesmo assim',
  'rental.priceChange.cancel': 'Cancelar',
  /** Ecos do `rental_price_change` (#2327/D5) — "Continuar" tem DUAS variantes
   *  fechadas (canal `rental-select`/`reply` vs. `interaction`, mesma frase nos
   *  dois primeiros; ver `rental_price_change/index.tsx`), e "Cancelar" uma. */
  'rentalPriceChange.replyContinue': 'Quero continuar mesmo com o novo preço.',
  'rentalPriceChange.replyContinueChallenge': 'Quero continuar com o novo preço.',
  'rentalPriceChange.replyCancel': 'Prefiro cancelar.',
  'booking.summaryAria': 'Resumo da reserva',
  'booking.inPolicy': 'Dentro da política',
  'booking.continuePurchase': 'Seguir com a compra',
  'booking.yourReservation': 'Sua reserva',
  'booking.fareClass': 'Classe',
  'booking.priceMayChange': 'Os preços podem variar entre a solicitação e a aprovação da viagem.',
  'booking.submitting': 'Enviando…',
  'booking.submit': 'Solicitar viagem',
  'approval.requestAria': 'Aprovação de voucher',
  'approval.resultAria': 'Resultado da aprovação',
  'approval.bulkResultAria': 'Resultado da aprovação em lote',
  'approval.pendingAria': 'Aprovações pendentes',
  'approval.voucher': 'Voucher',
  'approval.modal': 'Modal',
  'approval.traveler': 'Viajante',
  'approval.approver': 'Aprovador',
  'approval.justificationOptional': 'Justificativa (opcional)',
  'approval.reasonDecision': 'Motivo da decisão…',
  'approval.reasonDeny': 'Motivo da reprovação…',
  'approval.sent': 'Decisão enviada',
  'approval.approve': 'Aprovar',
  'approval.deny': 'Reprovar',
  'approval.approved': 'Aprovado',
  'approval.denied': 'Reprovado',
  'approval.publishFailed': '{{label}} — falha ao publicar',
  'approval.failures': 'Falhas',
  'approval.bulkSummary_one': '{{approved}} de {{total}} aprovado',
  'approval.bulkSummary_other': '{{approved}} de {{total}} aprovados',
  'approval.loose': 'Avulsos',
  'approval.tripFallback': 'Viagem',
  'approval.approveTrip': 'Aprovar viagem',
  'approval.approveTraveler': 'Aprovar do viajante',
  'approval.approveAll': 'Aprovar tudo',
  'approval.textApproveVoucher': 'Aprovar voucher.',
  'approval.textDenyVoucher': 'Reprovar voucher.',
  'approval.textApproveTrip': 'Aprovar a viagem {{name}}.',
  'approval.textApproveTraveler': 'Aprovar tudo de {{name}}.',
  'approval.textApproveAll': 'Aprovar todas as pendências.',
  // ── checkout corporativo (drawer "Solicitar viagem") ──
  'bookingCheckout.select': 'Selecionar',
  // ── detalhe do voo (drawer "Detalhe do voo") ──
  'flightDetail.title': 'Detalhe do voo',
  'flightDetail.drawerAria': 'Detalhe do voo',
  'flightDetail.backAria': 'Voltar',
  'flightDetail.closeAria': 'Fechar',
  'flightDetail.loading': 'Carregando detalhe do voo…',
  'flightDetail.loadError': 'Não foi possível carregar o detalhe do voo.',
  'flightDetail.retry': 'Tentar de novo',
  'flightDetail.select': 'Selecionar voo',
  'flightDetail.stop': 'Parada de {{duration}}',
  'flightDetail.aircraftChange': 'troca de aeronave',
  'flightDetail.flightDuration': '{{duration}} de voo',
  'flightDetail.totalDuration': '{{duration}} no total',
  'flightDetail.connection': '{{duration}} de conexão em {{airport}}',
  'flightDetail.continuesOn': 'continua no voo {{flight}}',
  'flightDetail.terminal': 'Terminal {{terminal}}',
  'flightDetail.onTimeStat': 'No horário em {{percent}}% dos últimos {{count}} voos',
  'flightDetail.soldAs': 'Vendido também como {{flights}}',
  'flightDetail.partnerDataNote':
    'Aeronave, terminal, pontualidade e codeshares: dados de parceiros, sujeitos a alteração.',
  'flightDetail.operatedBy': 'Operado por {{name}}',
  'flightDetail.dirOutbound': 'Ida',
  'flightDetail.dirReturn': 'Volta',
  // ── Mapa de assentos (drawer de voo) ──
  'flightSeatMap.sectionTitle': 'Mapa de assentos',
  'flightSeatMap.stripLabel': 'Assentos',
  'flightSeatMap.stripOpenAria': 'Ver mapa de assentos de {{route}}',
  'flightSeatMap.stripRetryAria': 'Tentar carregar o mapa de assentos de {{route}} de novo',
  'flightSeatMap.retry': 'Tentar de novo',
  'flightSeatMap.available_one': '1 assento livre',
  'flightSeatMap.available_other': '{{count}} assentos livres',
  'flightSeatMap.paidFrom': 'pagos desde {{price}}',
  'flightSeatMap.freeFromRow': 'grátis a partir da fileira {{row}}',
  'flightSeatMap.legendFree': 'Grátis',
  'flightSeatMap.legendPaid': 'Pago',
  'flightSeatMap.legendExtra': 'Espaço extra',
  'flightSeatMap.legendOccupied': 'Ocupado',
  'flightSeatMap.legendUnavailable': 'Indisponível',
  'flightSeatMap.exitRow': 'Saída de emergência',
  'flightSeatMap.rowAria': 'Fileira {{row}}',
  'flightSeatMap.loading': 'Carregando mapa de assentos…',
  'flightSeatMap.error': 'Não foi possível carregar o mapa de assentos.',
  'flightSeatMap.unavailable': 'Mapa de assentos indisponível para este voo.',
  'flightSeatMap.supplierUnavailable':
    'Mapa de assentos não disponível para consulta antes da compra.',
  'flightSeatMap.errorFinal': 'Mapa de assentos indisponível no momento.',
  'flightSeatMap.stripUnavailableAria': 'Mapa de assentos de {{route}} indisponível',
  'flightSeatMap.quoteExpired': 'O mapa de assentos não está mais disponível para esta cotação.',
  'flightSeatMap.disclaimer':
    'Leitura, não escolha · preço e escolha do assento acontecem na emissão.',
  // ── checkout de locação (drawer + cards) ──
  'productCheckout.closeAria': 'Fechar',
  // Rodapé do drawer no touch: o ✕ do topbar fica longe do polegar, então o
  // rodapé oferece a MESMA saída com verbo de navegação (não "cancelar" — o
  // descarte quem decide é a confirmação do host, este botão só abre a porta).
  'productCheckout.back': 'Voltar',
  // Condição de cancelamento da tarifa escolhida — o dado que muda a decisão
  // de apertar "Solicitar". Tri-estado: sem o campo no wire, nada é dito.
  'productCheckout.refundable': 'Reembolsável',
  'productCheckout.nonRefundable': 'Sem reembolso',
  'productCheckout.drawerTitle': 'Revisar e solicitar',
  'productCheckout.agentLine':
    'Adiantei seu checkout com o que já sei. Confira e complete o que faltar.',
  'productCheckout.inheritedHint': 'Herdado do seu perfil — só confirmo com você por garantia.',
  'productCheckout.inheritedHintOther':
    'Herdado do perfil do viajante — só confirmo com você por garantia.',
  'productCheckout.submitting': 'Solicitando',
  'productCheckout.retry': 'Tentar de novo',
  'productCheckout.action.submitWithTotal': 'Solicitar {{product}} · {{total}}',
  'productCheckout.price.approvalNote': 'Vai para aprovação. O preço pode variar até a emissão.',
  'productCheckout.trip.sectionTitle': 'Sua viagem',
  'productCheckout.trip.title': 'Viagem para {{city}}',
  'productCheckout.trip.alreadyBooked': 'Já na viagem',
  'productCheckout.trip.beingRequested': 'Nesta compra',
  'productCheckout.bus.seats': 'Poltronas',
  'productCheckout.bus.seat_one': 'Poltrona {{seats}}',
  'productCheckout.bus.totalRoundTrip': 'ida e volta, por pessoa',
  'productCheckout.bus.totalOneWay': 'só ida, por pessoa',
  'productCheckout.bus.seat_other': 'Poltronas {{seats}}',
  'productCheckout.bus.segment': 'Trecho {{n}}',
  'productCheckout.trip.nights_one': '{{count}} noite',
  'productCheckout.trip.nights_other': '{{count}} noites',
  // O checkout do pedido OFFLINE à equipe VOLL (`concierge_checkout`) — o concierge do legado.
  'conciergeCheckout.title': 'Revisar e solicitar à equipe VOLL',
  'conciergeCheckout.chip.flight': 'Aéreo offline',
  'conciergeCheckout.chip.hotel': 'Hotel offline',
  'conciergeCheckout.chip.bus': 'Ônibus offline',
  'conciergeCheckout.chip.rental': 'Carro offline',
  'conciergeCheckout.submit': 'Solicitar à equipe VOLL',
  'conciergeCheckout.back': 'Voltar',
  'conciergeCheckout.optional': 'opcional',
  'conciergeCheckout.noPriceBadge': 'Sem valor · a cotar',
  'conciergeCheckout.profileLabel': 'Perfil herdado da busca',
  'conciergeCheckout.profileHint': 'Herdado da busca. Para mudar, volte aos resultados.',
  'conciergeCheckout.reasonTitle': 'Por que pedir à equipe',
  'conciergeCheckout.reasonLoading': 'Carregando motivos…',
  'conciergeCheckout.detailsLabel': 'Observação para a equipe',
  'conciergeCheckout.flight.cardTitle': 'Pedido à equipe VOLL · aéreo',
  'conciergeCheckout.flight.asksTitle': 'O que você precisa nesse voo',
  'conciergeCheckout.flight.direct': 'Só voo direto',
  'conciergeCheckout.flight.luggage': 'Só com bagagem',
  'conciergeCheckout.flight.periodFor': 'Período · {{route}} · {{date}}',
  'conciergeCheckout.flight.periodAny': 'Indiferente',
  'conciergeCheckout.flight.detailsPlaceholder':
    'Ex.: reunião termina 17h em Alphaville; qualquer coisa a partir das 19h serve, GRU também.',
  'conciergeCheckout.period.morning': 'Manhã',
  'conciergeCheckout.period.afternoon': 'Tarde',
  'conciergeCheckout.period.night': 'Noite',
  'conciergeCheckout.period.dawn': 'Madrugada',
  'conciergeCheckout.hotel.cardTitle': 'Pedido à equipe VOLL · hospedagem',
  'conciergeCheckout.hotel.preferredHotel': 'Hotel preferido (opcional)',
  'conciergeCheckout.hotel.preferredHotelPlaceholder': 'Nome do hotel, se você já tem um em mente',
  'conciergeCheckout.hotel.bedType': 'Tipo de cama',
  'conciergeCheckout.hotel.bed.UNDEFINED': 'Indiferente',
  'conciergeCheckout.hotel.bed.SINGLE': 'Solteiro',
  'conciergeCheckout.hotel.bed.DOUBLE': 'Casal',
  'conciergeCheckout.hotel.detailsPlaceholder':
    'Ex.: reunião na Faria Lima às 9h, café incluso ajuda; cliente sugeriu o Unique.',
  'conciergeCheckout.hotel.detailsHint': 'A equipe usa esse texto como briefing da cotação.',
  'conciergeCheckout.panel.inProgress': 'Em construção',
  'conciergeCheckout.panel.flightItem': '{{route}} · pedido à equipe',
  'conciergeCheckout.panel.hotelItem': 'Hotel · pedido à equipe',
  'conciergeCheckout.panel.toQuote': 'a cotar',
  'conciergeCheckout.panel.requested': 'Solicitado',
  'conciergeCheckout.panel.heldOutbound': 'Guardada no pedido · não emitida agora',
  'conciergeCheckout.panel.totalLabel': 'Total da viagem',
  'conciergeCheckout.panel.totalToQuote': 'Sem valor até a cotação',
  'conciergeCheckout.panel.totalWithHeld': '{{held}} + trecho a cotar',
  'conciergeCheckout.panel.flightNote':
    'Nada é emitido agora. A equipe cota o trecho e você aprova tudo junto quando a cotação chegar na conversa da viagem.',
  'conciergeCheckout.panel.hotelNote':
    'O hotel entra no itinerário sem valor e ganha preço quando a cotação chegar. Política e teto já viajam no pedido.',
  'conciergeCheckout.panel.busItem': '{{route}} · pedido à equipe',
  'conciergeCheckout.panel.busNote':
    'Nada é emitido agora. A equipe cota a passagem e você aprova quando a cotação chegar na conversa da viagem.',
  'conciergeCheckout.bus.cardTitle': 'Pedido à equipe VOLL · ônibus',
  'conciergeCheckout.bus.asksTitle': 'Como você quer viajar',
  'conciergeCheckout.bus.choose': 'Selecione',
  'conciergeCheckout.bus.periodFor': 'Período · {{leg}} · {{date}}',
  'conciergeCheckout.bus.leg.outbound': 'Ida',
  'conciergeCheckout.bus.leg.return': 'Volta',
  'conciergeCheckout.bus.seatPosition': 'Posição no ônibus',
  'conciergeCheckout.bus.seatPosition.FRONT': 'Frente',
  'conciergeCheckout.bus.seatPosition.MIDDLE': 'Meio',
  'conciergeCheckout.bus.seatPosition.BACK': 'Fundo',
  'conciergeCheckout.bus.rowPosition': 'Posição na fileira',
  'conciergeCheckout.bus.rowPosition.WINDOW_SEAT': 'Janela',
  'conciergeCheckout.bus.rowPosition.AISLE_SEAT': 'Corredor',
  'conciergeCheckout.bus.seatClass': 'Classe',
  'conciergeCheckout.bus.seatClass.ALL': 'Todas',
  'conciergeCheckout.bus.seatClass.CONVENTIONAL': 'Convencional',
  'conciergeCheckout.bus.seatClass.EXECUTIVE': 'Executivo',
  'conciergeCheckout.bus.seatClass.HALF_BED': 'Semi-leito',
  'conciergeCheckout.bus.seatClass.BED': 'Leito',
  'conciergeCheckout.bus.seatClass.BED_FULL': 'Leito total',
  'conciergeCheckout.bus.detailsPlaceholder':
    'Ex.: mais de um trecho, horário de preferência, orçamento disponível, seguro viagem.',
  'conciergeCheckout.bus.detailsHint':
    'A equipe usa esse texto para cotar, e quem aprova a viagem também o lê.',
  'conciergeCheckout.rental.cardTitle': 'Pedido à equipe VOLL · carro',
  'conciergeCheckout.rental.category': 'Categoria do carro',
  'conciergeCheckout.rental.company': 'Locadora',
  'conciergeCheckout.rental.companyAny': 'Indiferente',
  'conciergeCheckout.rental.optionsLoading': 'Carregando categorias e locadoras…',
  'conciergeCheckout.rental.optionsFailed':
    'Não foi possível carregar as categorias de carro. Sem elas o pedido não pode ser enviado.',
  'conciergeCheckout.rental.retry': 'Tentar de novo',
  'conciergeCheckout.rental.detailsPlaceholder':
    'Ex.: cadeirinha infantil, segundo motorista, retirada direto no desembarque.',
  'conciergeCheckout.rental.detailsHint':
    'A equipe usa esse texto como briefing da cotação: extras, motorista adicional, agência.',
  'conciergeCheckout.panel.rentalItem': 'Carro · pedido à equipe',
  'conciergeCheckout.panel.rentalNote':
    'O carro entra no itinerário sem valor e ganha preço quando a cotação chegar. A política da categoria já viaja no pedido.',
  'productCheckout.noun.flight': 'voo',
  'productCheckout.noun.hotel': 'hotel',
  'productCheckout.noun.bus': 'ônibus',
  'productCheckout.noun.rental': 'carro',
  'productCheckout.noun.mobility': 'corrida',
  'productCheckout.chipLabel.flight': 'Aéreo',
  'productCheckout.chipLabel.hotel': 'Hotel',
  'productCheckout.chipLabel.bus': 'Ônibus',
  'productCheckout.chipLabel.rental': 'Carro',
  'productCheckout.chipLabel.mobility': 'Mobilidade',
  'productCheckout.loadingAria': 'Carregando reserva',
  'productCheckout.loading': 'Carregando reserva…',
  'productCheckout.errorMessage':
    'Não deu para carregar seus dados para a reserva. Nada foi enviado.',
  'productCheckout.fieldCompany': 'Empresa',
  'productCheckout.fieldCostCenter': 'Centro de custo',
  'productCheckout.fieldMotive': 'Motivo',
  'productCheckout.fieldProject': 'Projeto',
  'productCheckout.fieldJustification': 'Justificativa',
  'productCheckout.selectPlaceholder': 'Selecione',
  'productCheckout.motiveSearchPlaceholder': 'Buscar motivo',
  'productCheckout.motiveSearchEmpty': 'Nenhum motivo encontrado',
  'productCheckout.companySearchEmpty': 'Nenhuma empresa encontrada',
  'productCheckout.projectSearchEmpty': 'Nenhum projeto encontrado',
  'productCheckout.costCenterSearchPrompt': 'Digite para buscar',
  'productCheckout.costCenterSearchLoading': 'Buscando…',
  'productCheckout.costCenterSearchEmpty': 'Nenhum centro de custo encontrado',
  'productCheckout.split.percent': 'Porcentagem',
  'productCheckout.split.add': 'Adicionar centro de custo',
  'productCheckout.split.remove': 'Remover centro de custo',
  'productCheckout.split.row': 'Centro de custo {{position}}',
  'productCheckout.split.approvalFlow': 'Qual será o fluxo de aprovação?',
  'productCheckout.split.approvalFlowPhrase1':
    'O fluxo de aprovação é determinado pelo percentual do rateio informado para cada centro de custo.',
  'productCheckout.split.approvalFlowPhrase2':
    'Sempre será utilizado o fluxo de aprovação do centro de custo com a maior porcentagem, a menos que esteja configurado um fluxo específico para o usuário.',
  'productCheckout.split.approvalFlowPhrase3':
    'Em situações em que os maiores percentuais são idênticos, o fluxo de aprovação adotado será o do primeiro centro de custo informado que tenha a maior porcentagem.',
  'productCheckout.split.zeroPercent': 'Cada centro de custo precisa ter mais de 0%.',
  'productCheckout.split.sum': 'A soma dos centros de custo deve ser igual a 100%.',
  'productCheckout.split.repeated':
    'O mesmo centro de custo não pode aparecer duas vezes no rateio.',
  'rental.card.traveler': 'Viajante',
  'productCheckout.card.price': 'Valor da viagem',
  'rental.field.firstName': 'Nome',
  'rental.field.lastName': 'Sobrenome',
  'productCheckout.field.birthdate': 'Data de nascimento',
  'productCheckout.field.datePick': 'Escolher',
  'productCheckout.field.dateConfirm': 'Confirmar',
  'productCheckout.field.dateBack': 'Voltar',
  'productCheckout.field.datePrevMonth': 'Mês anterior',
  'productCheckout.field.dateNextMonth': 'Próximo mês',
  'productCheckout.field.nationality': 'Nacionalidade',
  'rental.field.gender': 'Gênero',
  'productCheckout.field.document': 'Documento',
  'productCheckout.field.cpf': 'CPF',
  'productCheckout.field.passport': 'Passaporte',
  'productCheckout.field.nationalDoc': 'Documento Nacional',
  'productCheckout.field.documentExpiry': 'Validade',
  'productCheckout.field.issuingCountry': 'País emissor',
  'productCheckout.field.nationalityPlaceholder': 'Selecione o país',
  'productCheckout.field.countrySearch': 'Buscar país',
  'productCheckout.field.countryEmpty': 'Nenhum país encontrado',
  'productCheckout.field.countryUnavailable':
    'Lista de países indisponível no momento. O valor gravado fica como está.',
  'productCheckout.field.countryOutsideCatalog': 'gravado no perfil',
  'productCheckout.field.countryRetry': 'Tentar de novo',
  'productCheckout.field.internalNotes': 'Observação para o aprovador',
  'productCheckout.field.internalNotesPlaceholder':
    'Contexto que ajuda quem aprova a decidir sem te perguntar.',
  'productCheckout.field.gender': 'Sexo (como no documento)',
  'productCheckout.field.genderNote':
    'Pedimos o sexo porque algumas companhias aéreas o exigem e podem usá-lo para incluir MR (Sr.) ou MRS (Sra.) antes do nome.',
  'productCheckout.field.genderWhy': 'Por que?',
  'productCheckout.field.genderFemale': 'Feminino',
  'productCheckout.field.genderMale': 'Masculino',
  'productCheckout.field.boardingName': 'Nome',
  'productCheckout.field.boardingSurname': 'Sobrenome',
  'productCheckout.field.phone': 'Telefone',
  'rental.pickup.label': 'Retirada',
  'rental.dropoff.label': 'Devolução',
  'rental.pickup.from': 'A partir de',
  'rental.dropoff.until': 'Até',
  'productCheckout.price.dailies_one': '{{count}} diária',
  'productCheckout.price.dailies_other': '{{count}} diárias',
  'productCheckout.price.eachDaily': '{{amount}} cada',
  'productCheckout.price.perTraveler': 'Valor por pessoa',
  'productCheckout.price.tripTotal': 'Total da viagem',
  'productCheckout.price.total': 'Total',
  'productCheckout.price.note': 'Os preços podem variar até a confirmação.',
  'productCheckout.action.edit': 'Editar',
  'productCheckout.action.done': 'Concluir',
  'productCheckout.action.submit': 'Solicitar viagem',
  'productCheckout.dock.submit': 'Solicitar',
  'productCheckout.budget.insufficient': 'Limite insuficiente',
  'productCheckout.budget.available': 'Saldo disponível',
  'rental.budget.checkError': 'Não foi possível verificar o orçamento',
  'rental.budget.overLimit': 'Acima do orçamento',
  'rental.budget.withinLimit': 'Dentro do limite',
  'productCheckout.hint.outOfPolicy': 'Fora da política, obrigatório justificar',
  'productCheckout.hint.outOfPolicyReasons':
    'Fora da política ({{reasons}}). Justificativa obrigatória.',
  'productCheckout.reason.bookingWindow_one':
    'antecedência mínima de {{count}} dia para viagens {{scope}}',
  'productCheckout.reason.bookingWindow_other':
    'antecedência mínima de {{count}} dias para viagens {{scope}}',
  'productCheckout.blockers': 'Para solicitar, preencha: {{items}}',
  'productCheckout.blocker.birthdate': 'data de nascimento',
  'productCheckout.blocker.nationality': 'nacionalidade',
  'productCheckout.blocker.gender': 'sexo',
  'productCheckout.blocker.document': 'documento',
  'productCheckout.blocker.embark': 'nome de embarque',
  'productCheckout.blocker.phone': 'telefone do viajante',
  'productCheckout.blocker.allocation': 'empresa e centro de custo',
  'productCheckout.blocker.project': 'projeto',
  'productCheckout.blocker.motive': 'motivo da viagem',
  'productCheckout.blocker.justification': 'justificativa',
  'productCheckout.blocker.travelDocs': 'confirmação dos documentos da viagem',
  'productCheckout.phonePending': 'Telefone pendente',
  'productCheckout.hint.noCorporateAccess':
    'Este viajante não tem centro de custo corporativo — não é possível reservar para ele.',
  'productCheckout.hint.noCostCenters':
    'Nenhum centro de custo disponível para o seu perfil nesta empresa — fale com o gestor da sua empresa.',
  'productCheckout.hint.allocationFromRequester':
    'O rateio de custos do solicitante vale para este hóspede.',
  'productCheckout.hint.allocationFromGuest':
    'O rateio de custos de {{name}} vale para este hóspede.',
  'rental.hint.documentMatch': 'Os dados precisam ser idênticos ao documento de viagem',
  'rental.hint.genderFemale': 'Feminino',
  'rental.hint.genderMale': 'Masculino',
  'rental.hint.cpfReadOnly': 'Preenchido automaticamente pelo sistema',
  'productCheckout.hint.documentOnFile': 'Em sistema: {{value}}',
  'productCheckout.hint.editInProfile':
    'Para alterar, fale com o gestor de viagens da sua empresa.',
  /* O QUANDO vazio no layout de PÁGINA (a busca determinística). Separado do
   * `datesTBD` do card/faixa de propósito: lá a pílula é estreita e "A definir"
   * é o que cabe; na página o campo tem 208px e o protótipo pede a pergunta
   * inteira, com os dois marcos nomeados. */
  'hotelPlanner.datesPlaceholder': 'Check-in e check-out',
  /* A garantia de que a política da empresa já está em jogo ANTES de buscar —
   * afirmação, não controle: não há tela de política para levar a. */
  'hotelPlanner.policyHint': 'Sua política de viagem',
  'hotelPlanner.noPlaces': 'Nenhum destino',
  'hotelPlanner.placeError': 'Não foi possível buscar destinos. Tente novamente.',
  'hotelPlanner.noResultsTitle': 'Nenhum hotel encontrado',
  'hotelPlanner.noResults': 'Tente outras datas, outro destino ou remova filtros.',
  'hotelPlanner.providerErrorTitle': 'Não foi possível buscar hotéis',
  'hotelPlanner.providerError':
    'Falha no fornecedor de tarifas. Tente de novo em instantes ou ajuste destino e datas.',
  'hotelPlanner.timeoutErrorTitle': 'A busca demorou demais',
  'hotelPlanner.timeoutError':
    'O fornecedor não respondeu a tempo. Tente novamente — se repetir, reduza o período ou troque o destino.',
  'hotelPlanner.search': 'Buscar',
  'hotelPlanner.searching': 'Buscando hotéis',
  /* A fala que abre o card: o que o viajante lê ANTES do formulário. Três
     variações — falta responder algo, a busca está EM VOO, ou já tem tudo o que
     precisa. `contextSearching` existe para não afirmar "tudo pronto" enquanto o
     dock ainda mostra só esqueleto (#2444). */
  'hotelPlanner.contextPending':
    'Complete os detalhes abaixo para encontrarmos as melhores opções de hospedagem para você:',
  'hotelPlanner.contextSearching':
    'Estou buscando as melhores opções de hospedagem para o seu período…',
  'hotelPlanner.contextReady':
    'Tudo pronto! Mapeei as melhores opções de hospedagem recomendadas para o seu período:',
  'hotelPlanner.contextEmpty': 'Não encontrei hotéis com esses critérios. Ajuste a busca:',
  /* Mensagem do viajante quando a busca sai como TURNO (card já superado por
     outro turno): o balão visível da nova busca. */
  'hotelPlanner.searchText': 'Buscar hotéis em {{place}} — {{dates}}.',
  'hotelPlanner.retry': 'Tentar novamente',
  'hotelPlanner.adjustSearch': 'Ajustar busca',
  'hotelPlanner.rooms': 'Quartos',
  'hotelPlanner.prevMonth': 'Mês anterior',
  'hotelPlanner.nextMonth': 'Próximo mês',
  'hotelPlanner.roomN': 'Quarto {{n}}',
  'hotelPlanner.removeRoom': 'Remover quarto {{n}}',
  'hotelPlanner.remove': 'Remover',
  'hotelPlanner.addRoom': 'Adicionar quarto',
  /* O painel `Quem`: quartos com hóspedes NOMEADOS (o stepper de adultos e o
   * campo `Pra quem` morreram juntos — a reserva fica no nome de um hóspede). */
  'hotelPlanner.chooseGuest': 'Escolher hóspede',
  'hotelPlanner.addGuest': 'Acompanhante',
  'hotelPlanner.addGuestAria': 'Adicionar hóspede — quarto {{n}}',
  'hotelPlanner.backToRooms': 'Voltar para os quartos',
  'hotelPlanner.roomGuestHeading': 'Quarto {{n}} · hóspede',
  'hotelPlanner.roomCompanionHeading': 'Quarto {{n}} · acompanhante',
  'hotelPlanner.peopleList': 'Pessoas',
  'hotelPlanner.noPeople': 'Ninguém encontrado.',
  'hotelPlanner.roomNameNote': 'A reserva de cada quarto fica no nome do primeiro hóspede.',
  /** Legenda de quem já está noutro quarto, travado na lista de hóspedes. */
  'hotelPlanner.guestInRoom': 'Já no quarto {{n}}',
  'hotelPlanner.holderBadge': 'Titular',
  'hotelPlanner.guestOptions': 'Opções de {{name}}',
  'hotelPlanner.makeHolder': 'Tornar titular do quarto',
  'hotelPlanner.swapGuest': 'Trocar hóspede',
  'hotelPlanner.removeFromRoom': 'Remover do quarto',
  'hotelPlanner.guestRole': 'hóspede',
  /* Os rótulos das cápsulas da faixa (`Onde · Quando · Quem`), na ordem da
   * pergunta. Não são mais visíveis (o valor com glifo se explica), mas abrem o
   * nome ACESSÍVEL de cada controle, à frente do nome de contrato
   * (`destination`/`checkInOut`/`rooms`), que é contrato de e2e. */
  'hotelPlanner.whereLabel': 'Onde',
  'hotelPlanner.whenLabel': 'Quando',
  'hotelPlanner.whoLabel': 'Quem',
  'hotelPlanner.destination': 'Destino',
  'hotelPlanner.destinationPlaceholder': 'Cidade ou referência',
  'hotelPlanner.myLocation': 'Minha localização',
  'hotelPlanner.myLocationError': 'Não foi possível usar sua localização',
  'hotelPlanner.recentPlaces': 'Recentes',
  'hotelPlanner.favoritePlaces': 'Lugares favoritos',
  // Primeira metade da segunda linha do item do campo `Onde` (`tipo · endereço`,
  // chamada do designer 2026-08-06): "Jardim Paulista" sozinho não diz se é
  // bairro, hotel ou escritório. Vocabulário FECHADO — o que o Google devolve
  // fora dele cai no endereço puro, sem prefixo inventado.
  'hotelPlanner.placeType.city': 'Cidade',
  'hotelPlanner.placeType.neighborhood': 'Bairro',
  'hotelPlanner.placeType.address': 'Endereço',
  'hotelPlanner.placeType.poi': 'Ponto de interesse',
  'hotelPlanner.placeType.lodging': 'Hotel',
  'hotelPlanner.placeType.airport': 'Aeroporto',
  'hotelPlanner.checkInOut': 'Check-in/out',
  'hotelPlanner.datesTBD': 'A definir',
  'hotelPlanner.chooseStayDates': 'Escolha a entrada e a saída',
  'hotelPlanner.missingDatesAlert': 'Selecione as datas para ver os quartos e preços disponíveis',
  'hotelPlanner.stayBack': 'Voltar',
  'hotelPlanner.chooseCheckout': '{{date}} · escolha a saída',
  'hotelPlanner.roomCount_one': '{{count}} quarto',
  'hotelPlanner.roomCount_other': '{{count}} quartos',
  /* Hóspede, não adulto: a ocupação conta gente, sem distinguir idade. */
  'hotelPlanner.guestCount_one': '{{count}} hóspede',
  'hotelPlanner.guestCount_other': '{{count}} hóspedes',
  'hotelPlanner.nights_one': '{{count}} noite',
  'hotelPlanner.nights_other': '{{count}} noites',
  'hotelPlanner.maxStayNights': 'máximo de {{count}} noites',
  'hotelPlanner.confirmDates': 'Confirmar {{range}}',
  'hotelPlanner.confirmDate': 'Confirmar data',
  /** O `Confirmar` FIXO da folha de datas do celular (#3446). */
  'hotelPlanner.confirmStayDates': 'Confirmar datas',
  'hotelPlanner.confirmAndSearch': 'Confirmar e buscar',
  'hotelPlanner.resetDates': 'Redefinir',
  'hotelPlanner.maxRooms': 'Máximo de {{n}} quartos',
  'hotelPlanner.maxGuests': 'Máximo de {{n}} hóspedes',
  'hotel.resultsListAria': 'Hotéis encontrados',
  'hotel.results.previous': 'Hotéis anteriores',
  'hotel.results.next': 'Próximos hotéis',
  'hotel.recommended': 'Sugerida VOLL',
  /* Rótulo do selo do eixo, na mesma redação do aéreo (`flight.badge.*`): a
   * marca já está no glifo do selo, então "VOLL" no texto era a marca dita duas
   * vezes — e dois serviços nomeando o mesmo eixo com palavras diferentes fazem
   * a pessoa reler em vez de reconhecer. */
  'hotel.badge.recommended': 'Recomendado',
  'hotel.badge.cheapest': 'Menor preço',
  'hotel.badge.closest': 'Mais perto',
  /* #3399 (b): o bloco é um RECORTE da busca (`offersTotal` maior que o
   * cartão) — o eixo foi medido só sobre o que o cartão carrega, e o rótulo
   * diz o recorte em vez de prometer o mínimo da busca inteira. */
  'hotel.badge.cheapestOnCard': 'Menor preço no cartão',
  'hotel.badge.closestOnCard': 'Mais perto no cartão',
  /* O selo PESSOAL, acima dos três eixos: o modal-session carimbou
   * `stayedBefore` na oferta (histórico de estadias do viajante). A variante
   * `Count` entra com `stayCount` ≥ 2. */
  'hotel.badge.stayedBefore': 'Você ficou aqui',
  'hotel.badge.stayedBeforeCount': 'Você ficou aqui · {{count}}×',
  /* O outro selo pessoal (item 1 F2), acima até da estadia: o coração que o
   * viajante marcou. Os dois rótulos seguintes são o nome acessível do toggle
   * em cada estado — o rótulo diz a AÇÃO do próximo clique. */
  'hotel.badge.favorite': 'Favorito',
  /* M1 · 1.2 — o destaque do AGENTE (`directives.highlightOfferIds`), quando
   * ele reemite o planner respondendo um refino ("só os com café" etc.).
   * Reusa o desenho sólido do recomendado; vence os outros selos. */
  'hotel.badge.agentPick': 'Sugestão do agente',
  'hotel.badge.agreedRate': 'Hotel com acordo',
  'hotel.favorite.mark': 'Favoritar hotel',
  'hotel.favorite.unmark': 'Remover favorito',
  'hotel.card.freeCancel': 'Cancelamento gratuito',
  /* Distância em linha reta até a âncora da busca — derivada no cliente, ver
   * `hotel/shared/haversine.ts`. O MODO virou glifo (pegada/carro) com o
   * `aria-label` abaixo: "14 min" sozinho não diz se é a pé ou de carro, e são
   * decisões opostas. */
  'hotel.card.distance': '{{km}} km',
  'hotel.card.minutes': '{{count}} min',
  'hotel.card.byFoot': 'a pé',
  'hotel.card.byCar': 'de carro',
  /* O escudo cortado colado no preço, sem palavra — o aria diz o que o glifo
   * significa pra quem não o vê. */
  /* A unidade colada no preço. Diária e estadia são a mesma string com decisões
   * opostas, então o número nunca aparece sem dizer o que cobre. */
  'hotel.card.perNight': '/noite',
  /* A condição de reembolso na linha de meta (#3404). A variante "a partir de"
   * existe porque a tarifa reembolsável pode ser OUTRO quarto, mais caro que o
   * preço exibido — o número diz de qual tarifa a palavra fala. */
  'hotel.card.refundable': 'Reembolsável',
  'hotel.card.refundableFrom': 'Reembolsável a partir de {{price}}',
  'hotel.photoPrev': 'Foto anterior',
  'hotel.photoNext': 'Próxima foto',
  'hotel.inPolicy': 'Dentro da política',
  'hotel.outPolicy': 'Fora da política',
  'hotel.policyRule.bookingWindow': 'Antecedência mínima não atendida',
  'hotel.policyRule.bookingWindowWithLimit': 'Antecedência mínima de {{value}} dias',
  'hotel.policyRule.bookingWindowInternational': 'Antecedência mínima internacional não atendida',
  'hotel.policyRule.bookingWindowInternationalWithLimit':
    'Antecedência mínima internacional de {{value}} dias',
  'hotel.policyRule.cheapestPrice': 'Não é a tarifa mais barata do hotel',
  'hotel.policyRule.cheapestHotel': 'Não é o hotel mais barato da busca',
  'hotel.policyRule.maxPrice': 'Acima do teto por noite',
  'hotel.policyRule.maxPriceWithLimit': 'Acima do teto de {{value}} por noite',
  'hotel.policyRule.maxPriceInternational': 'Acima do teto internacional por noite',
  'hotel.policyRule.maxPriceInternationalWithLimit':
    'Acima do teto internacional de {{value}} por noite',
  'hotel.policyRule.maxPricePerCity': 'Acima do teto da cidade',
  'hotel.policyRule.maxPricePerCityWithLimit': 'Acima do teto da cidade ({{value}} por noite)',
  'hotel.policyRule.maxPricePerCountry': 'Acima do teto do país',
  'hotel.policyRule.onlyRateWithBreakfast': 'Tarifa sem café (a política exige café)',
  'hotel.policyRule.onlyRateRefundable': 'Tarifa não reembolsável (a política exige reembolsável)',
  'hotelRooms.policyCeilingAbove': 'Acima de {{value}}',
  'hotelRooms.policyCeilingHint': 'Teto por noite da política da sua empresa nesta busca.',
  'hotel.breakfast': 'Café incluso',
  'hotel.priceUnavailable': 'Preço a confirmar',
  'hotel.soldOut': 'Sem disponibilidade para estas datas',
  'hotel.total': 'Total',
  'hotel.perNight': 'Diária {{price}}',
  'hotel.rating': 'nota {{rating}}',
  'hotel.category': '{{category}} estrelas',
  'hotel.night_one': '{{count}} diária',
  'hotel.night_other': '{{count}} diárias',
  'hotel.room': 'Quarto',
  'hotel.roomsTitle': 'Melhores quartos',
  'hotel.gallery.prev': 'Foto anterior',
  'hotel.gallery.next': 'Próxima foto',
  'hotel.gallery.photo': 'Foto {{position}}',
  'hotel.map.aria': 'Mapa de hotéis',
  'hotel.map.pinAria': '{{title}}, {{price}}',
  'hotel.map.seeHotel': 'Ver hotel',
  'hotel.map.expand': 'Expandir o mapa',
  'hotel.map.zoomIn': 'Aproximar',
  'hotel.map.zoomOut': 'Afastar',
  /* O rótulo da estadia no cartão do pino ("3 noites por R$ 1.836") — o total
   * é o número que vai pro aprovador; a diária mora no dock. */
  'hotel.map.nightsFor_one': '{{count}} noite por',
  'hotel.map.nightsFor_other': '{{count}} noites por',
  'hotel.map.recenter': 'Reenquadrar o mapa',
  'hotel.map.previewClose': 'Fechar a prévia do hotel',
  'hotel.results.chips.toolbar': 'Filtros e ordenação',
  'hotel.results.chips.priceUpTo': 'Até {{price}}',
  'hotel.results.chips.priceFrom': 'A partir de {{price}}',
  'hotel.results.chips.priceBetween': '{{min}} – {{max}}',
  'hotel.results.seeAll': 'Ver todos os hotéis',
  'hotel.results.sort': 'Ordenar',
  'hotel.results.sort.recommended': 'Recomendado',
  'hotel.results.sort.price': 'Preço',
  'hotel.results.sort.distance': 'Distância',
  // `reviews` ordena por QUANTIDADE de avaliações e `stars` pela NOTA (0–5) —
  // não pela classificação do prédio (`sortHotelOffers`). Os rótulos dizem o
  // que a ordem faz; a chave/option key `stars` fica como está (contrato com o
  // servidor), renomeá-la é follow-up.
  'hotel.results.sort.reviews': 'Avaliações',
  'hotel.results.sort.stars': 'Nota',
  'hotel.results.title': 'Hotéis',
  'hotel.results.pageTitle': 'Hotéis em {{place}}',
  'hotel.results.pageTitleNear': 'Hotéis perto de {{place}} · {{locality}}',
  'hotel.results.pageTitleMapArea': 'Hotéis na área do mapa',
  'hotel.results.pageContext_one': '{{count}} noite',
  'hotel.results.pageContext_other': '{{count}} noites',
  'hotel.results.close': 'Fechar hotéis',
  'hotel.results.pillAria': 'Contexto da busca',
  'hotel.results.searchAgain': 'Buscar novamente',
  'hotel.results.cancelEdit': 'Cancelar',
  'hotel.results.total_one': '{{count}} hotel',
  'hotel.results.total_other': '{{count}} hotéis',
  'hotel.results.visibleOfTotal_one': '{{visible}} hotel de {{total}}',
  'hotel.results.visibleOfTotal_other': '{{visible}} hotéis de {{total}}',
  'hotel.results.askPlaceholder': 'Mais perto, com café…',
  'hotel.results.back': 'Voltar',
  'hotel.results.filters': 'Filtros',
  'hotel.results.collapseFilters': 'Recolher os filtros',
  'hotel.results.closeFilters': 'Fechar os filtros',
  'hotel.results.applyFilters': 'Aplicar filtros',
  'hotel.results.expandFilters': 'Expandir os filtros',
  'hotel.results.showMap': 'Mapa',
  'hotel.results.share': 'Compartilhar a busca',
  'searchShare.copyLink': 'Copiar link',
  'searchShare.downloadPdf': 'Baixar PDF',
  'hotel.results.filteredEmpty': 'Nenhum hotel com esses filtros.',
  'hotel.results.clearFilters': 'Limpar filtros',
  'hotel.results.showList': 'Lista',
  'hotel.results.loadMore': 'Carregar mais hotéis',
  'hotel.results.offline.offerTitle': 'Precisa de algo que não está aqui?',
  'hotel.results.offline.offerBody':
    'A equipe VOLL busca hotéis parceiros — online e offline — que batam com o seu perfil e devolve a cotação na conversa da viagem.',
  'hotel.results.loadingMore': 'Carregando mais hotéis…',
  'hotel.results.allLoaded': 'Todos os hotéis carregados',
  'hotel.results.searchThisArea': 'Buscar nesta área',
  'hotel.results.mapArea': 'Área do mapa',
  'hotel.results.mapAreaEmpty': 'Mova o mapa e busque nesta área de novo.',
  'hotel.results.searchingThisArea': 'Buscando…',
  'hotel.results.loadingAll': 'Carregando todos os hotéis',
  'hotel.results.searching': 'Consultando disponibilidade e tarifas',
  'hotel.results.stillProcessing': 'Os hotéis ainda estão respondendo',
  'hotel.results.staleRetrying': 'Atualizando a busca',
  'hotel.results.unavailableTitle': 'Não foi possível carregar todos os hotéis',
  'hotel.results.unavailable':
    'Os hotéis abaixo continuam disponíveis. Tente de novo em instantes para ver a lista completa.',
  'hotel.results.staleTitle': 'Esta busca expirou',
  'hotel.results.stale':
    'O fornecedor não guarda a lista completa por muito tempo. Busque de novo para ver todos os hotéis.',
  'hotel.results.staleAction': 'Buscar de novo',
  'hotel.results.resetFilters': 'Redefinir',
  'hotel.results.policy': 'Política',
  'hotel.results.withinPolicy': 'Dentro da política',
  'hotel.results.rate': 'Tarifa',
  'hotel.results.breakfast': 'Café da manhã',
  'hotel.results.refundable': 'Reembolsável',
  'hotel.results.agreedRate': 'Tarifa acordo',
  'hotel.results.nightlyPrice': 'Diária',
  'hotel.results.minimumPrice': 'Diária mínima',
  'hotel.results.maximumPrice': 'Diária máxima',
  'hotel.results.rating': 'Nota',
  'hotel.results.distance': 'Distância do compromisso',
  'hotel.results.distanceUpTo': 'até {{distance}} km',
  'hotel.results.amenities': 'Comodidades',
  'hotel.results.send': 'Enviar',
  'hotel.results.visibleHotels_one': '{{count}} hotel visível no mapa',
  'hotel.results.visibleHotels_other': '{{count}} hotéis visíveis no mapa',
  'hotel.amenity.wifi': 'Wi-Fi',
  'hotel.amenity.breakfast': 'Café da manhã',
  'hotel.amenity.parking': 'Estacionamento',
  'hotel.amenity.pool': 'Piscina',
  'hotel.amenity.gym': 'Academia',
  'hotel.amenity.restaurant': 'Restaurante',
  'hotel.amenity.laundry': 'Lavanderia',
  'hotel.board.roomOnly': 'Apenas quarto',
  'hotel.board.breakfast': 'Café da manhã',
  'hotel.board.noBreakfast': 'Sem café da manhã',
  'hotel.board.continentalBreakfast': 'Café da manhã continental',
  'hotel.board.breakfastFor': 'Café da manhã para {{count}} pessoas',
  'hotel.board.halfBoard': 'Meia pensão',
  'hotel.board.fullBoard': 'Pensão completa',
  'hotel.board.allInclusive': 'Tudo incluído',
  'hotel.board.freeWifi': 'Wi-Fi grátis',
  'hotel.board.freeHighSpeedInternet': 'Internet de alta velocidade grátis',
  'hotel.board.freeSelfParking': 'Estacionamento grátis sem manobrista',
  'hotel.board.freeValetParking': 'Estacionamento com manobrista grátis',
  'hotelRooms.headerStay': '{{checkin}} – {{checkout}} · {{nights}}',
  'hotelRooms.headerOccupancy': '{{rooms}}, {{guests}}',
  'hotelRooms.reviews_one': '{{count}} avaliação',
  'hotelRooms.reviews_other': '{{count}} avaliações',
  'hotelRooms.ratingLabel.high': 'Excelente',
  'hotelRooms.ratingLabel.mid': 'Bom',
  'hotelRooms.ratingLabel.low': 'Razoável',
  'hotelRooms.negotiated': 'Tarifa negociada',
  /* O selo da tarifa cujo fornecedor não é marca de consumidor (#6895). */
  /* O título do quarto da tarifa de acordo cujo nome o fornecedor não descreve
   * (só o nome da empresa, #5922): o nome cru segue na linha da tarifa. */
  'hotelRooms.negotiatedRoom': 'Quarto com tarifa negociada',
  /* O selo do quarto INDICADO da lista (um por lista) e a escassez da tarifa
   * mostrada ("Últimos 2" — da tarifa, não do quarto: cada canal tem o próprio
   * estoque). */
  'hotelRooms.lastUnits_one': 'Último {{count}}',
  'hotelRooms.lastUnits_other': 'Últimos {{count}}',
  'hotelRooms.otherRates': 'Demais tarifas',
  'hotelRooms.otherOptions': 'Outras opções ({{count}})',
  'hotelRooms.rateTypeBreakfastRefundable': 'Com café · reembolsável',
  'hotelRooms.rateTypeBreakfast': 'Com café',
  'hotelRooms.rateTypeRefundable': 'Reembolsável',
  'hotelRooms.rateTypeRoomOnly': 'Sem café',
  'hotelRooms.rateTypeOther': 'Outras tarifas',
  'hotelRooms.showAllRates': 'Comparar tarifas',
  'hotelRooms.seeMoreRates': 'Ver mais {{count}}',
  'hotelRooms.seeLessRates': 'Ver menos',
  'hotelRooms.ratesAria': 'Tarifas do quarto',
  'hotelRooms.perNight': '/ noite',
  'hotelRooms.nights_one': '{{count}} noite',
  'hotelRooms.nights_other': '{{count}} noites',
  'hotelRooms.totalNights': '{{nights}} · {{total}}',
  'hotelRooms.select': 'Selecionar',
  'hotelRooms.selected': 'Selecionado',
  'hotelRooms.selectedPrefix': 'Selecionado:',
  /* Substantivo curto no presente e negação EXPLÍCITA no ausente, nas três
     línguas — é a grafia do alvo (`DIMENSOES_DE_HOTEL`) e a razão está escrita
     lá: a preposição é redundância paga em pixel, e pixel na horizontal decide
     quantas linhas a coluna quebra; mas o `sem` nunca sai, porque um chip
     vermelho escrito só `reembolso` lê como se TIVESSE reembolso. */
  'hotelRooms.chipBreakfast': 'café',
  'hotelRooms.chipNoBreakfast': 'sem café',
  'hotelRooms.chipRefundable': 'reembolso',
  'hotelRooms.chipNonRefundable': 'sem reembolso',
  'hotelRooms.freeCancelUntil': 'Cancela grátis até {{date}}',
  'hotelRooms.refundable': 'Reembolsável',
  'hotelRooms.moreFilters': 'Mais filtros',
  'hotelRooms.previousFilters': 'Filtros anteriores',
  'hotelRooms.outOfPolicy': 'Tarifa fora da política de viagens da sua empresa.',
  'hotelRooms.viewDetails': 'Ver detalhes',
  'hotelRooms.roomAmenities': 'O que o quarto tem',
  'hotelRooms.detailAmenities': 'Comodidades principais',
  'hotelRooms.detailOtherAmenities': 'Outras comodidades',
  'hotelRooms.deckToggleAria': 'Mostrar todos os quartos',
  'hotel.selectText': 'Quero ver os quartos do {{hotel}}.',
  'hotelRooms.selectText': 'Quero o {{room}} ({{rate}}) no hotel {{hotel}}.',
  'hotelRooms.selectTextNoRate': 'Quero o {{room}} no hotel {{hotel}}.',
  'roomTaxonomy.type.QUARTO': 'Quarto',
  'roomTaxonomy.type.SUITE': 'Suíte',
  'roomTaxonomy.type.STUDIO': 'Studio',
  'roomTaxonomy.type.APARTAMENTO': 'Apartamento',
  'roomTaxonomy.type.CHALE': 'Chalé',
  'roomTaxonomy.type.BUNGALOW': 'Bungalow',
  'roomTaxonomy.type.LOFT': 'Loft',
  'roomTaxonomy.type.FLAT': 'Flat',
  'roomTaxonomy.type.CABANA': 'Cabana',
  'roomTaxonomy.tier.ECONOMICO': 'Econômico',
  'roomTaxonomy.tier.STANDARD': 'Standard',
  'roomTaxonomy.tier.CLASSIC': 'Classic',
  'roomTaxonomy.tier.JUNIOR': 'Junior',
  'roomTaxonomy.tier.CONFORT': 'Conforto',
  'roomTaxonomy.tier.SUPERIOR': 'Superior',
  'roomTaxonomy.tier.EXECUTIVO': 'Executivo',
  'roomTaxonomy.tier.DELUXE': 'Deluxe',
  'roomTaxonomy.tier.LUXO': 'Luxo',
  'roomTaxonomy.tier.PRIME': 'Prime',
  'roomTaxonomy.tier.PREMIUM': 'Premium',
  'roomTaxonomy.tier.MASTER': 'Master',
  'roomTaxonomy.beds.SINGLE_one': '{{count}} cama de solteiro',
  'roomTaxonomy.beds.SINGLE_other': '{{count}} camas de solteiro',
  'roomTaxonomy.beds.DOUBLE_one': '{{count}} cama de casal',
  'roomTaxonomy.beds.DOUBLE_other': '{{count}} camas de casal',
  'roomTaxonomy.beds.SOFA_one': '{{count}} sofá-cama',
  'roomTaxonomy.beds.SOFA_other': '{{count}} sofás-cama',
  'roomTaxonomy.beds.UNKNOWN_one': '{{count}} cama',
  'roomTaxonomy.beds.UNKNOWN_other': '{{count}} camas',
  'roomTaxonomy.bedrooms_one': '{{count}} dormitório',
  'roomTaxonomy.bedrooms_other': '{{count}} dormitórios',
  'roomTaxonomy.capacity': 'até {{count}} pessoas',
  'roomTaxonomy.qualifier.NO_SMOKING': 'não fumante',
  'roomTaxonomy.qualifier.SMOKING': 'fumante',
  'roomTaxonomy.qualifier.NO_AC': 'sem ar-condicionado',
  'roomTaxonomy.qualifier.AC': 'com ar-condicionado',
  'roomTaxonomy.qualifier.SEA_VIEW': 'vista mar',
  'roomTaxonomy.qualifier.GARDEN_VIEW': 'vista jardim',
  'roomTaxonomy.qualifier.CITY_VIEW': 'vista cidade',
  'roomTaxonomy.qualifier.VIEW': 'com vista',
  'roomTaxonomy.qualifier.BALCONY': 'com varanda',
  'roomTaxonomy.qualifier.PRIVATE': 'privativo',
  'roomTaxonomy.qualifier.ACCESSIBLE': 'acessível (PCD)',
  'roomTaxonomy.qualifier.KITCHEN': 'com cozinha',
  'roomTaxonomy.qualifier.FAMILY': 'família',
  'hotelDetails.aria': 'Detalhes do hotel',
  'hotelDetails.close': 'Fechar',
  'hotelDetails.openInNewTab': 'Abrir em nova aba',
  'hotelDetails.loading': 'Carregando detalhes do hotel',
  'hotelDetails.errorTitle': 'Não conseguimos carregar os detalhes',
  'hotelDetails.errorBody': 'Tente novamente em instantes.',
  'hotelDetails.retry': 'Tentar novamente',
  'hotelDetails.about': 'Sobre o hotel',
  'hotelDetails.chooseRoom': 'Escolha o quarto',
  'hotelDetails.reserveRoom': 'Reservar',
  /* Rótulos das abas-âncora do drawer — curtos porque seis abas dividem 432px.
   * `location` também intitula a seção. */
  'hotelDetails.summary': 'Resumo',
  'hotelDetails.roomsTab': 'Quartos',
  'hotelDetails.roomsEmpty':
    'Este hotel não tem quarto disponível para as datas da busca. Tente outras datas ou outro hotel.',
  'hotelDetails.sectionsNavigation': 'Seções do hotel',
  'hotelDetails.previousTabs': 'Seções anteriores',
  'hotelDetails.nextTabs': 'Mais seções',
  'hotelDetails.aboutShort': 'Sobre',
  'hotelDetails.location': 'Localização',
  'hotelDetails.cancellation': 'Política de cancelamento',
  'hotelDetails.cancellationFreeUntil': 'Cancelamento grátis até {{date}}',
  'hotelDetails.cancellationCharged': 'Cancelamento com multa',
  'hotelDetails.cancellationFreeEnded': 'Cancelamento grátis encerrado em {{date}}',
  'hotelDetails.cancellationForRate': 'Condições da tarifa {{rate}}',
  'hotelDetails.cancellationFrom': 'A partir de {{date}}',
  'hotelDetails.cancellationUntil': 'Até {{date}}',
  'hotelDetails.cancellationBetween': 'De {{from}} a {{until}}',
  'hotelDetails.cancellationPenalty': 'Multa de {{amount}}',
  'hotelDetails.cancellationFree': 'Sem multa',
  'hotelDetails.cancellationVaries': 'Outras tarifas podem ter condições diferentes.',
  'hotelDetails.fromPlace': 'de {{place}}',
  'hotelDetails.readMore': 'Ler mais',
  'hotelDetails.readLess': 'Ler menos',
  'hotelDetails.galleryAria': 'Fotos do hotel',
  'hotelDetails.thumbnailsNav': 'Miniaturas das fotos',
  'hotelDetails.allPhotos': 'Ver todas as fotos',
  'hotelDetails.photoCounter': '{{position}} / {{total}}',
  'hotelDetails.goToPhoto': 'Ir para a foto {{position}}',
  'hotelDetails.amenities': 'O que este hotel oferece',
  'hotelDetails.allAmenities': 'Ver todas as comodidades',
  'hotelDetails.fewerAmenities': 'Menos comodidades',
  'hotelDetails.amenitiesTitle': 'Comodidades oferecidas',
  'hotelDetails.reviews': 'Avaliações',
  'hotelDetails.allReviews': 'Ler todas as avaliações',
  'hotelDetails.reviewBy': '{{author}} · {{when}}',
  'hotelDetails.noFilteredRooms': 'Nenhum quarto com esses filtros',
  'hotelDetails.noFilteredRoomsHint': 'Solte um filtro para ver as opções de novo.',
  'hotelDetails.clearFilters': 'Limpar filtros',
  'hotelDetails.allRoomsMeetFilter': 'Todos os quartos desta lista atendem a “{{filter}}”.',
  'hotelReservation.checkin': 'Check-in',
  'hotelReservation.checkout': 'Check-out',
  'hotelRateWarning.title': 'As condições desta tarifa foram atualizadas',
  'hotelRateWarning.continue': 'Continuar mesmo assim',
  'hotelRateWarning.chooseAnother': 'Escolher outra tarifa',
  'hotelRateWarning.continueText': 'Quero continuar mesmo com as condições atualizadas.',
  'hotelRateWarning.chooseAnotherText': 'Quero escolher outra tarifa neste hotel.',
  'hotelRateWarning.reason.breakfast_changed': 'O café da manhã desta tarifa mudou',
  'hotelRateWarning.reason.refundable_changed': 'A condição de reembolso desta tarifa mudou',
  'hotelRateWarning.reason.non_refundable':
    'Esta tarifa não tem mais cancelamento grátis: agora é não reembolsável',
  'hotelRateWarning.reason.free_cancellation_ended':
    'Esta tarifa não tem mais cancelamento grátis: cancelar agora gera multa',
  'hotelRateWarning.reason.free_cancellation_until': 'Cancelamento sem multa até {{date}}',
  'hotelRateWarning.policyTurnedOut': 'Esta tarifa passou a ficar fora da política de viagens',
  'voucherCancel.aria': 'Confirmação de cancelamento de voucher',
  'voucherCancel.leadPostEmission':
    'Este voucher está **emitido**: o cancelamento será uma **solicitação** (revisão manual) e o voucher ficará marcado como **não utilizado**.',
  'voucherCancel.k.voucher': 'Voucher',
  'voucherCancel.k.modal': 'Modal',
  'voucherCancel.k.route': 'Trecho',
  'voucherCancel.k.place': 'Local',
  'voucherCancel.k.status': 'Status',
  'voucherCancel.k.reason': 'Motivo',
  'voucherCancel.k.when': 'Horário',
  'voucherCancel.ride.title': 'Cancelar esta corrida?',
  'voucherCancel.ride.keep': 'Manter corrida',
  'voucherCancel.ride.sent': 'Cancelamento solicitado',
  'voucherCancel.confirm': 'Confirmar cancelamento',
  'voucherCancel.sent': 'Confirmação enviada',
  'voucherCancel.keep': 'Manter voucher',
  'voucherCancel.replyKeep': 'Não quero cancelar.',
  'voucherCancel.replyConfirm': 'Confirmar cancelamento.',
  'voucherCancelBulk.aria': 'Cancelamento de vouchers em lote',
  'voucherCancelBulk.lead':
    'Selecione os vouchers que deseja cancelar e toque em **Cancelar selecionados**.',
  'voucherCancelBulk.note':
    'Vouchers **emitidos** viram uma **solicitação** de cancelamento (revisão manual) e ficam marcados como **não utilizado**.',
  'voucherCancelBulk.issued': 'Emitido',
  'voucherCancelBulk.submit': 'Cancelar selecionados',
  'voucherCancelBulk.submitCount': 'Cancelar selecionados ({{count}})',
  'voucherCancelBulk.sent': 'Cancelamento enviado',
  'voucherCancelBulk.keepAll': 'Manter todos',
  'voucherCancelBulk.replyKeepAll': 'Não quero cancelar nenhum.',
  'voucherCancelBulk.replySubmit': 'Cancelar os vouchers selecionados.',
  /* Rótulo do bloco meta do acordeão (e título de fallback do voucher sem
   * rota/fornecedor). A palavra é a mesma nos três idiomas — a chave existe pra
   * que a tela não carregue literal, e pra que trocar a copy seja uma edição só. */
  'voucher.meta.heading': 'Voucher',
  /* Subtítulo de datas do planner/explorer (`fmtTripDates`). QUAL forma do mês
   * entra na frase é decisão do CHAMADOR (curto em en nas frases de dois meses,
   * longo em pt/es) — assim os quatro templates dividem o mesmo conjunto de
   * placeholders nos três idiomas. */
  'tripDates.oneWay': '{{d}} de {{month}}',
  'tripDates.sameMonth': '{{d1}} a {{d2}} de {{month}}',
  'tripDates.crossMonth': '{{d1}} de {{month1}} a {{d2}} de {{month2}}',
  'tripDates.crossYear': '{{d1}} de {{month1}} de {{y1}} a {{d2}} de {{month2}} de {{y2}}',
  /* Régua de etapas da reserva (slot `header`). O wire carrega o CÓDIGO da
   * etapa (`search|quote|confirmation|booking`) e o texto nasce aqui — a barra
   * fala o idioma de quem LÊ, não o do processo que emitiu o card. */
} as const;

export type GenUiKey = keyof typeof ptBR;

const en: Record<GenUiKey, string> = {
  'common.close': 'Close',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.copyFailed': "Couldn't copy",
  'common.total': 'Total',
  'common.perPerson': 'Per person',
  'common.direct': 'Direct',
  'common.notInformed': 'Not provided',
  'common.toConfirm': 'To be confirmed',
  'common.toDefine': 'TBD',
  'common.loadingMore': 'Loading more…',
  'common.tryAgain': 'Try again',
  'resultsOffline.offerAction': 'Ask the VOLL team',
  'consultPlaces.aria': 'Places recommended in the consultation',
  'consultPlaces.title': 'Places to explore',
  'consultPlaces.place': 'Place',
  'consultPlaces.rating': '{{rating}} out of 5',
  'consultPlaces.open': 'Open now',
  'consultPlaces.closed': 'Closed now',
  'consultPlaces.details': 'View details — {{place}}',
  'consultPlaces.loadingPlace': 'Loading place…',
  'consultPlaces.placeErrorAt': "Couldn't load place {{position}} of the list.",
  'consultPlaces.loadingMap': 'Loading map…',
  'consultPlaces.mapAria': 'Map of recommended places',
  'consultPlaces.mapUnavailable': 'Map unavailable in this session',
  'consultPlaces.mapError': "Couldn't load the map. The places remain available below.",
  'consultPlaces.retryMap': 'Try again',
  'consultPlaces.list': 'Places list',
  'consultPlaces.detailsShort': 'View details',
  'consultPlaces.loadingDetails': 'Loading details…',
  'consultPlaces.detailsError': "Couldn't load this place's details.",
  'consultPlaces.photos': 'Place photos',
  'consultPlaces.address': 'Address',
  'consultPlaces.phone': 'Phone',
  'consultPlaces.website': 'Website',
  'consultPlaces.openWebsite': 'Open website',
  'consultPlaces.hours': 'Hours',
  'consultPlaces.reviews': 'Reviews',
  'consultPlaces.sourceGoogle': 'View review on Google Maps',
  'consultPlaces.sourcePlaceGoogle': 'Place information from Google Maps',
  'consultPlaces.expandedMap': 'Expanded map',
  'consultPlaces.referencePlace': 'Reference place',
  'consultPlaces.expandMap': 'Expand map',
  'consultPlaces.openGoogleMaps': 'Open in Google Maps',
  'nextServices.hotel': 'Book a hotel',
  'nextServices.flight': 'Search flights',
  'nextServices.seat': 'Choose a seat',
  'nextServices.car': 'Rent a car',
  'nextServices.train': 'Search trains',
  'nextServices.bus': 'Search buses',
  'nextServices.insurance': 'Add travel insurance',
  'nextServices.transferToAirport': 'Car to the airport',
  'nextServices.transferAirportHotel': 'Car from the airport to the hotel',
  'nextServices.transferToHotel': 'Car to the hotel',
  'nextServices.recommended': 'Recommended',
  'nextServices.note': 'You can add the rest later, right on your trip page.',
  'flight.nextServices.ask.hotelRange':
    'I want to book a hotel in {{city}} from {{from}} to {{to}}.',
  'flight.nextServices.ask.hotelOpen': 'I want to book a hotel in {{city}} starting {{from}}.',
  'flight.nextServices.ask.hotelBare': 'I want to book a hotel in {{city}}.',
  'flight.nextServices.ask.seat': 'I want to pick my seats for the flight I just booked.',
  'flight.nextServices.ask.flight': 'I want to search for another flight.',
  'flight.nextServices.ask.carRange': 'I want to rent a car in {{city}} from {{from}} to {{to}}.',
  'flight.nextServices.ask.carOpen': 'I want to rent a car in {{city}} starting {{from}}.',
  'flight.nextServices.ask.carBare': 'I want to rent a car in {{city}}.',
  'flight.nextServices.ask.train': 'I want to search train tickets to {{city}}.',
  'flight.nextServices.ask.bus': 'I want to search bus tickets to {{city}}.',
  'flight.nextServices.ask.insurance': 'I want to add travel insurance to my trip.',
  'flight.nextServices.ask.transferToAirport': 'I want to schedule a car to the airport.',
  'flight.nextServices.ask.transferAirportHotel':
    'I want to schedule a car from the airport to the hotel.',
  'flight.nextServices.ask.transferToHotel': 'I want to schedule a car to the hotel.',
  'nextServices.ask.hotelRange': 'I want to book a hotel near {{place}} from {{from}} to {{to}}.',
  'nextServices.ask.hotelOpen': 'I want to book a hotel near {{place}} starting {{from}}.',
  'nextServices.ask.hotelBare': 'I want to book a hotel near {{place}}.',
  'nextServices.ask.hotelTrip': 'I want to book a hotel for this trip.',
  'nextServices.ask.carRange': 'I want to rent a car near {{place}} from {{from}} to {{to}}.',
  'nextServices.ask.carOpen': 'I want to rent a car near {{place}} starting {{from}}.',
  'nextServices.ask.carBare': 'I want to rent a car near {{place}}.',
  'nextServices.ask.carTrip': 'I want to rent a car for this trip.',
  'nextServices.ask.insurance': 'I want to add travel insurance to my trip.',
  'nextServices.ask.flight': 'I want to search for a flight for this trip.',
  'nextServices.ask.seat': 'I want to pick the seats for my flight.',
  'nextServices.ask.train': 'I want to search train tickets for this trip.',
  'nextServices.ask.bus': 'I want to search bus tickets for this trip.',
  'nextServices.ask.transferToAirport': 'I want to schedule a car to the airport.',
  'nextServices.ask.transferAirportHotel':
    'I want to schedule a car from the airport to the hotel.',
  'nextServices.ask.transferToHotel': 'I want to schedule a car to the hotel.',
  'hotel.nextServices.ask.carRange': 'I want to rent a car near {{hotel}} from {{from}} to {{to}}.',
  'hotel.nextServices.ask.carOpen': 'I want to rent a car near {{hotel}} starting {{from}}.',
  'hotel.nextServices.ask.carBare': 'I want to rent a car near {{hotel}}.',
  'hotel.nextServices.ask.carTrip': 'I want to rent a car for this trip.',
  'hotel.nextServices.ask.train': 'I want to search train tickets to get to {{hotel}}.',
  'hotel.nextServices.ask.trainTrip': 'I want to search train tickets for this trip.',
  'hotel.nextServices.ask.bus': 'I want to search bus tickets to get to {{hotel}}.',
  'hotel.nextServices.ask.busTrip': 'I want to search bus tickets for this trip.',
  'hotel.nextServices.ask.insurance': 'I want to add travel insurance to my trip.',
  'hotel.nextServices.ask.seat': 'I want to pick my flight seats.',
  'hotel.nextServices.ask.flight': 'I want to search for a flight for this trip.',
  'hotel.nextServices.ask.hotel': 'I want to book another hotel for this trip.',
  'hotel.nextServices.ask.transferToAirport': 'I want to schedule a car to the airport.',
  'hotel.nextServices.ask.transferAirportHotel':
    'I want to schedule a car from the airport to the hotel.',
  'hotel.nextServices.ask.transferToHotel': 'I want to schedule a car to the hotel.',
  'guidedFlows.label': 'Or would you rather pick the service yourself?',
  'guidedFlows.label.onBehalf': 'Or would you rather pick the service for {{traveler}}?',
  'guidedFlows.flow.flight': 'Flights',
  'guidedFlows.flow.hotel': 'Hotels',
  'guidedFlows.flow.rental': 'Cars',
  'guidedFlows.flow.bus': 'Bus',
  'guidedFlows.reply.flight': 'I want to book a flight.',
  'guidedFlows.reply.hotel': 'I want to book a hotel.',
  'guidedFlows.reply.rental': 'I want to rent a car.',
  'guidedFlows.reply.bus': 'I want a bus ticket.',
  'guidedFlows.reply.flight.onBehalf': 'I want to book a flight for {{traveler}}.',
  'guidedFlows.reply.hotel.onBehalf': 'I want to book a hotel for {{traveler}}.',
  'guidedFlows.reply.rental.onBehalf': 'I want to rent a car for {{traveler}}.',
  'guidedFlows.reply.bus.onBehalf': 'I want a bus ticket for {{traveler}}.',
  'flightOffers.title': 'Best flights',
  'flightOffers.outbound': 'Outbound',
  'flightOffers.return': 'Return',
  'flightOffers.itinerary_one': '{{count}} itinerary',
  'flightOffers.itinerary_other': '{{count}} itineraries',
  'flightOffers.seeAll': 'See all flights',
  'flightOffers.quoteSpecific': 'I want to quote flight {{carrier}} {{flightNumber}}{{where}}.',
  'flightOffers.quoteGeneric': 'I want to quote this option.',
  'flightOffers.viewFares': 'View fares',
  'flightOffers.closeFares': 'Close fares',
  'flightOffers.faresBundled': 'Fares · round trip',
  'flightOffers.bothLegs': 'Round trip',
  'flightOffers.asymmetricBaggage':
    'You only check a bag on one leg — {{article}} {{leg}} leg has no checked bag.',
  'flightOffers.asymmetricBaggageFee':
    'Free checked bag on one leg only — {{article}} {{leg}} leg charges for checked bags.',
  'flightOffers.legArticleF': 'the',
  'flight.badge.best': 'Recommended',
  'flight.badge.cheap': 'Lowest price',
  'flight.badge.fast': 'Shortest time',
  'flightOffers.baseOneWay': 'One way for',
  'flightQuote.oneWay': 'one way',
  'flightQuote.roundTrip': 'round trip',
  'flightOffers.baseRoundTrip': 'Round trip for',
  'flightOffers.recommended': 'Recommended',
  'flightOffers.loadingFares': 'Loading fares…',
  'flightOffers.faresError': 'Could not load the fares.',
  'flightOffers.faresStale': 'This option has expired. Search again.',
  'flightOffers.faresLegUnavailableOutbound':
    'The outbound flight you chose has no fares available. Choose another flight.',
  'flightOffers.faresLegUnavailableReturn':
    'The return flight you chose has no fares available. Choose another flight.',
  'flightOffers.searchAgain': 'Search again',
  'flightOffers.searchingAgain': 'Searching again…',
  'flightOffers.searchAgainReply': 'Can you search again? These options have expired.',
  'flightOffers.seats': 'Seats',
  'flightOffers.legOutbound': 'Depart',
  'flightOffers.legReturn': 'Return',
  'flightOffers.legSingle': 'Flight',
  'flightOffers.fromPrice': 'From',
  'flightOffers.slideAria': 'Offer {{position}} of {{total}}',
  'flightOffers.pickedFares': 'I picked option {{position}} ({{carrier}}) with the {{fares}} fare.',
  'flightOffers.chosen': 'Selected',
  'flightMultiOffers.title': 'Pick a flight for each leg',
  'flightMultiOffers.leg': 'Leg {{n}}',
  'flightMultiOffers.bestOptions': '{{count}} best options',
  'flightMultiOffers.chosenCount': '{{chosen}} of {{total}} picked',
  'flightMultiOffers.partialTotal': 'Partial total',
  'flightMultiOffers.choosingNow': 'Picking now…',
  'flightMultiOffers.waiting': 'Waiting…',
  'flightMultiOffers.waitingOutbound': 'Waiting for your outbound…',
  'flightMultiOffers.stepDone': 'Leg {{n}} done',
  'flightMultiOffers.acceptCheapestFares_one': 'Accept the cheapest fare (1)',
  'flightMultiOffers.acceptCheapestFares_other': 'Accept the cheapest fares ({{count}})',
  'flightMultiOffers.acceptCheapestFaresShort_one': 'Cheapest fare (1)',
  'flightMultiOffers.acceptCheapestFaresShort_other': 'Cheapest fares ({{count}})',
  'flightMultiOffers.undo': 'Undo',
  'flightMultiOffers.confirmPick': 'Confirm',
  'flightMultiOffers.pickedAll':
    "I've picked flights for all {{count}} legs. Total {{total}}. Let's go to checkout.",
  'busOffers.outbound': 'Outbound',
  'busOffers.return': 'Return',
  'busOffers.connection_one': '{{count}} connection',
  'busOffers.connection_other': '{{count}} connections',
  'busOffers.pickText': 'I want ticket option {{position}} ({{carrier}} {{from}}→{{to}}).',
  'busResults.title': '{{leg}} — {{place}} · {{date}}',
  'busResults.legOutbound': 'Outbound',
  'busResults.legReturn': 'Return',
  'busResults.headline': '{{leg}} · {{date}}',
  'busResults.share': 'Share this search',
  'busResults.chosen_one': '{{chosen}} of {{count}} chosen',
  'busResults.chosen_other': '{{chosen}} of {{count}} chosen',
  'busResults.total_one': '{{count}} bus',
  'busResults.total_other': '{{count}} buses',
  'busResults.visible': '{{visible}} of {{total}} buses',
  'busResults.resultsLabel': 'Buses found',
  'busResults.applyFilters': 'Apply filters',
  'busResults.loadMore': 'Show more buses',
  'busResults.dates': 'Change the date',
  'busResults.filteredEmpty': 'No buses match these filters.',
  'busResults.clearFilters': 'Clear filters',
  'busResults.sort': 'Sort',
  'busResults.chips.toolbar': 'Filters and sorting',
  'busResults.chips.back': 'Back',
  'busResults.chips.policy': 'Within policy',
  'busResults.order.departure': 'Departure time',
  'busResults.order.arrival': 'Arrival time',
  'busResults.order.price': 'Lowest price',
  'busResults.order.duration': 'Shortest duration',
  'busResults.card.preferredCarrier': 'Your bus company',
  'busResults.card.preferredSeatClass': 'Your class',
  'busResults.card.preferredDeparture': 'Your time',
  'busResults.card.select': 'Select',
  'busResults.card.perPerson': '/person',
  'busResults.card.details': 'Details',
  'busResults.card.direct': 'Direct',
  'busResults.card.withConnection': 'With connection',
  'busResults.card.withConnectionAt': 'With connection in {{stop}}',
  'busResults.card.connectionStop': 'Connection in {{stop}}',
  'busResults.card.connectionTimes': 'Arrives at {{arrival}} and leaves at {{departure}}',
  'busResults.card.connectionWait': '{{duration}} wait at the connection',
  'busResults.card.seatsLeft_one': '{{seatClass}} · {{count}} seat left',
  'busResults.card.seatsLeft_other': '{{seatClass}} · {{count}} seats left',
  'busResults.outOfPolicy': 'Out of policy',
  'busResults.legs.label': 'Trip legs',
  'busResults.legs.outboundDone': 'Outbound chosen',
  'busResults.legs.returnDone': 'Return chosen',
  'busResults.legs.choosing': 'Choosing now…',
  'busResults.legs.pending': 'Waiting for your outbound…',
  'busResults.dayStrip.label': 'Search another day',
  'busOffers.pickDayText': 'Show me the departures on {{day}}',
  'busResults.legs.seat_one': 'seat {{seats}}',
  'busResults.legs.seat_other': 'seats {{seats}}',
  'busResults.filters.filters': 'Filters',
  'busResults.filters.collapse': 'Collapse filters',
  'busResults.filters.close': 'Close filters',
  'busResults.filters.reset': 'Reset',
  'busResults.filters.policy': 'Policy',
  'busResults.filters.withinPolicy': 'Within policy only',
  'busResults.filters.seatClass': 'Seat class',
  'busResults.filters.time': 'Time',
  'busResults.filters.departure': 'Departure',
  'busResults.filters.arrival': 'Arrival',
  'busResults.filters.price': 'Price up to',
  'busResults.filters.minimumPrice': 'Minimum price',
  'busResults.filters.maximumPrice': 'Maximum price',
  'busResults.filters.company': 'Bus company',
  'busResults.shortlist': 'Trips picked for you',
  'busResults.seeAll': 'See all {{count}} departures',
  'busResults.close': 'Close the bus list',
  'busResults.seatsTitle': 'Choose your seat',
  'busResults.seatsClose': 'Close seat selection',
  'bus.nextServices.note': 'You can add the rest later, right on your trip page.',
  'bus.nextServices.ask.hotelRange': 'I want to book a hotel in {{city}} from {{from}} to {{to}}.',
  'bus.nextServices.ask.hotelOpen': 'I want to book a hotel in {{city}} from {{from}}.',
  'bus.nextServices.ask.hotelTrip': 'I want to book a hotel for this trip.',
  'bus.nextServices.ask.carRange': 'I want to rent a car in {{city}} from {{from}} to {{to}}.',
  'bus.nextServices.ask.carOpen': 'I want to rent a car in {{city}} from {{from}}.',
  'bus.nextServices.ask.carTrip': 'I want to rent a car for this trip.',
  'bus.nextServices.ask.insurance': 'I want to add travel insurance to my trip.',
  'bus.nextServices.ask.seat': 'I want to choose another seat.',
  'bus.nextServices.ask.flight': 'I want to search for a flight for this trip.',
  'bus.nextServices.ask.train': 'I want to search train tickets for this trip.',
  'bus.nextServices.ask.bus': 'I want to search another bus ticket.',
  'bus.nextServices.ask.transferToAirport': 'I want to schedule a car to the airport.',
  'bus.nextServices.ask.transferAirportHotel':
    'I want to schedule a car from the airport to the hotel.',
  'bus.nextServices.ask.transferToHotel': 'I want to schedule a car to the hotel.',
  'busSearch.who': 'Who',
  'busSearch.origin': 'Origin',
  'busSearch.destination': 'Destination',
  'busSearch.originPlaceholder': 'Origin city',
  'busSearch.destinationPlaceholder': 'Destination city',
  'busSearch.departingFrom': 'Leaving from {{origin}}',
  'busSearch.swap': 'Swap origin and destination',
  'busSearch.tripType.roundTrip': 'Round trip',
  'busSearch.tripType.oneWay': 'One-way',
  'busSearch.recentGroup': 'Recent',
  'busSearch.noStops': 'No bus station found',
  'busSearch.stopError': 'Could not load the bus stations',
  'busSearch.stopPrompt': 'Type at least 2 letters',
  'busSearch.when': 'When',
  'busSearch.toBeDefined': 'To be defined',
  'busSearch.chooseRoundTrip': 'Choose departure and return',
  'busSearch.returnPending': '{{date}} → choose the return',
  'busSearch.confirmDates': 'Confirm {{range}}',
  'busSearch.confirmDatesSheet': 'Confirm dates',
  'busSearch.confirmAndSearch': 'Confirm and search',
  'busSearch.previousMonth': 'Previous month',
  'busSearch.nextMonth': 'Next month',
  'busSearch.submit': 'Search',
  'busSearch.back': 'Back',
  'busSearch.searchText': 'Bus from {{from}} to {{to}}, {{when}}.',
  'rentalSearch.driver': 'Who',
  'rentalSearch.swap': 'Swap pick-up and drop-off',
  'rentalSearch.sameDropoff': 'Same drop-off location',
  'rentalSearch.location': 'Pick-up and drop-off location',
  'rentalSearch.pickupLocation': 'Pick-up location',
  'rentalSearch.dropoffLocation': 'Drop-off location',
  'rentalSearch.locationPlaceholder': 'Location or airport',
  'rentalSearch.noPlaces': 'No places found',
  'rentalSearch.placeError': 'Places could not be loaded',
  'rentalSearch.myLocation': 'My location',
  'rentalSearch.myLocationError': 'Your location could not be used',
  'rentalSearch.when': 'When',
  'rentalSearch.dates': 'Pick-up and drop-off',
  'rentalSearch.chooseDates': 'Choose pick-up and drop-off',
  'rentalSearch.chooseDropoff': '{{date}} · choose drop-off',
  'rentalSearch.days_one': '{{count}} day',
  'rentalSearch.days_other': '{{count}} days',
  'rentalSearch.confirmDates': 'Confirm {{range}}',
  'rentalSearch.confirmDatesSheet': 'Confirm dates',
  'rentalSearch.confirmAndSearch': 'Confirm and search',
  'rentalSearch.pickupTime': 'Pick-up time',
  'rentalSearch.dropoffTime': 'Drop-off time',
  'rentalSearch.previousMonth': 'Previous month',
  'rentalSearch.nextMonth': 'Next month',
  'rentalSearch.submit': 'Search',
  'rentalSearch.back': 'Back',
  'rentalSearch.retry': 'Try again',
  'rentalSearch.change': 'Change dates or times',
  'rentalSearch.searchTextSame': 'Search cars with pick-up and drop-off at {{place}}, {{window}}.',
  'rentalSearch.searchTextDifferent':
    'Search cars with pick-up at {{pickup}}, {{pickupAt}} and drop-off at {{dropoff}}, {{dropoffAt}}.',
  'mobilityPlanner.origin': 'From',
  'mobilityPlanner.destination': 'To',
  'mobilityPlanner.placeholder': 'Address or place',
  'mobilityPlanner.suggestions': 'Suggestions',
  'mobilityPlanner.noPlaces': 'No place found',
  'mobilityPlanner.placesError': 'Could not load the places',
  'mobilityPlanner.retry': 'Try again',
  'mobilityPlanner.prompt': 'Type at least 2 letters',
  'mobilityPlanner.submit': 'Search cars',
  'mobilityPlanner.when': 'When',
  'mobilityPlanner.whenPending': 'To be defined',
  'mobilityPlanner.whenMissing': 'Choose the date and time',
  'mobilityPlanner.whenMissingDate': 'Choose the date',
  'mobilityPlanner.whenMissingTime': 'Choose the time',
  'mobilityPlanner.whenConfirm': 'Confirm',
  'mobilityPlanner.whenBack': 'Back',
  'mobilityPlanner.previousMonth': 'Previous month',
  'mobilityPlanner.nextMonth': 'Next month',
  'mobilityPlanner.hour': 'Hour',
  'mobilityPlanner.minute': 'Minute',
  'mobilityPlanner.who': 'Who',
  'mobilityPlanner.tripType': 'Direction',
  'mobilityPlanner.tripType.oneWay': 'One way',
  'mobilityPlanner.tripType.roundTrip': 'Round trip',
  'mobilityPlanner.passengers': 'People',
  'mobilityPlanner.passengersCount_one': '{{count}} person',
  'mobilityPlanner.passengersCount_other': '{{count}} people',
  'mobilityPlanner.passengersLess': 'One person less',
  'mobilityPlanner.passengersMore': 'One more person',
  'mobilityPlanner.outbound': 'Outbound',
  'mobilityPlanner.return': 'Return',
  'mobilityPlanner.returnRoute': 'Return · {{from}} → {{to}}',
  'mobilityPlanner.searchText': 'Ride from {{from}} to {{to}}.',
  'mobilityPlanner.searchTextRoundTrip': 'Ride from {{from}} to {{to}}, round trip.',
  'mobilityPlanner.searchTextNoDestination': 'Car leaving from {{from}}.',
  'busResults.badge.recommended': 'Recommended',
  'busResults.badge.cheapest': 'Lowest price',
  'busResults.badge.fastest': 'Shortest time',
  'busResults.merit.recommended': 'The best balance of price and time, in {{seatClass}}.',
  'busResults.merit.cheapest': '{{amount}} less than the recommended one.',
  'busResults.merit.cheapestAt': '{{amount}} less — with a bus change in {{stop}}.',
  'busResults.merit.fastest': '{{duration}} faster than the recommended one.',
  'busResults.merit.fastestDirect': '{{duration}} faster and with no stops.',
  'busSeats.segments': 'Trip segments',
  'busSeats.segment': 'Segment {{segment}}',
  'busSeats.segmentRoute': 'Segment {{segment}} · {{from}} → {{to}}',
  'busSeats.entrance': 'Entrance',
  'busSeats.driver': 'Driver',
  'busSeats.floor': 'Deck {{floor}}',
  'busSeats.seat': 'Seat {{code}}',
  'busSeats.seatOccupied': 'Seat {{code}}, taken',
  'busSeats.loading': 'Loading the seats…',
  'busSeats.error': "We couldn't load the seats for this segment right now. Try again in a moment.",
  'busSeats.offline': "We couldn't load the seats. Check your connection and try again.",
  'busSeats.notOffered': "The bus company doesn't allow choosing a seat on this segment.",
  'busSeats.retry': 'Try again',
  'busSeats.full': 'Not enough free seats for everyone on this segment.',
  'busSeats.noSeat': 'Continue without a seat',
  'busSeats.noSeatSummary': 'no seat',
  'busSeats.pending_one': 'Choose {{count}} more seat',
  'busSeats.pending_other': 'Choose {{count}} more seats',
  'busSeats.confirm': 'Select',
  'busSeats.suggested': 'Seat recommended based on your preferences',
  'busSeats.legend.free': 'Free',
  'busSeats.legend.occupied': 'Taken',
  'busSeats.legend.selected': 'Yours',
  'busSeats.legend.recommended': 'Recommended',
  'busSeats.legend.window': 'Window',
  'busSeats.detail.window': 'Window',
  'busSeats.detail.aisle': 'Aisle',
  'busSeats.detail.windowAndAisle': 'Window and aisle',
  'busSeats.detail.individual': 'Single seat',
  'busSeats.detail.area.front': 'Front',
  'busSeats.detail.area.middle': 'Middle',
  'busSeats.detail.area.back': 'Back',
  'busSeats.detail.neighborFree': 'Seat beside is free',
  'busSeats.detail.neighborOccupied': 'Seat beside is taken',
  'busSeats.detail.recommended': 'Recommended for your preferences',
  'busSeats.detail.hint': 'Tap a seat to see its details.',
  'busSeats.collapse': 'Close seat selection',
  'busSeats.price.oneWay': 'one way, per person',
  'busSeats.price.outbound': 'outbound, per person',
  'busSeats.price.return': 'return, per person',
  'busOffers.pickSeatsText': 'I want the {{time}} trip ({{carrier}}), {{seats}}.',
  'busOffers.pickSeat': 'seat {{code}}',
  'busOffers.pickNoSeat': 'no seat',
  'busStops.aria': 'Bus station picker',
  'busStops.titleOrigin': 'Which bus station do you want to depart from?',
  'busStops.titleDestination': 'Which bus station are you going to?',
  'bus.inPolicy': 'Within policy',
  'busSeatMap.aria': 'Seat map',
  'busSeatMap.title': 'Choose your seat',
  'busSeatMap.picked': "I'll take seat {{code}}.",
  'busSeatMap.free': 'Seat {{code}} (available)',
  'busSeatMap.occupied': 'Seat {{code}} (occupied)',
  'busSeatMap.legendFree': 'Available',
  'busSeatMap.legendOccupied': 'Occupied',
  'busSeatMap.legendSelected': 'Selected',
  'busSeatMap.noSeat': 'Proceed without a seat',
  'busSeatMap.noSeatHint': 'The VOLL team will pick a seat for you.',
  'busSeatMap.noSeatPicked': "It's fine to proceed without a seat — the VOLL team will choose.",
  'busSeatMap.pickedSeat': 'seat {{code}}',
  'busSeatMap.pickedNoSeat': 'no seat',
  'busSeatMap.pickedRound': 'Outbound: {{outbound}} · Return: {{return}}.',
  'busSeatMap.errorTitle': "Couldn't load the seats",
  'busSeatMap.otherTrip': 'Choose another trip',
  'busSeatMap.otherTripText': "I'd like to choose another trip.",
  'busSeatMap.continue': 'Continue',
  'busSeatMap.legOutbound': 'Outbound',
  'busSeatMap.legReturn': 'Return',
  'busSeatMap.noSeatSelected': 'No seat (the VOLL team will choose)',
  'busSeatMap.front': 'Front',
  'busSeatMap.floor': 'Floor {{n}}',
  'flightFilter.aria': 'Filter flights',
  'flightFilter.title': 'Flights',
  'flightFilter.option_one': '{{count}} option',
  'flightFilter.option_other': '{{count}} options',
  'flightFilter.empty.title': 'No flights match these filters.',
  'flightFilter.empty.hint': 'Try loosening stops, time, or price.',
  'flightFilter.empty.hintAxes': 'Try loosening {{axes}}.',
  'flightFilter.filteredAria': 'Filtered flights',
  'flightFilter.selectOffer': 'Select',
  'flightFilter.selectOfferAria': 'Select offer — option {{position}}',
  'flightFilter.found_one': '{{count}} flight found',
  'flightFilter.found_other': '{{count}} flights found',
  'flightFilter.foundOfTotal': '{{count}} of {{total}} flights',
  'flightFilter.sort.label': 'Sort',
  'flightFilter.sort.price': 'Cheapest',
  'flightFilter.sort.dep': 'Earliest',
  'flightFilter.sort.dur': 'Fastest',
  'flightFilter.sortMenu.rec': 'Recommended',
  'flightFilter.sortMenu.price': 'Price',
  'flightFilter.sortMenu.dur': 'Duration',
  'flightFilter.sortMenu.dep': 'Departure',
  'flightFilter.sortMenu.arr': 'Arrival',
  'flightFilter.stops.label': 'Stops',
  'flightFilter.stops.direct': 'Nonstop',
  'flightFilter.stops.one': 'Up to 1 stop',
  'flightFilter.stops.two': 'Up to 2 stops',
  'flightFilter.stops.above': 'More than 2 stops',
  'flightFilter.airline': 'Airlines',
  'flightFilter.time': 'Times',
  'flightFilter.departs': 'Departure',
  'flightFilter.arrives': 'Arrival',
  'flightFilter.departureTime': 'Departure time',
  'flightFilter.price': 'Price',
  'flightFilter.minPrice': 'Minimum price',
  'flightFilter.maxPrice': 'Maximum price',
  'flightFilter.flightNumber': 'Flight number',
  'flightFilter.flightNumberPlaceholder': 'E.g.: VL 0000',
  'flightFilter.filters': 'Filters',
  'flightFilter.apply': 'Apply filters',
  'flightFilter.reset': 'Reset all',
  'flightFilter.close': 'Close filters',
  'flightFilter.policy': 'Policy',
  'flightFilter.withinPolicy': 'Within policy only',
  'flightExplorer.chips.toolbar': 'Filters and sorting',
  'flightExplorer.chips.back': 'Back',
  'flightExplorer.chips.policy': 'Within policy',
  'flightFilter.fromPrice': 'from {{price}}',
  'flightFilter.arrivalTime': 'Arrival time',
  'flightFilter.airports': 'Airports',
  'flightFilter.otherCities': 'Other',
  'flightChips.placeholder': 'Filter your way',
  'flightChips.send': 'Apply text filter',
  'flightChips.dictate': 'Dictate a filter',
  'flightChips.dictationCancel': 'Cancel dictation',
  'flightChips.dictationStop': 'Stop and review',
  'flightChips.dictationSend': 'Stop and apply',
  'flightChips.transcribing': 'Transcribing…',
  'flightChips.dictationFailed': 'Could not transcribe. Try again.',
  'flightChips.applying': 'Applying your filter…',
  'flightChips.remove': 'Remove filter: {{label}}',
  'flightChips.leg.outbound': 'Outbound',
  'flightChips.leg.inbound': 'Return',
  'flightChips.leg.n': 'Leg {{n}}',
  'flightChips.leg.prefixed': '{{leg}} · {{label}}',
  'flightChips.airline.only': 'Only {{name}}',
  'flightChips.airline.exclude': 'No {{name}}',
  'flightChips.stops.nonstop': 'Nonstop',
  'flightChips.stops.up_to_1': 'Up to 1 stop',
  'flightChips.stops.up_to_2': 'Up to 2 stops',
  'flightChips.stops.any': 'With or without stops',
  'flightChips.time.arriveBefore': 'Arrive by {{time}}',
  'flightChips.time.arriveAfter': 'Arrive after {{time}}',
  'flightChips.time.arriveBetween': 'Arrive between {{start}} and {{end}}',
  'flightChips.time.departBefore': 'Depart by {{time}}',
  'flightChips.time.departAfter': 'Depart after {{time}}',
  'flightChips.time.departBetween': 'Depart between {{start}} and {{end}}',
  'flightChips.price.max': 'Up to {{price}}',
  'flightChips.price.min': 'From {{price}}',
  'flightChips.price.between': 'Between {{min}} and {{max}}',
  'flightChips.policy.only': 'In policy only',
  'flightChips.policy.release': 'Including out-of-policy options',
  'flightChips.via.avoid': 'No connection in {{place}}',
  'flightChips.via.through': 'Connecting in {{place}}',
  'flightChips.airport.from': 'Departing from {{place}}',
  'flightChips.airport.to': 'Arriving at {{place}}',
  'flightChips.airport.only': 'Only {{place}}',
  'flightChips.airport.exclude': 'Not {{place}}',
  'flightChips.sort.rec': 'Recommended first',
  'flightChips.sort.price': 'Cheapest first',
  'flightChips.sort.dur': 'Fastest first',
  'flightChips.sort.dep': 'Earliest departure first',
  'flightChips.sort.arr': 'Earliest arrival first',
  'flightChips.axis.punctuality': 'Punctuality',
  'flightChips.axis.baggage': 'Baggage',
  'flightChips.axis.seat': 'Seat',
  'flightChips.axis.aircraft': 'Aircraft',
  'flightChips.axis.layover': 'Layover time',
  'flightChips.axis.duration': 'Total duration',
  'flightChips.axis.cabin': 'Cabin',
  'flightChips.axis.fare': 'Fare rules',
  'flightChips.axis.airline': 'Airline',
  'flightChips.axis.via': 'Connection',
  'flightChips.axis.airport': 'Airport',
  'flightChips.axis.time': 'Time',
  'flightChips.axis.stops': 'Stops',
  'flightChips.axis.period': 'Time of day',
  'flightChips.axis.price': 'Price',
  'flightChips.axis.policy': 'Policy',
  'flightChips.axis.sort': 'Sorting',
  'flightChips.axis.other': 'Filter',
  'flightChips.reason.airport_not_in_search': 'no option in this search goes through there',
  'flightChips.reason.airline_not_in_search': 'that airline is not in this search',
  'flightChips.reason.no_punctuality_data': 'no punctuality data in this list',
  'flightChips.reason.axis_not_available': 'that criterion is not available in this list',
  'flightChips.reason.axis_not_in_panel': "layover time and duration can't be filtered here yet",
  'flightChips.reason.window_not_contiguous':
    "can't exclude a midday period here — adjust the slider",
  'flightChips.reason.low_confidence': "I wasn't sure — try rephrasing",
  'flightChips.reason.other': "couldn't apply it",
  'flightChips.note.question': "That's a question — ask it in the chat.",
  'flightChips.note.new_search': 'That looks like a new search — use the search bar at the top.',
  'flightChips.note.noise': "I couldn't read that as a filter.",
  'flightChips.note.failed': "Couldn't interpret it right now. Try again.",
  'flightChips.note.cabin': 'Searched again in {{cabin}}',
  'flightChips.note.cabinSame': 'The search is already in {{cabin}}',
  'flightChips.note.cabinUnavailable': "Cabin can't be changed from here on this screen",
  'flightChips.note.reset': 'Filters cleared',
  'flightExplorer.cabin': 'Cabin',
  'flightExplorer.cabin.economy': 'Economy',
  'flightExplorer.cabin.premium': 'Premium',
  'flightExplorer.cabin.business': 'Business',
  'flightExplorer.cabin.first': 'First',
  'flightExplorer.cabinRefetch': 'Changing the cabin runs a new search',
  'flightExplorer.cabinOutOfPolicy': 'outside the company policy',
  'flightExplorer.cabinAllowedFromHours': 'allowed on this route (flights from {{hours}}h)',
  'flightExplorer.cabinOutBelowHours':
    'outside the policy on this route (only on flights from {{hours}}h)',
  'flightExplorer.searchCta': 'Search',
  'flightExplorer.searching': 'Searching flights…',
  'flightExplorer.searchingLong':
    'The search is still running. Some routes take more than a minute.',
  'flightExplorer.refreshing': 'refreshing the list…',
  'flightExplorer.whyRecommended': 'Best balance of price, flight time and stops in this list.',
  'flightExplorer.whyRecommendedDirect':
    'Best balance of price, flight time and stops in this list — and it is nonstop.',
  'flightExplorer.whyRecommendedInPolicy':
    'Best balance of price, flight time and stops in this list — and it is within policy.',
  'flightExplorer.whyRecommendedDirectInPolicy':
    'Best balance of price, flight time and stops in this list — and it is nonstop, within policy.',
  'flightExplorer.loadMore': 'See more flights',
  'flightExplorer.offline.offerTitle': 'Could not find the flight you need?',
  'flightExplorer.offline.offerBody':
    'The VOLL team searches flights outside the online catalog — airlines, fares and times — that match your profile and brings the quote back to the trip conversation.',
  'flightExplorer.offline.returnOn': 'return {{date}}',
  'flightExplorer.pinnedFilter': 'Pinned ({{count}})',
  'flightExplorer.error': 'We could not search flights',
  'flightExplorer.timeout': 'The search took too long',
  'flightExplorer.timeoutHint': 'The supplier did not answer in time. Try again in a moment.',
  'flightExplorer.timeoutCity': 'The search across all {{city}} airports took too long',
  'flightExplorer.timeoutCityHint': 'Pick an airport for a faster search.',
  'flightExplorer.timeoutAirports': 'Search one airport',
  'flightExplorer.policyEvaluation': "We couldn't evaluate the traveler's policy",
  'flightExplorer.policyEvaluationHint': 'Please talk to your company administrator.',
  'flightExplorer.searchExpired': 'This search has expired',
  'flightExplorer.searchExpiredHint':
    'The supplier only keeps a search for a while. Search again: we keep your outbound flight if it is still available.',
  'flightExplorer.searchExpiredAction': 'Search again',
  'flightExplorer.outboundGone':
    'The outbound flight you picked is no longer available. Pick another outbound flight.',
  'flightExplorer.chooseOutbound': 'Choose the outbound flight',
  'flightExplorer.chooseReturn': 'Choose the return flight',
  'flightExplorer.removePick': 'Remove the leg {{n}} pick',
  'flightExplorer.policyCeilingAbove': 'Above {{price}}',
  'flightExplorer.policyCeilingOut': 'is out of policy',
  'flightFares.policyReason': 'Out of policy: {{reasons}}',
  'flightExplorer.listTitle': 'Flights to {{city}} · {{date}}',
  'flightExplorer.share': 'Share this search',
  'flightExplorer.listTitleOutbound': 'Choose the outbound — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.listTitleReturn': 'Choose the return — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.confirm': 'Confirm',
  'flightExplorer.researchKeptCabin': 'We could not search {{cabin}}. We kept the previous list.',
  'flightExplorer.researchEmptyCabin':
    'There are no {{cabin}} flights on this route. We kept the previous list.',
  'flightExplorer.researchKept': 'We could not redo the search. We kept the previous list.',
  'flightExplorer.researchKeptPolicy':
    "We couldn't evaluate the traveler's policy. We kept the previous list; please talk to your company administrator.",
  'flightExplorer.draftPending': 'Search before confirming — the bar has unsearched changes.',
  'flightExplorer.confirmUnavailable':
    "The selection can't be completed: the legs came back in different currencies. Change the fare or search again.",
  'flightExplorer.replacesTrip': 'Confirming replaces the trip on the card.',
  'flightExplorer.replacesTripDiscards':
    'Confirming replaces the trip and discards the picks made on the other legs.',
  'flightExplorer.aria': 'Explore flights',
  'flightExplorer.pillAria': 'Search context',
  'flightExplorer.pill.legs_one': '{{count}} leg',
  'flightExplorer.pill.legs_other': '{{count}} legs',
  'flightExplorer.pillEdit': 'Edit search',
  'flightExplorer.searchAgain': 'Search again',
  'flightExplorer.legCta': 'See all flights',
  'flightExplorer.legCtaShort': 'All flights',
  'flightExplorer.frozenEmpty': 'The offers from this search are no longer available.',
  'flightExplorer.emptyResults': 'We found no flights to {{destination}} on these dates',
  'searchEmpty.newSearch': 'New search',
  'searchEmpty.retry': 'Try again',
  'flightSeatLink.cta': 'Choose seat',
  'ride99Link.title': 'Ride with 99',
  'ride99Link.cta': 'Request ride',
  'walletLink.title.balance': 'Balance request',
  'walletLink.title.home': 'Wallet',
  'walletLink.cta': 'Open Wallet',
  'eventPremises.missing_one': '{{count}} item missing from the sheet',
  'eventPremises.missing_other': '{{count}} items missing from the sheet',
  'eventPremises.ready': 'Sheet ready to confirm',
  'eventPremises.draft': 'Draft · {{done}} of {{total}}',
  'eventPremises.readyToPublish': 'Ready to publish',
  'eventPremises.published': 'Published',
  'trips.unnamed': 'Unnamed trip',
  'trips.routeTbd': 'Route TBD',
  'trips.totalTbd': 'To be confirmed',
  'trips.collapse': 'Collapse',
  'trips.seeAll': 'See all {{count}} trips',
  'trips.drawer.ariaFallback': 'Trip details',
  'trips.drawer.summary': 'Summary',
  'trips.drawer.period': 'Period',
  'trips.drawer.path': 'Route',
  'trips.drawer.modals': 'Travel modes',
  'trips.drawer.requester': 'Requester',
  'trips.drawer.company': 'Company',
  'trips.drawer.vouchers': 'Vouchers',
  'trips.drawer.processingTitle': 'Vouchers are being processed',
  'trips.drawer.processingBody': 'Vouchers will appear here once suppliers confirm the booking.',
  'trip.modal.FLIGHT': 'Flight',
  'trip.modal.HOTEL': 'Hotel',
  'trip.modal.BUS': 'Bus',
  'trip.modal.RENTAL': 'Car rental',
  'trip.modal.MOBILITY': 'Mobility',
  'trip.status.UPCOMING': 'Upcoming',
  'trip.status.ONGOING': 'In progress',
  'trip.status.COMPLETED': 'Completed',
  'trip.status.CANCELLED': 'Canceled',
  'voucher.status.IN_PROGRESS': 'In progress',
  'voucher.status.AWAITING_APPROVAL': 'Awaiting approval',
  'voucher.status.CONFIRMED': 'Confirmed',
  'voucher.status.CANCELLED': 'Canceled',
  'voucher.status.EXPIRED': 'Expired',
  'voucher.status.DECLINED': 'Declined',
  'voucher.meta.supplier': 'Supplier',
  'voucher.meta.status': 'Status',
  'voucher.meta.reservation': 'Reservation',
  'voucher.meta.issuedAt': 'Issued at',
  'voucher.meta.notIssued': 'Not issued',
  'voucher.segment.outbound': 'Outbound',
  'voucher.segment.return': 'Return',
  'voucher.segment.generic': 'Segment',
  'voucher.segment.count': '{{dir}} · segment {{index}} of {{total}}',
  'voucher.detail.airline': 'Airline',
  'voucher.detail.locator': 'Locator',
  'voucher.detail.departure': 'Departure',
  'voucher.detail.arrival': 'Arrival',
  'voucher.detail.duration': 'Duration',
  'voucher.detail.connections': 'Connections',
  'voucher.detail.stop_one': '{{count}} stop',
  'voucher.detail.stop_other': '{{count}} stops',
  'voucher.detail.cabin': 'Cabin',
  'voucher.detail.seat': 'Seat',
  'voucher.detail.baggage': 'Baggage',
  'voucher.detail.operator': 'Operator',
  'voucher.detail.boarding': 'Boarding',
  'voucher.detail.disembark': 'Disembark',
  'voucher.detail.class': 'Class',
  'voucher.detail.hotel': 'Hotel',
  'voucher.detail.address': 'Address',
  'voucher.detail.city': 'City',
  'voucher.detail.checkIn': 'Check-in',
  'voucher.detail.checkOut': 'Check-out',
  'voucher.detail.nights': 'Nights',
  'voucher.detail.rooms': 'Rooms',
  'voucher.detail.guests': 'Guests',
  'voucher.detail.cancellationPolicy': 'Cancellation policy',
  'voucher.detail.fine': 'Fine {{value}}',
  'voucher.detail.available': 'Available',
  'voucher.detail.rentalCompany': 'Rental company',
  'voucher.detail.pickup': 'Pick-up',
  'voucher.detail.dropoff': 'Drop-off',
  'voucher.detail.model': 'Model',
  'voucher.detail.group': 'Group',
  'voucher.detail.category': 'Category',
  'voucher.detail.distance': 'Distance',
  'voucher.block.lodging': 'Lodging',
  'voucher.block.rental': 'Car rental',
  'voucher.block.mobility': 'Mobility',
  'flight.policyDetails.title': 'Policy Details',
  'flight.policyDetails.inPolicy': 'Within policy',
  'flight.policyDetails.outPolicy': 'Out of policy',
  'flight.policyRule.advance': 'Minimum advance',
  'flight.policyRule.allowedClass': 'Allowed class',
  'flight.policyRule.baggage': 'Baggage',
  'flight.policyRule.baggageOnlyAboveTripLength': 'Baggage by trip length',
  'flight.policyRule.baggageOnlyOnMultipleSegments': 'Baggage on multi-leg trips',
  'flight.policyRule.bookingWindow': 'Minimum advance',
  'flight.policyRule.bookingWindowInternational': 'Minimum advance (international)',
  'flight.policyRule.cheapestPrice': 'Lowest price',
  'flight.policyRule.cheapestPriceFastest': 'Lowest price among the fastest flights',
  'flight.policyRule.cheapestPriceNonStop': 'Lowest non-stop price',
  'flight.policyRule.cheapestPricePeriod': 'Lowest price in the period',
  'flight.policyRule.class': 'Allowed class',
  'flight.policyRule.classLimits': 'Price cap per class',
  'flight.policyRule.price': 'Price cap',
  'flight.policyRule.withinPolicy': 'Trip policy',
  'flight.policyRule.withinPolitics': 'Trip policy',
  'flight.policyRule.policyProfile': 'Applied policy',
  'flight.policyRule.policyProfileUnresolved': "Traveler's policy could not be identified",
  'flight.policyRule.companyGuidance': 'Company guidance',
  'flight.policyRule.bookingWindowText_one':
    'Book at least {{count}} day in advance for {{scope}} trips.',
  'flight.policyRule.bookingWindowText_other':
    'Book at least {{count}} days in advance for {{scope}} trips.',
  'flight.policyRule.scope.domestic': 'domestic',
  'flight.policyRule.scope.international': 'international',
  'flight.policyRule.classLimitsText':
    "This fare's price is above the limit set by the policy for this class.",
  'flight.policyRule.classLimitsTextWithLimit':
    "This fare's price is above the {{value}} cap set by the policy for this class.",
  'flight.policyRule.withinPolicyText':
    'This fare is outside the trip policy, but the supplier did not say which rule was broken.',
  'flight.row.outPolicyAria': 'Flight out of policy',
  'flight.segment.viewDetail': 'Details',
  'flight.pin.off': 'Pin',
  'flight.pin.on': 'Pinned',
  'flight.segment.detailsTitle': 'Flight detail',
  'flight.segment.detailBack': 'Back',
  'flight.segment.arrivesNextDay_one': 'Arrives next day',
  'flight.segment.arrivesNextDay_other': 'Arrives {{count}} days later',
  'flight.fareBreakdown.fare': 'Fare',
  'flight.fareBreakdown.taxes': 'Taxes and fees',
  'flight.fareBreakdown.totalPerPerson': 'Total per person',
  'flight.quote.outbound': 'Outbound',
  'flight.quote.return': 'Return',
  'flight.reservation.aria': 'Reservation ticket',
  'flight.reservation.leg': 'Leg {{n}}',
  'flight.reservation.locator': 'Locator',
  'flight.reservation.locatorPending': 'will be sent shortly',
  'flight.reservation.emailSent': "Reservation being issued · we've emailed you the details",
  'flight.reservation.cpf': 'CPF {{value}}',
  'traveler.policyHolder': 'Policy holder',
  'traveler.costCenter': 'Cost center',
  'traveler.motive': 'Trip reason',
  'flight.reservation.flight': 'Flight',
  'flight.reservation.date': 'Date',
  'flight.reservation.outboundDate': 'Outbound date',
  'flight.reservation.returnDate': 'Return date',
  'flight.reservation.totalPaid': 'Total paid',
  'flight.search.aria': 'Search confirmation',
  'flight.search.cabin.economy': 'Economy',
  'flight.search.cabin.premium': 'Premium',
  'flight.search.cabin.business': 'Business',
  'flight.search.cabin.first': 'First',
  'flight.search.passenger_one': '{{count}} passenger',
  'flight.search.passenger_other': '{{count}} passengers',
  'flight.search.adult_one': '{{count}} adult',
  'flight.search.adult_other': '{{count}} adults',
  'flight.search.child_one': '{{count}} child',
  'flight.search.child_other': '{{count}} children',
  'flight.search.infant_one': '{{count}} infant',
  'flight.search.infant_other': '{{count}} infants',
  'flight.search.adult': 'Adult',
  'flight.search.child': 'Child',
  'flight.search.infant': 'Infant',
  'flight.search.roundTrip': 'Round trip',
  'flight.search.oneWay': 'One way',
  'flight.search.tripType': 'Trip type',
  'flight.search.day_one': '{{count}} day',
  'flight.search.day_other': '{{count}} days',
  'flight.search.class': 'Class',
  'flight.search.searchFlights': 'Search flights',
  'flightSearchConfirmation.replySearch': 'Search flights.',
  'flightCheaperDates.roundTrip': 'Round Trip',
  'flightCheaperDates.oneWay': 'One Way',
  'flightCheaperDates.dates': 'Dates',
  'flightCheaperDates.cheapest': 'cheapest',
  'flightCheaperDates.mostExpensive': 'most expensive',
  'flightCheaperDates.search': 'Search flights on these dates',
  'flightCheaperDates.keep': 'Keep {{date}}',
  'flightCheaperDates.searchTextOneWay':
    'Search flights from {{origin}} to {{destination}} ({{originCode}}→{{destinationCode}}), departing {{outbound}}.',
  'flightCheaperDates.searchTextRoundTrip':
    'Search flights from {{origin}} to {{destination}} ({{originCode}}→{{destinationCode}}), departing {{outbound}} and returning {{return}}.',
  'flightPlanner.calDisabledHint': 'Pick origin and destination to see prices',
  'flightPlanner.allAirports': 'all airports',
  'flightPlanner.stateNeedsOut': 'Pick the outbound date',
  'flightPlanner.stateNeedsRet': 'Out {{date}} · pick the return',
  'flightPlanner.stateOneWay': 'Out {{date}}',
  'flightPlanner.stateRange_one': '{{from}} - {{to}} • {{nights}} night',
  'flightPlanner.stateRange_other': '{{from}} - {{to}} • {{nights}} nights',
  'flightPlanner.bestDayOut': '{{date}} is the cheapest outbound',
  'flightPlanner.stateLeg': 'Flight {{leg}} · {{date}}',
  'flightPlanner.stateNeedsLegDate': 'Pick the date for flight {{leg}}',
  'flightPlanner.bestDayLeg': '{{date}} is the cheapest date for flight {{leg}}',
  'flightPlanner.bestDayRet': '{{date}} is the cheapest return',
  'flightPlanner.savesLine': 'Fly this day and save {{amount}}',
  'flightPlanner.savesLineInline': 'fly this day and save {{amount}}',
  'flightPlanner.chosenCheapest': 'You picked the cheapest date',
  'flightPlanner.awaitingDate': 'Waiting for the date...',
  'flightPlanner.who': 'Who',
  'flightPlanner.whoPolicy': 'Policy of',
  'flightPlanner.from': 'From',
  'flightPlanner.to': 'To',
  'flightPlanner.when': 'When',
  'flightPlanner.whenEmpty': 'To be set',
  'flightPlanner.whenReturnPending': '– Return',
  'flightPlanner.confirmDates': 'Confirm dates',
  'flightPlanner.confirmAndSearch': 'Confirm and search',
  'flightPlanner.confirmRange': 'Confirm {{range}}',
  'flightPlanner.sheetOneWay': 'One-way',
  'flightPlanner.sheetOneWayTo': 'One-way to {{city}}',
  'flightPlanner.sheetLeg': '{{n}} of {{total}}',
  'flightPlanner.sheetLegFrom': '{{n}} of {{total}} · from {{code}}',
  'flightPlanner.cancelEdit': 'Cancel',
  'flightPlanner.back': 'Back',
  'flightPlanner.applyEdit': 'Update',
  'flightPlanner.loadingPrices': 'Fetching prices…',
  'flightPlanner.noPriceTrend': 'No price trend for this period',
  'flightPlanner.swap': 'Swap origin and destination',
  'flightPlanner.analyzed': '{{count}} dates analyzed',
  'flightPlanner.reset': 'Reset',
  'flightPlanner.outboundTag': 'OUT',
  'flightPlanner.returnTag': 'BACK',
  'flightPlanner.prevMonth': 'Previous month',
  'flightPlanner.nextMonth': 'Next month',
  'flightPlanner.search': 'Search flights on these dates',
  'flightPlanner.searchShort': 'Search',
  'flightPlanner.fromEmpty': 'Where from?',
  'flightPlanner.toEmpty': 'Where to?',
  'flightPlanner.airportSearchPrompt': 'Type to search',
  'flightPlanner.noAirports': 'No airports',
  'flightPlanner.airportsSearchFailed': "We couldn't search airports right now. Try again.",
  'flightPlanner.airportsSearchRetry': 'Try again',
  'flightPlanner.sameAirports': 'Origin and destination must be different.',
  'flightPlanner.airportRecent': 'Recent',
  'travelers.you': 'You',
  'travelers.searchPlaceholder': 'Search person',
  'travelers.searchError': 'Could not search people. Try again.',
  'travelers.loadMore': 'Load more',
  'travelers.policyLabel': 'Apply policy of',
  'travelers.done': 'Done',
  'travelers.travelerRole': 'traveler',
  'travelers.selectAria': 'Select travelers',
  'travelers.count_one': '{{count}} traveler',
  'travelers.count_other': '{{count}} travelers',
  'travelers.back': 'Back',
  'travelers.empty': 'No one found',
  'travelers.selectedGroup': 'On the trip',
  'travelers.maxReached': 'At most {{max}} travelers',
  'travelers.minReached': 'At least 1 traveler',
  'travelers.inSearch_one': '{{count}} traveler in the search',
  'travelers.inSearch_other': '{{count}} travelers in the search',
  'flightPlanner.tripType.oneWay': 'One-way',
  'flightPlanner.tripType.roundTrip': 'Round trip',
  'flightPlanner.tripType.multiLeg': 'Multi-city',
  'flightPlanner.searchTextOneWay':
    'Search flights from {{origin}} to {{destination}}, departing {{outbound}}.',
  'flightPlanner.searchTextRoundTrip':
    'Search flights from {{origin}} to {{destination}}, departing {{outbound}} and returning {{return}}.',
  'flightPlanner.searchTextMulti': 'Search flights: {{legs}}.',
  'flightPlanner.searchTextMultiLeg': '{{origin}} → {{destination}} ({{date}})',
  'flightPlanner.multiLeg.editLegs': 'Edit flights',
  'flightPlanner.multiLeg.addLeg': 'Add flight',
  'flightPlanner.multiLeg.removeLeg': 'Remove flight',
  'flightPlanner.multiLeg.reset': 'Reset',
  'flightPlanner.multiLeg.transfer': 'transfer',
  'flightPlanner.multiLeg.summaryUndefined': 'To be set',
  'flightPlanner.multiLeg.missing.origin': 'Flight {{n}} is missing its origin.',
  'flightPlanner.multiLeg.missing.destination': 'Flight {{n}} is missing its destination.',
  'flightPlanner.multiLeg.missing.date': 'Flight {{n}} is missing its date.',
  'flightPlanner.multiLeg.ready': 'Your {{count}} flights are ready.',
  'fareSelector.recommendedWhy.checked': 'Cheapest with a checked bag included',
  'fareSelector.recommendedWhy.cheapest': 'Cheapest',
  'fareCard.carryYes': 'Carry-on bag',
  'fareCard.carryNo': 'No carry-on bag',
  'fareCard.checkedYes': 'Checked bag',
  'fareCard.checkedNo': 'No checked bag',
  'fareCard.seatYes': 'Seat selection',
  'fareCard.seatNo': 'No seat selection',
  'fareCard.changeYes': 'Changes',
  'fareCard.changeNo': 'No changes',
  'fareCard.refundYes': 'Refund',
  'fareCard.refundNo': 'Non-refundable',
  'fareCard.fee': '{{benefit}} for a fee',
  'fareCard.unknown': '{{benefit}} · not provided',
  'fareCard.allowancePiecesKg_one': '{{benefit}} · {{count}} piece of {{kg}} kg',
  'fareCard.allowancePiecesKg_other': '{{benefit}} · {{count}} pieces of {{kg}} kg',
  'fareCard.allowancePieces_one': '{{benefit}} · {{count}} piece',
  'fareCard.allowancePieces_other': '{{benefit}} · {{count}} pieces',
  'fareCard.allowanceKg': '{{benefit}} · up to {{kg}} kg',
  'fareCard.supplierNotes': 'Rules as provided by the supplier',
  'fareCard.curatedRules': 'Supplier rules, completed by VOLL',
  'flightFares.title': 'Choose the fare for each flight',
  'flightFares.subtitle': '{{route}} · {{trip}}',
  'flightFares.subtitleWithTravelers': '{{route}} · {{trip}} · {{travelers}}',
  'flightFares.selectedFlights': 'Selected flights',
  'flightFares.airportSwitch.title': 'Heads up: different airports',
  'flightFares.airportSwitch.body':
    'The outbound arrives at {{arrival}}, but the return departs from {{departure}}.',
  'flightFares.airportSwitch.ack': 'I understand the return departs from a different airport',
  'flightOffers.airportSwitch.row':
    'Departs from {{departure}} — not the outbound arrival airport ({{arrival}})',
  'flightOffers.airportSwitch.rowExpected': 'Departs from {{departure}}',
  'flightExplorer.airportSwitchNotice':
    'The outbound arrives at {{arrival}}. Some returns in this list depart from a different airport — check before choosing.',
  'flightFares.changeFlight': 'Change flight',
  'flightFares.changeFlightAria': 'Change the {{leg}} flight',
  'flightFares.faresOf': 'Fares · {{leg}}',
  'flightFares.perTraveler': '/traveler',
  'flightFares.railPrevious': 'Scroll back in {{group}}',
  'flightFares.railNext': 'Scroll forward in {{group}}',
  'flightFares.checkout': 'Go to checkout',
  'flightFares.selected': 'Selected',
  'flightFares.back': 'Back',
  'flightFares.titleShort': 'Choose the fares',
  'flightFares.slideAria': 'Fare {{position}} of {{total}}',
  'flightFares.next': 'Next',
  'flightFares.summaryAria': 'Chosen fares',
  'rental.viewPolicies': 'View policies',
  'rental.results.title': 'Cars in {{place}}',
  'rental.results.total_one': 'We found {{count}} car',
  'rental.results.total_other': 'We found {{count}} cars',
  'rental.results.visible': '{{visible}} of {{total}} cars',
  'rental.results.resultsLabel': 'Cars found',
  'rental.results.filteredEmpty': 'No cars match these filters.',
  'rental.results.clearFilters': 'Clear filters',
  'rental.results.applyFilters': 'Apply filters',
  'rental.results.showMap': 'Map',
  'rental.results.loadMore': 'Show more cars',
  'rental.results.sort': 'Sort',
  'rental.results.chips.toolbar': 'Filters and sorting',
  'rental.results.chips.back': 'Back',
  'rental.results.chips.policy': 'Within policy',
  'rental.results.chips.dailyPrice': 'Daily rate',
  'rental.results.chips.priceUpTo': 'Up to {{price}}',
  'rental.results.chips.priceFrom': 'From {{price}}',
  'rental.results.chips.priceBetween': '{{min}} – {{max}}',
  'rental.results.order.recommended': 'Recommended',
  'rental.results.order.price': 'Price',
  'rental.results.order.distance': 'Distance',
  'rental.results.order.category': 'Category',
  'rental.results.order.company': 'Rental company',
  'rental.results.filters.collapse': 'Collapse filters',
  'rental.results.filters.close': 'Close filters',
  'rental.results.filters.accessories': 'Features',
  'rental.results.filters.automatic': 'Automatic',
  'rental.results.filters.airConditioning': 'Air conditioning',
  'rental.results.filters.dailyPrice': 'Daily rate up to',
  'rental.results.filters.minimumPrice': 'Minimum daily rate',
  'rental.results.filters.maximumPrice': 'Maximum daily rate',
  'rental.results.filters.company': 'Rental company',
  'rental.results.priceDetail_one': '{{count}} day',
  'rental.results.priceDetail_other': '{{count}} days · {{daily}}/day',
  'rental.results.dailyPrice': '{{daily}}/day',
  'rental.results.cheapest': 'Lowest price',
  'rental.results.agencyFallback': '{{company}} branch',
  'rental.results.pillAria': 'Car search context',
  'rental.results.close': 'Close cars',
  'rental.vehicle.orSimilar': 'or similar',
  'rental.drawer.title': 'All vehicles',
  'rental.filters': 'Filters',
  'rental.reset': 'Reset',
  'rental.category': 'Category',
  'rental.company': 'Rental company',
  'rental.policy': 'Policy',
  'rental.onlyInPolicy': 'Only within policy',
  'rental.loadingVehicles': 'Loading vehicles…',
  'rental.loadError': 'Could not load. Try again.',
  'rental.automatic': 'Automatic',
  'rental.automaticShort': 'Auto',
  'rental.manual': 'Manual',
  'rental.airConditioning': 'A/C',
  'rental.noAirConditioning': 'No A/C',
  'rental.inPolicy': 'Within policy',
  'rental.outPolicy': 'Out of policy',
  'rental.rent': 'Rent',
  'rentalOffers.pickText': 'I want the {{title}} from {{company}}, {{total}}.',
  'rentalOffers.title': 'Best cars',
  'rentalOffers.total_one': '{{count}} car',
  'rentalOffers.total_other': '{{count}} cars',
  'rentalOffers.viewPolicies': 'See your policies',
  'rentalOffers.seeAll': 'See all cars',
  'rentalOffers.slideAria': 'Car {{position}} of {{total}}',
  'rentalOffers.badge.recommended': 'Recommended',
  'rentalOffers.badge.cheapest': 'Lowest price',
  'rentalOffers.badge.closest': 'Closest',
  'rentalOffers.merit.recommended': 'Lowest total among the cars within your policy.',
  'rentalOffers.merit.recommendedOutside': 'Lowest total in the search.',
  'rentalOffers.merit.cheapest': '{{amount}} less than the recommended one.',
  'rentalOffers.merit.closest': 'Pickup {{distance}} from the address you searched.',
  'rental.policyDrawer.title': 'Rental policies',
  'rental.deckToggleAria': 'Expand or collapse vehicle list',
  'rental.selection.continue': 'Continue',
  'rental.selection.chooseAnother': 'Choose another vehicle',
  'productCheckout.hint.cpfNotOnFile':
    'No CPF on file. Enter your CPF here or use a passport/national document.',
  'productCheckout.hint.cpfNotOnFileNoAlternative': 'No CPF on file. Enter your CPF here.',
  'productCheckout.hint.cpfInvalid': 'Invalid CPF. Check the numbers.',
  'productCheckout.chooseAnotherReply': 'I want to see the other options.',
  'mobility.sectionRide': 'Ride',
  'mobility.sectionCarDriver': 'Car with driver',
  'mobility.inPolicy': 'In policy',
  'mobility.outPolicy': 'Out of policy',
  'mobility.choose': 'Choose',
  'mobility.chosen': 'Chosen',
  'mobility.chooseAria': 'Choose {{title}}, {{price}}, {{label}}',
  'mobility.estimate': 'estimated price',
  'mobility.roundTripEstimate': 'round trip · estimated price',
  'mobility.estimateNote': 'The final price depends on the route and traffic.',
  'mobility.distanceKmPerLeg': '{{km}} km each way',
  'mobility.overLimit': 'Out of policy · {{km}} km, your company limit is {{limit}} km',
  'mobility.overLimitNoCap': 'Out of policy · {{km}} km, over your company limit',
  'mobility.priceOnRequest': 'On request',
  'mobility.distanceKm': '{{km}} km',
  'mobility.hoursPerDay': '{{hours}}h/day',
  'mobility.day_one': '{{count}} day',
  'mobility.day_other': '{{count}} days',
  'mobility.bilingual': 'Bilingual driver',
  'mobility.overnight_one': '{{count}} overnight stay',
  'mobility.overnight_other': '{{count}} overnight stays',
  'mobility.stops_one': 'Stop: {{stops}}',
  'mobility.stops_other': 'Stops: {{stops}}',
  'mobility.freeKm': 'unlimited km',
  'mobility.excludesStops': 'Estimated price does not include the stops.',
  'mobility.franchise': 'Daily package: {{hours}}h with {{km}} km',
  'mobility.franchiseFreeKm': 'Daily package: {{hours}}h with unlimited km',
  'mobility.perDailySuffix': 'per day',
  'mobility.daysOfDaily_one': '{{count}} day at {{daily}}',
  'mobility.daysOfDaily_other': '{{count}} days at {{daily}}',
  'productCheckout.otherOptions': 'See other options',
  'productCheckout.frozenNotice': 'This quote is no longer active. Ask the agent to quote again.',
  'productCheckout.mobility.cooperative': 'Cooperative taxi',
  'productCheckout.mobility.outward': 'Outbound',
  'productCheckout.mobility.return': 'Return',
  'productCheckout.mobility.start': 'Start',
  'productCheckout.mobility.pickup': 'departure',
  'productCheckout.mobility.package': 'Package',
  'productCheckout.mobility.companions_one': '{{count}} companion',
  'productCheckout.mobility.companions_other': '{{count}} companions',
  'productCheckout.mobility.companionsLabel': 'Companions',
  'productCheckout.mobility.extras': 'Extras',
  'productCheckout.mobility.details': 'Details',
  'productCheckout.mobility.detailsTitle': 'Ride details',
  'productCheckout.mobility.pickupAt': 'Departure',
  'productCheckout.mobility.from': 'From',
  'productCheckout.mobility.to': 'To',
  'productCheckout.mobility.ride': 'Ride',
  'productCheckout.mobility.policy': 'Policy',
  'productCheckout.mobility.stops': 'Stops',
  'productCheckout.mobility.stop': 'Stop',
  'productCheckout.mobility.flight': 'Flight',
  'productCheckout.mobility.totalEstimated': 'Estimated total',
  'productCheckout.mobility.totalEstimatedRoundTrip': 'Estimated total · round trip',
  'productCheckout.mobility.estimateNote': 'The final price depends on the route and traffic.',
  'productCheckout.mobility.notes': 'Notes',
  'productCheckout.mobility.contact': 'Contact',
  'productCheckout.mobility.done': 'Ride scheduled',
  'productCheckout.mobility.pendingApproval':
    'Sent for approval. The ride will be scheduled once your manager approves it.',
  'productCheckout.mobility.pendingApprovalShort': 'Sent for approval',
  'productCheckout.mobility.outOfPolicyDistance': 'Out of policy: {{km}} km, limit {{limit}} km',
  'productCheckout.mobility.outOfPolicyKm': 'Out of policy: {{km}} km',
  'productCheckout.oneClick.suggested': 'suggested by AI',
  'mobilityRideDetails.title': 'Ride details',
  'mobilityRideDetails.phoneSelf': 'Your phone',
  'mobilityRideDetails.phoneOf': "{{name}}'s phone",
  'mobilityRideDetails.phoneHelpSelf': 'The driver uses this number for this ride only.',
  'mobilityRideDetails.phoneHelpOf':
    "The driver uses this number for this ride only. It is not saved to {{name}}'s profile.",
  'mobilityRideDetails.phoneError': 'Check the number, including the area code',
  'mobilityRideDetails.phonePlaceholder': '+55 (11) 9 0000-0000',
  'mobilityRideDetails.notes': 'Notes for the driver (optional)',
  'mobilityRideDetails.notesHelp': 'E.g. flight number, preferred driver, accessible vehicle.',
  'mobilityRideDetails.continue': 'Continue',
  'mobilityRideDetails.echoSelf': 'My phone: {{phone}}.',
  'mobilityRideDetails.echoOf': "{{name}}'s phone: {{phone}}.",
  'mobilityRideDetails.echoNotes': 'Note: {{notes}}',
  'productCheckout.loyalty.applied': 'Miles program applied',
  'productCheckout.loyalty.appliedProgram': 'Loyalty program applied',
  'productCheckout.loyalty.add': 'Add loyalty program',
  'productCheckout.loyalty.addAria': 'Add {{program}} to your profile',
  'productCheckout.loyalty.aria': 'Loyalty programs',
  'productCheckout.oneClick.reviewTitle': 'Review the details below',
  'productCheckout.oneClick.prefilledHint': 'Based on your profile. Check that it is right.',
  'productCheckout.oneClick.completeTitle': 'Complete your details',
  'productCheckout.oneClick.completeHint':
    'Missing from your profile. What you fill in here is saved to it.',
  'productCheckout.oneClick.completeTitleGuest': "Complete {{name}}'s details",
  'productCheckout.oneClick.completeHintGuest':
    "Missing from {{name}}'s profile. They apply to this booking only.",
  'productCheckout.oneClick.prefilledHintMulti':
    "Based on each traveler's profile. Check that it is right.",
  'productCheckout.oneClick.bookingTitle': 'For the request (applies to everyone)',
  'productCheckout.oneClick.required': 'Required',
  'productCheckout.oneClick.review': 'Review details',
  'productCheckout.oneClick.submit': 'Request with 1-Click',
  'productCheckout.oneClick.budgetInsufficient': 'Insufficient balance for this request.',
  'productCheckout.oneClick.submitting': 'Requesting',
  'productCheckout.oneClick.done': 'Requested successfully!',
  'productCheckout.oneClick.indeterminate':
    "Request under review. We'll let you know once it's confirmed.",
  'productCheckout.oneClick.failed': "The request didn't go through. See why in the reply below.",
  'productCheckout.oneClick.failedNewCheckout':
    "The request didn't go through. There's a new request right below.",
  'productCheckout.oneClick.unsent': "We couldn't send it. Try again.",
  'productCheckout.oneClick.loadError':
    "We couldn't load your details. Review and request on the full screen.",
  'productCheckout.oneClick.loading': 'Loading your details',
  'productCheckout.outOfPolicy': 'Out of policy',
  'productCheckout.reserveRental': 'Request car',
  'productCheckout.hotel.outOfPolicy': 'Above the policy cap',
  'productCheckout.reserveFlight': 'Request flight',
  'checkoutTravelDocs.title': 'Travel documents',
  'checkoutTravelDocs.source': 'From the IATA Timatic database',
  'checkoutTravelDocs.fallback': "We couldn't check the database right now",
  'checkoutTravelDocs.entry': 'Entry · {{country}}',
  'checkoutTravelDocs.transit': 'Connection · {{country}}',
  'checkoutTravelDocs.have': 'I have it',
  'checkoutTravelDocs.status.required': 'Required',
  'checkoutTravelDocs.status.attention': 'Check',
  'checkoutTravelDocs.status.ok': 'OK',
  'checkoutTravelDocs.detail': 'See details',
  'checkoutTravelDocs.footer':
    'The airline and immigration validate documents; confirm with the consulate.',
  'productCheckout.flight.outOfPolicyAria': 'Flight out of policy',
  'productCheckout.reserveHotel': 'Request hotel',
  'productCheckout.reserveBus': 'Request bus',
  'productCheckout.reserveMobility': 'Request ride',
  'productCheckout.rentalDate': 'Rental date',
  'productCheckout.rentalDays': 'Rental days',
  'productCheckout.pickupLocation': 'Pickup location',
  'productCheckout.rental.orSimilar': '{{model}} or similar',
  'productCheckout.rental.daysNote_one': 'for {{count}} day',
  'productCheckout.rental.daysNote_other': 'for {{count}} days',
  'productCheckout.hotelStay': 'Stay dates',
  'productCheckout.hotel.head': 'Accommodation',
  'productCheckout.hotel.distanceFrom': '{{km}} km from {{place}}',
  'productCheckout.hotel.details': 'Details',
  'productCheckout.hotel.nightsNote_one': 'for {{count}} night',
  'productCheckout.hotel.nightsNote_other': 'for {{count}} nights',
  'productCheckout.genderMale': 'Male',
  'productCheckout.genderFemale': 'Female',
  'productCheckout.documentMatchWarning': 'The details must match the travel document.',
  'productCheckout.motiveTitle': "What's the reason for the trip?",
  'productCheckout.mobility.motiveTitle': "What's the reason for the ride?",
  'productCheckout.mobility.blockerMotive': 'ride reason',
  'productCheckout.fieldJustificationOptional': 'Justification (optional)',
  'productCheckout.fieldProjectOptional': 'Project (optional)',
  'productCheckout.budget.sufficientIn': 'Sufficient limit in {{costCenter}} for this request.',
  'productCheckout.budget.warning': 'Heads up! The limit is almost exhausted.',
  'productCheckout.budget.sufficient': 'Sufficient limit for this request.',
  'rental.priceChange.continue': 'Continue anyway',
  'rental.priceChange.cancel': 'Cancel',
  'rentalPriceChange.replyContinue': 'I want to continue even with the new price.',
  'rentalPriceChange.replyContinueChallenge': 'I want to continue with the new price.',
  'rentalPriceChange.replyCancel': "I'd rather cancel.",
  'booking.summaryAria': 'Reservation summary',
  'booking.inPolicy': 'Within policy',
  'booking.continuePurchase': 'Continue purchase',
  'booking.yourReservation': 'Your reservation',
  'booking.fareClass': 'Class',
  'booking.priceMayChange': 'Prices may change between the trip request and approval.',
  'booking.submitting': 'Sending…',
  'booking.submit': 'Request trip',
  'approval.requestAria': 'Voucher approval',
  'approval.resultAria': 'Approval result',
  'approval.bulkResultAria': 'Bulk approval result',
  'approval.pendingAria': 'Pending approvals',
  'approval.voucher': 'Voucher',
  'approval.modal': 'Mode',
  'approval.traveler': 'Traveler',
  'approval.approver': 'Approver',
  'approval.justificationOptional': 'Justification (optional)',
  'approval.reasonDecision': 'Reason for the decision…',
  'approval.reasonDeny': 'Reason for denial…',
  'approval.sent': 'Decision sent',
  'approval.approve': 'Approve',
  'approval.deny': 'Deny',
  'approval.approved': 'Approved',
  'approval.denied': 'Denied',
  'approval.publishFailed': '{{label}} — failed to publish',
  'approval.failures': 'Failures',
  'approval.bulkSummary_one': '{{approved}} of {{total}} approved',
  'approval.bulkSummary_other': '{{approved}} of {{total}} approved',
  'approval.loose': 'Ungrouped',
  'approval.tripFallback': 'Trip',
  'approval.approveTrip': 'Approve trip',
  'approval.approveTraveler': 'Approve traveler',
  'approval.approveAll': 'Approve all',
  'approval.textApproveVoucher': 'Approve voucher.',
  'approval.textDenyVoucher': 'Deny voucher.',
  'approval.textApproveTrip': 'Approve trip {{name}}.',
  'approval.textApproveTraveler': 'Approve everything for {{name}}.',
  'approval.textApproveAll': 'Approve all pending items.',
  // ── booking checkout ──
  'bookingCheckout.select': 'Select',
  // ── flight detail ──
  'flightDetail.title': 'Flight detail',
  'flightDetail.drawerAria': 'Flight detail',
  'flightDetail.backAria': 'Back',
  'flightDetail.closeAria': 'Close',
  'flightDetail.loading': 'Loading flight detail…',
  'flightDetail.loadError': "Couldn't load the flight detail.",
  'flightDetail.retry': 'Try again',
  'flightDetail.select': 'Select flight',
  'flightDetail.stop': '{{duration}} layover',
  'flightDetail.aircraftChange': 'aircraft change',
  'flightDetail.flightDuration': '{{duration}} flight',
  'flightDetail.totalDuration': '{{duration}} total',
  'flightDetail.connection': '{{duration}} connection in {{airport}}',
  'flightDetail.continuesOn': 'continues on flight {{flight}}',
  'flightDetail.terminal': 'Terminal {{terminal}}',
  'flightDetail.onTimeStat': 'On time in {{percent}}% of the last {{count}} flights',
  'flightDetail.soldAs': 'Also sold as {{flights}}',
  'flightDetail.partnerDataNote':
    'Aircraft, terminal, punctuality and codeshares: partner data, subject to change.',
  'flightDetail.operatedBy': 'Operated by {{name}}',
  'flightDetail.dirOutbound': 'Outbound',
  'flightDetail.dirReturn': 'Return',
  // ── seat map (flight drawer) ──
  'flightSeatMap.sectionTitle': 'Seat map',
  'flightSeatMap.stripLabel': 'Seats',
  'flightSeatMap.stripOpenAria': 'View seat map for {{route}}',
  'flightSeatMap.stripRetryAria': 'Try loading the seat map for {{route}} again',
  'flightSeatMap.retry': 'Try again',
  'flightSeatMap.available_one': '1 seat available',
  'flightSeatMap.available_other': '{{count}} seats available',
  'flightSeatMap.paidFrom': 'paid from {{price}}',
  'flightSeatMap.freeFromRow': 'free from row {{row}}',
  'flightSeatMap.legendFree': 'Free',
  'flightSeatMap.legendPaid': 'Paid',
  'flightSeatMap.legendExtra': 'Extra legroom',
  'flightSeatMap.legendOccupied': 'Occupied',
  'flightSeatMap.legendUnavailable': 'Unavailable',
  'flightSeatMap.exitRow': 'Emergency exit',
  'flightSeatMap.rowAria': 'Row {{row}}',
  'flightSeatMap.loading': 'Loading seat map…',
  'flightSeatMap.error': "Couldn't load the seat map.",
  'flightSeatMap.unavailable': 'Seat map unavailable for this flight.',
  'flightSeatMap.supplierUnavailable': "The seat map isn't available to view before booking.",
  'flightSeatMap.errorFinal': 'Seat map unavailable right now.',
  'flightSeatMap.stripUnavailableAria': 'Seat map for {{route}} unavailable',
  'flightSeatMap.quoteExpired': 'The seat map is no longer available for this quote.',
  'flightSeatMap.disclaimer':
    'Reading, not choosing · seat price and selection happen at ticketing.',
  // ── rental checkout ──
  'productCheckout.closeAria': 'Close',
  'productCheckout.back': 'Back',
  'productCheckout.refundable': 'Refundable',
  'productCheckout.nonRefundable': 'Non-refundable',
  'productCheckout.drawerTitle': 'Review and request',
  'productCheckout.agentLine':
    'I got your checkout ready with what I already know. Check it and fill in what is missing.',
  'productCheckout.inheritedHint':
    'Inherited from your profile — I only confirm with you to be safe.',
  'productCheckout.inheritedHintOther':
    "Inherited from the traveler's profile — I only confirm with you to be safe.",
  'productCheckout.submitting': 'Requesting',
  'productCheckout.retry': 'Try again',
  'productCheckout.action.submitWithTotal': 'Request {{product}} · {{total}}',
  'productCheckout.price.approvalNote': 'Goes to approval. The price may change until ticketing.',
  'productCheckout.trip.sectionTitle': 'Your trip',
  'productCheckout.trip.title': 'Trip to {{city}}',
  'productCheckout.trip.alreadyBooked': 'Already booked',
  'productCheckout.trip.beingRequested': 'Being requested now',
  'productCheckout.bus.seats': 'Seats',
  'productCheckout.bus.seat_one': 'Seat {{seats}}',
  'productCheckout.bus.totalRoundTrip': 'round trip, per person',
  'productCheckout.bus.totalOneWay': 'one way, per person',
  'productCheckout.bus.seat_other': 'Seats {{seats}}',
  'productCheckout.bus.segment': 'Segment {{n}}',
  'productCheckout.trip.nights_one': '{{count}} night',
  'productCheckout.trip.nights_other': '{{count}} nights',
  'conciergeCheckout.title': 'Review and request from the VOLL team',
  'conciergeCheckout.chip.flight': 'Offline flight',
  'conciergeCheckout.chip.hotel': 'Offline hotel',
  'conciergeCheckout.chip.bus': 'Offline bus',
  'conciergeCheckout.chip.rental': 'Offline car',
  'conciergeCheckout.submit': 'Request from the VOLL team',
  'conciergeCheckout.back': 'Back',
  'conciergeCheckout.optional': 'optional',
  'conciergeCheckout.noPriceBadge': 'No price · to be quoted',
  'conciergeCheckout.profileLabel': 'Profile inherited from the search',
  'conciergeCheckout.profileHint':
    'Inherited from the search. To change it, go back to the results.',
  'conciergeCheckout.reasonTitle': 'Why ask the team',
  'conciergeCheckout.reasonLoading': 'Loading reasons…',
  'conciergeCheckout.detailsLabel': 'Note for the team',
  'conciergeCheckout.flight.cardTitle': 'Request to the VOLL team · flight',
  'conciergeCheckout.flight.asksTitle': 'What you need on this flight',
  'conciergeCheckout.flight.direct': 'Nonstop only',
  'conciergeCheckout.flight.luggage': 'With checked bag only',
  'conciergeCheckout.flight.periodFor': 'Time of day · {{route}} · {{date}}',
  'conciergeCheckout.flight.periodAny': 'Any',
  'conciergeCheckout.flight.detailsPlaceholder':
    'E.g.: meeting ends at 5pm in Alphaville; anything after 7pm works, GRU too.',
  'conciergeCheckout.period.morning': 'Morning',
  'conciergeCheckout.period.afternoon': 'Afternoon',
  'conciergeCheckout.period.night': 'Evening',
  'conciergeCheckout.period.dawn': 'Early morning',
  'conciergeCheckout.hotel.cardTitle': 'Request to the VOLL team · lodging',
  'conciergeCheckout.hotel.preferredHotel': 'Preferred hotel (optional)',
  'conciergeCheckout.hotel.preferredHotelPlaceholder':
    'Hotel name, if you already have one in mind',
  'conciergeCheckout.hotel.bedType': 'Bed type',
  'conciergeCheckout.hotel.bed.UNDEFINED': 'Any',
  'conciergeCheckout.hotel.bed.SINGLE': 'Single',
  'conciergeCheckout.hotel.bed.DOUBLE': 'Double',
  'conciergeCheckout.hotel.detailsPlaceholder':
    'E.g.: meeting on Faria Lima at 9am, breakfast included helps; the client suggested the Unique.',
  'conciergeCheckout.hotel.detailsHint': 'The team uses this text as the briefing for the quote.',
  'conciergeCheckout.panel.inProgress': 'In progress',
  'conciergeCheckout.panel.flightItem': '{{route}} · team request',
  'conciergeCheckout.panel.hotelItem': 'Hotel · team request',
  'conciergeCheckout.panel.toQuote': 'to be quoted',
  'conciergeCheckout.panel.requested': 'Requested',
  'conciergeCheckout.panel.heldOutbound': 'Held in the request · not issued now',
  'conciergeCheckout.panel.totalLabel': 'Trip total',
  'conciergeCheckout.panel.totalToQuote': 'No price until the quote',
  'conciergeCheckout.panel.totalWithHeld': '{{held}} + leg to be quoted',
  'conciergeCheckout.panel.flightNote':
    'Nothing is issued now. The team quotes the leg and you approve everything together when the quote reaches the trip conversation.',
  'conciergeCheckout.panel.hotelNote':
    'The hotel joins the itinerary without a price and gets one when the quote arrives. Policy and cap already travel with the request.',
  'conciergeCheckout.panel.busItem': '{{route}} · team request',
  'conciergeCheckout.panel.busNote':
    'Nothing is issued now. The team quotes the ticket and you approve it when the quote arrives in the trip conversation.',
  'conciergeCheckout.bus.cardTitle': 'Request to the VOLL team · bus',
  'conciergeCheckout.bus.asksTitle': 'How you want to travel',
  'conciergeCheckout.bus.choose': 'Select',
  'conciergeCheckout.bus.periodFor': 'Time of day · {{leg}} · {{date}}',
  'conciergeCheckout.bus.leg.outbound': 'Outbound',
  'conciergeCheckout.bus.leg.return': 'Return',
  'conciergeCheckout.bus.seatPosition': 'Position on the bus',
  'conciergeCheckout.bus.seatPosition.FRONT': 'Front',
  'conciergeCheckout.bus.seatPosition.MIDDLE': 'Middle',
  'conciergeCheckout.bus.seatPosition.BACK': 'Back',
  'conciergeCheckout.bus.rowPosition': 'Position in the row',
  'conciergeCheckout.bus.rowPosition.WINDOW_SEAT': 'Window',
  'conciergeCheckout.bus.rowPosition.AISLE_SEAT': 'Aisle',
  'conciergeCheckout.bus.seatClass': 'Class',
  'conciergeCheckout.bus.seatClass.ALL': 'Any',
  'conciergeCheckout.bus.seatClass.CONVENTIONAL': 'Conventional',
  'conciergeCheckout.bus.seatClass.EXECUTIVE': 'Executive',
  'conciergeCheckout.bus.seatClass.HALF_BED': 'Semi-sleeper',
  'conciergeCheckout.bus.seatClass.BED': 'Sleeper',
  'conciergeCheckout.bus.seatClass.BED_FULL': 'Full sleeper',
  'conciergeCheckout.bus.detailsPlaceholder':
    'E.g.: more than one leg, preferred times, available budget, travel insurance.',
  'conciergeCheckout.bus.detailsHint':
    'The team uses this text for the quote, and the trip approver reads it too.',
  'conciergeCheckout.rental.cardTitle': 'Request to the VOLL team · car',
  'conciergeCheckout.rental.category': 'Car category',
  'conciergeCheckout.rental.company': 'Rental company',
  'conciergeCheckout.rental.companyAny': 'Any',
  'conciergeCheckout.rental.optionsLoading': 'Loading categories and rental companies…',
  'conciergeCheckout.rental.optionsFailed':
    'We could not load the car categories. The request cannot be sent without them.',
  'conciergeCheckout.rental.retry': 'Try again',
  'conciergeCheckout.rental.detailsPlaceholder':
    'E.g.: child seat, second driver, pick-up right at arrivals.',
  'conciergeCheckout.rental.detailsHint':
    'The team uses this text as the quote brief: extras, additional driver, branch.',
  'conciergeCheckout.panel.rentalItem': 'Car · team request',
  'conciergeCheckout.panel.rentalNote':
    'The car joins the itinerary without a price and gets one when the quote arrives. The category policy already travels with the request.',
  'productCheckout.noun.flight': 'flight',
  'productCheckout.noun.hotel': 'hotel',
  'productCheckout.noun.bus': 'bus',
  'productCheckout.noun.rental': 'car',
  'productCheckout.noun.mobility': 'ride',
  'productCheckout.chipLabel.flight': 'Flights',
  'productCheckout.chipLabel.hotel': 'Hotel',
  'productCheckout.chipLabel.bus': 'Bus',
  'productCheckout.chipLabel.rental': 'Car',
  'productCheckout.chipLabel.mobility': 'Mobility',
  'productCheckout.loadingAria': 'Loading reservation',
  'productCheckout.loading': 'Loading reservation…',
  'productCheckout.errorMessage':
    "We couldn't load your details for this booking. Nothing was sent.",
  'productCheckout.fieldCompany': 'Company',
  'productCheckout.fieldCostCenter': 'Cost center',
  'productCheckout.fieldMotive': 'Purpose',
  'productCheckout.fieldProject': 'Project',
  'productCheckout.fieldJustification': 'Justification',
  'productCheckout.selectPlaceholder': 'Select',
  'productCheckout.motiveSearchPlaceholder': 'Search purpose',
  'productCheckout.motiveSearchEmpty': 'No purpose found',
  'productCheckout.companySearchEmpty': 'No company found',
  'productCheckout.projectSearchEmpty': 'No project found',
  'productCheckout.costCenterSearchPrompt': 'Type to search',
  'productCheckout.costCenterSearchLoading': 'Searching…',
  'productCheckout.costCenterSearchEmpty': 'No cost center found',
  'productCheckout.split.percent': 'Percentage',
  'productCheckout.split.add': 'Add cost center',
  'productCheckout.split.remove': 'Remove cost center',
  'productCheckout.split.row': 'Cost center {{position}}',
  'productCheckout.split.approvalFlow': 'What will the approval flow be?',
  'productCheckout.split.approvalFlowPhrase1':
    'The approval flow is determined by the split percentage entered for each cost center.',
  'productCheckout.split.approvalFlowPhrase2':
    'The approval flow of the cost center with the highest percentage is always used, unless a user-specific flow is configured.',
  'productCheckout.split.approvalFlowPhrase3':
    'When the highest percentages are identical, the approval flow used is the one of the first cost center entered with the highest percentage.',
  'productCheckout.split.zeroPercent': 'Each cost center needs more than 0%.',
  'productCheckout.split.sum': 'The cost centers must add up to 100%.',
  'productCheckout.split.repeated': 'The same cost center cannot appear twice in the split.',
  'rental.card.traveler': 'Traveler',
  'productCheckout.card.price': 'Trip cost',
  'rental.field.firstName': 'First name',
  'rental.field.lastName': 'Last name',
  'productCheckout.field.birthdate': 'Date of birth',
  'productCheckout.field.datePick': 'Choose',
  'productCheckout.field.dateConfirm': 'Confirm',
  'productCheckout.field.dateBack': 'Back',
  'productCheckout.field.datePrevMonth': 'Previous month',
  'productCheckout.field.dateNextMonth': 'Next month',
  'productCheckout.field.nationality': 'Nationality',
  'rental.field.gender': 'Gender',
  'productCheckout.field.document': 'Document',
  'productCheckout.field.cpf': 'CPF',
  'productCheckout.field.passport': 'Passport',
  'productCheckout.field.nationalDoc': 'National ID',
  'productCheckout.field.documentExpiry': 'Expiry date',
  'productCheckout.field.issuingCountry': 'Issuing country',
  'productCheckout.field.nationalityPlaceholder': 'Select country',
  'productCheckout.field.countrySearch': 'Search country',
  'productCheckout.field.countryEmpty': 'No country found',
  'productCheckout.field.countryUnavailable':
    'Country list unavailable right now. The saved value stays as it is.',
  'productCheckout.field.countryOutsideCatalog': 'saved in your profile',
  'productCheckout.field.countryRetry': 'Try again',
  'productCheckout.field.internalNotes': 'Note for the approver',
  'productCheckout.field.internalNotesPlaceholder':
    'Context that helps the approver decide without asking you.',
  'productCheckout.field.gender': 'Sex (as on travel document)',
  'productCheckout.field.genderNote':
    'Some airlines require it and may use it to add MR or MS/MRS to your boarding name.',
  'productCheckout.field.genderWhy': 'Why?',
  'productCheckout.field.genderFemale': 'Female',
  'productCheckout.field.genderMale': 'Male',
  'productCheckout.field.boardingName': 'First name',
  'productCheckout.field.boardingSurname': 'Last name',
  'productCheckout.field.phone': 'Phone',
  'rental.pickup.label': 'Pickup',
  'rental.dropoff.label': 'Drop-off',
  'rental.pickup.from': 'From',
  'rental.dropoff.until': 'Until',
  'productCheckout.price.dailies_one': '{{count}} day',
  'productCheckout.price.dailies_other': '{{count}} days',
  'productCheckout.price.eachDaily': '{{amount}} each',
  'productCheckout.price.perTraveler': 'Price per person',
  'productCheckout.price.tripTotal': 'Trip total',
  'productCheckout.price.total': 'Total',
  'productCheckout.price.note': 'Prices may vary until confirmation.',
  'productCheckout.action.edit': 'Edit',
  'productCheckout.action.done': 'Done',
  'productCheckout.action.submit': 'Request trip',
  'productCheckout.dock.submit': 'Request',
  'productCheckout.budget.insufficient': 'Insufficient limit',
  'productCheckout.budget.available': 'Available balance',
  'rental.budget.checkError': 'Could not verify budget',
  'rental.budget.overLimit': 'Over budget',
  'rental.budget.withinLimit': 'Within budget limit',
  'productCheckout.hint.outOfPolicy': 'Out of policy, justification required',
  'productCheckout.hint.outOfPolicyReasons': 'Out of policy ({{reasons}}). Justification required.',
  'productCheckout.reason.bookingWindow_one':
    'minimum advance of {{count}} day for {{scope}} trips',
  'productCheckout.reason.bookingWindow_other':
    'minimum advance of {{count}} days for {{scope}} trips',
  'productCheckout.blockers': 'To request, fill in: {{items}}',
  'productCheckout.blocker.birthdate': 'date of birth',
  'productCheckout.blocker.nationality': 'nationality',
  'productCheckout.blocker.gender': 'sex',
  'productCheckout.blocker.document': 'document',
  'productCheckout.blocker.embark': 'boarding name',
  'productCheckout.blocker.phone': "traveler's phone",
  'productCheckout.blocker.allocation': 'company and cost center',
  'productCheckout.blocker.project': 'project',
  'productCheckout.blocker.motive': 'trip reason',
  'productCheckout.blocker.justification': 'justification',
  'productCheckout.blocker.travelDocs': 'travel documents confirmation',
  'productCheckout.phonePending': 'Phone missing',
  'productCheckout.hint.noCorporateAccess':
    'This traveler has no corporate cost center — they cannot be booked.',
  'productCheckout.hint.noCostCenters':
    'No cost center is available for your profile at this company — contact your company manager.',
  'productCheckout.hint.allocationFromRequester':
    "The requester's cost split applies to this guest.",
  'productCheckout.hint.allocationFromGuest': "{{name}}'s cost split applies to this guest.",
  'rental.hint.documentMatch': 'Details must match the travel document exactly',
  'rental.hint.genderFemale': 'Female',
  'rental.hint.genderMale': 'Male',
  'rental.hint.cpfReadOnly': 'Filled automatically by the system',
  'productCheckout.hint.documentOnFile': 'On file: {{value}}',
  'productCheckout.hint.editInProfile':
    'To change this, contact the travel manager at your company.',
  'hotelPlanner.datesPlaceholder': 'Check-in and check-out',
  'hotelPlanner.policyHint': 'Your travel policy',
  'hotelPlanner.noPlaces': 'No destinations',
  'hotelPlanner.placeError': 'Could not search destinations. Try again.',
  'hotelPlanner.noResultsTitle': 'No hotels found',
  'hotelPlanner.noResults': 'Try other dates, another destination, or clear the filters.',
  'hotelPlanner.providerErrorTitle': 'Could not search hotels',
  'hotelPlanner.providerError':
    'The rates provider failed. Try again shortly, or adjust the destination and dates.',
  'hotelPlanner.timeoutErrorTitle': 'The search took too long',
  'hotelPlanner.timeoutError':
    'The provider did not answer in time. Try again — if it repeats, shorten the stay or change the destination.',
  'hotelPlanner.search': 'Search',
  'hotelPlanner.searching': 'Searching for hotels',
  'hotelPlanner.contextPending':
    'Complete the details below so we can find the best hotel options for you:',
  'hotelPlanner.contextSearching': "I'm searching for the best hotel options for your dates…",
  'hotelPlanner.contextReady': "All set! I've mapped the best hotel options for your dates:",
  'hotelPlanner.contextEmpty': 'No hotels matched these criteria. Adjust the search:',
  'hotelPlanner.searchText': 'Search hotels in {{place}} — {{dates}}.',
  'hotelPlanner.retry': 'Try again',
  'hotelPlanner.adjustSearch': 'Adjust search',
  'hotelPlanner.rooms': 'Rooms',
  'hotelPlanner.prevMonth': 'Previous month',
  'hotelPlanner.nextMonth': 'Next month',
  'hotelPlanner.roomN': 'Room {{n}}',
  'hotelPlanner.removeRoom': 'Remove room {{n}}',
  'hotelPlanner.remove': 'Remove',
  'hotelPlanner.addRoom': 'Add room',
  'hotelPlanner.chooseGuest': 'Choose guest',
  'hotelPlanner.addGuest': 'Companion',
  'hotelPlanner.addGuestAria': 'Add guest — room {{n}}',
  'hotelPlanner.backToRooms': 'Back to rooms',
  'hotelPlanner.roomGuestHeading': 'Room {{n}} · guest',
  'hotelPlanner.roomCompanionHeading': 'Room {{n}} · companion',
  'hotelPlanner.peopleList': 'People',
  'hotelPlanner.noPeople': 'No one found.',
  'hotelPlanner.roomNameNote': "Each room is booked under its first guest's name.",
  'hotelPlanner.guestInRoom': 'Already in room {{n}}',
  'hotelPlanner.holderBadge': 'Holder',
  'hotelPlanner.guestOptions': 'Options for {{name}}',
  'hotelPlanner.makeHolder': 'Make room holder',
  'hotelPlanner.swapGuest': 'Swap guest',
  'hotelPlanner.removeFromRoom': 'Remove from room',
  'hotelPlanner.guestRole': 'guest',
  'hotelPlanner.whereLabel': 'Where',
  'hotelPlanner.whenLabel': 'When',
  'hotelPlanner.whoLabel': 'Who',
  'hotelPlanner.destination': 'Destination',
  'hotelPlanner.destinationPlaceholder': 'City or landmark',
  'hotelPlanner.myLocation': 'My location',
  'hotelPlanner.myLocationError': 'We could not use your location',
  'hotelPlanner.recentPlaces': 'Recent',
  'hotelPlanner.favoritePlaces': 'Favorite places',
  'hotelPlanner.placeType.city': 'City',
  'hotelPlanner.placeType.neighborhood': 'Neighborhood',
  'hotelPlanner.placeType.address': 'Address',
  'hotelPlanner.placeType.poi': 'Point of interest',
  'hotelPlanner.placeType.lodging': 'Hotel',
  'hotelPlanner.placeType.airport': 'Airport',
  'hotelPlanner.checkInOut': 'Check-in/out',
  'hotelPlanner.datesTBD': 'TBD',
  'hotelPlanner.chooseStayDates': 'Choose check-in and check-out',
  'hotelPlanner.missingDatesAlert': 'Select the dates to see available rooms and prices',
  'hotelPlanner.stayBack': 'Back',
  'hotelPlanner.chooseCheckout': '{{date}} · choose check-out',
  'hotelPlanner.roomCount_one': '{{count}} room',
  'hotelPlanner.roomCount_other': '{{count}} rooms',
  'hotelPlanner.guestCount_one': '{{count}} guest',
  'hotelPlanner.guestCount_other': '{{count}} guests',
  'hotelPlanner.nights_one': '{{count}} night',
  'hotelPlanner.nights_other': '{{count}} nights',
  'hotelPlanner.maxStayNights': 'up to {{count}} nights',
  'hotelPlanner.confirmDates': 'Confirm {{range}}',
  'hotelPlanner.confirmDate': 'Confirm date',
  'hotelPlanner.confirmStayDates': 'Confirm dates',
  'hotelPlanner.confirmAndSearch': 'Confirm and search',
  'hotelPlanner.resetDates': 'Reset',
  'hotelPlanner.maxRooms': 'Maximum of {{n}} rooms',
  'hotelPlanner.maxGuests': 'Maximum of {{n}} guests',
  'hotel.resultsListAria': 'Hotels found',
  'hotel.results.previous': 'Previous hotels',
  'hotel.results.next': 'Next hotels',
  'hotel.recommended': 'VOLL pick',
  'hotel.badge.recommended': 'Recommended',
  'hotel.badge.cheapest': 'Lowest price',
  'hotel.badge.closest': 'Closest',
  'hotel.badge.cheapestOnCard': 'Lowest price on the card',
  'hotel.badge.closestOnCard': 'Closest on the card',
  'hotel.badge.stayedBefore': 'You stayed here',
  'hotel.badge.stayedBeforeCount': 'You stayed here · {{count}}×',
  'hotel.badge.favorite': 'Favorite',
  'hotel.badge.agentPick': 'Agent pick',
  'hotel.badge.agreedRate': 'Company agreement',
  'hotel.favorite.mark': 'Favorite hotel',
  'hotel.favorite.unmark': 'Remove favorite',
  'hotel.card.freeCancel': 'Free cancellation',
  'hotel.card.distance': '{{km}} km',
  'hotel.card.minutes': '{{count}} min',
  'hotel.card.byFoot': 'on foot',
  'hotel.card.byCar': 'by car',
  'hotel.card.perNight': '/night',
  'hotel.card.refundable': 'Refundable',
  'hotel.card.refundableFrom': 'Refundable from {{price}}',
  'hotel.photoPrev': 'Previous photo',
  'hotel.photoNext': 'Next photo',
  'hotel.inPolicy': 'Within policy',
  'hotel.outPolicy': 'Out of policy',
  'hotel.policyRule.bookingWindow': 'Minimum advance booking not met',
  'hotel.policyRule.bookingWindowWithLimit': 'Minimum advance booking of {{value}} days',
  'hotel.policyRule.bookingWindowInternational': 'Minimum international advance booking not met',
  'hotel.policyRule.bookingWindowInternationalWithLimit':
    'Minimum international advance booking of {{value}} days',
  'hotel.policyRule.cheapestPrice': "Not the hotel's cheapest rate",
  'hotel.policyRule.cheapestHotel': 'Not the cheapest hotel in the search',
  'hotel.policyRule.maxPrice': 'Above the nightly cap',
  'hotel.policyRule.maxPriceWithLimit': 'Above the {{value}} nightly cap',
  'hotel.policyRule.maxPriceInternational': 'Above the international nightly cap',
  'hotel.policyRule.maxPriceInternationalWithLimit':
    'Above the international nightly cap of {{value}}',
  'hotel.policyRule.maxPricePerCity': 'Above the city cap',
  'hotel.policyRule.maxPricePerCityWithLimit': 'Above the city cap ({{value}} per night)',
  'hotel.policyRule.maxPricePerCountry': 'Above the country cap',
  'hotel.policyRule.onlyRateWithBreakfast': 'Rate without breakfast (policy requires breakfast)',
  'hotel.policyRule.onlyRateRefundable': 'Non-refundable rate (policy requires refundable)',
  'hotelRooms.policyCeilingAbove': 'Above {{value}}',
  'hotelRooms.policyCeilingHint': "Nightly cap in your company's policy for this search.",
  'hotel.breakfast': 'Breakfast included',
  'hotel.priceUnavailable': 'Price to confirm',
  'hotel.soldOut': 'No availability for these dates',
  'hotel.total': 'Total',
  'hotel.perNight': 'Nightly {{price}}',
  'hotel.rating': 'rating {{rating}}',
  'hotel.category': '{{category}} stars',
  'hotel.night_one': '{{count}} night',
  'hotel.night_other': '{{count}} nights',
  'hotel.room': 'Room',
  'hotel.roomsTitle': 'Best rooms',
  'hotel.gallery.prev': 'Previous photo',
  'hotel.gallery.next': 'Next photo',
  'hotel.gallery.photo': 'Photo {{position}}',
  'hotel.map.aria': 'Hotel map',
  'hotel.map.pinAria': '{{title}}, {{price}}',
  'hotel.map.seeHotel': 'See hotel',
  'hotel.map.expand': 'Expand map',
  'hotel.map.zoomIn': 'Zoom in',
  'hotel.map.zoomOut': 'Zoom out',
  'hotel.map.nightsFor_one': '{{count}} night for',
  'hotel.map.nightsFor_other': '{{count}} nights for',
  'hotel.map.recenter': 'Reframe map',
  'hotel.map.previewClose': 'Close hotel preview',
  'hotel.results.chips.toolbar': 'Filters and sorting',
  'hotel.results.chips.priceUpTo': 'Up to {{price}}',
  'hotel.results.chips.priceFrom': 'From {{price}}',
  'hotel.results.chips.priceBetween': '{{min}} – {{max}}',
  'hotel.results.seeAll': 'See all hotels',
  'hotel.results.sort': 'Sort',
  'hotel.results.sort.recommended': 'Recommended',
  'hotel.results.sort.price': 'Price',
  'hotel.results.sort.distance': 'Distance',
  'hotel.results.sort.reviews': 'Reviews',
  'hotel.results.sort.stars': 'Rating',
  'hotel.results.title': 'Hotels',
  'hotel.results.pageTitle': 'Hotels in {{place}}',
  'hotel.results.pageTitleNear': 'Hotels near {{place}} · {{locality}}',
  'hotel.results.pageTitleMapArea': 'Hotels in the map area',
  'hotel.results.pageContext_one': '{{count}} night',
  'hotel.results.pageContext_other': '{{count}} nights',
  'hotel.results.close': 'Close hotels',
  'hotel.results.pillAria': 'Search context',
  'hotel.results.searchAgain': 'Search again',
  'hotel.results.cancelEdit': 'Cancel',
  'hotel.results.total_one': '{{count}} hotel',
  'hotel.results.total_other': '{{count}} hotels',
  'hotel.results.visibleOfTotal_one': '{{visible}} hotel of {{total}}',
  'hotel.results.visibleOfTotal_other': '{{visible}} hotels of {{total}}',
  'hotel.results.askPlaceholder': 'Closer, with breakfast…',
  'hotel.results.back': 'Back',
  'hotel.results.filters': 'Filters',
  'hotel.results.collapseFilters': 'Collapse filters',
  'hotel.results.closeFilters': 'Close filters',
  'hotel.results.applyFilters': 'Apply filters',
  'hotel.results.expandFilters': 'Expand filters',
  'hotel.results.showMap': 'Map',
  'hotel.results.share': 'Share this search',
  'searchShare.copyLink': 'Copy link',
  'searchShare.downloadPdf': 'Download PDF',
  'hotel.results.filteredEmpty': 'No hotels match these filters.',
  'hotel.results.clearFilters': 'Clear filters',
  'hotel.results.showList': 'List',
  'hotel.results.loadMore': 'Load more hotels',
  'hotel.results.offline.offerTitle': 'Need something that is not here?',
  'hotel.results.offline.offerBody':
    'The VOLL team searches partner hotels — online and offline — that match your profile and brings the quote back to the trip conversation.',
  'hotel.results.loadingMore': 'Loading more hotels…',
  'hotel.results.allLoaded': 'Every hotel loaded',
  'hotel.results.searchThisArea': 'Search this area',
  'hotel.results.mapArea': 'Map area',
  'hotel.results.mapAreaEmpty': 'Move the map and search this area again.',
  'hotel.results.searchingThisArea': 'Searching…',
  'hotel.results.loadingAll': 'Loading every hotel',
  'hotel.results.searching': 'Checking availability and rates',
  'hotel.results.stillProcessing': 'Hotels are still responding',
  'hotel.results.staleRetrying': 'Refreshing the search',
  'hotel.results.unavailableTitle': 'Could not load every hotel',
  'hotel.results.unavailable':
    'The hotels below are still available. Try again shortly to see the full list.',
  'hotel.results.staleTitle': 'This search expired',
  'hotel.results.stale':
    'The provider does not keep the full list for long. Search again to see every hotel.',
  'hotel.results.staleAction': 'Search again',
  'hotel.results.resetFilters': 'Reset',
  'hotel.results.policy': 'Policy',
  'hotel.results.withinPolicy': 'Within policy',
  'hotel.results.rate': 'Rate',
  'hotel.results.breakfast': 'Breakfast',
  'hotel.results.refundable': 'Refundable',
  'hotel.results.agreedRate': 'Agreed rate',
  'hotel.results.nightlyPrice': 'Nightly rate',
  'hotel.results.minimumPrice': 'Minimum nightly rate',
  'hotel.results.maximumPrice': 'Maximum nightly rate',
  'hotel.results.rating': 'Rating',
  'hotel.results.distance': 'Distance from the appointment',
  'hotel.results.distanceUpTo': 'up to {{distance}} km',
  'hotel.results.amenities': 'Amenities',
  'hotel.results.send': 'Send',
  'hotel.results.visibleHotels_one': '{{count}} hotel visible on the map',
  'hotel.results.visibleHotels_other': '{{count}} hotels visible on the map',
  'hotel.amenity.wifi': 'Wi-Fi',
  'hotel.amenity.breakfast': 'Breakfast',
  'hotel.amenity.parking': 'Parking',
  'hotel.amenity.pool': 'Pool',
  'hotel.amenity.gym': 'Gym',
  'hotel.amenity.restaurant': 'Restaurant',
  'hotel.amenity.laundry': 'Laundry',
  'hotel.board.roomOnly': 'Room only',
  'hotel.board.breakfast': 'Breakfast',
  'hotel.board.noBreakfast': 'No breakfast',
  'hotel.board.continentalBreakfast': 'Continental breakfast',
  'hotel.board.breakfastFor': 'Breakfast for {{count}}',
  'hotel.board.halfBoard': 'Half board',
  'hotel.board.fullBoard': 'Full board',
  'hotel.board.allInclusive': 'All inclusive',
  'hotel.board.freeWifi': 'Free Wi-Fi',
  'hotel.board.freeHighSpeedInternet': 'Free high-speed internet',
  'hotel.board.freeSelfParking': 'Free self parking',
  'hotel.board.freeValetParking': 'Free valet parking',
  'hotelRooms.headerStay': '{{checkin}} – {{checkout}} · {{nights}}',
  'hotelRooms.headerOccupancy': '{{rooms}}, {{guests}}',
  'hotelRooms.reviews_one': '{{count}} review',
  'hotelRooms.reviews_other': '{{count}} reviews',
  'hotelRooms.ratingLabel.high': 'Excellent',
  'hotelRooms.ratingLabel.mid': 'Good',
  'hotelRooms.ratingLabel.low': 'Fair',
  'hotelRooms.negotiated': 'Negotiated rate',
  'hotelRooms.negotiatedRoom': 'Room with negotiated rate',
  'hotelRooms.lastUnits_one': 'Last {{count}}',
  'hotelRooms.lastUnits_other': 'Last {{count}}',
  'hotelRooms.otherRates': 'Other rates',
  'hotelRooms.otherOptions': 'Other options ({{count}})',
  'hotelRooms.rateTypeBreakfastRefundable': 'Breakfast · refundable',
  'hotelRooms.rateTypeBreakfast': 'With breakfast',
  'hotelRooms.rateTypeRefundable': 'Refundable',
  'hotelRooms.rateTypeRoomOnly': 'No breakfast',
  'hotelRooms.rateTypeOther': 'Other rate types',
  'hotelRooms.showAllRates': 'Compare rates',
  'hotelRooms.seeMoreRates': 'View {{count}} more',
  'hotelRooms.seeLessRates': 'View less',
  'hotelRooms.ratesAria': 'Room rates',
  'hotelRooms.perNight': '/ night',
  'hotelRooms.nights_one': '{{count}} night',
  'hotelRooms.nights_other': '{{count}} nights',
  'hotelRooms.totalNights': '{{nights}} · {{total}}',
  'hotelRooms.select': 'Select',
  'hotelRooms.selected': 'Selected',
  'hotelRooms.selectedPrefix': 'Selected:',
  'hotelRooms.chipBreakfast': 'breakfast',
  'hotelRooms.chipNoBreakfast': 'no breakfast',
  'hotelRooms.chipRefundable': 'refund',
  'hotelRooms.chipNonRefundable': 'no refund',
  'hotelRooms.freeCancelUntil': 'Free cancellation until {{date}}',
  'hotelRooms.refundable': 'Refundable',
  'hotelRooms.moreFilters': 'More filters',
  'hotelRooms.previousFilters': 'Previous filters',
  'hotelRooms.outOfPolicy': "This rate is outside your company's travel policy.",
  'hotelRooms.viewDetails': 'View details',
  'hotelRooms.roomAmenities': 'What this room has',
  'hotelRooms.detailAmenities': 'Top amenities',
  'hotelRooms.detailOtherAmenities': 'Other amenities',
  'hotelRooms.deckToggleAria': 'Show all rooms',
  'hotel.selectText': 'I want to see the rooms at {{hotel}}.',
  'hotelRooms.selectText': 'I want the {{room}} ({{rate}}) at {{hotel}}.',
  'hotelRooms.selectTextNoRate': 'I want the {{room}} at {{hotel}}.',
  'roomTaxonomy.type.QUARTO': 'Room',
  'roomTaxonomy.type.SUITE': 'Suite',
  'roomTaxonomy.type.STUDIO': 'Studio',
  'roomTaxonomy.type.APARTAMENTO': 'Apartment',
  'roomTaxonomy.type.CHALE': 'Chalet',
  'roomTaxonomy.type.BUNGALOW': 'Bungalow',
  'roomTaxonomy.type.LOFT': 'Loft',
  'roomTaxonomy.type.FLAT': 'Flat',
  'roomTaxonomy.type.CABANA': 'Cabin',
  'roomTaxonomy.tier.ECONOMICO': 'Economy',
  'roomTaxonomy.tier.STANDARD': 'Standard',
  'roomTaxonomy.tier.CLASSIC': 'Classic',
  'roomTaxonomy.tier.JUNIOR': 'Junior',
  'roomTaxonomy.tier.CONFORT': 'Comfort',
  'roomTaxonomy.tier.SUPERIOR': 'Superior',
  'roomTaxonomy.tier.EXECUTIVO': 'Executive',
  'roomTaxonomy.tier.DELUXE': 'Deluxe',
  'roomTaxonomy.tier.LUXO': 'Luxury',
  'roomTaxonomy.tier.PRIME': 'Prime',
  'roomTaxonomy.tier.PREMIUM': 'Premium',
  'roomTaxonomy.tier.MASTER': 'Master',
  'roomTaxonomy.beds.SINGLE_one': '{{count}} single bed',
  'roomTaxonomy.beds.SINGLE_other': '{{count}} single beds',
  'roomTaxonomy.beds.DOUBLE_one': '{{count}} double bed',
  'roomTaxonomy.beds.DOUBLE_other': '{{count}} double beds',
  'roomTaxonomy.beds.SOFA_one': '{{count}} sofa bed',
  'roomTaxonomy.beds.SOFA_other': '{{count}} sofa beds',
  'roomTaxonomy.beds.UNKNOWN_one': '{{count}} bed',
  'roomTaxonomy.beds.UNKNOWN_other': '{{count}} beds',
  'roomTaxonomy.bedrooms_one': '{{count}} bedroom',
  'roomTaxonomy.bedrooms_other': '{{count}} bedrooms',
  'roomTaxonomy.capacity': 'up to {{count}} guests',
  'roomTaxonomy.qualifier.NO_SMOKING': 'non-smoking',
  'roomTaxonomy.qualifier.SMOKING': 'smoking',
  'roomTaxonomy.qualifier.NO_AC': 'no air conditioning',
  'roomTaxonomy.qualifier.AC': 'air conditioning',
  'roomTaxonomy.qualifier.SEA_VIEW': 'sea view',
  'roomTaxonomy.qualifier.GARDEN_VIEW': 'garden view',
  'roomTaxonomy.qualifier.CITY_VIEW': 'city view',
  'roomTaxonomy.qualifier.VIEW': 'with view',
  'roomTaxonomy.qualifier.BALCONY': 'with balcony',
  'roomTaxonomy.qualifier.PRIVATE': 'private',
  'roomTaxonomy.qualifier.ACCESSIBLE': 'accessible',
  'roomTaxonomy.qualifier.KITCHEN': 'with kitchen',
  'roomTaxonomy.qualifier.FAMILY': 'family',
  'hotelDetails.aria': 'Hotel details',
  'hotelDetails.close': 'Close',
  'hotelDetails.openInNewTab': 'Open in new tab',
  'hotelDetails.loading': 'Loading hotel details',
  'hotelDetails.errorTitle': 'We could not load the details',
  'hotelDetails.errorBody': 'Please try again in a moment.',
  'hotelDetails.retry': 'Try again',
  'hotelDetails.about': 'About the hotel',
  'hotelDetails.chooseRoom': 'Choose your room',
  'hotelDetails.reserveRoom': 'Book',
  'hotelDetails.summary': 'Summary',
  'hotelDetails.roomsTab': 'Rooms',
  'hotelDetails.roomsEmpty':
    'This hotel has no room available for the searched dates. Try other dates or another hotel.',
  'hotelDetails.sectionsNavigation': 'Hotel sections',
  'hotelDetails.previousTabs': 'Previous sections',
  'hotelDetails.nextTabs': 'More sections',
  'hotelDetails.aboutShort': 'About',
  'hotelDetails.location': 'Location',
  'hotelDetails.cancellation': 'Cancellation policy',
  'hotelDetails.cancellationFreeUntil': 'Free cancellation until {{date}}',
  'hotelDetails.cancellationCharged': 'Cancellation carries a fee',
  'hotelDetails.cancellationFreeEnded': 'Free cancellation ended on {{date}}',
  'hotelDetails.cancellationForRate': 'Terms for the {{rate}} rate',
  'hotelDetails.cancellationFrom': 'From {{date}}',
  'hotelDetails.cancellationUntil': 'Until {{date}}',
  'hotelDetails.cancellationBetween': 'From {{from}} to {{until}}',
  'hotelDetails.cancellationPenalty': '{{amount}} fee',
  'hotelDetails.cancellationFree': 'No fee',
  'hotelDetails.cancellationVaries': 'Other rates may have different terms.',
  'hotelDetails.fromPlace': 'from {{place}}',
  'hotelDetails.readMore': 'Read more',
  'hotelDetails.readLess': 'Read less',
  'hotelDetails.galleryAria': 'Hotel photos',
  'hotelDetails.thumbnailsNav': 'Photo thumbnails',
  'hotelDetails.allPhotos': 'View all photos',
  'hotelDetails.photoCounter': '{{position}} / {{total}}',
  'hotelDetails.goToPhoto': 'Go to photo {{position}}',
  'hotelDetails.amenities': 'What this hotel offers',
  'hotelDetails.allAmenities': 'View all amenities',
  'hotelDetails.fewerAmenities': 'Fewer amenities',
  'hotelDetails.amenitiesTitle': 'Amenities offered',
  'hotelDetails.reviews': 'Reviews',
  'hotelDetails.allReviews': 'Read all reviews',
  'hotelDetails.reviewBy': '{{author}} · {{when}}',
  'hotelDetails.noFilteredRooms': 'No rooms match these filters',
  'hotelDetails.noFilteredRoomsHint': 'Drop a filter to see the options again.',
  'hotelDetails.clearFilters': 'Clear filters',
  'hotelDetails.allRoomsMeetFilter': 'Every room in this list meets “{{filter}}”.',
  'hotelReservation.checkin': 'Check-in',
  'hotelReservation.checkout': 'Check-out',
  'hotelRateWarning.title': 'The conditions for this rate have changed',
  'hotelRateWarning.continue': 'Continue anyway',
  'hotelRateWarning.chooseAnother': 'Choose another rate',
  'hotelRateWarning.continueText': 'I want to continue with the updated conditions.',
  'hotelRateWarning.chooseAnotherText': 'I want to choose another rate at this hotel.',
  'hotelRateWarning.reason.breakfast_changed': 'Breakfast changed on this rate',
  'hotelRateWarning.reason.refundable_changed': 'The refund conditions of this rate changed',
  'hotelRateWarning.reason.non_refundable':
    'This rate no longer has free cancellation: it is now non-refundable',
  'hotelRateWarning.reason.free_cancellation_ended':
    'This rate no longer has free cancellation: cancelling now incurs a fee',
  'hotelRateWarning.reason.free_cancellation_until': 'Free cancellation until {{date}}',
  'hotelRateWarning.policyTurnedOut': 'This rate is now outside the travel policy',
  'voucherCancel.aria': 'Voucher cancellation confirmation',
  'voucherCancel.leadPostEmission':
    'This voucher is **issued**: the cancellation becomes a **request** (manual review) and the voucher is marked **unused**.',
  'voucherCancel.k.voucher': 'Voucher',
  'voucherCancel.k.modal': 'Type',
  'voucherCancel.k.route': 'Route',
  'voucherCancel.k.place': 'Place',
  'voucherCancel.k.status': 'Status',
  'voucherCancel.k.reason': 'Reason',
  'voucherCancel.k.when': 'Time',
  'voucherCancel.ride.title': 'Cancel this ride?',
  'voucherCancel.ride.keep': 'Keep ride',
  'voucherCancel.ride.sent': 'Cancellation requested',
  'voucherCancel.confirm': 'Confirm cancellation',
  'voucherCancel.sent': 'Confirmation sent',
  'voucherCancel.keep': 'Keep voucher',
  'voucherCancel.replyKeep': "I don't want to cancel.",
  'voucherCancel.replyConfirm': 'Confirm cancellation.',
  'voucherCancelBulk.aria': 'Bulk voucher cancellation',
  'voucherCancelBulk.lead': 'Select the vouchers you want to cancel and tap **Cancel selected**.',
  'voucherCancelBulk.note':
    '**Issued** vouchers become a cancellation **request** (manual review) and are marked **unused**.',
  'voucherCancelBulk.issued': 'Issued',
  'voucherCancelBulk.submit': 'Cancel selected',
  'voucherCancelBulk.submitCount': 'Cancel selected ({{count}})',
  'voucherCancelBulk.sent': 'Cancellation sent',
  'voucherCancelBulk.keepAll': 'Keep all',
  'voucherCancelBulk.replyKeepAll': "I don't want to cancel any.",
  'voucherCancelBulk.replySubmit': 'Cancel the selected vouchers.',
  'voucher.meta.heading': 'Voucher',
  'tripDates.oneWay': '{{month}} {{d}}',
  'tripDates.sameMonth': '{{month}} {{d1}}–{{d2}}',
  'tripDates.crossMonth': '{{month1}} {{d1}} – {{month2}} {{d2}}',
  'tripDates.crossYear': '{{month1}} {{d1}}, {{y1}} – {{month2}} {{d2}}, {{y2}}',
};

const es: Record<GenUiKey, string> = {
  'common.close': 'Cerrar',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.copyFailed': 'No se pudo copiar',
  'common.total': 'Total',
  'common.perPerson': 'Por persona',
  'common.direct': 'Directo',
  'common.notInformed': 'No informado',
  'common.toConfirm': 'Por confirmar',
  'common.toDefine': 'Por definir',
  'common.loadingMore': 'Cargando más…',
  'common.tryAgain': 'Intentar de nuevo',
  'resultsOffline.offerAction': 'Pedir al equipo VOLL',
  'consultPlaces.aria': 'Lugares recomendados en la consulta',
  'consultPlaces.title': 'Lugares para conocer',
  'consultPlaces.place': 'Lugar',
  'consultPlaces.rating': 'Calificación {{rating}} de 5',
  'consultPlaces.open': 'Abierto ahora',
  'consultPlaces.closed': 'Cerrado ahora',
  'consultPlaces.details': 'Ver detalles — {{place}}',
  'consultPlaces.loadingPlace': 'Cargando lugar…',
  'consultPlaces.placeErrorAt': 'No se pudo cargar el lugar {{position}} de la lista.',
  'consultPlaces.loadingMap': 'Cargando mapa…',
  'consultPlaces.mapAria': 'Mapa de lugares recomendados',
  'consultPlaces.mapUnavailable': 'Mapa no disponible en esta sesión',
  'consultPlaces.mapError': 'No se pudo cargar el mapa. Los lugares siguen disponibles abajo.',
  'consultPlaces.retryMap': 'Intentar de nuevo',
  'consultPlaces.list': 'Lista de lugares',
  'consultPlaces.detailsShort': 'Ver detalles',
  'consultPlaces.loadingDetails': 'Cargando detalles…',
  'consultPlaces.detailsError': 'No se pudieron cargar los detalles de este lugar.',
  'consultPlaces.photos': 'Fotos del lugar',
  'consultPlaces.address': 'Dirección',
  'consultPlaces.phone': 'Teléfono',
  'consultPlaces.website': 'Sitio web',
  'consultPlaces.openWebsite': 'Abrir sitio web',
  'consultPlaces.hours': 'Horarios',
  'consultPlaces.reviews': 'Opiniones',
  'consultPlaces.sourceGoogle': 'Ver opinión en Google Maps',
  'consultPlaces.sourcePlaceGoogle': 'Información de Google Maps',
  'consultPlaces.expandedMap': 'Mapa ampliado',
  'consultPlaces.referencePlace': 'Lugar de referencia',
  'consultPlaces.expandMap': 'Ampliar mapa',
  'consultPlaces.openGoogleMaps': 'Abrir en Google Maps',
  'nextServices.hotel': 'Reservar hotel',
  'nextServices.flight': 'Buscar vuelos',
  'nextServices.seat': 'Reservar asiento',
  'nextServices.car': 'Alquilar un auto',
  'nextServices.train': 'Buscar trenes',
  'nextServices.bus': 'Buscar autobuses',
  'nextServices.insurance': 'Agregar seguro de viaje',
  'nextServices.transferToAirport': 'Auto al aeropuerto',
  'nextServices.transferAirportHotel': 'Auto del aeropuerto al hotel',
  'nextServices.transferToHotel': 'Auto al hotel',
  'nextServices.recommended': 'Recomendado',
  'nextServices.note': 'Puedes agregar el resto después, directamente en la página de tu viaje.',
  'flight.nextServices.ask.hotelRange':
    'Quiero reservar un hotel en {{city}} del {{from}} al {{to}}.',
  'flight.nextServices.ask.hotelOpen':
    'Quiero reservar un hotel en {{city}} a partir del {{from}}.',
  'flight.nextServices.ask.hotelBare': 'Quiero reservar un hotel en {{city}}.',
  'flight.nextServices.ask.seat': 'Quiero elegir mis asientos del vuelo que acabo de reservar.',
  'flight.nextServices.ask.flight': 'Quiero buscar otro vuelo.',
  'flight.nextServices.ask.carRange': 'Quiero alquilar un auto en {{city}} del {{from}} al {{to}}.',
  'flight.nextServices.ask.carOpen': 'Quiero alquilar un auto en {{city}} a partir del {{from}}.',
  'flight.nextServices.ask.carBare': 'Quiero alquilar un auto en {{city}}.',
  'flight.nextServices.ask.train': 'Quiero buscar billetes de tren a {{city}}.',
  'flight.nextServices.ask.bus': 'Quiero buscar billetes de autobús a {{city}}.',
  'flight.nextServices.ask.insurance': 'Quiero agregar un seguro de viaje a mi viaje.',
  'flight.nextServices.ask.transferToAirport': 'Quiero programar un auto al aeropuerto.',
  'flight.nextServices.ask.transferAirportHotel':
    'Quiero programar un auto del aeropuerto al hotel.',
  'flight.nextServices.ask.transferToHotel': 'Quiero programar un auto al hotel.',
  'nextServices.ask.hotelRange':
    'Quiero reservar un hotel cerca de {{place}} del {{from}} al {{to}}.',
  'nextServices.ask.hotelOpen':
    'Quiero reservar un hotel cerca de {{place}} a partir del {{from}}.',
  'nextServices.ask.hotelBare': 'Quiero reservar un hotel cerca de {{place}}.',
  'nextServices.ask.hotelTrip': 'Quiero reservar un hotel para este viaje.',
  'nextServices.ask.carRange': 'Quiero alquilar un auto cerca de {{place}} del {{from}} al {{to}}.',
  'nextServices.ask.carOpen': 'Quiero alquilar un auto cerca de {{place}} a partir del {{from}}.',
  'nextServices.ask.carBare': 'Quiero alquilar un auto cerca de {{place}}.',
  'nextServices.ask.carTrip': 'Quiero alquilar un auto para este viaje.',
  'nextServices.ask.insurance': 'Quiero agregar un seguro de viaje a mi viaje.',
  'nextServices.ask.flight': 'Quiero buscar un vuelo para este viaje.',
  'nextServices.ask.seat': 'Quiero elegir los asientos de mi vuelo.',
  'nextServices.ask.train': 'Quiero buscar pasajes de tren para este viaje.',
  'nextServices.ask.bus': 'Quiero buscar pasajes de autobús para este viaje.',
  'nextServices.ask.transferToAirport': 'Quiero agendar un auto hasta el aeropuerto.',
  'nextServices.ask.transferAirportHotel': 'Quiero agendar un auto del aeropuerto al hotel.',
  'nextServices.ask.transferToHotel': 'Quiero agendar un auto hasta el hotel.',
  'hotel.nextServices.ask.carRange':
    'Quiero alquilar un auto cerca del hotel {{hotel}}, del {{from}} al {{to}}.',
  'hotel.nextServices.ask.carOpen':
    'Quiero alquilar un auto cerca del hotel {{hotel}} a partir del {{from}}.',
  'hotel.nextServices.ask.carBare': 'Quiero alquilar un auto cerca del hotel {{hotel}}.',
  'hotel.nextServices.ask.carTrip': 'Quiero alquilar un auto para este viaje.',
  'hotel.nextServices.ask.train': 'Quiero buscar billetes de tren para llegar al hotel {{hotel}}.',
  'hotel.nextServices.ask.trainTrip': 'Quiero buscar billetes de tren para este viaje.',
  'hotel.nextServices.ask.bus': 'Quiero buscar billetes de autobús para llegar al hotel {{hotel}}.',
  'hotel.nextServices.ask.busTrip': 'Quiero buscar billetes de autobús para este viaje.',
  'hotel.nextServices.ask.insurance': 'Quiero agregar un seguro de viaje a mi viaje.',
  'hotel.nextServices.ask.seat': 'Quiero elegir los asientos de mi vuelo.',
  'hotel.nextServices.ask.flight': 'Quiero buscar un vuelo para este viaje.',
  'hotel.nextServices.ask.hotel': 'Quiero reservar otro hotel para este viaje.',
  'hotel.nextServices.ask.transferToAirport': 'Quiero programar un auto al aeropuerto.',
  'hotel.nextServices.ask.transferAirportHotel':
    'Quiero programar un auto del aeropuerto al hotel.',
  'hotel.nextServices.ask.transferToHotel': 'Quiero programar un auto al hotel.',
  'guidedFlows.label': '¿O prefieres elegir el servicio tú mismo?',
  'guidedFlows.label.onBehalf': '¿O prefieres elegir el servicio para {{traveler}}?',
  'guidedFlows.flow.flight': 'Vuelos',
  'guidedFlows.flow.hotel': 'Hoteles',
  'guidedFlows.flow.rental': 'Autos',
  'guidedFlows.flow.bus': 'Autobús',
  'guidedFlows.reply.flight': 'Quiero reservar un vuelo.',
  'guidedFlows.reply.hotel': 'Quiero reservar un hotel.',
  'guidedFlows.reply.rental': 'Quiero alquilar un auto.',
  'guidedFlows.reply.bus': 'Quiero un boleto de autobús.',
  'guidedFlows.reply.flight.onBehalf': 'Quiero reservar un vuelo para {{traveler}}.',
  'guidedFlows.reply.hotel.onBehalf': 'Quiero reservar un hotel para {{traveler}}.',
  'guidedFlows.reply.rental.onBehalf': 'Quiero alquilar un auto para {{traveler}}.',
  'guidedFlows.reply.bus.onBehalf': 'Quiero un boleto de autobús para {{traveler}}.',
  'flightOffers.title': 'Mejores vuelos',
  'flightOffers.outbound': 'Ida',
  'flightOffers.return': 'Vuelta',
  'flightOffers.itinerary_one': '{{count}} itinerario',
  'flightOffers.itinerary_other': '{{count}} itinerarios',
  'flightOffers.seeAll': 'Ver todos los vuelos',
  'flightOffers.quoteSpecific': 'Quiero cotizar el vuelo {{carrier}} {{flightNumber}}{{where}}.',
  'flightOffers.quoteGeneric': 'Quiero cotizar esta opción.',
  'flightOffers.viewFares': 'Ver tarifas',
  'flightOffers.closeFares': 'Cerrar tarifas',
  'flightOffers.faresBundled': 'Tarifas · ida y vuelta',
  'flightOffers.bothLegs': 'Ida y vuelta',
  'flightOffers.asymmetricBaggage':
    'Solo facturas en un tramo — {{article}} {{leg}} no lleva bodega.',
  'flightOffers.asymmetricBaggageFee':
    'Equipaje en bodega gratis solo en un tramo — {{article}} {{leg}} cobra por despachar.',
  'flightOffers.legArticleF': 'la',
  'flight.badge.best': 'Recomendado',
  'flight.badge.cheap': 'Menor precio',
  'flight.badge.fast': 'Menos tiempo',
  'flightOffers.baseOneWay': 'Solo ida por',
  'flightQuote.oneWay': 'solo ida',
  'flightQuote.roundTrip': 'ida y vuelta',
  'flightOffers.baseRoundTrip': 'Ida y vuelta por',
  'flightOffers.recommended': 'Recomendada',
  'flightOffers.loadingFares': 'Cargando tarifas…',
  'flightOffers.faresError': 'No se pudieron cargar las tarifas.',
  'flightOffers.faresStale': 'Esta opción expiró. Haz la búsqueda de nuevo.',
  'flightOffers.faresLegUnavailableOutbound':
    'El vuelo de ida elegido no tiene tarifas disponibles. Elige otro vuelo.',
  'flightOffers.faresLegUnavailableReturn':
    'El vuelo de vuelta elegido no tiene tarifas disponibles. Elige otro vuelo.',
  'flightOffers.searchAgain': 'Buscar de nuevo',
  'flightOffers.searchingAgain': 'Buscando de nuevo…',
  'flightOffers.searchAgainReply': '¿Puedes buscar de nuevo? Estas opciones expiraron.',
  'flightOffers.seats': 'Asientos',
  'flightOffers.legOutbound': 'Ida',
  'flightOffers.legReturn': 'Vuelta',
  'flightOffers.legSingle': 'Vuelo',
  'flightOffers.fromPrice': 'Desde',
  'flightOffers.slideAria': 'Oferta {{position}} de {{total}}',
  'flightOffers.pickedFares': 'Elegí la opción {{position}} ({{carrier}}) con la tarifa {{fares}}.',
  'flightOffers.chosen': 'Elegido',
  'flightMultiOffers.title': 'Elige el vuelo de cada tramo',
  'flightMultiOffers.leg': 'Tramo {{n}}',
  'flightMultiOffers.bestOptions': '{{count}} mejores opciones',
  'flightMultiOffers.chosenCount': '{{chosen}} de {{total}} elegidos',
  'flightMultiOffers.partialTotal': 'Total parcial',
  'flightMultiOffers.choosingNow': 'Eligiendo ahora…',
  'flightMultiOffers.waiting': 'Esperando…',
  'flightMultiOffers.waitingOutbound': 'Esperando tu ida…',
  'flightMultiOffers.stepDone': 'Tramo {{n}} completado',
  'flightMultiOffers.acceptCheapestFares_one': 'Aceptar la tarifa más barata (1)',
  'flightMultiOffers.acceptCheapestFares_other': 'Aceptar las tarifas más baratas ({{count}})',
  'flightMultiOffers.acceptCheapestFaresShort_one': 'Tarifa más barata (1)',
  'flightMultiOffers.acceptCheapestFaresShort_other': 'Tarifas más baratas ({{count}})',
  'flightMultiOffers.undo': 'Deshacer',
  'flightMultiOffers.confirmPick': 'Confirmar',
  'flightMultiOffers.pickedAll':
    'Elegí los vuelos de los {{count}} tramos. Total {{total}}. Quiero seguir al checkout.',
  'busOffers.outbound': 'Ida',
  'busOffers.return': 'Vuelta',
  'busOffers.connection_one': '{{count}} conexión',
  'busOffers.connection_other': '{{count}} conexiones',
  'busOffers.pickText': 'Quiero el pasaje de la opción {{position}} ({{carrier}} {{from}}→{{to}}).',
  'busResults.title': '{{leg}} — {{place}} · {{date}}',
  'busResults.legOutbound': 'Ida',
  'busResults.legReturn': 'Vuelta',
  'busResults.headline': '{{leg}} · {{date}}',
  'busResults.share': 'Compartir la búsqueda',
  'busResults.chosen_one': '{{chosen}} de {{count}} elegido',
  'busResults.chosen_other': '{{chosen}} de {{count}} elegidos',
  'busResults.total_one': '{{count}} autobús',
  'busResults.total_other': '{{count}} autobuses',
  'busResults.visible': '{{visible}} de {{total}} autobuses',
  'busResults.resultsLabel': 'Autobuses encontrados',
  'busResults.applyFilters': 'Aplicar filtros',
  'busResults.loadMore': 'Ver más autobuses',
  'busResults.dates': 'Cambiar la fecha',
  'busResults.filteredEmpty': 'Ningún autobús con estos filtros.',
  'busResults.clearFilters': 'Limpiar filtros',
  'busResults.sort': 'Ordenar',
  'busResults.chips.toolbar': 'Filtros y orden',
  'busResults.chips.back': 'Volver',
  'busResults.chips.policy': 'Dentro de la política',
  'busResults.order.departure': 'Hora de salida',
  'busResults.order.arrival': 'Hora de llegada',
  'busResults.order.price': 'Menor precio',
  'busResults.order.duration': 'Menor duración',
  'busResults.card.preferredCarrier': 'Tu empresa',
  'busResults.card.preferredSeatClass': 'Tu clase',
  'busResults.card.preferredDeparture': 'Tu horario',
  'busResults.card.select': 'Seleccionar',
  'busResults.card.perPerson': '/persona',
  'busResults.card.details': 'Detalles',
  'busResults.card.direct': 'Directo',
  'busResults.card.withConnection': 'Con conexión',
  'busResults.card.withConnectionAt': 'Con conexión en {{stop}}',
  'busResults.card.connectionStop': 'Conexión en {{stop}}',
  'busResults.card.connectionTimes': 'Llega a las {{arrival}} y sale a las {{departure}}',
  'busResults.card.connectionWait': 'Espera de {{duration}} en la conexión',
  'busResults.card.seatsLeft_one': '{{seatClass}} · {{count}} asiento libre',
  'busResults.card.seatsLeft_other': '{{seatClass}} · {{count}} asientos libres',
  'busResults.outOfPolicy': 'Fuera de la política',
  'busResults.legs.label': 'Tramos del viaje',
  'busResults.legs.outboundDone': 'Ida elegida',
  'busResults.legs.returnDone': 'Vuelta elegida',
  'busResults.legs.choosing': 'Eligiendo ahora…',
  'busResults.legs.pending': 'Esperando tu ida…',
  'busResults.dayStrip.label': 'Buscar en otro día',
  'busOffers.pickDayText': 'Quiero ver las salidas del {{day}}',
  'busResults.legs.seat_one': 'asiento {{seats}}',
  'busResults.legs.seat_other': 'asientos {{seats}}',
  'busResults.filters.filters': 'Filtros',
  'busResults.filters.collapse': 'Contraer los filtros',
  'busResults.filters.close': 'Cerrar los filtros',
  'busResults.filters.reset': 'Restablecer',
  'busResults.filters.policy': 'Política',
  'busResults.filters.withinPolicy': 'Solo dentro de la política',
  'busResults.filters.seatClass': 'Clase de asiento',
  'busResults.filters.time': 'Horario',
  'busResults.filters.departure': 'Salida',
  'busResults.filters.arrival': 'Llegada',
  'busResults.filters.price': 'Precio hasta',
  'busResults.filters.minimumPrice': 'Precio mínimo',
  'busResults.filters.maximumPrice': 'Precio máximo',
  'busResults.filters.company': 'Empresa',
  'busResults.shortlist': 'Salidas elegidas para ti',
  'busResults.seeAll': 'Ver las {{count}} salidas',
  'busResults.close': 'Cerrar la lista de autobuses',
  'busResults.seatsTitle': 'Elige tu asiento',
  'busResults.seatsClose': 'Cerrar la elección de asiento',
  'bus.nextServices.note': 'Puedes agregar el resto después, directo en la página de tu viaje.',
  'bus.nextServices.ask.hotelRange': 'Quiero reservar hotel en {{city}} del {{from}} al {{to}}.',
  'bus.nextServices.ask.hotelOpen': 'Quiero reservar hotel en {{city}} desde el {{from}}.',
  'bus.nextServices.ask.hotelTrip': 'Quiero reservar hotel para este viaje.',
  'bus.nextServices.ask.carRange': 'Quiero alquilar un auto en {{city}} del {{from}} al {{to}}.',
  'bus.nextServices.ask.carOpen': 'Quiero alquilar un auto en {{city}} desde el {{from}}.',
  'bus.nextServices.ask.carTrip': 'Quiero alquilar un auto para este viaje.',
  'bus.nextServices.ask.insurance': 'Quiero agregar seguro de viaje a mi viaje.',
  'bus.nextServices.ask.seat': 'Quiero elegir otro asiento.',
  'bus.nextServices.ask.flight': 'Quiero buscar un vuelo para este viaje.',
  'bus.nextServices.ask.train': 'Quiero buscar pasajes de tren para este viaje.',
  'bus.nextServices.ask.bus': 'Quiero buscar otro pasaje de autobús.',
  'bus.nextServices.ask.transferToAirport': 'Quiero programar un auto al aeropuerto.',
  'bus.nextServices.ask.transferAirportHotel': 'Quiero programar un auto del aeropuerto al hotel.',
  'bus.nextServices.ask.transferToHotel': 'Quiero programar un auto al hotel.',
  'busSearch.who': 'Quién',
  'busSearch.origin': 'Origen',
  'busSearch.destination': 'Destino',
  'busSearch.originPlaceholder': 'Ciudad de origen',
  'busSearch.destinationPlaceholder': 'Ciudad de destino',
  'busSearch.departingFrom': 'Saliendo de {{origin}}',
  'busSearch.swap': 'Invertir origen y destino',
  'busSearch.tripType.roundTrip': 'Ida y vuelta',
  'busSearch.tripType.oneWay': 'Solo ida',
  'busSearch.recentGroup': 'Recientes',
  'busSearch.noStops': 'No se encontró ninguna terminal',
  'busSearch.stopError': 'No fue posible cargar las terminales',
  'busSearch.stopPrompt': 'Escribe al menos 2 letras',
  'busSearch.when': 'Cuándo',
  'busSearch.toBeDefined': 'Por definir',
  'busSearch.chooseRoundTrip': 'Elige la ida y la vuelta',
  'busSearch.returnPending': '{{date}} → elige la vuelta',
  'busSearch.confirmDates': 'Confirmar {{range}}',
  'busSearch.confirmDatesSheet': 'Confirmar fechas',
  'busSearch.confirmAndSearch': 'Confirmar y buscar',
  'busSearch.previousMonth': 'Mes anterior',
  'busSearch.nextMonth': 'Mes siguiente',
  'busSearch.submit': 'Buscar',
  'busSearch.back': 'Volver',
  'busSearch.searchText': 'Autobús de {{from}} a {{to}}, {{when}}.',
  'rentalSearch.driver': 'Quién',
  'rentalSearch.swap': 'Invertir retiro y devolución',
  'rentalSearch.sameDropoff': 'Devolución en el mismo lugar',
  'rentalSearch.location': 'Lugar de retiro y devolución',
  'rentalSearch.pickupLocation': 'Lugar de retiro',
  'rentalSearch.dropoffLocation': 'Lugar de devolución',
  'rentalSearch.locationPlaceholder': 'Lugar o aeropuerto',
  'rentalSearch.noPlaces': 'No se encontraron lugares',
  'rentalSearch.placeError': 'No se pudieron cargar los lugares',
  'rentalSearch.myLocation': 'Mi ubicación',
  'rentalSearch.myLocationError': 'No se pudo usar tu ubicación',
  'rentalSearch.when': 'Cuándo',
  'rentalSearch.dates': 'Retiro y devolución',
  'rentalSearch.chooseDates': 'Elige el retiro y la devolución',
  'rentalSearch.chooseDropoff': '{{date}} · elige la devolución',
  'rentalSearch.days_one': '{{count}} día',
  'rentalSearch.days_other': '{{count}} días',
  'rentalSearch.confirmDates': 'Confirmar {{range}}',
  'rentalSearch.confirmDatesSheet': 'Confirmar fechas',
  'rentalSearch.confirmAndSearch': 'Confirmar y buscar',
  'rentalSearch.pickupTime': 'Hora de retiro',
  'rentalSearch.dropoffTime': 'Hora de devolución',
  'rentalSearch.previousMonth': 'Mes anterior',
  'rentalSearch.nextMonth': 'Mes siguiente',
  'rentalSearch.submit': 'Buscar',
  'rentalSearch.back': 'Volver',
  'rentalSearch.retry': 'Intentar de nuevo',
  'rentalSearch.change': 'Cambiar fechas u horarios',
  'rentalSearch.searchTextSame': 'Buscar coches con retiro y devolución en {{place}}, {{window}}.',
  'rentalSearch.searchTextDifferent':
    'Buscar coches con retiro en {{pickup}}, {{pickupAt}} y devolución en {{dropoff}}, {{dropoffAt}}.',
  'mobilityPlanner.origin': 'Desde dónde',
  'mobilityPlanner.destination': 'Hacia dónde',
  'mobilityPlanner.placeholder': 'Dirección o lugar',
  'mobilityPlanner.suggestions': 'Sugerencias',
  'mobilityPlanner.noPlaces': 'No se encontró ningún lugar',
  'mobilityPlanner.placesError': 'No fue posible cargar los lugares',
  'mobilityPlanner.retry': 'Intentar de nuevo',
  'mobilityPlanner.prompt': 'Escribe al menos 2 letras',
  'mobilityPlanner.submit': 'Buscar autos',
  'mobilityPlanner.when': 'Cuándo',
  'mobilityPlanner.whenPending': 'Por definir',
  'mobilityPlanner.whenMissing': 'Indica la fecha y la hora',
  'mobilityPlanner.whenMissingDate': 'Indica la fecha',
  'mobilityPlanner.whenMissingTime': 'Indica la hora',
  'mobilityPlanner.whenConfirm': 'Confirmar',
  'mobilityPlanner.whenBack': 'Volver',
  'mobilityPlanner.previousMonth': 'Mes anterior',
  'mobilityPlanner.nextMonth': 'Mes siguiente',
  'mobilityPlanner.hour': 'Hora',
  'mobilityPlanner.minute': 'Minuto',
  'mobilityPlanner.who': 'Quién',
  'mobilityPlanner.tripType': 'Sentido',
  'mobilityPlanner.tripType.oneWay': 'Solo ida',
  'mobilityPlanner.tripType.roundTrip': 'Ida y vuelta',
  'mobilityPlanner.passengers': 'Personas',
  'mobilityPlanner.passengersCount_one': '{{count}} persona',
  'mobilityPlanner.passengersCount_other': '{{count}} personas',
  'mobilityPlanner.passengersLess': 'Una persona menos',
  'mobilityPlanner.passengersMore': 'Una persona más',
  'mobilityPlanner.outbound': 'Ida',
  'mobilityPlanner.return': 'Vuelta',
  'mobilityPlanner.returnRoute': 'Vuelta · {{from}} → {{to}}',
  'mobilityPlanner.searchText': 'Viaje de {{from}} a {{to}}.',
  'mobilityPlanner.searchTextRoundTrip': 'Viaje de {{from}} a {{to}}, ida y vuelta.',
  'mobilityPlanner.searchTextNoDestination': 'Auto saliendo de {{from}}.',
  'busResults.badge.recommended': 'Recomendado',
  'busResults.badge.cheapest': 'Menor precio',
  'busResults.badge.fastest': 'Menos tiempo',
  'busResults.merit.recommended': 'El mejor equilibrio entre precio y tiempo, en {{seatClass}}.',
  'busResults.merit.cheapest': '{{amount}} menos que la recomendada.',
  'busResults.merit.cheapestAt': '{{amount}} menos — con cambio de autobús en {{stop}}.',
  'busResults.merit.fastest': '{{duration}} más rápido que la recomendada.',
  'busResults.merit.fastestDirect': '{{duration}} más rápido y sin paradas.',
  'busSeats.segments': 'Tramos del viaje',
  'busSeats.segment': 'Tramo {{segment}}',
  'busSeats.segmentRoute': 'Tramo {{segment}} · {{from}} → {{to}}',
  'busSeats.entrance': 'Entrada',
  'busSeats.driver': 'Conductor',
  'busSeats.floor': 'Piso {{floor}}',
  'busSeats.seat': 'Asiento {{code}}',
  'busSeats.seatOccupied': 'Asiento {{code}}, ocupado',
  'busSeats.loading': 'Cargando los asientos…',
  'busSeats.error':
    'No pudimos cargar los asientos de este tramo ahora. Inténtalo de nuevo en unos instantes.',
  'busSeats.offline': 'No pudimos cargar los asientos. Revisa tu conexión e inténtalo de nuevo.',
  'busSeats.notOffered': 'La empresa no permite elegir asiento en este tramo.',
  'busSeats.retry': 'Intentar de nuevo',
  'busSeats.full': 'No hay asientos libres para todos en este tramo.',
  'busSeats.noSeat': 'Seguir sin asiento',
  'busSeats.noSeatSummary': 'sin asiento',
  'busSeats.pending_one': 'Elige {{count}} asiento más',
  'busSeats.pending_other': 'Elige {{count}} asientos más',
  'busSeats.confirm': 'Seleccionar',
  'busSeats.suggested': 'Asiento recomendado según tus preferencias',
  'busSeats.legend.free': 'Libre',
  'busSeats.legend.occupied': 'Ocupado',
  'busSeats.legend.selected': 'Tuyo',
  'busSeats.legend.recommended': 'Recomendado',
  'busSeats.legend.window': 'Ventana',
  'busSeats.detail.window': 'Ventana',
  'busSeats.detail.aisle': 'Pasillo',
  'busSeats.detail.windowAndAisle': 'Ventana y pasillo',
  'busSeats.detail.individual': 'Individual',
  'busSeats.detail.area.front': 'Adelante',
  'busSeats.detail.area.middle': 'Medio',
  'busSeats.detail.area.back': 'Atrás',
  'busSeats.detail.neighborFree': 'Asiento de al lado libre',
  'busSeats.detail.neighborOccupied': 'Asiento de al lado ocupado',
  'busSeats.detail.recommended': 'Recomendado según tus preferencias',
  'busSeats.detail.hint': 'Toca un asiento para ver los detalles.',
  'busSeats.collapse': 'Cerrar la elección de asiento',
  'busSeats.price.oneWay': 'solo ida, por persona',
  'busSeats.price.outbound': 'ida, por persona',
  'busSeats.price.return': 'vuelta, por persona',
  'busOffers.pickSeatsText': 'Quiero el viaje de las {{time}} ({{carrier}}), {{seats}}.',
  'busOffers.pickSeat': 'asiento {{code}}',
  'busOffers.pickNoSeat': 'sin asiento',
  'busStops.aria': 'Selección de terminal',
  'busStops.titleOrigin': '¿De qué terminal quieres salir?',
  'busStops.titleDestination': '¿A qué terminal vas?',
  'bus.inPolicy': 'Dentro de la política',
  'busSeatMap.aria': 'Mapa de asientos',
  'busSeatMap.title': 'Elige tu asiento',
  'busSeatMap.picked': 'Quiero el asiento {{code}}.',
  'busSeatMap.free': 'Asiento {{code}} (libre)',
  'busSeatMap.occupied': 'Asiento {{code}} (ocupado)',
  'busSeatMap.legendFree': 'Libre',
  'busSeatMap.legendOccupied': 'Ocupado',
  'busSeatMap.legendSelected': 'Seleccionado',
  'busSeatMap.noSeat': 'Continuar sin asiento',
  'busSeatMap.noSeatHint': 'El equipo VOLL elige el asiento por ti.',
  'busSeatMap.noSeatPicked': 'Puedo continuar sin asiento — el equipo VOLL elige.',
  'busSeatMap.pickedSeat': 'asiento {{code}}',
  'busSeatMap.pickedNoSeat': 'sin asiento',
  'busSeatMap.pickedRound': 'Ida: {{outbound}} · Vuelta: {{return}}.',
  'busSeatMap.errorTitle': 'No pude cargar los asientos',
  'busSeatMap.otherTrip': 'Elegir otro viaje',
  'busSeatMap.otherTripText': 'Quiero elegir otro viaje.',
  'busSeatMap.continue': 'Continuar',
  'busSeatMap.legOutbound': 'Ida',
  'busSeatMap.legReturn': 'Vuelta',
  'busSeatMap.noSeatSelected': 'Sin asiento (el equipo VOLL elige)',
  'busSeatMap.front': 'Frente',
  'busSeatMap.floor': '{{n}}º piso',
  'flightFilter.aria': 'Filtrar vuelos',
  'flightFilter.title': 'Vuelos',
  'flightFilter.option_one': '{{count}} opción',
  'flightFilter.option_other': '{{count}} opciones',
  'flightFilter.empty.title': 'Ningún vuelo coincide con esos filtros.',
  'flightFilter.empty.hint': 'Prueba flexibilizar escalas, horario o precio.',
  'flightFilter.empty.hintAxes': 'Prueba flexibilizar {{axes}}.',
  'flightFilter.filteredAria': 'Vuelos filtrados',
  'flightFilter.selectOffer': 'Seleccionar',
  'flightFilter.selectOfferAria': 'Seleccionar oferta — opción {{position}}',
  'flightFilter.found_one': '{{count}} vuelo encontrado',
  'flightFilter.found_other': '{{count}} vuelos encontrados',
  'flightFilter.foundOfTotal': '{{count}} de {{total}} vuelos',
  'flightFilter.sort.label': 'Ordenar',
  'flightFilter.sort.price': 'Más barato',
  'flightFilter.sort.dep': 'Más temprano',
  'flightFilter.sort.dur': 'Más rápido',
  'flightFilter.sortMenu.rec': 'Recomendado',
  'flightFilter.sortMenu.price': 'Precio',
  'flightFilter.sortMenu.dur': 'Duración',
  'flightFilter.sortMenu.dep': 'Salida',
  'flightFilter.sortMenu.arr': 'Llegada',
  'flightFilter.stops.label': 'Escalas',
  'flightFilter.stops.direct': 'Vuelo directo',
  'flightFilter.stops.one': 'Hasta 1 escala',
  'flightFilter.stops.two': 'Hasta 2 escalas',
  'flightFilter.stops.above': 'Más de 2 escalas',
  'flightFilter.airline': 'Aerolíneas',
  'flightFilter.time': 'Horarios',
  'flightFilter.departs': 'Salida',
  'flightFilter.arrives': 'Llegada',
  'flightFilter.departureTime': 'Horario de salida',
  'flightFilter.price': 'Precio',
  'flightFilter.minPrice': 'Precio mínimo',
  'flightFilter.maxPrice': 'Precio máximo',
  'flightFilter.flightNumber': 'Número de vuelo',
  'flightFilter.flightNumberPlaceholder': 'Ej.: VL 0000',
  'flightFilter.filters': 'Filtros',
  'flightFilter.apply': 'Aplicar filtros',
  'flightFilter.reset': 'Restablecer todo',
  'flightFilter.close': 'Cerrar filtros',
  'flightFilter.policy': 'Política',
  'flightFilter.withinPolicy': 'Solo dentro de la política',
  'flightExplorer.chips.toolbar': 'Filtros y orden',
  'flightExplorer.chips.back': 'Volver',
  'flightExplorer.chips.policy': 'Dentro de la política',
  'flightFilter.fromPrice': 'desde {{price}}',
  'flightFilter.arrivalTime': 'Horario de llegada',
  'flightFilter.airports': 'Aeropuertos',
  'flightFilter.otherCities': 'Otros',
  'flightChips.placeholder': 'Filtra a tu manera',
  'flightChips.send': 'Aplicar filtro por texto',
  'flightChips.dictate': 'Dictar filtro',
  'flightChips.dictationCancel': 'Cancelar dictado',
  'flightChips.dictationStop': 'Parar y revisar',
  'flightChips.dictationSend': 'Parar y aplicar',
  'flightChips.transcribing': 'Transcribiendo…',
  'flightChips.dictationFailed': 'No se pudo transcribir. Inténtalo de nuevo.',
  'flightChips.applying': 'Aplicando tu filtro…',
  'flightChips.remove': 'Quitar filtro: {{label}}',
  'flightChips.leg.outbound': 'Ida',
  'flightChips.leg.inbound': 'Vuelta',
  'flightChips.leg.n': 'Tramo {{n}}',
  'flightChips.leg.prefixed': '{{leg}} · {{label}}',
  'flightChips.airline.only': 'Solo {{name}}',
  'flightChips.airline.exclude': 'Sin {{name}}',
  'flightChips.stops.nonstop': 'Vuelo directo',
  'flightChips.stops.up_to_1': 'Hasta 1 escala',
  'flightChips.stops.up_to_2': 'Hasta 2 escalas',
  'flightChips.stops.any': 'Con o sin escalas',
  'flightChips.time.arriveBefore': 'Llegada hasta las {{time}}',
  'flightChips.time.arriveAfter': 'Llegada a partir de las {{time}}',
  'flightChips.time.arriveBetween': 'Llegada entre {{start}} y {{end}}',
  'flightChips.time.departBefore': 'Salida hasta las {{time}}',
  'flightChips.time.departAfter': 'Salida a partir de las {{time}}',
  'flightChips.time.departBetween': 'Salida entre {{start}} y {{end}}',
  'flightChips.price.max': 'Hasta {{price}}',
  'flightChips.price.min': 'Desde {{price}}',
  'flightChips.price.between': 'Entre {{min}} y {{max}}',
  'flightChips.policy.only': 'Solo dentro de la política',
  'flightChips.policy.release': 'Con opciones fuera de la política',
  'flightChips.via.avoid': 'Sin escala en {{place}}',
  'flightChips.via.through': 'Con escala en {{place}}',
  'flightChips.airport.from': 'Saliendo de {{place}}',
  'flightChips.airport.to': 'Llegando a {{place}}',
  'flightChips.airport.only': 'Solo {{place}}',
  'flightChips.airport.exclude': 'Sin {{place}}',
  'flightChips.sort.rec': 'Recomendados primero',
  'flightChips.sort.price': 'Más baratos primero',
  'flightChips.sort.dur': 'Más rápidos primero',
  'flightChips.sort.dep': 'Salida más temprano primero',
  'flightChips.sort.arr': 'Llegada más temprano primero',
  'flightChips.axis.punctuality': 'Puntualidad',
  'flightChips.axis.baggage': 'Equipaje',
  'flightChips.axis.seat': 'Asiento',
  'flightChips.axis.aircraft': 'Aeronave',
  'flightChips.axis.layover': 'Tiempo de escala',
  'flightChips.axis.duration': 'Duración total',
  'flightChips.axis.cabin': 'Cabina',
  'flightChips.axis.fare': 'Tarifa',
  'flightChips.axis.airline': 'Aerolínea',
  'flightChips.axis.via': 'Escala',
  'flightChips.axis.airport': 'Aeropuerto',
  'flightChips.axis.time': 'Horario',
  'flightChips.axis.stops': 'Escalas',
  'flightChips.axis.period': 'Momento del día',
  'flightChips.axis.price': 'Precio',
  'flightChips.axis.policy': 'Política',
  'flightChips.axis.sort': 'Orden',
  'flightChips.axis.other': 'Filtro',
  'flightChips.reason.airport_not_in_search': 'ninguna opción de esta búsqueda pasa por ahí',
  'flightChips.reason.airline_not_in_search': 'esa aerolínea no está en esta búsqueda',
  'flightChips.reason.no_punctuality_data': 'sin dato de puntualidad en esta lista',
  'flightChips.reason.axis_not_available': 'ese criterio no está disponible en esta lista',
  'flightChips.reason.axis_not_in_panel': 'tiempo de escala y duración aún no se filtran aquí',
  'flightChips.reason.window_not_contiguous':
    'no se puede excluir un período del medio del día aquí — ajusta la barra',
  'flightChips.reason.low_confidence': 'no lo entendí con seguridad; intenta reformular',
  'flightChips.reason.other': 'no se pudo aplicar',
  'flightChips.note.question': 'Eso es una pregunta: hazla en el chat.',
  'flightChips.note.new_search': 'Parece una búsqueda nueva: usa la búsqueda de arriba.',
  'flightChips.note.noise': 'No lo entendí como filtro.',
  'flightChips.note.failed': 'No se pudo interpretar ahora. Inténtalo de nuevo.',
  'flightChips.note.cabin': 'Repetí la búsqueda en {{cabin}}',
  'flightChips.note.cabinSame': 'La búsqueda ya está en {{cabin}}',
  'flightChips.note.cabinUnavailable': 'La cabina no se cambia desde aquí en esta pantalla',
  'flightChips.note.reset': 'Filtros limpiados',
  'flightExplorer.cabin': 'Cabina',
  'flightExplorer.cabin.economy': 'Económica',
  'flightExplorer.cabin.premium': 'Premium',
  'flightExplorer.cabin.business': 'Ejecutiva',
  'flightExplorer.cabin.first': 'Primera',
  'flightExplorer.cabinRefetch': 'Cambiar la cabina rehace la búsqueda',
  'flightExplorer.cabinOutOfPolicy': 'fuera de la política de la empresa',
  'flightExplorer.cabinAllowedFromHours': 'permitida en este tramo (vuelos desde {{hours}}h)',
  'flightExplorer.cabinOutBelowHours':
    'fuera de la política en este tramo (solo en vuelos desde {{hours}}h)',
  'flightExplorer.searchCta': 'Buscar',
  'flightExplorer.searching': 'Buscando vuelos…',
  'flightExplorer.searchingLong':
    'La búsqueda sigue en curso. Algunas rutas tardan más de un minuto.',
  'flightExplorer.refreshing': 'actualizando la lista…',
  'flightExplorer.whyRecommended':
    'Mejor equilibrio entre precio, tiempo de vuelo y escalas de esta lista.',
  'flightExplorer.whyRecommendedDirect':
    'Mejor equilibrio entre precio, tiempo de vuelo y escalas de esta lista — y es directo.',
  'flightExplorer.whyRecommendedInPolicy':
    'Mejor equilibrio entre precio, tiempo de vuelo y escalas de esta lista — y está dentro de la política.',
  'flightExplorer.whyRecommendedDirectInPolicy':
    'Mejor equilibrio entre precio, tiempo de vuelo y escalas de esta lista — y es directo, dentro de la política.',
  'flightExplorer.loadMore': 'Ver más vuelos',
  'flightExplorer.offline.offerTitle': '¿No encontraste el vuelo que necesitas?',
  'flightExplorer.offline.offerBody':
    'El equipo VOLL busca vuelos fuera del catálogo online — aerolíneas, tarifas y horarios — que coincidan con tu perfil y devuelve la cotización en la conversación del viaje.',
  'flightExplorer.offline.returnOn': 'vuelta {{date}}',
  'flightExplorer.pinnedFilter': 'Fijados ({{count}})',
  'flightExplorer.error': 'No fue posible buscar los vuelos',
  'flightExplorer.timeout': 'La búsqueda tardó demasiado',
  'flightExplorer.timeoutHint':
    'El proveedor no respondió a tiempo. Inténtalo de nuevo en unos instantes.',
  'flightExplorer.timeoutCity': 'La búsqueda en todos los aeropuertos de {{city}} tardó demasiado',
  'flightExplorer.timeoutCityHint': 'Elige un aeropuerto para buscar más rápido.',
  'flightExplorer.timeoutAirports': 'Buscar un aeropuerto',
  'flightExplorer.policyEvaluation': 'No fue posible evaluar la política del viajero',
  'flightExplorer.policyEvaluationHint': 'Habla con el administrador de la empresa.',
  'flightExplorer.searchExpired': 'Esta búsqueda expiró',
  'flightExplorer.searchExpiredHint':
    'El proveedor no guarda la búsqueda por mucho tiempo. Busca de nuevo: mantenemos tu ida, si sigue disponible.',
  'flightExplorer.searchExpiredAction': 'Buscar de nuevo',
  'flightExplorer.outboundGone':
    'El vuelo de ida que elegiste ya no está disponible. Elige otra ida.',
  'flightExplorer.chooseOutbound': 'Elige el vuelo de ida',
  'flightExplorer.chooseReturn': 'Elige el vuelo de vuelta',
  'flightExplorer.removePick': 'Quitar la elección del tramo {{n}}',
  'flightExplorer.policyCeilingAbove': 'Por encima de {{price}}',
  'flightExplorer.policyCeilingOut': 'queda fuera de la política',
  'flightFares.policyReason': 'Fuera de la política: {{reasons}}',
  'flightExplorer.listTitle': 'Vuelos a {{city}} · {{date}}',
  'flightExplorer.share': 'Compartir la búsqueda',
  'flightExplorer.listTitleOutbound': 'Elige la ida — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.listTitleReturn': 'Elige la vuelta — {{iata}} · {{city}} · {{date}}',
  'flightExplorer.confirm': 'Confirmar',
  'flightExplorer.researchKeptCabin':
    'No fue posible buscar en {{cabin}}. Mantuvimos la lista anterior.',
  'flightExplorer.researchEmptyCabin':
    'No hay vuelos en {{cabin}} en este trayecto. Mantuvimos la lista anterior.',
  'flightExplorer.researchKept':
    'No fue posible rehacer la búsqueda. Mantuvimos la lista anterior.',
  'flightExplorer.researchKeptPolicy':
    'No fue posible evaluar la política del viajero. Mantuvimos la lista anterior; habla con el administrador de la empresa.',
  'flightExplorer.draftPending': 'Busca antes de confirmar — la franja tiene cambios sin buscar.',
  'flightExplorer.confirmUnavailable':
    'No fue posible cerrar la selección: los tramos llegaron en monedas distintas. Cambia la tarifa o busca de nuevo.',
  'flightExplorer.replacesTrip': 'Confirmar sustituye el viaje de la tarjeta.',
  'flightExplorer.replacesTripDiscards':
    'Confirmar sustituye el viaje y descarta las elecciones de los otros tramos.',
  'flightExplorer.aria': 'Explorar vuelos',
  'flightExplorer.pillAria': 'Contexto de la búsqueda',
  'flightExplorer.pill.legs_one': '{{count}} tramo',
  'flightExplorer.pill.legs_other': '{{count}} tramos',
  'flightExplorer.pillEdit': 'Editar búsqueda',
  'flightExplorer.searchAgain': 'Buscar de nuevo',
  'flightExplorer.legCta': 'Ver todos los vuelos',
  'flightExplorer.legCtaShort': 'Todos los vuelos',
  'flightExplorer.frozenEmpty': 'Las ofertas de esta búsqueda ya no están disponibles.',
  'flightExplorer.emptyResults': 'No encontramos vuelos a {{destination}} en esas fechas',
  'searchEmpty.newSearch': 'Nueva búsqueda',
  'searchEmpty.retry': 'Intentar de nuevo',
  'flightSeatLink.cta': 'Elegir asiento',
  'ride99Link.title': 'Viaje con 99',
  'ride99Link.cta': 'Pedir viaje',
  'walletLink.title.balance': 'Solicitud de saldo',
  'walletLink.title.home': 'Billetera',
  'walletLink.cta': 'Abrir Billetera',
  'eventPremises.missing_one': 'Falta {{count}} elemento en la ficha',
  'eventPremises.missing_other': 'Faltan {{count}} elementos en la ficha',
  'eventPremises.ready': 'Ficha lista para confirmar',
  'eventPremises.draft': 'Borrador · {{done}} de {{total}}',
  'eventPremises.readyToPublish': 'Listo para publicar',
  'eventPremises.published': 'Publicadas',
  'trips.unnamed': 'Viaje sin nombre',
  'trips.routeTbd': 'Ruta por definir',
  'trips.totalTbd': 'Por confirmar',
  'trips.collapse': 'Contraer',
  'trips.seeAll': 'Ver los {{count}} viajes',
  'trips.drawer.ariaFallback': 'Detalle del viaje',
  'trips.drawer.summary': 'Resumen',
  'trips.drawer.period': 'Período',
  'trips.drawer.path': 'Trayecto',
  'trips.drawer.modals': 'Servicios',
  'trips.drawer.requester': 'Solicitante',
  'trips.drawer.company': 'Empresa',
  'trips.drawer.vouchers': 'Vouchers',
  'trips.drawer.processingTitle': 'Vouchers en procesamiento',
  'trips.drawer.processingBody':
    'Los vouchers aparecerán aquí cuando la reserva sea confirmada por los proveedores.',
  'trip.modal.FLIGHT': 'Vuelo',
  'trip.modal.HOTEL': 'Hotel',
  'trip.modal.BUS': 'Bus',
  'trip.modal.RENTAL': 'Alquiler de autos',
  'trip.modal.MOBILITY': 'Movilidad',
  'trip.status.UPCOMING': 'Próximo',
  'trip.status.ONGOING': 'En viaje',
  'trip.status.COMPLETED': 'Concluido',
  'trip.status.CANCELLED': 'Cancelado',
  'voucher.status.IN_PROGRESS': 'En curso',
  'voucher.status.AWAITING_APPROVAL': 'Esperando aprobación',
  'voucher.status.CONFIRMED': 'Confirmado',
  'voucher.status.CANCELLED': 'Cancelado',
  'voucher.status.EXPIRED': 'Vencido',
  'voucher.status.DECLINED': 'Rechazado',
  'voucher.meta.supplier': 'Proveedor',
  'voucher.meta.status': 'Estado',
  'voucher.meta.reservation': 'Reserva',
  'voucher.meta.issuedAt': 'Emitido el',
  'voucher.meta.notIssued': 'No emitida',
  'voucher.segment.outbound': 'Ida',
  'voucher.segment.return': 'Vuelta',
  'voucher.segment.generic': 'Tramo',
  'voucher.segment.count': '{{dir}} · tramo {{index}} de {{total}}',
  'voucher.detail.airline': 'Aerolínea',
  'voucher.detail.locator': 'Localizador',
  'voucher.detail.departure': 'Salida',
  'voucher.detail.arrival': 'Llegada',
  'voucher.detail.duration': 'Duración',
  'voucher.detail.connections': 'Conexiones',
  'voucher.detail.stop_one': '{{count}} escala',
  'voucher.detail.stop_other': '{{count}} escalas',
  'voucher.detail.cabin': 'Cabina',
  'voucher.detail.seat': 'Asiento',
  'voucher.detail.baggage': 'Equipaje',
  'voucher.detail.operator': 'Operadora',
  'voucher.detail.boarding': 'Embarque',
  'voucher.detail.disembark': 'Desembarque',
  'voucher.detail.class': 'Clase',
  'voucher.detail.hotel': 'Hotel',
  'voucher.detail.address': 'Dirección',
  'voucher.detail.city': 'Ciudad',
  'voucher.detail.checkIn': 'Check-in',
  'voucher.detail.checkOut': 'Check-out',
  'voucher.detail.nights': 'Noches',
  'voucher.detail.rooms': 'Habitaciones',
  'voucher.detail.guests': 'Huéspedes',
  'voucher.detail.cancellationPolicy': 'Política de cancelación',
  'voucher.detail.fine': 'Multa {{value}}',
  'voucher.detail.available': 'Disponible',
  'voucher.detail.rentalCompany': 'Rentadora',
  'voucher.detail.pickup': 'Retiro',
  'voucher.detail.dropoff': 'Devolución',
  'voucher.detail.model': 'Modelo',
  'voucher.detail.group': 'Grupo',
  'voucher.detail.category': 'Categoría',
  'voucher.detail.distance': 'Distancia',
  'voucher.block.lodging': 'Hospedaje',
  'voucher.block.rental': 'Alquiler de autos',
  'voucher.block.mobility': 'Movilidad',
  'flight.policyDetails.title': 'Detalles de la política',
  'flight.policyDetails.inPolicy': 'Dentro de la política',
  'flight.policyDetails.outPolicy': 'Fuera de la política',
  'flight.policyRule.advance': 'Anticipación mínima',
  'flight.policyRule.allowedClass': 'Clase permitida',
  'flight.policyRule.baggage': 'Equipaje',
  'flight.policyRule.baggageOnlyAboveTripLength': 'Equipaje según la duración del viaje',
  'flight.policyRule.baggageOnlyOnMultipleSegments': 'Equipaje en múltiples tramos',
  'flight.policyRule.bookingWindow': 'Anticipación mínima',
  'flight.policyRule.bookingWindowInternational': 'Anticipación mínima internacional',
  'flight.policyRule.cheapestPrice': 'Menor precio',
  'flight.policyRule.cheapestPriceFastest': 'Menor precio entre los vuelos más rápidos',
  'flight.policyRule.cheapestPriceNonStop': 'Menor precio sin escalas',
  'flight.policyRule.cheapestPricePeriod': 'Menor precio en el período',
  'flight.policyRule.class': 'Clase permitida',
  'flight.policyRule.classLimits': 'Precio máximo por clase',
  'flight.policyRule.price': 'Precio máximo',
  'flight.policyRule.withinPolicy': 'Política del viaje',
  'flight.policyRule.withinPolitics': 'Política del viaje',
  'flight.policyRule.policyProfile': 'Política aplicada',
  'flight.policyRule.policyProfileUnresolved': 'Política del viajero no identificada',
  'flight.policyRule.companyGuidance': 'Orientaciones de la empresa',
  'flight.policyRule.bookingWindowText_one':
    'Anticipación mínima de {{count}} día para viajes {{scope}}.',
  'flight.policyRule.bookingWindowText_other':
    'Anticipación mínima de {{count}} días para viajes {{scope}}.',
  'flight.policyRule.scope.domestic': 'nacionales',
  'flight.policyRule.scope.international': 'internacionales',
  'flight.policyRule.classLimitsText':
    'El precio de esta tarifa supera el límite definido por la política para esta clase.',
  'flight.policyRule.classLimitsTextWithLimit':
    'El precio de esta tarifa supera el tope de {{value}} definido por la política para esta clase.',
  'flight.policyRule.withinPolicyText':
    'Esta tarifa está fuera de la política del viaje, pero el proveedor no informó qué regla se incumplió.',
  'flight.row.outPolicyAria': 'Vuelo fuera de la política',
  'flight.segment.viewDetail': 'Detalles',
  'flight.pin.off': 'Fijar',
  'flight.pin.on': 'Fijado',
  'flight.segment.detailsTitle': 'Detalle del vuelo',
  'flight.segment.detailBack': 'Volver',
  'flight.segment.arrivesNextDay_one': 'Llega al día siguiente',
  'flight.segment.arrivesNextDay_other': 'Llega {{count}} días después',
  'flight.fareBreakdown.fare': 'Tarifa',
  'flight.fareBreakdown.taxes': 'Tasas y cargos',
  'flight.fareBreakdown.totalPerPerson': 'Total por persona',
  'flight.quote.outbound': 'Ida',
  'flight.quote.return': 'Vuelta',
  'flight.reservation.aria': 'Billete de reserva',
  'flight.reservation.leg': 'Tramo {{n}}',
  'flight.reservation.locator': 'Localizador',
  'flight.reservation.locatorPending': 'se enviará en instantes',
  'flight.reservation.emailSent': 'Reserva en emisión · te enviamos los detalles por e-mail',
  'flight.reservation.cpf': 'CPF {{value}}',
  'traveler.policyHolder': 'Titular de la política',
  'traveler.costCenter': 'Centro de costo',
  'traveler.motive': 'Motivo del viaje',
  'flight.reservation.flight': 'Vuelo',
  'flight.reservation.date': 'Fecha',
  'flight.reservation.outboundDate': 'Fecha de ida',
  'flight.reservation.returnDate': 'Fecha de vuelta',
  'flight.reservation.totalPaid': 'Total pagado',
  'flight.search.aria': 'Confirmación de búsqueda',
  'flight.search.cabin.economy': 'Económica',
  'flight.search.cabin.premium': 'Premium',
  'flight.search.cabin.business': 'Ejecutiva',
  'flight.search.cabin.first': 'Primera',
  'flight.search.passenger_one': '{{count}} pasajero',
  'flight.search.passenger_other': '{{count}} pasajeros',
  'flight.search.adult_one': '{{count}} adulto',
  'flight.search.adult_other': '{{count}} adultos',
  'flight.search.child_one': '{{count}} niño',
  'flight.search.child_other': '{{count}} niños',
  'flight.search.infant_one': '{{count}} bebé',
  'flight.search.infant_other': '{{count}} bebés',
  'flight.search.adult': 'Adulto',
  'flight.search.child': 'Niño',
  'flight.search.infant': 'Bebé',
  'flight.search.roundTrip': 'Ida y vuelta',
  'flight.search.oneWay': 'Solo ida',
  'flight.search.tripType': 'Tipo de viaje',
  'flight.search.day_one': '{{count}} día',
  'flight.search.day_other': '{{count}} días',
  'flight.search.class': 'Clase',
  'flight.search.searchFlights': 'Buscar vuelos',
  'flightSearchConfirmation.replySearch': 'Buscar vuelos.',
  'flightCheaperDates.roundTrip': 'Ida y Vuelta',
  'flightCheaperDates.oneWay': 'Solo Ida',
  'flightCheaperDates.dates': 'Fechas',
  'flightCheaperDates.cheapest': 'más barato',
  'flightCheaperDates.mostExpensive': 'más caro',
  'flightCheaperDates.search': 'Buscar vuelos en estas fechas',
  'flightCheaperDates.keep': 'Mantener {{date}}',
  'flightCheaperDates.searchTextOneWay':
    'Buscar vuelos de {{origin}} a {{destination}} ({{originCode}}→{{destinationCode}}), ida {{outbound}}.',
  'flightCheaperDates.searchTextRoundTrip':
    'Buscar vuelos de {{origin}} a {{destination}} ({{originCode}}→{{destinationCode}}), ida {{outbound}} y vuelta {{return}}.',
  'flightPlanner.calDisabledHint': 'Elige origen y destino para ver los precios',
  'flightPlanner.allAirports': 'todos los aeropuertos',
  'flightPlanner.stateNeedsOut': 'Elige la fecha de ida',
  'flightPlanner.stateNeedsRet': 'Ida {{date}} · elige la vuelta',
  'flightPlanner.stateOneWay': 'Ida {{date}}',
  'flightPlanner.stateRange_one': '{{from}} - {{to}} • {{nights}} noche',
  'flightPlanner.stateRange_other': '{{from}} - {{to}} • {{nights}} noches',
  'flightPlanner.bestDayOut': '{{date}} es la ida más económica',
  'flightPlanner.stateLeg': 'Tramo {{leg}} · {{date}}',
  'flightPlanner.stateNeedsLegDate': 'Elige la fecha del tramo {{leg}}',
  'flightPlanner.bestDayLeg': '{{date}} es la fecha más económica del tramo {{leg}}',
  'flightPlanner.bestDayRet': '{{date}} es la vuelta más económica',
  'flightPlanner.savesLine': 'Vuela este día y ahorra {{amount}}',
  'flightPlanner.savesLineInline': 'vuela este día y ahorra {{amount}}',
  'flightPlanner.chosenCheapest': 'Elegiste la fecha más económica',
  'flightPlanner.awaitingDate': 'Esperando la fecha...',
  'flightPlanner.who': 'Quién',
  'flightPlanner.whoPolicy': 'Política de',
  'flightPlanner.from': 'De',
  'flightPlanner.to': 'Para',
  'flightPlanner.when': 'Cuándo',
  'flightPlanner.whenEmpty': 'Por definir',
  'flightPlanner.whenReturnPending': '– Vuelta',
  'flightPlanner.confirmDates': 'Confirmar fechas',
  'flightPlanner.confirmAndSearch': 'Confirmar y buscar',
  'flightPlanner.confirmRange': 'Confirmar {{range}}',
  'flightPlanner.sheetOneWay': 'Ida',
  'flightPlanner.sheetOneWayTo': 'Ida a {{city}}',
  'flightPlanner.sheetLeg': '{{n}} de {{total}}',
  'flightPlanner.sheetLegFrom': '{{n}} de {{total}} · saliendo de {{code}}',
  'flightPlanner.cancelEdit': 'Cancelar',
  'flightPlanner.back': 'Volver',
  'flightPlanner.applyEdit': 'Actualizar',
  'flightPlanner.loadingPrices': 'Buscando precios…',
  'flightPlanner.noPriceTrend': 'Sin tendencia de precio para el período',
  'flightPlanner.swap': 'Invertir origen y destino',
  'flightPlanner.analyzed': '{{count}} fechas analizadas',
  'flightPlanner.reset': 'Restablecer',
  'flightPlanner.outboundTag': 'IDA',
  'flightPlanner.returnTag': 'VUELTA',
  'flightPlanner.prevMonth': 'Mes anterior',
  'flightPlanner.nextMonth': 'Mes siguiente',
  'flightPlanner.search': 'Buscar vuelos en estas fechas',
  'flightPlanner.searchShort': 'Buscar',
  'flightPlanner.fromEmpty': '¿De dónde sales?',
  'flightPlanner.toEmpty': '¿A dónde vas?',
  'flightPlanner.airportSearchPrompt': 'Escribe para buscar',
  'flightPlanner.noAirports': 'Ningún aeropuerto',
  'flightPlanner.airportsSearchFailed': 'No pudimos buscar aeropuertos ahora. Inténtalo de nuevo.',
  'flightPlanner.airportsSearchRetry': 'Intentar de nuevo',
  'flightPlanner.sameAirports': 'El origen y el destino deben ser diferentes.',
  'flightPlanner.airportRecent': 'Recientes',
  'travelers.you': 'Tú',
  'travelers.searchPlaceholder': 'Buscar persona',
  'travelers.searchError': 'No se pudieron buscar personas. Inténtalo de nuevo.',
  'travelers.loadMore': 'Cargar más',
  'travelers.policyLabel': 'Aplicar política de',
  'travelers.done': 'Listo',
  'travelers.travelerRole': 'viajero',
  'travelers.selectAria': 'Seleccionar viajeros',
  'travelers.count_one': '{{count}} viajero',
  'travelers.count_other': '{{count}} viajeros',
  'travelers.back': 'Volver',
  'travelers.empty': 'No se encontró a nadie',
  'travelers.selectedGroup': 'En el viaje',
  'travelers.maxReached': 'Máximo de {{max}} viajeros',
  'travelers.minReached': 'Al menos 1 viajero',
  'travelers.inSearch_one': '{{count}} viajero en la búsqueda',
  'travelers.inSearch_other': '{{count}} viajeros en la búsqueda',
  'flightPlanner.tripType.oneWay': 'Solo ida',
  'flightPlanner.tripType.roundTrip': 'Ida y vuelta',
  'flightPlanner.tripType.multiLeg': 'Multidestino',
  'flightPlanner.searchTextOneWay':
    'Buscar vuelos de {{origin}} a {{destination}}, ida {{outbound}}.',
  'flightPlanner.searchTextRoundTrip':
    'Buscar vuelos de {{origin}} a {{destination}}, ida {{outbound}} y vuelta {{return}}.',
  'flightPlanner.searchTextMulti': 'Buscar vuelos: {{legs}}.',
  'flightPlanner.searchTextMultiLeg': '{{origin}} → {{destination}} ({{date}})',
  'flightPlanner.multiLeg.editLegs': 'Editar tramos',
  'flightPlanner.multiLeg.addLeg': 'Añadir vuelo',
  'flightPlanner.multiLeg.removeLeg': 'Quitar vuelo',
  'flightPlanner.multiLeg.reset': 'Restablecer',
  'flightPlanner.multiLeg.transfer': 'traslado',
  'flightPlanner.multiLeg.summaryUndefined': 'Por definir',
  'flightPlanner.multiLeg.missing.origin': 'Falta el origen del tramo {{n}}.',
  'flightPlanner.multiLeg.missing.destination': 'Falta el destino del tramo {{n}}.',
  'flightPlanner.multiLeg.missing.date': 'Falta la fecha del tramo {{n}}.',
  'flightPlanner.multiLeg.ready': 'Tus {{count}} tramos están listos.',
  'fareSelector.recommendedWhy.checked': 'Más barata con bodega incluida',
  'fareSelector.recommendedWhy.cheapest': 'Más barata',
  'fareCard.carryYes': 'Equipaje de mano',
  'fareCard.carryNo': 'Sin equipaje de mano',
  'fareCard.checkedYes': 'Con equipaje facturado',
  'fareCard.checkedNo': 'Sin equipaje facturado',
  'fareCard.seatYes': 'Asiento',
  'fareCard.seatNo': 'Sin selección de asiento',
  'fareCard.changeYes': 'Cambios',
  'fareCard.changeNo': 'Sin cambios',
  'fareCard.refundYes': 'Reembolso',
  'fareCard.refundNo': 'Sin reembolso',
  'fareCard.fee': '{{benefit}} con cargo',
  'fareCard.unknown': '{{benefit}} · no informado',
  'fareCard.allowancePiecesKg_one': '{{benefit}} · {{count}} pieza de {{kg}} kg',
  'fareCard.allowancePiecesKg_other': '{{benefit}} · {{count}} piezas de {{kg}} kg',
  'fareCard.allowancePieces_one': '{{benefit}} · {{count}} pieza',
  'fareCard.allowancePieces_other': '{{benefit}} · {{count}} piezas',
  'fareCard.allowanceKg': '{{benefit}} · hasta {{kg}} kg',
  'fareCard.supplierNotes': 'Reglas informadas por el proveedor',
  'fareCard.curatedRules': 'Reglas del proveedor, completadas por VOLL',
  'flightFares.title': 'Elige las tarifas de cada vuelo',
  'flightFares.subtitle': '{{route}} · {{trip}}',
  'flightFares.subtitleWithTravelers': '{{route}} · {{trip}} · {{travelers}}',
  'flightFares.selectedFlights': 'Vuelos seleccionados',
  'flightFares.airportSwitch.title': 'Atención: aeropuertos diferentes',
  'flightFares.airportSwitch.body':
    'La ida llega a {{arrival}}, pero la vuelta sale de {{departure}}.',
  'flightFares.airportSwitch.ack': 'Entiendo que la vuelta sale de otro aeropuerto',
  'flightOffers.airportSwitch.row':
    'Sale de {{departure}} — aeropuerto distinto de la llegada de la ida ({{arrival}})',
  'flightOffers.airportSwitch.rowExpected': 'Sale de {{departure}}',
  'flightExplorer.airportSwitchNotice':
    'La ida llega a {{arrival}}. Hay vueltas en esta lista que salen de otro aeropuerto — revisa antes de elegir.',
  'flightFares.changeFlight': 'Cambiar vuelo',
  'flightFares.changeFlightAria': 'Cambiar el vuelo de {{leg}}',
  'flightFares.faresOf': 'Tarifas · {{leg}}',
  'flightFares.perTraveler': '/viajero',
  'flightFares.railPrevious': 'Retroceder en {{group}}',
  'flightFares.railNext': 'Avanzar en {{group}}',
  'flightFares.checkout': 'Ir al checkout',
  'flightFares.selected': 'Seleccionado',
  'flightFares.back': 'Volver',
  'flightFares.titleShort': 'Elige las tarifas',
  'flightFares.slideAria': 'Tarifa {{position}} de {{total}}',
  'flightFares.next': 'Siguiente',
  'flightFares.summaryAria': 'Tarifas elegidas',
  'rental.viewPolicies': 'Ver políticas',
  'rental.results.title': 'Coches en {{place}}',
  'rental.results.total_one': 'Encontramos {{count}} coche',
  'rental.results.total_other': 'Encontramos {{count}} coches',
  'rental.results.visible': '{{visible}} de {{total}} coches',
  'rental.results.resultsLabel': 'Coches encontrados',
  'rental.results.filteredEmpty': 'Ningún coche con estos filtros.',
  'rental.results.clearFilters': 'Limpiar filtros',
  'rental.results.applyFilters': 'Aplicar filtros',
  'rental.results.showMap': 'Mapa',
  'rental.results.loadMore': 'Ver más coches',
  'rental.results.sort': 'Ordenar',
  'rental.results.chips.toolbar': 'Filtros y orden',
  'rental.results.chips.back': 'Volver',
  'rental.results.chips.policy': 'Dentro de la política',
  'rental.results.chips.dailyPrice': 'Diaria',
  'rental.results.chips.priceUpTo': 'Hasta {{price}}',
  'rental.results.chips.priceFrom': 'Desde {{price}}',
  'rental.results.chips.priceBetween': '{{min}} – {{max}}',
  'rental.results.order.recommended': 'Recomendado',
  'rental.results.order.price': 'Precio',
  'rental.results.order.distance': 'Distancia',
  'rental.results.order.category': 'Categoría',
  'rental.results.order.company': 'Agencia',
  'rental.results.filters.collapse': 'Plegar los filtros',
  'rental.results.filters.close': 'Cerrar los filtros',
  'rental.results.filters.accessories': 'Accesorios',
  'rental.results.filters.automatic': 'Automático',
  'rental.results.filters.airConditioning': 'Aire acondicionado',
  'rental.results.filters.dailyPrice': 'Diaria hasta',
  'rental.results.filters.minimumPrice': 'Tarifa diaria mínima',
  'rental.results.filters.maximumPrice': 'Tarifa diaria máxima',
  'rental.results.filters.company': 'Agencia',
  'rental.results.priceDetail_one': '{{count}} día',
  'rental.results.priceDetail_other': '{{count}} días · {{daily}}/día',
  'rental.results.dailyPrice': '{{daily}}/día',
  'rental.results.cheapest': 'Menor precio',
  'rental.results.agencyFallback': 'Agencia {{company}}',
  'rental.results.pillAria': 'Contexto de la búsqueda de coches',
  'rental.results.close': 'Cerrar coches',
  'rental.vehicle.orSimilar': 'o similar',
  'rental.drawer.title': 'Todos los vehículos',
  'rental.filters': 'Filtros',
  'rental.reset': 'Restablecer',
  'rental.category': 'Categoría',
  'rental.company': 'Rentadora',
  'rental.policy': 'Política',
  'rental.onlyInPolicy': 'Solo dentro de la política',
  'rental.loadingVehicles': 'Cargando vehículos…',
  'rental.loadError': 'No se pudo cargar. Intenta de nuevo.',
  'rental.automatic': 'Automático',
  'rental.automaticShort': 'Autom.',
  'rental.manual': 'Manual',
  'rental.airConditioning': 'A/C',
  'rental.noAirConditioning': 'Sin A/C',
  'rental.inPolicy': 'Dentro de la política',
  'rental.outPolicy': 'Fuera de la política',
  'rental.rent': 'Alquilar',
  'rentalOffers.pickText': 'Quiero el {{title}} de {{company}}, {{total}}.',
  'rentalOffers.title': 'Mejores coches',
  'rentalOffers.total_one': '{{count}} coche',
  'rentalOffers.total_other': '{{count}} coches',
  'rentalOffers.viewPolicies': 'Ver tus políticas',
  'rentalOffers.seeAll': 'Ver todos los coches',
  'rentalOffers.slideAria': 'Coche {{position}} de {{total}}',
  'rentalOffers.badge.recommended': 'Recomendado',
  'rentalOffers.badge.cheapest': 'Menor precio',
  'rentalOffers.badge.closest': 'Más cerca',
  'rentalOffers.merit.recommended': 'Menor total entre los coches dentro de tu política.',
  'rentalOffers.merit.recommendedOutside': 'Menor total de la búsqueda.',
  'rentalOffers.merit.cheapest': '{{amount}} menos que el recomendado.',
  'rentalOffers.merit.closest': 'Retiro a {{distance}} de la dirección buscada.',
  'rental.policyDrawer.title': 'Políticas de alquiler',
  'rental.deckToggleAria': 'Expandir o contraer lista de vehículos',
  'rental.selection.continue': 'Continuar',
  'rental.selection.chooseAnother': 'Elegir otro vehículo',
  'productCheckout.hint.cpfNotOnFile':
    'CPF no registrado. Ingresa tu CPF aquí o usa pasaporte/documento nacional.',
  'productCheckout.hint.cpfNotOnFileNoAlternative': 'CPF no registrado. Ingresa tu CPF aquí.',
  'productCheckout.hint.cpfInvalid': 'CPF inválido. Revisa los números.',
  'productCheckout.chooseAnotherReply': 'Quiero ver las otras opciones.',
  'mobility.sectionRide': 'Viaje',
  'mobility.sectionCarDriver': 'Coche con conductor',
  'mobility.inPolicy': 'En política',
  'mobility.outPolicy': 'Fuera de política',
  'mobility.choose': 'Elegir',
  'mobility.chosen': 'Elegida',
  'mobility.chooseAria': 'Elegir {{title}}, {{price}}, {{label}}',
  'mobility.estimate': 'valor estimado',
  'mobility.roundTripEstimate': 'ida y vuelta · valor estimado',
  'mobility.estimateNote': 'El valor final depende del trayecto y del tráfico.',
  'mobility.distanceKmPerLeg': '{{km}} km por tramo',
  'mobility.overLimit': 'Fuera de política · {{km}} km, el límite de tu empresa es {{limit}} km',
  'mobility.overLimitNoCap': 'Fuera de política · {{km}} km, por encima del límite de tu empresa',
  'mobility.priceOnRequest': 'A consultar',
  'mobility.distanceKm': '{{km}} km',
  'mobility.hoursPerDay': '{{hours}}h/día',
  'mobility.day_one': '{{count}} día',
  'mobility.day_other': '{{count}} días',
  'mobility.bilingual': 'Conductor bilingüe',
  'mobility.overnight_one': '{{count}} pernocte',
  'mobility.overnight_other': '{{count}} pernoctes',
  'mobility.stops_one': 'Parada: {{stops}}',
  'mobility.stops_other': 'Paradas: {{stops}}',
  'mobility.freeKm': 'km libre',
  'mobility.excludesStops': 'Valor estimado sin las paradas.',
  'mobility.franchise': 'Diaria de {{hours}}h con {{km}} km',
  'mobility.franchiseFreeKm': 'Diaria de {{hours}}h con km libre',
  'mobility.perDailySuffix': 'por día',
  'mobility.daysOfDaily_one': '{{count}} día de {{daily}}',
  'mobility.daysOfDaily_other': '{{count}} días de {{daily}}',
  'productCheckout.otherOptions': 'Ver otras opciones',
  'productCheckout.frozenNotice':
    'Esta cotización ya no está activa. Pide al agente que vuelva a cotizar.',
  'productCheckout.mobility.cooperative': 'Taxi de cooperativa',
  'productCheckout.mobility.outward': 'Ida',
  'productCheckout.mobility.return': 'Vuelta',
  'productCheckout.mobility.start': 'Inicio',
  'productCheckout.mobility.pickup': 'salida',
  'productCheckout.mobility.package': 'Paquete',
  'productCheckout.mobility.companions_one': '{{count}} acompañante',
  'productCheckout.mobility.companions_other': '{{count}} acompañantes',
  'productCheckout.mobility.companionsLabel': 'Acompañantes',
  'productCheckout.mobility.extras': 'Extras',
  'productCheckout.mobility.details': 'Detalles',
  'productCheckout.mobility.detailsTitle': 'Detalles del viaje',
  'productCheckout.mobility.pickupAt': 'Salida',
  'productCheckout.mobility.from': 'Origen',
  'productCheckout.mobility.to': 'Destino',
  'productCheckout.mobility.ride': 'Viaje',
  'productCheckout.mobility.policy': 'Política',
  'productCheckout.mobility.stops': 'Paradas',
  'productCheckout.mobility.stop': 'Parada',
  'productCheckout.mobility.flight': 'Vuelo',
  'productCheckout.mobility.totalEstimated': 'Total previsto',
  'productCheckout.mobility.totalEstimatedRoundTrip': 'Total previsto · ida y vuelta',
  'productCheckout.mobility.estimateNote': 'El valor final depende del trayecto y del tráfico.',
  'productCheckout.mobility.notes': 'Observaciones',
  'productCheckout.mobility.contact': 'Contacto',
  'productCheckout.mobility.done': 'Viaje programado',
  'productCheckout.mobility.pendingApproval':
    'Enviado para aprobación. El viaje se programará cuando tu gestor lo apruebe.',
  'productCheckout.mobility.pendingApprovalShort': 'Enviado para aprobación',
  'productCheckout.mobility.outOfPolicyDistance':
    'Fuera de la política: {{km}} km, límite {{limit}} km',
  'productCheckout.mobility.outOfPolicyKm': 'Fuera de la política: {{km}} km',
  'productCheckout.oneClick.suggested': 'con sugerencia de la IA',
  'mobilityRideDetails.title': 'Detalles del viaje',
  'mobilityRideDetails.phoneSelf': 'Tu teléfono',
  'mobilityRideDetails.phoneOf': 'Teléfono de {{name}}',
  'mobilityRideDetails.phoneHelpSelf': 'El conductor usa este número solo en este viaje.',
  'mobilityRideDetails.phoneHelpOf':
    'El conductor usa este número solo en este viaje. No se guarda en el perfil de {{name}}.',
  'mobilityRideDetails.phoneError': 'Revisa el número con el código de área',
  'mobilityRideDetails.phonePlaceholder': '+55 (11) 9 0000-0000',
  'mobilityRideDetails.notes': 'Observaciones para el conductor (opcional)',
  'mobilityRideDetails.notesHelp': 'Ej.: número de vuelo, conductor preferido, vehículo accesible.',
  'mobilityRideDetails.continue': 'Continuar',
  'mobilityRideDetails.echoSelf': 'Mi teléfono: {{phone}}.',
  'mobilityRideDetails.echoOf': 'Teléfono de {{name}}: {{phone}}.',
  'mobilityRideDetails.echoNotes': 'Mensaje: {{notes}}',
  'productCheckout.loyalty.applied': 'Programa de millas aplicado',
  'productCheckout.loyalty.appliedProgram': 'Programa de fidelidad aplicado',
  'productCheckout.loyalty.add': 'Agregar programa de fidelidad',
  'productCheckout.loyalty.addAria': 'Agregar {{program}} a tu perfil',
  'productCheckout.loyalty.aria': 'Programas de fidelidad',
  'productCheckout.oneClick.reviewTitle': 'Revisa los datos a continuación',
  'productCheckout.oneClick.prefilledHint': 'Según tu perfil. Verifica que esté correcto.',
  'productCheckout.oneClick.completeTitle': 'Completa tus datos',
  'productCheckout.oneClick.completeHint':
    'Faltan en tu perfil. Lo que completes aquí se guarda en él.',
  'productCheckout.oneClick.completeTitleGuest': 'Completa los datos de {{name}}',
  'productCheckout.oneClick.completeHintGuest':
    'Faltan en el perfil de {{name}}. Valen solo para esta reserva.',
  'productCheckout.oneClick.prefilledHintMulti':
    'Según el perfil de cada viajero. Verifica que esté correcto.',
  'productCheckout.oneClick.bookingTitle': 'De la solicitud (vale para todos)',
  'productCheckout.oneClick.required': 'Obligatorio',
  'productCheckout.oneClick.review': 'Revisar datos',
  'productCheckout.oneClick.submit': 'Pedir con 1-Click',
  'productCheckout.oneClick.budgetInsufficient': 'Saldo insuficiente para esta solicitud.',
  'productCheckout.oneClick.submitting': 'Solicitando',
  'productCheckout.oneClick.done': '¡Solicitado con éxito!',
  'productCheckout.oneClick.indeterminate':
    'Solicitud en análisis. Te avisaremos cuando se confirme.',
  'productCheckout.oneClick.failed':
    'No se pudo solicitar. Mira el motivo en la respuesta de abajo.',
  'productCheckout.oneClick.failedNewCheckout':
    'No se pudo solicitar. Hay una nueva solicitud justo abajo.',
  'productCheckout.oneClick.unsent': 'No pudimos enviarlo. Inténtalo de nuevo.',
  'productCheckout.oneClick.loadError':
    'No se pudieron cargar tus datos. Revisa y solicita en la pantalla completa.',
  'productCheckout.oneClick.loading': 'Cargando tus datos',
  'productCheckout.outOfPolicy': 'Fuera de la política',
  'productCheckout.reserveRental': 'Solicitar auto',
  'productCheckout.hotel.outOfPolicy': 'Por encima del tope de la política',
  'productCheckout.reserveFlight': 'Solicitar vuelo',
  'checkoutTravelDocs.title': 'Documentos del viaje',
  'checkoutTravelDocs.source': 'Según la base IATA Timatic',
  'checkoutTravelDocs.fallback': 'No pudimos consultar la base ahora',
  'checkoutTravelDocs.entry': 'Entrada · {{country}}',
  'checkoutTravelDocs.transit': 'Conexión · {{country}}',
  'checkoutTravelDocs.have': 'Lo tengo',
  'checkoutTravelDocs.status.required': 'Exigido',
  'checkoutTravelDocs.status.attention': 'Atención',
  'checkoutTravelDocs.status.ok': 'OK',
  'checkoutTravelDocs.detail': 'Ver detalle',
  'checkoutTravelDocs.footer':
    'Quien valida es la aerolínea y la inmigración; confirma con el consulado.',
  'productCheckout.flight.outOfPolicyAria': 'Vuelo fuera de la política',
  'productCheckout.reserveHotel': 'Solicitar hotel',
  'productCheckout.reserveBus': 'Solicitar autobús',
  'productCheckout.reserveMobility': 'Solicitar traslado',
  'productCheckout.rentalDate': 'Fecha de alquiler',
  'productCheckout.rentalDays': 'Número de días',
  'productCheckout.pickupLocation': 'Lugar de recogida',
  'productCheckout.rental.orSimilar': '{{model}} o similar',
  'productCheckout.rental.daysNote_one': 'por {{count}} día',
  'productCheckout.rental.daysNote_other': 'por {{count}} días',
  'productCheckout.hotelStay': 'Fechas de estadía',
  'productCheckout.hotel.head': 'Alojamiento',
  'productCheckout.hotel.distanceFrom': '{{km}} km de {{place}}',
  'productCheckout.hotel.details': 'Detalles',
  'productCheckout.hotel.nightsNote_one': 'por {{count}} noche',
  'productCheckout.hotel.nightsNote_other': 'por {{count}} noches',
  'productCheckout.genderMale': 'Masculino',
  'productCheckout.genderFemale': 'Femenino',
  'productCheckout.documentMatchWarning': 'Los datos deben coincidir con el documento de viaje.',
  'productCheckout.motiveTitle': '¿Cuál es el motivo del viaje?',
  'productCheckout.mobility.motiveTitle': '¿Cuál es el motivo del traslado?',
  'productCheckout.mobility.blockerMotive': 'motivo del traslado',
  'productCheckout.fieldJustificationOptional': 'Justificación (opcional)',
  'productCheckout.fieldProjectOptional': 'Proyecto (opcional)',
  'productCheckout.budget.sufficientIn': 'Límite suficiente en {{costCenter}} para esta solicitud.',
  'productCheckout.budget.warning': 'Atención. El límite está por agotarse.',
  'productCheckout.budget.sufficient': 'Límite suficiente para la solicitud.',
  'rental.priceChange.continue': 'Continuar de todos modos',
  'rental.priceChange.cancel': 'Cancelar',
  'rentalPriceChange.replyContinue': 'Quiero continuar aun con el nuevo precio.',
  'rentalPriceChange.replyContinueChallenge': 'Quiero continuar con el nuevo precio.',
  'rentalPriceChange.replyCancel': 'Prefiero cancelar.',
  'booking.summaryAria': 'Resumen de la reserva',
  'booking.inPolicy': 'Dentro de la política',
  'booking.continuePurchase': 'Continuar compra',
  'booking.yourReservation': 'Tu reserva',
  'booking.fareClass': 'Clase',
  'booking.priceMayChange':
    'Los precios pueden variar entre la solicitud y la aprobación del viaje.',
  'booking.submitting': 'Enviando…',
  'booking.submit': 'Solicitar viaje',
  'approval.requestAria': 'Aprobación de voucher',
  'approval.resultAria': 'Resultado de aprobación',
  'approval.bulkResultAria': 'Resultado de aprobación en lote',
  'approval.pendingAria': 'Aprobaciones pendientes',
  'approval.voucher': 'Voucher',
  'approval.modal': 'Servicio',
  'approval.traveler': 'Viajero',
  'approval.approver': 'Aprobador',
  'approval.justificationOptional': 'Justificación (opcional)',
  'approval.reasonDecision': 'Motivo de la decisión…',
  'approval.reasonDeny': 'Motivo del rechazo…',
  'approval.sent': 'Decisión enviada',
  'approval.approve': 'Aprobar',
  'approval.deny': 'Rechazar',
  'approval.approved': 'Aprobado',
  'approval.denied': 'Rechazado',
  'approval.publishFailed': '{{label}} — error al publicar',
  'approval.failures': 'Errores',
  'approval.bulkSummary_one': '{{approved}} de {{total}} aprobado',
  'approval.bulkSummary_other': '{{approved}} de {{total}} aprobados',
  'approval.loose': 'Sueltos',
  'approval.tripFallback': 'Viaje',
  'approval.approveTrip': 'Aprobar viaje',
  'approval.approveTraveler': 'Aprobar del viajero',
  'approval.approveAll': 'Aprobar todo',
  'approval.textApproveVoucher': 'Aprobar voucher.',
  'approval.textDenyVoucher': 'Rechazar voucher.',
  'approval.textApproveTrip': 'Aprobar el viaje {{name}}.',
  'approval.textApproveTraveler': 'Aprobar todo de {{name}}.',
  'approval.textApproveAll': 'Aprobar todas las pendientes.',
  // ── checkout corporativo ──
  'bookingCheckout.select': 'Seleccionar',
  // ── detalle del vuelo ──
  'flightDetail.title': 'Detalle del vuelo',
  'flightDetail.drawerAria': 'Detalle del vuelo',
  'flightDetail.backAria': 'Volver',
  'flightDetail.closeAria': 'Cerrar',
  'flightDetail.loading': 'Cargando detalle del vuelo…',
  'flightDetail.loadError': 'No se pudo cargar el detalle del vuelo.',
  'flightDetail.retry': 'Reintentar',
  'flightDetail.select': 'Seleccionar vuelo',
  'flightDetail.stop': 'Parada de {{duration}}',
  'flightDetail.aircraftChange': 'cambio de aeronave',
  'flightDetail.flightDuration': '{{duration}} de vuelo',
  'flightDetail.totalDuration': '{{duration}} en total',
  'flightDetail.connection': '{{duration}} de conexión en {{airport}}',
  'flightDetail.continuesOn': 'continúa en el vuelo {{flight}}',
  'flightDetail.terminal': 'Terminal {{terminal}}',
  'flightDetail.onTimeStat': 'Puntual en el {{percent}}% de los últimos {{count}} vuelos',
  'flightDetail.soldAs': 'También vendido como {{flights}}',
  'flightDetail.partnerDataNote':
    'Aeronave, terminal, puntualidad y códigos compartidos: datos de socios, sujetos a cambios.',
  'flightDetail.operatedBy': 'Operado por {{name}}',
  'flightDetail.dirOutbound': 'Ida',
  'flightDetail.dirReturn': 'Vuelta',
  // ── mapa de asientos (drawer de vuelo) ──
  'flightSeatMap.sectionTitle': 'Mapa de asientos',
  'flightSeatMap.stripLabel': 'Asientos',
  'flightSeatMap.stripOpenAria': 'Ver mapa de asientos de {{route}}',
  'flightSeatMap.stripRetryAria': 'Intentar cargar el mapa de asientos de {{route}} de nuevo',
  'flightSeatMap.retry': 'Intentar de nuevo',
  'flightSeatMap.available_one': '1 asiento libre',
  'flightSeatMap.available_other': '{{count}} asientos libres',
  'flightSeatMap.paidFrom': 'de pago desde {{price}}',
  'flightSeatMap.freeFromRow': 'gratis desde la fila {{row}}',
  'flightSeatMap.legendFree': 'Gratis',
  'flightSeatMap.legendPaid': 'De pago',
  'flightSeatMap.legendExtra': 'Espacio extra',
  'flightSeatMap.legendOccupied': 'Ocupado',
  'flightSeatMap.legendUnavailable': 'No disponible',
  'flightSeatMap.exitRow': 'Salida de emergencia',
  'flightSeatMap.rowAria': 'Fila {{row}}',
  'flightSeatMap.loading': 'Cargando mapa de asientos…',
  'flightSeatMap.error': 'No se pudo cargar el mapa de asientos.',
  'flightSeatMap.unavailable': 'Mapa de asientos no disponible para este vuelo.',
  'flightSeatMap.supplierUnavailable':
    'El mapa de asientos no está disponible para consulta antes de la compra.',
  'flightSeatMap.errorFinal': 'Mapa de asientos no disponible en este momento.',
  'flightSeatMap.stripUnavailableAria': 'Mapa de asientos de {{route}} no disponible',
  'flightSeatMap.quoteExpired': 'El mapa de asientos ya no está disponible para esta cotización.',
  'flightSeatMap.disclaimer':
    'Lectura, no elección · el precio y la elección del asiento ocurren en la emisión.',
  // ── checkout de alquiler ──
  'productCheckout.closeAria': 'Cerrar',
  'productCheckout.back': 'Volver',
  'productCheckout.refundable': 'Reembolsable',
  'productCheckout.nonRefundable': 'Sin reembolso',
  'productCheckout.drawerTitle': 'Revisar y solicitar',
  'productCheckout.agentLine':
    'Adelanté tu checkout con lo que ya sé. Revisa y completa lo que falte.',
  'productCheckout.inheritedHint': 'Heredado de tu perfil — solo lo confirmo contigo por garantía.',
  'productCheckout.inheritedHintOther':
    'Heredado del perfil del viajero — solo lo confirmo contigo por garantía.',
  'productCheckout.submitting': 'Solicitando',
  'productCheckout.retry': 'Intentar de nuevo',
  'productCheckout.action.submitWithTotal': 'Solicitar {{product}} · {{total}}',
  'productCheckout.price.approvalNote': 'Va a aprobación. El precio puede variar hasta la emisión.',
  'productCheckout.trip.sectionTitle': 'Tu viaje',
  'productCheckout.trip.title': 'Viaje a {{city}}',
  'productCheckout.trip.alreadyBooked': 'Ya en el viaje',
  'productCheckout.trip.beingRequested': 'En esta compra',
  'productCheckout.bus.seats': 'Asientos',
  'productCheckout.bus.seat_one': 'Asiento {{seats}}',
  'productCheckout.bus.totalRoundTrip': 'ida y vuelta, por persona',
  'productCheckout.bus.totalOneWay': 'solo ida, por persona',
  'productCheckout.bus.seat_other': 'Asientos {{seats}}',
  'productCheckout.bus.segment': 'Tramo {{n}}',
  'productCheckout.trip.nights_one': '{{count}} noche',
  'productCheckout.trip.nights_other': '{{count}} noches',
  'conciergeCheckout.title': 'Revisar y solicitar al equipo VOLL',
  'conciergeCheckout.chip.flight': 'Aéreo offline',
  'conciergeCheckout.chip.hotel': 'Hotel offline',
  'conciergeCheckout.chip.bus': 'Autobús offline',
  'conciergeCheckout.chip.rental': 'Auto offline',
  'conciergeCheckout.submit': 'Solicitar al equipo VOLL',
  'conciergeCheckout.back': 'Volver',
  'conciergeCheckout.optional': 'opcional',
  'conciergeCheckout.noPriceBadge': 'Sin valor · a cotizar',
  'conciergeCheckout.profileLabel': 'Perfil heredado de la búsqueda',
  'conciergeCheckout.profileHint':
    'Heredado de la búsqueda. Para cambiarlo, vuelve a los resultados.',
  'conciergeCheckout.reasonTitle': 'Por qué pedir al equipo',
  'conciergeCheckout.reasonLoading': 'Cargando motivos…',
  'conciergeCheckout.detailsLabel': 'Observación para el equipo',
  'conciergeCheckout.flight.cardTitle': 'Pedido al equipo VOLL · aéreo',
  'conciergeCheckout.flight.asksTitle': 'Qué necesitas en este vuelo',
  'conciergeCheckout.flight.direct': 'Solo vuelo directo',
  'conciergeCheckout.flight.luggage': 'Solo con equipaje',
  'conciergeCheckout.flight.periodFor': 'Período · {{route}} · {{date}}',
  'conciergeCheckout.flight.periodAny': 'Indiferente',
  'conciergeCheckout.flight.detailsPlaceholder':
    'Ej.: la reunión termina a las 17h en Alphaville; cualquier cosa desde las 19h sirve, GRU también.',
  'conciergeCheckout.period.morning': 'Mañana',
  'conciergeCheckout.period.afternoon': 'Tarde',
  'conciergeCheckout.period.night': 'Noche',
  'conciergeCheckout.period.dawn': 'Madrugada',
  'conciergeCheckout.hotel.cardTitle': 'Pedido al equipo VOLL · alojamiento',
  'conciergeCheckout.hotel.preferredHotel': 'Hotel preferido (opcional)',
  'conciergeCheckout.hotel.preferredHotelPlaceholder':
    'Nombre del hotel, si ya tienes uno en mente',
  'conciergeCheckout.hotel.bedType': 'Tipo de cama',
  'conciergeCheckout.hotel.bed.UNDEFINED': 'Indiferente',
  'conciergeCheckout.hotel.bed.SINGLE': 'Individual',
  'conciergeCheckout.hotel.bed.DOUBLE': 'Matrimonial',
  'conciergeCheckout.hotel.detailsPlaceholder':
    'Ej.: reunión en Faria Lima a las 9h, desayuno incluido ayuda; el cliente sugirió el Unique.',
  'conciergeCheckout.hotel.detailsHint': 'El equipo usa este texto como briefing de la cotización.',
  'conciergeCheckout.panel.inProgress': 'En construcción',
  'conciergeCheckout.panel.flightItem': '{{route}} · pedido al equipo',
  'conciergeCheckout.panel.hotelItem': 'Hotel · pedido al equipo',
  'conciergeCheckout.panel.toQuote': 'a cotizar',
  'conciergeCheckout.panel.requested': 'Solicitado',
  'conciergeCheckout.panel.heldOutbound': 'Guardada en el pedido · no emitida ahora',
  'conciergeCheckout.panel.totalLabel': 'Total del viaje',
  'conciergeCheckout.panel.totalToQuote': 'Sin valor hasta la cotización',
  'conciergeCheckout.panel.totalWithHeld': '{{held}} + tramo a cotizar',
  'conciergeCheckout.panel.flightNote':
    'Nada se emite ahora. El equipo cotiza el tramo y apruebas todo junto cuando la cotización llegue a la conversación del viaje.',
  'conciergeCheckout.panel.hotelNote':
    'El hotel entra en el itinerario sin valor y gana precio cuando llegue la cotización. La política y el tope ya viajan en el pedido.',
  'conciergeCheckout.panel.busItem': '{{route}} · pedido al equipo',
  'conciergeCheckout.panel.busNote':
    'Nada se emite ahora. El equipo cotiza el pasaje y lo apruebas cuando la cotización llegue a la conversación del viaje.',
  'conciergeCheckout.bus.cardTitle': 'Pedido al equipo VOLL · autobús',
  'conciergeCheckout.bus.asksTitle': 'Cómo quieres viajar',
  'conciergeCheckout.bus.choose': 'Selecciona',
  'conciergeCheckout.bus.periodFor': 'Período · {{leg}} · {{date}}',
  'conciergeCheckout.bus.leg.outbound': 'Ida',
  'conciergeCheckout.bus.leg.return': 'Vuelta',
  'conciergeCheckout.bus.seatPosition': 'Posición en el autobús',
  'conciergeCheckout.bus.seatPosition.FRONT': 'Adelante',
  'conciergeCheckout.bus.seatPosition.MIDDLE': 'Medio',
  'conciergeCheckout.bus.seatPosition.BACK': 'Atrás',
  'conciergeCheckout.bus.rowPosition': 'Posición en la fila',
  'conciergeCheckout.bus.rowPosition.WINDOW_SEAT': 'Ventanilla',
  'conciergeCheckout.bus.rowPosition.AISLE_SEAT': 'Pasillo',
  'conciergeCheckout.bus.seatClass': 'Clase',
  'conciergeCheckout.bus.seatClass.ALL': 'Todas',
  'conciergeCheckout.bus.seatClass.CONVENTIONAL': 'Convencional',
  'conciergeCheckout.bus.seatClass.EXECUTIVE': 'Ejecutivo',
  'conciergeCheckout.bus.seatClass.HALF_BED': 'Semicama',
  'conciergeCheckout.bus.seatClass.BED': 'Cama',
  'conciergeCheckout.bus.seatClass.BED_FULL': 'Cama total',
  'conciergeCheckout.bus.detailsPlaceholder':
    'Ej.: más de un tramo, horario de preferencia, presupuesto disponible, seguro de viaje.',
  'conciergeCheckout.bus.detailsHint':
    'El equipo usa este texto para cotizar, y quien aprueba el viaje también lo lee.',
  'conciergeCheckout.rental.cardTitle': 'Pedido al equipo VOLL · auto',
  'conciergeCheckout.rental.category': 'Categoría del auto',
  'conciergeCheckout.rental.company': 'Rentadora',
  'conciergeCheckout.rental.companyAny': 'Indiferente',
  'conciergeCheckout.rental.optionsLoading': 'Cargando categorías y rentadoras…',
  'conciergeCheckout.rental.optionsFailed':
    'No fue posible cargar las categorías de auto. Sin ellas el pedido no se puede enviar.',
  'conciergeCheckout.rental.retry': 'Intentar de nuevo',
  'conciergeCheckout.rental.detailsPlaceholder':
    'Ej.: silla infantil, segundo conductor, retiro directo en el desembarque.',
  'conciergeCheckout.rental.detailsHint':
    'El equipo usa este texto como brief de la cotización: extras, conductor adicional, agencia.',
  'conciergeCheckout.panel.rentalItem': 'Auto · pedido al equipo',
  'conciergeCheckout.panel.rentalNote':
    'El auto entra en el itinerario sin valor y gana precio cuando llegue la cotización. La política de la categoría ya viaja en el pedido.',
  'productCheckout.noun.flight': 'vuelo',
  'productCheckout.noun.hotel': 'hotel',
  'productCheckout.noun.bus': 'autobús',
  'productCheckout.noun.rental': 'auto',
  'productCheckout.noun.mobility': 'traslado',
  'productCheckout.chipLabel.flight': 'Aéreo',
  'productCheckout.chipLabel.hotel': 'Hotel',
  'productCheckout.chipLabel.bus': 'Autobús',
  'productCheckout.chipLabel.rental': 'Auto',
  'productCheckout.chipLabel.mobility': 'Movilidad',
  'productCheckout.loadingAria': 'Cargando reserva',
  'productCheckout.loading': 'Cargando reserva…',
  'productCheckout.errorMessage': 'No pudimos cargar tus datos para la reserva. No se envió nada.',
  'productCheckout.fieldCompany': 'Empresa',
  'productCheckout.fieldCostCenter': 'Centro de costo',
  'productCheckout.fieldMotive': 'Motivo',
  'productCheckout.fieldProject': 'Proyecto',
  'productCheckout.fieldJustification': 'Justificación',
  'productCheckout.selectPlaceholder': 'Seleccionar',
  'productCheckout.motiveSearchPlaceholder': 'Buscar motivo',
  'productCheckout.motiveSearchEmpty': 'Ningún motivo encontrado',
  'productCheckout.companySearchEmpty': 'No se encontró ninguna empresa',
  'productCheckout.projectSearchEmpty': 'No se encontró ningún proyecto',
  'productCheckout.costCenterSearchPrompt': 'Escribe para buscar',
  'productCheckout.costCenterSearchLoading': 'Buscando…',
  'productCheckout.costCenterSearchEmpty': 'No se encontró ningún centro de costo',
  'productCheckout.split.percent': 'Porcentaje',
  'productCheckout.split.add': 'Añadir centro de costo',
  'productCheckout.split.remove': 'Quitar centro de costo',
  'productCheckout.split.row': 'Centro de costo {{position}}',
  'productCheckout.split.approvalFlow': '¿Cuál va a ser el flujo de aprobación?',
  'productCheckout.split.approvalFlowPhrase1':
    'El flujo de aprobación se determina por el porcentaje del prorrateo informado para cada centro de costo.',
  'productCheckout.split.approvalFlowPhrase2':
    'Siempre se usa el flujo de aprobación del centro de costo con mayor porcentaje, salvo cuando haya un flujo específico para el usuario.',
  'productCheckout.split.approvalFlowPhrase3':
    'Cuando los mayores porcentajes son iguales, se usa el flujo de aprobación del primer centro de costo informado con el mayor porcentaje.',
  'productCheckout.split.zeroPercent': 'Cada centro de costo necesita más del 0%.',
  'productCheckout.split.sum': 'La suma de los centros de costo debe ser igual al 100%.',
  'productCheckout.split.repeated':
    'El mismo centro de costo no puede aparecer dos veces en el prorrateo.',
  'rental.card.traveler': 'Viajero',
  'productCheckout.card.price': 'Costo del viaje',
  'rental.field.firstName': 'Nombre',
  'rental.field.lastName': 'Apellido',
  'productCheckout.field.birthdate': 'Fecha de nacimiento',
  'productCheckout.field.datePick': 'Elegir',
  'productCheckout.field.dateConfirm': 'Confirmar',
  'productCheckout.field.dateBack': 'Volver',
  'productCheckout.field.datePrevMonth': 'Mes anterior',
  'productCheckout.field.dateNextMonth': 'Mes siguiente',
  'productCheckout.field.nationality': 'Nacionalidad',
  'rental.field.gender': 'Género',
  'productCheckout.field.document': 'Documento',
  'productCheckout.field.cpf': 'CPF',
  'productCheckout.field.passport': 'Pasaporte',
  'productCheckout.field.nationalDoc': 'Documento Nacional',
  'productCheckout.field.documentExpiry': 'Validez',
  'productCheckout.field.issuingCountry': 'País emisor',
  'productCheckout.field.nationalityPlaceholder': 'Selecciona el país',
  'productCheckout.field.countrySearch': 'Buscar país',
  'productCheckout.field.countryEmpty': 'Ningún país encontrado',
  'productCheckout.field.countryUnavailable':
    'Lista de países no disponible ahora. El valor guardado queda como está.',
  'productCheckout.field.countryOutsideCatalog': 'guardado en el perfil',
  'productCheckout.field.countryRetry': 'Intentar de nuevo',
  'productCheckout.field.internalNotes': 'Observación para el aprobador',
  'productCheckout.field.internalNotesPlaceholder':
    'Contexto que ayuda a quien aprueba a decidir sin preguntarte.',
  'productCheckout.field.gender': 'Sexo (como en el documento)',
  'productCheckout.field.genderNote':
    'Algunas aerolíneas lo exigen y pueden usarlo para incluir MR (Sr.) o MS/MRS (Sra.) en su nombre.',
  'productCheckout.field.genderWhy': '¿Por qué?',
  'productCheckout.field.genderFemale': 'Femenino',
  'productCheckout.field.genderMale': 'Masculino',
  'productCheckout.field.boardingName': 'Nombre',
  'productCheckout.field.boardingSurname': 'Apellido',
  'productCheckout.field.phone': 'Teléfono',
  'rental.pickup.label': 'Recogida',
  'rental.dropoff.label': 'Devolución',
  'rental.pickup.from': 'A partir de',
  'rental.dropoff.until': 'Hasta',
  'productCheckout.price.dailies_one': '{{count}} día',
  'productCheckout.price.dailies_other': '{{count}} días',
  'productCheckout.price.eachDaily': '{{amount}} cada',
  'productCheckout.price.perTraveler': 'Valor por persona',
  'productCheckout.price.tripTotal': 'Total del viaje',
  'productCheckout.price.total': 'Total',
  'productCheckout.price.note': 'Los precios pueden variar hasta la confirmación.',
  'productCheckout.action.edit': 'Editar',
  'productCheckout.action.done': 'Listo',
  'productCheckout.action.submit': 'Solicitar viaje',
  'productCheckout.dock.submit': 'Solicitar',
  'productCheckout.budget.insufficient': 'Límite insuficiente',
  'productCheckout.budget.available': 'Saldo disponible',
  'rental.budget.checkError': 'No fue posible verificar el presupuesto',
  'rental.budget.overLimit': 'Por encima del presupuesto',
  'rental.budget.withinLimit': 'Dentro del límite',
  'productCheckout.hint.outOfPolicy': 'Fuera de política, justificación obligatoria',
  'productCheckout.hint.outOfPolicyReasons':
    'Fuera de la política ({{reasons}}). Justificación obligatoria.',
  'productCheckout.reason.bookingWindow_one':
    'anticipación mínima de {{count}} día para viajes {{scope}}',
  'productCheckout.reason.bookingWindow_other':
    'anticipación mínima de {{count}} días para viajes {{scope}}',
  'productCheckout.blockers': 'Para solicitar, completa: {{items}}',
  'productCheckout.blocker.birthdate': 'fecha de nacimiento',
  'productCheckout.blocker.nationality': 'nacionalidad',
  'productCheckout.blocker.gender': 'sexo',
  'productCheckout.blocker.document': 'documento',
  'productCheckout.blocker.embark': 'nombre de embarque',
  'productCheckout.blocker.phone': 'teléfono del viajero',
  'productCheckout.blocker.allocation': 'empresa y centro de costo',
  'productCheckout.blocker.project': 'proyecto',
  'productCheckout.blocker.motive': 'motivo del viaje',
  'productCheckout.blocker.justification': 'justificación',
  'productCheckout.blocker.travelDocs': 'confirmación de los documentos del viaje',
  'productCheckout.phonePending': 'Teléfono pendiente',
  'productCheckout.hint.noCorporateAccess':
    'Este viajero no tiene centro de costo corporativo — no se puede reservar para él.',
  'productCheckout.hint.noCostCenters':
    'No hay ningún centro de costo disponible para tu perfil en esta empresa — habla con el gestor de tu empresa.',
  'productCheckout.hint.allocationFromRequester':
    'El reparto de costos del solicitante se aplica a este huésped.',
  'productCheckout.hint.allocationFromGuest':
    'El reparto de costos de {{name}} se aplica a este huésped.',
  'rental.hint.documentMatch': 'Los datos deben ser idénticos al documento de viaje',
  'rental.hint.genderFemale': 'Femenino',
  'rental.hint.genderMale': 'Masculino',
  'rental.hint.cpfReadOnly': 'Completado automáticamente por el sistema',
  'productCheckout.hint.documentOnFile': 'En sistema: {{value}}',
  'productCheckout.hint.editInProfile':
    'Para cambiarlo, habla con el gestor de viajes de tu empresa.',
  'hotelPlanner.datesPlaceholder': 'Check-in y check-out',
  'hotelPlanner.policyHint': 'Tu política de viaje',
  'hotelPlanner.noPlaces': 'Ningún destino',
  'hotelPlanner.placeError': 'No se pudieron buscar destinos. Inténtalo de nuevo.',
  'hotelPlanner.noResultsTitle': 'No se encontraron hoteles',
  'hotelPlanner.noResults': 'Prueba otras fechas, otro destino o quita los filtros.',
  'hotelPlanner.providerErrorTitle': 'No se pudieron buscar hoteles',
  'hotelPlanner.providerError':
    'Falló el proveedor de tarifas. Inténtalo de nuevo en unos instantes o ajusta el destino y las fechas.',
  'hotelPlanner.timeoutErrorTitle': 'La búsqueda tardó demasiado',
  'hotelPlanner.timeoutError':
    'El proveedor no respondió a tiempo. Inténtalo de nuevo — si se repite, acorta la estadía o cambia el destino.',
  'hotelPlanner.search': 'Buscar',
  'hotelPlanner.searching': 'Buscando hoteles',
  'hotelPlanner.contextPending':
    'Completa los datos a continuación para encontrar las mejores opciones de hospedaje para ti:',
  'hotelPlanner.contextSearching':
    'Estoy buscando las mejores opciones de hospedaje para tu período…',
  'hotelPlanner.contextReady':
    '¡Todo listo! Mapeé las mejores opciones de hospedaje recomendadas para tu período:',
  'hotelPlanner.contextEmpty': 'No encontré hoteles con esos criterios. Ajusta la búsqueda:',
  'hotelPlanner.searchText': 'Buscar hoteles en {{place}} — {{dates}}.',
  'hotelPlanner.retry': 'Intentar de nuevo',
  'hotelPlanner.adjustSearch': 'Ajustar búsqueda',
  'hotelPlanner.rooms': 'Habitaciones',
  'hotelPlanner.prevMonth': 'Mes anterior',
  'hotelPlanner.nextMonth': 'Mes siguiente',
  'hotelPlanner.roomN': 'Habitación {{n}}',
  'hotelPlanner.removeRoom': 'Eliminar habitación {{n}}',
  'hotelPlanner.remove': 'Eliminar',
  'hotelPlanner.addRoom': 'Añadir habitación',
  'hotelPlanner.chooseGuest': 'Elegir huésped',
  'hotelPlanner.addGuest': 'Acompañante',
  'hotelPlanner.addGuestAria': 'Agregar huésped — habitación {{n}}',
  'hotelPlanner.backToRooms': 'Volver a las habitaciones',
  'hotelPlanner.roomGuestHeading': 'Habitación {{n}} · huésped',
  'hotelPlanner.roomCompanionHeading': 'Habitación {{n}} · acompañante',
  'hotelPlanner.peopleList': 'Personas',
  'hotelPlanner.noPeople': 'Nadie encontrado.',
  'hotelPlanner.roomNameNote': 'La reserva de cada habitación va a nombre del primer huésped.',
  'hotelPlanner.guestInRoom': 'Ya en la habitación {{n}}',
  'hotelPlanner.holderBadge': 'Titular',
  'hotelPlanner.guestOptions': 'Opciones de {{name}}',
  'hotelPlanner.makeHolder': 'Hacer titular de la habitación',
  'hotelPlanner.swapGuest': 'Cambiar huésped',
  'hotelPlanner.removeFromRoom': 'Quitar de la habitación',
  'hotelPlanner.guestRole': 'huésped',
  'hotelPlanner.whereLabel': 'Dónde',
  'hotelPlanner.whenLabel': 'Cuándo',
  'hotelPlanner.whoLabel': 'Quién',
  'hotelPlanner.destination': 'Destino',
  'hotelPlanner.destinationPlaceholder': 'Ciudad o referencia',
  'hotelPlanner.myLocation': 'Mi ubicación',
  'hotelPlanner.myLocationError': 'No pudimos usar tu ubicación',
  'hotelPlanner.recentPlaces': 'Recientes',
  'hotelPlanner.favoritePlaces': 'Lugares favoritos',
  'hotelPlanner.placeType.city': 'Ciudad',
  'hotelPlanner.placeType.neighborhood': 'Barrio',
  'hotelPlanner.placeType.address': 'Dirección',
  'hotelPlanner.placeType.poi': 'Punto de interés',
  'hotelPlanner.placeType.lodging': 'Hotel',
  'hotelPlanner.placeType.airport': 'Aeropuerto',
  'hotelPlanner.checkInOut': 'Check-in/out',
  'hotelPlanner.datesTBD': 'Por definir',
  'hotelPlanner.chooseStayDates': 'Elige la entrada y la salida',
  'hotelPlanner.missingDatesAlert':
    'Selecciona las fechas para ver las habitaciones y los precios disponibles',
  'hotelPlanner.stayBack': 'Volver',
  'hotelPlanner.chooseCheckout': '{{date}} · elige la salida',
  'hotelPlanner.roomCount_one': '{{count}} habitación',
  'hotelPlanner.roomCount_other': '{{count}} habitaciones',
  'hotelPlanner.guestCount_one': '{{count}} huésped',
  'hotelPlanner.guestCount_other': '{{count}} huéspedes',
  'hotelPlanner.nights_one': '{{count}} noche',
  'hotelPlanner.nights_other': '{{count}} noches',
  'hotelPlanner.maxStayNights': 'máximo de {{count}} noches',
  'hotelPlanner.confirmDates': 'Confirmar {{range}}',
  'hotelPlanner.confirmDate': 'Confirmar fecha',
  'hotelPlanner.confirmStayDates': 'Confirmar fechas',
  'hotelPlanner.confirmAndSearch': 'Confirmar y buscar',
  'hotelPlanner.resetDates': 'Restablecer',
  'hotelPlanner.maxRooms': 'Máximo de {{n}} habitaciones',
  'hotelPlanner.maxGuests': 'Máximo de {{n}} huéspedes',
  'hotel.resultsListAria': 'Hoteles encontrados',
  'hotel.results.previous': 'Hoteles anteriores',
  'hotel.results.next': 'Hoteles siguientes',
  'hotel.recommended': 'Sugerido VOLL',
  'hotel.badge.recommended': 'Recomendado',
  'hotel.badge.cheapest': 'Menor precio',
  'hotel.badge.closest': 'Más cerca',
  'hotel.badge.cheapestOnCard': 'Menor precio en la tarjeta',
  'hotel.badge.closestOnCard': 'Más cerca en la tarjeta',
  'hotel.badge.stayedBefore': 'Ya te hospedaste aquí',
  'hotel.badge.stayedBeforeCount': 'Ya te hospedaste aquí · {{count}}×',
  'hotel.badge.favorite': 'Favorito',
  'hotel.badge.agentPick': 'Sugerencia del agente',
  'hotel.badge.agreedRate': 'Hotel con convenio',
  'hotel.favorite.mark': 'Marcar hotel como favorito',
  'hotel.favorite.unmark': 'Quitar favorito',
  'hotel.card.freeCancel': 'Cancelación gratis',
  'hotel.card.distance': '{{km}} km',
  'hotel.card.minutes': '{{count}} min',
  'hotel.card.byFoot': 'a pie',
  'hotel.card.byCar': 'en auto',
  'hotel.card.perNight': '/noche',
  'hotel.card.refundable': 'Reembolsable',
  'hotel.card.refundableFrom': 'Reembolsable desde {{price}}',
  'hotel.photoPrev': 'Foto anterior',
  'hotel.photoNext': 'Foto siguiente',
  'hotel.inPolicy': 'Dentro de la política',
  'hotel.outPolicy': 'Fuera de la política',
  'hotel.policyRule.bookingWindow': 'Antelación mínima no cumplida',
  'hotel.policyRule.bookingWindowWithLimit': 'Antelación mínima de {{value}} días',
  'hotel.policyRule.bookingWindowInternational': 'Antelación mínima internacional no cumplida',
  'hotel.policyRule.bookingWindowInternationalWithLimit':
    'Antelación mínima internacional de {{value}} días',
  'hotel.policyRule.cheapestPrice': 'No es la tarifa más barata del hotel',
  'hotel.policyRule.cheapestHotel': 'No es el hotel más barato de la búsqueda',
  'hotel.policyRule.maxPrice': 'Por encima del tope por noche',
  'hotel.policyRule.maxPriceWithLimit': 'Por encima del tope de {{value}} por noche',
  'hotel.policyRule.maxPriceInternational': 'Por encima del tope internacional por noche',
  'hotel.policyRule.maxPriceInternationalWithLimit':
    'Por encima del tope internacional de {{value}} por noche',
  'hotel.policyRule.maxPricePerCity': 'Por encima del tope de la ciudad',
  'hotel.policyRule.maxPricePerCityWithLimit':
    'Por encima del tope de la ciudad ({{value}} por noche)',
  'hotel.policyRule.maxPricePerCountry': 'Por encima del tope del país',
  'hotel.policyRule.onlyRateWithBreakfast': 'Tarifa sin desayuno (la política exige desayuno)',
  'hotel.policyRule.onlyRateRefundable': 'Tarifa no reembolsable (la política exige reembolsable)',
  'hotelRooms.policyCeilingAbove': 'Por encima de {{value}}',
  'hotelRooms.policyCeilingHint': 'Tope por noche de la política de tu empresa en esta búsqueda.',
  'hotel.breakfast': 'Desayuno incluido',
  'hotel.priceUnavailable': 'Precio por confirmar',
  'hotel.soldOut': 'Sin disponibilidad para estas fechas',
  'hotel.total': 'Total',
  'hotel.perNight': 'Diaria {{price}}',
  'hotel.rating': 'calificación {{rating}}',
  'hotel.category': '{{category}} estrellas',
  'hotel.night_one': '{{count}} diaria',
  'hotel.night_other': '{{count}} diarias',
  'hotel.room': 'Habitación',
  'hotel.roomsTitle': 'Mejores habitaciones',
  'hotel.gallery.prev': 'Foto anterior',
  'hotel.gallery.next': 'Foto siguiente',
  'hotel.gallery.photo': 'Foto {{position}}',
  'hotel.map.aria': 'Mapa de hoteles',
  'hotel.map.pinAria': '{{title}}, {{price}}',
  'hotel.map.seeHotel': 'Ver hotel',
  'hotel.map.expand': 'Ampliar el mapa',
  'hotel.map.zoomIn': 'Acercar',
  'hotel.map.zoomOut': 'Alejar',
  'hotel.map.nightsFor_one': '{{count}} noche por',
  'hotel.map.nightsFor_other': '{{count}} noches por',
  'hotel.map.recenter': 'Reencuadrar el mapa',
  'hotel.map.previewClose': 'Cerrar la vista previa del hotel',
  'hotel.results.chips.toolbar': 'Filtros y orden',
  'hotel.results.chips.priceUpTo': 'Hasta {{price}}',
  'hotel.results.chips.priceFrom': 'Desde {{price}}',
  'hotel.results.chips.priceBetween': '{{min}} – {{max}}',
  'hotel.results.seeAll': 'Ver todos los hoteles',
  'hotel.results.sort': 'Ordenar',
  'hotel.results.sort.recommended': 'Recomendado',
  'hotel.results.sort.price': 'Precio',
  'hotel.results.sort.distance': 'Distancia',
  'hotel.results.sort.reviews': 'Reseñas',
  'hotel.results.sort.stars': 'Calificación',
  'hotel.results.title': 'Hoteles',
  'hotel.results.pageTitle': 'Hoteles en {{place}}',
  'hotel.results.pageTitleNear': 'Hoteles cerca de {{place}} · {{locality}}',
  'hotel.results.pageTitleMapArea': 'Hoteles en el área del mapa',
  'hotel.results.pageContext_one': '{{count}} noche',
  'hotel.results.pageContext_other': '{{count}} noches',
  'hotel.results.close': 'Cerrar hoteles',
  'hotel.results.pillAria': 'Contexto de la búsqueda',
  'hotel.results.searchAgain': 'Buscar de nuevo',
  'hotel.results.cancelEdit': 'Cancelar',
  'hotel.results.total_one': '{{count}} hotel',
  'hotel.results.total_other': '{{count}} hoteles',
  'hotel.results.visibleOfTotal_one': '{{visible}} hotel de {{total}}',
  'hotel.results.visibleOfTotal_other': '{{visible}} hoteles de {{total}}',
  'hotel.results.askPlaceholder': 'Más cerca, con desayuno…',
  'hotel.results.back': 'Volver',
  'hotel.results.filters': 'Filtros',
  'hotel.results.collapseFilters': 'Ocultar filtros',
  'hotel.results.closeFilters': 'Cerrar filtros',
  'hotel.results.applyFilters': 'Aplicar filtros',
  'hotel.results.expandFilters': 'Mostrar filtros',
  'hotel.results.showMap': 'Mapa',
  'hotel.results.share': 'Compartir la búsqueda',
  'searchShare.copyLink': 'Copiar enlace',
  'searchShare.downloadPdf': 'Descargar PDF',
  'hotel.results.filteredEmpty': 'Ningún hotel coincide con esos filtros.',
  'hotel.results.clearFilters': 'Limpiar filtros',
  'hotel.results.showList': 'Lista',
  'hotel.results.loadMore': 'Cargar más hoteles',
  'hotel.results.offline.offerTitle': '¿Necesitas algo que no está aquí?',
  'hotel.results.offline.offerBody':
    'El equipo VOLL busca hoteles asociados — online y offline — que coincidan con tu perfil y devuelve la cotización en la conversación del viaje.',
  'hotel.results.loadingMore': 'Cargando más hoteles…',
  'hotel.results.allLoaded': 'Todos los hoteles cargados',
  'hotel.results.searchThisArea': 'Buscar en esta zona',
  'hotel.results.mapArea': 'Área del mapa',
  'hotel.results.mapAreaEmpty': 'Mueve el mapa y busca en esta área de nuevo.',
  'hotel.results.searchingThisArea': 'Buscando…',
  'hotel.results.loadingAll': 'Cargando todos los hoteles',
  'hotel.results.searching': 'Consultando disponibilidad y tarifas',
  'hotel.results.stillProcessing': 'Los hoteles todavía están respondiendo',
  'hotel.results.staleRetrying': 'Actualizando la búsqueda',
  'hotel.results.unavailableTitle': 'No se pudieron cargar todos los hoteles',
  'hotel.results.unavailable':
    'Los hoteles de abajo siguen disponibles. Inténtalo de nuevo en unos instantes para ver la lista completa.',
  'hotel.results.staleTitle': 'Esta búsqueda expiró',
  'hotel.results.stale':
    'El proveedor no guarda la lista completa por mucho tiempo. Busca de nuevo para ver todos los hoteles.',
  'hotel.results.staleAction': 'Buscar de nuevo',
  'hotel.results.resetFilters': 'Restablecer',
  'hotel.results.policy': 'Política',
  'hotel.results.withinPolicy': 'Dentro de la política',
  'hotel.results.rate': 'Tarifa',
  'hotel.results.breakfast': 'Desayuno',
  'hotel.results.refundable': 'Reembolsable',
  'hotel.results.agreedRate': 'Tarifa de convenio',
  'hotel.results.nightlyPrice': 'Tarifa por noche',
  'hotel.results.minimumPrice': 'Tarifa mínima por noche',
  'hotel.results.maximumPrice': 'Tarifa máxima por noche',
  'hotel.results.rating': 'Calificación',
  'hotel.results.distance': 'Distancia de la cita',
  'hotel.results.distanceUpTo': 'hasta {{distance}} km',
  'hotel.results.amenities': 'Comodidades',
  'hotel.results.send': 'Enviar',
  'hotel.results.visibleHotels_one': '{{count}} hotel visible en el mapa',
  'hotel.results.visibleHotels_other': '{{count}} hoteles visibles en el mapa',
  'hotel.amenity.wifi': 'Wi-Fi',
  'hotel.amenity.breakfast': 'Desayuno',
  'hotel.amenity.parking': 'Estacionamiento',
  'hotel.amenity.pool': 'Piscina',
  'hotel.amenity.gym': 'Gimnasio',
  'hotel.amenity.restaurant': 'Restaurante',
  'hotel.amenity.laundry': 'Lavandería',
  'hotel.board.roomOnly': 'Solo habitación',
  'hotel.board.breakfast': 'Desayuno',
  'hotel.board.noBreakfast': 'Sin desayuno',
  'hotel.board.continentalBreakfast': 'Desayuno continental',
  'hotel.board.breakfastFor': 'Desayuno para {{count}}',
  'hotel.board.halfBoard': 'Media pensión',
  'hotel.board.fullBoard': 'Pensión completa',
  'hotel.board.allInclusive': 'Todo incluido',
  'hotel.board.freeWifi': 'Wi-Fi gratis',
  'hotel.board.freeHighSpeedInternet': 'Internet de alta velocidad gratis',
  'hotel.board.freeSelfParking': 'Estacionamiento gratis sin valet',
  'hotel.board.freeValetParking': 'Estacionamiento con valet gratis',
  'hotelRooms.headerStay': '{{checkin}} – {{checkout}} · {{nights}}',
  'hotelRooms.headerOccupancy': '{{rooms}}, {{guests}}',
  'hotelRooms.reviews_one': '{{count}} opinión',
  'hotelRooms.reviews_other': '{{count}} opiniones',
  'hotelRooms.ratingLabel.high': 'Excelente',
  'hotelRooms.ratingLabel.mid': 'Bueno',
  'hotelRooms.ratingLabel.low': 'Razonable',
  'hotelRooms.negotiated': 'Tarifa negociada',
  'hotelRooms.negotiatedRoom': 'Habitación con tarifa negociada',
  'hotelRooms.lastUnits_one': 'Última {{count}}',
  'hotelRooms.lastUnits_other': 'Últimas {{count}}',
  'hotelRooms.otherRates': 'Otras tarifas',
  'hotelRooms.otherOptions': 'Otras opciones ({{count}})',
  'hotelRooms.rateTypeBreakfastRefundable': 'Con desayuno · reembolsable',
  'hotelRooms.rateTypeBreakfast': 'Con desayuno',
  'hotelRooms.rateTypeRefundable': 'Reembolsable',
  'hotelRooms.rateTypeRoomOnly': 'Sin desayuno',
  'hotelRooms.rateTypeOther': 'Otros tipos de tarifa',
  'hotelRooms.showAllRates': 'Comparar tarifas',
  'hotelRooms.seeMoreRates': 'Ver {{count}} más',
  'hotelRooms.seeLessRates': 'Ver menos',
  'hotelRooms.ratesAria': 'Tarifas de la habitación',
  'hotelRooms.perNight': '/ noche',
  'hotelRooms.nights_one': '{{count}} noche',
  'hotelRooms.nights_other': '{{count}} noches',
  'hotelRooms.totalNights': '{{nights}} · {{total}}',
  'hotelRooms.select': 'Seleccionar',
  'hotelRooms.selected': 'Seleccionado',
  'hotelRooms.selectedPrefix': 'Seleccionado:',
  'hotelRooms.chipBreakfast': 'desayuno',
  'hotelRooms.chipNoBreakfast': 'sin desayuno',
  'hotelRooms.chipRefundable': 'reembolso',
  'hotelRooms.chipNonRefundable': 'sin reembolso',
  'hotelRooms.freeCancelUntil': 'Cancelación gratis hasta {{date}}',
  'hotelRooms.refundable': 'Reembolsable',
  'hotelRooms.moreFilters': 'Más filtros',
  'hotelRooms.previousFilters': 'Filtros anteriores',
  'hotelRooms.outOfPolicy': 'Tarifa fuera de la política de viajes de tu empresa.',
  'hotelRooms.viewDetails': 'Ver detalles',
  'hotelRooms.roomAmenities': 'Lo que tiene la habitación',
  'hotelRooms.detailAmenities': 'Comodidades principales',
  'hotelRooms.detailOtherAmenities': 'Otras comodidades',
  'hotelRooms.deckToggleAria': 'Mostrar todas las habitaciones',
  'hotel.selectText': 'Quiero ver las habitaciones del {{hotel}}.',
  'hotelRooms.selectText': 'Quiero la {{room}} ({{rate}}) en el hotel {{hotel}}.',
  'hotelRooms.selectTextNoRate': 'Quiero la {{room}} en el hotel {{hotel}}.',
  'roomTaxonomy.type.QUARTO': 'Habitación',
  'roomTaxonomy.type.SUITE': 'Suite',
  'roomTaxonomy.type.STUDIO': 'Estudio',
  'roomTaxonomy.type.APARTAMENTO': 'Apartamento',
  'roomTaxonomy.type.CHALE': 'Chalet',
  'roomTaxonomy.type.BUNGALOW': 'Bungalow',
  'roomTaxonomy.type.LOFT': 'Loft',
  'roomTaxonomy.type.FLAT': 'Flat',
  'roomTaxonomy.type.CABANA': 'Cabaña',
  'roomTaxonomy.tier.ECONOMICO': 'Económico',
  'roomTaxonomy.tier.STANDARD': 'Standard',
  'roomTaxonomy.tier.CLASSIC': 'Classic',
  'roomTaxonomy.tier.JUNIOR': 'Junior',
  'roomTaxonomy.tier.CONFORT': 'Confort',
  'roomTaxonomy.tier.SUPERIOR': 'Superior',
  'roomTaxonomy.tier.EXECUTIVO': 'Ejecutivo',
  'roomTaxonomy.tier.DELUXE': 'Deluxe',
  'roomTaxonomy.tier.LUXO': 'Lujo',
  'roomTaxonomy.tier.PRIME': 'Prime',
  'roomTaxonomy.tier.PREMIUM': 'Premium',
  'roomTaxonomy.tier.MASTER': 'Master',
  'roomTaxonomy.beds.SINGLE_one': '{{count}} cama individual',
  'roomTaxonomy.beds.SINGLE_other': '{{count}} camas individuales',
  'roomTaxonomy.beds.DOUBLE_one': '{{count}} cama doble',
  'roomTaxonomy.beds.DOUBLE_other': '{{count}} camas dobles',
  'roomTaxonomy.beds.SOFA_one': '{{count}} sofá cama',
  'roomTaxonomy.beds.SOFA_other': '{{count}} sofás cama',
  'roomTaxonomy.beds.UNKNOWN_one': '{{count}} cama',
  'roomTaxonomy.beds.UNKNOWN_other': '{{count}} camas',
  'roomTaxonomy.bedrooms_one': '{{count}} dormitorio',
  'roomTaxonomy.bedrooms_other': '{{count}} dormitorios',
  'roomTaxonomy.capacity': 'hasta {{count}} personas',
  'roomTaxonomy.qualifier.NO_SMOKING': 'no fumador',
  'roomTaxonomy.qualifier.SMOKING': 'fumador',
  'roomTaxonomy.qualifier.NO_AC': 'sin aire acondicionado',
  'roomTaxonomy.qualifier.AC': 'con aire acondicionado',
  'roomTaxonomy.qualifier.SEA_VIEW': 'vista al mar',
  'roomTaxonomy.qualifier.GARDEN_VIEW': 'vista al jardín',
  'roomTaxonomy.qualifier.CITY_VIEW': 'vista a la ciudad',
  'roomTaxonomy.qualifier.VIEW': 'con vista',
  'roomTaxonomy.qualifier.BALCONY': 'con balcón',
  'roomTaxonomy.qualifier.PRIVATE': 'privado',
  'roomTaxonomy.qualifier.ACCESSIBLE': 'accesible',
  'roomTaxonomy.qualifier.KITCHEN': 'con cocina',
  'roomTaxonomy.qualifier.FAMILY': 'familiar',
  'hotelDetails.aria': 'Detalles del hotel',
  'hotelDetails.close': 'Cerrar',
  'hotelDetails.openInNewTab': 'Abrir en una pestaña nueva',
  'hotelDetails.loading': 'Cargando detalles del hotel',
  'hotelDetails.errorTitle': 'No pudimos cargar los detalles',
  'hotelDetails.errorBody': 'Inténtalo de nuevo en un momento.',
  'hotelDetails.retry': 'Intentar de nuevo',
  'hotelDetails.about': 'Sobre el hotel',
  'hotelDetails.chooseRoom': 'Elige la habitación',
  'hotelDetails.reserveRoom': 'Reservar',
  'hotelDetails.summary': 'Resumen',
  'hotelDetails.roomsTab': 'Habitaciones',
  'hotelDetails.roomsEmpty':
    'Este hotel no tiene habitación disponible para las fechas buscadas. Prueba otras fechas u otro hotel.',
  'hotelDetails.sectionsNavigation': 'Secciones del hotel',
  'hotelDetails.previousTabs': 'Secciones anteriores',
  'hotelDetails.nextTabs': 'Más secciones',
  'hotelDetails.aboutShort': 'Sobre',
  'hotelDetails.location': 'Ubicación',
  'hotelDetails.cancellation': 'Política de cancelación',
  'hotelDetails.cancellationFreeUntil': 'Cancelación gratis hasta {{date}}',
  'hotelDetails.cancellationCharged': 'La cancelación tiene cargo',
  'hotelDetails.cancellationFreeEnded': 'La cancelación gratis terminó el {{date}}',
  'hotelDetails.cancellationForRate': 'Condiciones de la tarifa {{rate}}',
  'hotelDetails.cancellationFrom': 'A partir del {{date}}',
  'hotelDetails.cancellationUntil': 'Hasta el {{date}}',
  'hotelDetails.cancellationBetween': 'Del {{from}} al {{until}}',
  'hotelDetails.cancellationPenalty': 'Cargo de {{amount}}',
  'hotelDetails.cancellationFree': 'Sin cargo',
  'hotelDetails.cancellationVaries': 'Otras tarifas pueden tener condiciones diferentes.',
  'hotelDetails.fromPlace': 'de {{place}}',
  'hotelDetails.readMore': 'Leer más',
  'hotelDetails.readLess': 'Leer menos',
  'hotelDetails.galleryAria': 'Fotos del hotel',
  'hotelDetails.thumbnailsNav': 'Miniaturas de las fotos',
  'hotelDetails.allPhotos': 'Ver todas las fotos',
  'hotelDetails.photoCounter': '{{position}} / {{total}}',
  'hotelDetails.goToPhoto': 'Ir a la foto {{position}}',
  'hotelDetails.amenities': 'Lo que ofrece este hotel',
  'hotelDetails.allAmenities': 'Ver todas las comodidades',
  'hotelDetails.fewerAmenities': 'Menos comodidades',
  'hotelDetails.amenitiesTitle': 'Comodidades ofrecidas',
  'hotelDetails.reviews': 'Opiniones',
  'hotelDetails.allReviews': 'Leer todas las opiniones',
  'hotelDetails.reviewBy': '{{author}} · {{when}}',
  'hotelDetails.noFilteredRooms': 'Ninguna habitación coincide con esos filtros',
  'hotelDetails.noFilteredRoomsHint': 'Suelta un filtro para ver las opciones de nuevo.',
  'hotelDetails.clearFilters': 'Limpiar filtros',
  'hotelDetails.allRoomsMeetFilter':
    'Todas las habitaciones de esta lista cumplen con “{{filter}}”.',
  'hotelReservation.checkin': 'Check-in',
  'hotelReservation.checkout': 'Check-out',
  'hotelRateWarning.title': 'Las condiciones de esta tarifa cambiaron',
  'hotelRateWarning.continue': 'Continuar de todos modos',
  'hotelRateWarning.chooseAnother': 'Elegir otra tarifa',
  'hotelRateWarning.continueText': 'Quiero continuar con las condiciones actualizadas.',
  'hotelRateWarning.chooseAnotherText': 'Quiero elegir otra tarifa en este hotel.',
  'hotelRateWarning.reason.breakfast_changed': 'El desayuno de esta tarifa cambió',
  'hotelRateWarning.reason.refundable_changed': 'La condición de reembolso de esta tarifa cambió',
  'hotelRateWarning.reason.non_refundable':
    'Esta tarifa ya no tiene cancelación gratis: ahora no es reembolsable',
  'hotelRateWarning.reason.free_cancellation_ended':
    'Esta tarifa ya no tiene cancelación gratis: cancelar ahora genera multa',
  'hotelRateWarning.reason.free_cancellation_until': 'Cancelación sin multa hasta {{date}}',
  'hotelRateWarning.policyTurnedOut': 'Esta tarifa quedó fuera de la política de viajes',
  'voucherCancel.aria': 'Confirmación de cancelación de voucher',
  'voucherCancel.leadPostEmission':
    'Este voucher está **emitido**: la cancelación será una **solicitud** (revisión manual) y el voucher quedará marcado como **no utilizado**.',
  'voucherCancel.k.voucher': 'Voucher',
  'voucherCancel.k.modal': 'Modalidad',
  'voucherCancel.k.route': 'Tramo',
  'voucherCancel.k.place': 'Lugar',
  'voucherCancel.k.status': 'Estado',
  'voucherCancel.k.reason': 'Motivo',
  'voucherCancel.k.when': 'Horario',
  'voucherCancel.ride.title': '¿Cancelar este viaje?',
  'voucherCancel.ride.keep': 'Mantener viaje',
  'voucherCancel.ride.sent': 'Cancelación solicitada',
  'voucherCancel.confirm': 'Confirmar cancelación',
  'voucherCancel.sent': 'Confirmación enviada',
  'voucherCancel.keep': 'Mantener voucher',
  'voucherCancel.replyKeep': 'No quiero cancelar.',
  'voucherCancel.replyConfirm': 'Confirmar cancelación.',
  'voucherCancelBulk.aria': 'Cancelación de vouchers en lote',
  'voucherCancelBulk.lead':
    'Selecciona los vouchers que quieres cancelar y toca **Cancelar seleccionados**.',
  'voucherCancelBulk.note':
    'Los vouchers **emitidos** pasan a ser una **solicitud** de cancelación (revisión manual) y quedan marcados como **no utilizado**.',
  'voucherCancelBulk.issued': 'Emitido',
  'voucherCancelBulk.submit': 'Cancelar seleccionados',
  'voucherCancelBulk.submitCount': 'Cancelar seleccionados ({{count}})',
  'voucherCancelBulk.sent': 'Cancelación enviada',
  'voucherCancelBulk.keepAll': 'Mantener todos',
  'voucherCancelBulk.replyKeepAll': 'No quiero cancelar ninguno.',
  'voucherCancelBulk.replySubmit': 'Cancelar los vouchers seleccionados.',
  'voucher.meta.heading': 'Voucher',
  'tripDates.oneWay': '{{d}} de {{month}}',
  'tripDates.sameMonth': 'del {{d1}} al {{d2}} de {{month}}',
  'tripDates.crossMonth': 'del {{d1}} de {{month1}} al {{d2}} de {{month2}}',
  'tripDates.crossYear': 'del {{d1}} de {{month1}} de {{y1}} al {{d2}} de {{month2}} de {{y2}}',
};

const messages = {
  'pt-BR': ptBR,
  en,
  es,
};

export function resolveGenUiLocale(locale: string | null | undefined): GenUiLocale {
  const normalized = locale?.replaceAll('_', '-').toLowerCase();
  if (normalized?.startsWith('en')) return 'en';
  if (normalized?.startsWith('es')) return 'es';
  return 'pt-BR';
}

export function genUiT(locale: string | null | undefined, key: GenUiKey, values?: Values): string {
  const resolved = resolveGenUiLocale(locale);
  const template = messages[resolved][key];
  return template.replaceAll(/{{\s*(\w+)\s*}}/g, (_match, name: string) => {
    const value = values?.[name];
    return value == null ? '' : String(value);
  });
}

/** A função de tradução que os cards recebem do hook — nomeada porque helpers
 *  puros (fora de componente) a recebem por parâmetro. */
export type GenUiTranslate = (key: GenUiKey, values?: Values) => string;
